/**
 * Mail-Tab (W1, 2026-07-07).
 *
 * Live-Inbox (email-service) + SF-Anreicherung, reine Lese-Ansicht.
 * Desktop = Split (Liste links ~380px, Detail rechts); Mobile = genau EINE
 * Pane (Liste ODER Detail-Vollansicht mit Zurück, kein Side-Drawer). Die
 * selektierte Mail lebt als URL-Param ?m=<encoded internet_message_id>, damit
 * Deep-Link + Browser-Back tragen.
 *
 * Kontrakt: siehe app/routers/mail.py. Body-HTML wird via DOMPurify sanitized;
 * externe Bilder (http/https) werden geblockt (Tracking-Schutz) + einmalig
 * ein Hinweis gezeigt.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  Mail as MailIcon,
  Paperclip,
  RefreshCw,
  ArrowLeft,
  Inbox as InboxIcon,
  ServerCrash,
  Link2,
  AlertTriangle,
  Sparkles,
  ClipboardCopy,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  X,
  FileText,
  FlaskConical,
  Building2,
  Settings as SettingsIcon,
  BarChart3,
  Package,
  ClipboardList,
  Globe,
  Rocket,
  Send,
  Loader2,
  Undo2,
  Table2,
  CornerDownLeft,
  Info,
  Check,
  ArrowDownLeft,
  ArrowUpRight,
  PanelRight,
  PanelRightClose,
  MessageSquare,
  AlarmClockPlus,
  AlarmClockCheck,
  Circle,
  CircleDot,
  CircleCheck,
  Triangle,
  ToggleLeft,
  ToggleRight,
  ReplyAll,
  Forward,
  MailPlus,
  MoreHorizontal,
  Archive,
  FolderInput,
  Search,
  SlidersHorizontal,
  Bell,
  BellRing,
  Bold,
  Italic,
  List,
  CalendarClock,
  CalendarPlus,
  Keyboard,
  MousePointerClick,
  UserRound,
  ExternalLink,
  SearchX,
  Download,
  HelpCircle,
} from "lucide-react";
import clsx from "clsx";
import { api, ApiError, apiUrl, authToken } from "../api";
import ContactCreate, { type ContactCandidate } from "../mail/ContactCreate";
import {
  SHORTCUTS,
  shortcutHint,
  shortcutKeys,
  shortcutLabel,
  isMailKbdHintSeen,
  setMailKbdHintSeen,
  isMailRowMenuHintSeen,
  setMailRowMenuHintSeen,
} from "../mail/shortcuts";
import {
  splitQuotedSegments,
  splitQuotedTextSegments,
  type QuoteHead,
} from "../mail/quoteSegments";
import {
  mdToHtml,
  preNormalizeBlocks,
  normalizeInlineTags,
  spliceSignature,
  closingToHtml,
  isBlankHtml,
  normalizeUrl,
  escapeHtml,
  EDITOR_ALLOWED_TAGS,
  EDITOR_ALLOWED_ATTR,
} from "./mailRichText";
import {
  detectTrigger,
  stripInlineMarkers,
  stripLeadingRepeat,
} from "./mailComposerAssist";
// Welches Datum eine Fahrplan-Stufe beschriftet (gesendet ≠ geplant, F-13) —
// eigene Datei, damit die Auswahl ohne Browser prüfbar ist.
import { berlinDayMonth, fahrplanStepDate, fmtDayMonth } from "./fahrplanStepDate";
import { useMailUnread, UnreadPill } from "../ui/mailUnread";
// Der Rahmen der beiden Kopf-Menüs (E1) — seit Etappe 2 geteilt mit dem
// Kalender, deshalb in components/ statt hier.
import { KopfMenu, KopfMenuItem } from "../components/KopfMenu";
import { rankColleagues, type Colleague } from "../ui/recipientRank";
// Personen-Vorschläge für die vier Empfänger-Felder des Nachfass-Dialogs.
// DIESELBE Such-/Rang-Mechanik wie der Kalender-Picker (Kollegen + externe
// Salesforce-Kontakte, max. 3 Treffer, 250 ms Debounce) — nur die Anzeige ist
// hier die kompakte Chips-Zeile. Warum nicht `ParticipantPicker` selbst:
// s. Kommentar an `useContactHits`.
import {
  CONTACT_DEBOUNCE_MS,
  MAX_HITS,
  MIN_CONTACT_QUERY,
  isExternalParticipant,
  isSameAddress,
  looksLikeEmailAddress,
  mergeHits,
  toParticipant,
  type ContactSuggestion,
} from "../ui/participants";
// „Termin aus dieser Mail" (P2.16): der bestehende Kalender-Wizard wird
// PREFILLED wiederverwendet (kein zweiter Kalender-Flow). Named export aus der
// Kalender-Seite; `Colleague` bleibt die EINE Quelle aus ../ui/recipientRank.
import { FindMeetingWizard } from "./CalendarMs";
import { parseGermanDate } from "../ui/parseGermanDate";
// Deutsche Datums-EINGABE — dieselbe Implementierung, die der Kalender nutzt
// (kein zweiter Parser). `parseGermanDate` oben ist etwas anderes: der Freitext-
// Erkenner für „Termin aus dieser Mail".
import {
  TIME_OPTIONS,
  deDateToIso,
  fmtDeDate,
  fmtIsoDate,
  isoToDeDate,
  joinLocalNaive,
  parseDeDate,
  splitLocalNaive,
  withCurrent,
} from "../ui/germanDate";
import {
  UNDO_SEND_OPTIONS,
  UNDO_SEND_DEFAULT_S,
  getUndoWindowSeconds,
  setUndoWindowSeconds,
} from "../ui/undoSendPref";
import {
  useInboxDepth,
  INBOX_DEPTH_PRESETS,
  INBOX_DEPTH_OPTIONS,
  nextInboxDepth,
} from "../ui/mailInboxDepth";
import { useAutoMarkRead } from "../ui/mailAutoMarkRead";
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  StatusBadge,
  Tabs,
  Textarea,
  useModal,
  useToast,
} from "../ui";
import type { BadgeStatus } from "../ui";
// Oberflaechen-Texte (docs/i18n.md). `useT()` in Komponenten, `katalog()` in
// Helfern und nach einem `await` — beide lesen denselben Sprach-Speicher.
import { useT } from "../i18n";
import { katalog } from "../i18n/lang";

// ---- Typen (Spiegel des API-Kontrakts) ------------------------------------

// Zustand der SF-Anreicherung. Die drei Miss-Zustaende sagen dem Nutzer etwas
// VERSCHIEDENES und duerfen nicht zusammenfallen (Spiegel von
// app/schemas/mail.py::EnrichmentStatus):
//   ready      Kontext liegt vor.
//   sf_pending Eine SF-Abfrage scheiterte -> TRANSIENT, kommt beim naechsten
//              Refresh ggf. doch.
//   no_match   Alle Abfragen liefen, SF kennt den Absender nicht -> TERMINAL.
//   internal   Interne Mail, es wurde bewusst kein Kundenkontext gesucht.
type EnrichmentStatus = "ready" | "sf_pending" | "no_match" | "internal";

interface InboxEnrichment {
  status: EnrichmentStatus;
  summary: string | null;
  thread_category: string | null;
  thread_sub_category: string | null;
  account_name: string | null;
  direction: string | null;
  sentiment: string | null;
  record_links_count: number;
}

interface MailRecipient {
  name: string | null;
  address: string;
}

interface InboxItem {
  graph_id: string;
  internet_message_id: string;
  subject: string;
  from_name: string;
  from_address: string;
  received_at: string;
  is_read: boolean;
  body_preview: string;
  has_attachments: boolean;
  importance: string;
  to_recipients: MailRecipient[];
  cc_recipients: MailRecipient[];
  enrichment: InboxEnrichment;
}

interface InboxResponse {
  mailbox_enabled: boolean;
  mailbox: string | null;
  fetched_at: string | null;
  messages: InboxItem[];
  // Nur der Suchpfad: Microsoft hat mehr Treffer, als in `messages` stehen.
  truncated?: boolean;
}

// Ordner-Liste (P1.5). `well_known_name` = inbox/sentitems/drafts/archive/… für
// Standard-Ordner, null für eigene Nutzer-Ordner.
// `folders` trägt die OBERSTE Ebene; jeder Knoten hängt seinen Unterbaum unter
// `children` (leer, wenn der Ordner keine Unterordner hat). Wer `children`
// ignoriert, sieht exakt die frühere flache Liste — der Vertrag ist additiv.
interface FolderOut {
  folder_id: string;
  display_name: string;
  well_known_name: string | null;
  total_count: number;
  unread_count: number;
  children?: FolderOut[];
}

interface FolderListResponse {
  mailbox_enabled: boolean;
  mailbox: string | null;
  folders: FolderOut[];
}

// Ein Ordner wird über seinen Well-Known-Namen angesteuert, sonst über die rohe
// Graph-Id — dieselbe Ableitung wie beim Klick, sonst findet der Auto-Aufklapper
// den aktiven Ordner nicht wieder.
const folderKey = (f: FolderOut) => f.well_known_name || f.folder_id;

// ── Suchbereich (F-35) ──────────────────────────────────────────────────────
// Ordner, die die Postfach-Suche EINZELN durchsuchen kann. Geschlossene Liste,
// wortgleich mit `MailSearchFolder` (app/clients/email_service.py:100-103) —
// der Server nimmt nichts anderes an, weil der Wert drüben im PFAD der
// Graph-URL landet. Ein EIGENER Unterordner hat keinen solchen Namen; dort
// bleibt die Suche postfachweit, und die Oberfläche sagt das dann auch.
// `folderView` trägt genau diese Namen, weil `folderKey` den
// `well_known_name` bevorzugt — kein zweites Vokabular.
// Hier steht nur die WHITELIST; die Anzeige-Namen liegen im Katalog
// (`mail.ordner.suchbereich`), damit es sie genau einmal gibt.
const SUCHBARE_ORDNER: Record<string, true> = {
  inbox: true,
  archive: true,
  sentitems: true,
  drafts: true,
  deleteditems: true,
  junkemail: true,
  outbox: true,
};

// Sichtbare Höhe der Ordnerliste im Verschieben-Dialog (Tailwind `max-h-80`).
// Als Zahl, weil die Höhe beim Filtern eingefroren wird — der Wert muss zur
// Klasse passen.
const MOVE_LIST_MAX_H = 320;

type FolderRow = {
  folder: FolderOut;
  depth: number;
  /** aufklappbar: hat Kinder */
  hasChildren: boolean;
  /** Namen der Vorfahren, oberste zuerst — „Posteingang", „Vorstand", … */
  path: string[];
};

/** Ein Zielordner-Vorschlag (F-15), wie ihn `GET /mail/folder-suggestions`
 *  liefert. Der Server schickt bewusst NUR die `folder_id` — Name und Pfad
 *  löst der Dialog aus dem Ordnerbaum auf, den er ohnehin geladen hat. Ein
 *  zweites Mal übertragene Namen wären ab der ersten Umbenennung falsch.
 *
 *  `domain`/`count`/`share` sind die BEGRÜNDUNG. Ohne sie wäre der Vorschlag
 *  eine Behauptung, die der Nutzer nicht nachvollziehen kann — und ein
 *  unbegründeter Vorschlag ist genau der, dem man nach dem ersten Fehlgriff
 *  nie wieder folgt. */
type FolderSuggestion = {
  folder_id: string;
  domain: string;
  count: number;
  share: number;
};

/** Voller Ordnerpfad als Tooltip: „Posteingang / Vorstand / Strategy / China".
 *
 *  Die Einrückung ist ab Tiefe 4 gedeckelt (sonst frisst sie bei Namen bis 49
 *  Zeichen den Text auf), Ebene 5 und 6 sähen dadurch gleich tief aus. Der Pfad
 *  im `title` macht die Ebene wieder eindeutig, ohne die Breite zu sprengen. */
const folderTitle = (row: FolderRow) =>
  [...row.path, row.folder.display_name].join(" / ");

// Sichtbare Zeilen des Baums (Vorordnung), abhängig davon was aufgeklappt ist.
//
// KEINE Tiefengrenze: bis 2026-08 wurden nur drei Ebenen dargestellt und die
// restlichen Ordner (bei wolfgang.hansen 32 von 66) trugen nur noch eine
// „+N"-Marke mit Outlook-Hinweis. Der Melder eines Tester-Befunds konnte seinen
// Unterordner deshalb nicht ansteuern. Das Backend erlaubt jede Tiefe — die
// Move-Whitelist läuft rekursiv über den ganzen Baum und GET
// /mail/folder/{id} nimmt jede Id.
function flattenFolderTree(
  nodes: FolderOut[],
  expanded: Set<string>,
  depth = 0,
  path: string[] = []
): FolderRow[] {
  const rows: FolderRow[] = [];
  for (const folder of nodes) {
    const kids = folder.children ?? [];
    const hasChildren = kids.length > 0;
    rows.push({ folder, depth, hasChildren, path });
    if (hasChildren && expanded.has(folder.folder_id)) {
      rows.push(
        ...flattenFolderTree(kids, expanded, depth + 1, [
          ...path,
          folder.display_name,
        ])
      );
    }
  }
  return rows;
}

// Postfach-Umschalter (P2.13): server-berechnete Menge = eigenes Postfach
// (`active`) + freigegebene Funktionspostfächer (Allow-List ∩ email-service-
// Katalog). Der Client kann NUR aus dieser Menge wählen.
interface MailboxOut {
  upn: string;
  display: string;
  /** Server-Schlüssel für Namen, die die OBERFLÄCHE bestimmt (heute nur
   *  ``own_mailbox`` = „Mein Postfach"). Die Namen der Funktionspostfächer sind
   *  dagegen DATEN und kommen unverändert aus dem email-service — sie werden
   *  nicht übersetzt. Fehlt das Feld (älteres Backend), bleibt es bei
   *  ``display``. */
  display_key?: string | null;
}

interface MailboxListResponse {
  active: string;
  mailboxes: MailboxOut[];
}

interface RecordLink {
  type: string | null;
  confidence: number | null;
  target_kind: string | null;
  target_id: string | null;
  name: string | null;
  // Handlungsrelevante Sachfelder des Ziel-Datensatzes — je Objekttyp anders
  // belegt (s. RECORD_GROUPS). Nur befüllte Werte kommen mit.
  //
  // OPTIONAL, und das ist Absicht: `fields`/`url` gibt es erst seit diesem
  // Branch. Ein Backend davor (Deploy-Fenster zwischen API- und Frontend-
  // Container, Backend-Rollback) liefert sie NICHT — verifiziert an echten
  // Prod-Payloads. Als Pflichtfeld deklariert war das eine Lüge gegenüber dem
  // Compiler, und der Zugriff `link.fields[…]` riss die GANZE Seite mit
  // (weiße Seite statt Postfach). Optional lassen = TS erzwingt jeden Zugriff.
  fields?: Record<string, string | number | null>;
  // SF-Deep-Link (serverseitig gebaut). null/fehlend → nur Name, kein Link.
  url?: string | null;
}

// Absender der Mail als SF-Datensatz (Contact bzw. offener Lead). Bei `lead`
// steht die Lead-`Company` an der Account-Stelle.
interface SenderContact {
  kind: "contact" | "lead";
  id: string | null;
  name: string | null;
  title: string | null;
  email: string | null;
  account_id: string | null;
  account_name: string | null;
}

interface MessageEnrichment {
  status: EnrichmentStatus;
  summary: string | null;
  direction: string | null;
  sentiment: string | null;
  urgency: string | null;
  thread_id: string | null;
  thread_subject: string | null;
  thread_summary: string | null;
  thread_category: string | null;
  thread_sub_category: string | null;
  account_id: string | null;
  account_name: string | null;
  mail_domain: string | null;
  record_links: RecordLink[];
  // true = Link-Abfrage gescheitert, `record_links` ist UNBEKANNT statt leer.
  // Optional wie `fields`/`url` (s.o.): erst seit diesem Branch im Kontrakt.
  record_links_degraded?: boolean;
  sender_contact?: SenderContact | null;
}

interface MessageResponse {
  graph_id: string;
  internet_message_id: string;
  subject: string;
  from_name: string;
  from_address: string;
  received_at: string;
  is_read: boolean;
  has_attachments: boolean;
  importance: string;
  body_html: string | null;
  body_text: string | null;
  to_recipients: MailRecipient[];
  cc_recipients: MailRecipient[];
  enrichment: MessageEnrichment;
  // F-11: nur gesetzt, wenn die Mail eine Termin-Nachricht IST. ``null`` heißt
  // „normale Mail" — die drei Antwort-Schaltflächen bleiben dann weg.
  meeting_message_type?: string | null;
  // Termin-Id aus demselben Graph-Aufruf: damit geht die Zu-/Absage direkt
  // raus, ohne dass der Nutzer den Termin erst im Kalender suchen muss.
  event_id?: string | null;
  event_response?: string | null;
}

/** Alle Anlagen EINER Mail samt Bytes (Spiegel von ``AttachmentBundleResponse``)
 *  — die Grundlage von „Alle herunterladen (n)". */
interface AttachmentBundleResponse {
  attachments: {
    attachment_id: string;
    name: string;
    content_type: string;
    size: number;
    /** base64, so wie Graph es liefert. */
    content_bytes: string;
  }[];
  total_bytes: number;
}

/** base64 → Blob. ``atob`` gibt eine Zeichenkette mit Codepoints 0-255 zurück;
 *  daraus muss byteweise ein ``Uint8Array`` werden. Der naheliegende Weg über
 *  ``TextEncoder`` wäre falsch: er kodiert nach UTF-8 und macht aus jedem Byte
 *  ≥ 0x80 zwei — die Datei käme beschädigt an. */
function b64ZuBlob(b64: string, typ: string): Blob {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) buf[i] = bin.charCodeAt(i);
  return new Blob([buf], { type: typ || "application/octet-stream" });
}

/** Eine Anlage der geöffneten Mail (Spiegel von ``MessageAttachmentOut``).
 *  Metadaten only — die Bytes holt die Vorschau erst auf Klick. */
interface MessageAttachment {
  attachment_id: string;
  name: string;
  content_type: string;
  size: number;
  is_inline: boolean;
  content_id: string | null;
}

// ---- W2: Antwort-Vorschläge + Templates (Spiegel des API-Kontrakts) --------

interface ReplyVariant {
  approach_label: string;
  subject: string;
  body_md: string;
  used_sources: number[];
}

interface ReplySourceRef {
  n: number;
  title: string | null;
  locator: string | null;
}

interface ReplyContext {
  category: string | null;
  sub_category: string | null;
  templates_used: string[];
  ectoin_used: boolean;
  sources: ReplySourceRef[];
}

interface ReplySuggestResponse {
  variants: ReplyVariant[];
  context: ReplyContext;
}

interface Template {
  id: string;
  department: string;
  category: string;
  sub_category: string | null;
  name: string;
  body_md: string;
  usage_notes: string | null;
  language: string;
  active: boolean;
  source: string;
}

// Merge-Resolver (POST /mail/resolve-template) — Spiegel des API-Kontrakts.
interface MailAttachment {
  content_version_id: string;
  filename: string | null;
}
interface ResolveTemplateResult {
  filled: Record<string, string>;
  gaps: string[];
  unknown: string[];
  attachments: MailAttachment[];
  preview_md: string;
  sf_error: boolean;
}

// ---- Formatierungs-Helfer --------------------------------------------------

/** Listenzeile: heute → HH:mm, sonst kurzes Datum (dd. Mon). */
function fmtListTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
}

/** Detail-Kopf: volles Datum + Uhrzeit. */
function fmtFullDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** „Stand HH:mm:ss" aus fetched_at. */
function fmtStand(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Sanitized Body-HTML + Block externer Bilder. Skript/Style verboten;
 * <img src="http(s)/…" oder protokoll-relativ> wird entfernt und die Tatsache
 * per Flag zurückgemeldet (einmaliger Hinweis im UI).
 */
/**
 * Prüft, ob der Host einer (remote) img-src-URL zur vertrauten Domain gehört.
 * Erlaubt exakte Domain UND Subdomains; case-insensitive. Behandelt sowohl
 * absolute (http(s)://) als auch protokoll-relative (//host/…) URLs.
 * Nur echte http(s)-Hosts werden je als vertrauenswürdig eingestuft — javascript:/
 * data:/mailto: o.ä. haben keinen passenden Host und liefern deshalb false.
 */
function isTrustedImageSrc(src: string, trustedDomain: string | null | undefined): boolean {
  if (!trustedDomain) return false;
  const domain = trustedDomain.trim().toLowerCase().replace(/\.$/, "");
  if (!domain) return false;
  let host: string;
  try {
    // Protokoll-relative URLs (//host/…) auf https normalisieren, damit URL() greift.
    const normalized = /^\/\//.test(src) ? `https:${src}` : src;
    const parsed = new URL(normalized);
    // NUR http(s) vertrauen — data:/javascript:/blob: etc. nie durchlassen.
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
    host = parsed.hostname.toLowerCase();
  } catch {
    return false;
  }
  return host === domain || host.endsWith("." + domain);
}

/**
 * Stammt das Bild von der EIGENEN Herkunft — also von dem Server, der Atlas
 * gerade ausliefert?
 *
 * Warum es das braucht (F-28): seit `530b5be` hostet Atlas das Signatur-Logo
 * SELBST und schreibt es als absolute URL in jede Signatur
 * (`app/mail/signature.py:58/:74` → `{ATLAS_PUBLIC_BASE_URL}/logo-bitop-
 * signatur.png`, `app/config.py:233`). Die Fremdbild-Sperre ist älter als diese
 * Umstellung und kannte die eigene Herkunft nicht — sie hat damit das eigene
 * Logo aus jeder bitop-Signatur entfernt und „Externe Bilder ausgeblendet"
 * darüber geschrieben.
 *
 * Gewählt ist `window.location.hostname` und NICHT die serverseitige Basis-URL:
 *  - In Produktion sind beide dasselbe — das Frontend wird von genau dieser URL
 *    ausgeliefert (`app/config.py:233`), die Signatur-URL zeigt also per Bauart
 *    auf die eigene Herkunft.
 *  - Die Browser-Herkunft ist die Grenze, um die es SACHLICH geht: die Sperre
 *    existiert, damit ein Dritter nicht per Zählpixel erfährt, dass die Mail
 *    geöffnet wurde. Ein Bild vom eigenen Server verrät genau niemandem etwas
 *    Neues — der Server beantwortet ohnehin gerade jede Anfrage dieser Sitzung.
 *  - Sie kann nicht veralten: zieht Atlas auf eine andere Domain, wandert die
 *    Herkunft automatisch mit, während ein durchgereichter Konfigurationswert
 *    synchron nachgezogen werden müsste.
 *
 * Die Sperre selbst bleibt vollständig: NUR http(s) (kein `data:`/`javascript:`/
 * `blob:`) und NUR der eigene Host — das ist kein Freibrief für alle Bilder.
 * Nachladen kann darüber niemand etwas: die Anmeldung hängt an einem
 * Bearer-Token aus dem localStorage (`src/api.ts:37`), nicht an einem Cookie —
 * eine `<img>`-Anfrage trägt also keine Berechtigung mit sich.
 */
function isOwnOriginImage(src: string): boolean {
  const own = window.location.hostname.toLowerCase();
  if (!own) return false;
  try {
    // Gleiche Normalisierung wie oben: protokoll-relative URLs (//host/…).
    const normalized = /^\/\//.test(src) ? `https:${src}` : src;
    const parsed = new URL(normalized);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
    return parsed.hostname.toLowerCase() === own;
  } catch {
    return false;
  }
}

function sanitizeBody(
  html: string,
  trustedDomain?: string | null,
  trustAll = false
): { clean: string; blockedImages: boolean } {
  let blocked = false;
  const hook = (node: Element) => {
    if (node.tagName === "IMG") {
      const src = node.getAttribute("src") || "";
      if (/^(https?:)?\/\//i.test(src)) {
        // Eigene Herkunft (F-28) → immer durchlassen, UNABHÄNGIG von SF: das
        // Signatur-Logo liefert Atlas selbst aus. Vorher hing seine Sichtbarkeit
        // daran, ob Salesforce den Vorgang schon kannte (`account_id` entsteht
        // erst durch den asynchronen SF-Sync nach dem Versand, plus 10 min
        // Miss-Cache) — dasselbe Logo war deshalb erst weg und ~20 min später
        // da. Genau diese „Selbstheilung" hat der Melder beobachtet.
        //
        // SF-verifizierter Absender (account_id gematcht) → alle remote-Bilder
        // durchlassen (die Verify ist durch Salesforce gewährleistet). Sonst
        // nur Bilder der Absender-Domain; alle anderen entfernen + Banner-Flag.
        if (trustAll || isOwnOriginImage(src) || isTrustedImageSrc(src, trustedDomain)) {
          return;
        }
        blocked = true;
        node.parentNode?.removeChild(node);
      }
    }
  };
  DOMPurify.addHook("uponSanitizeElement", hook as (n: Node) => void);
  const clean = DOMPurify.sanitize(html, {
    FORBID_TAGS: ["script", "style"],
    FORBID_ATTR: ["srcset"],
  });
  DOMPurify.removeHook("uponSanitizeElement");
  return { clean, blockedImages: blocked };
}

// ---- #84: eingebettete Bilder (``cid:``) -----------------------------------
//
// Externe Absender hängen ihr Signatur-Logo NICHT als Datei an, sondern betten
// es ein: der Body trägt ``<img src="cid:image001.png@01DD20EE.9785B350">``,
// die Bytes liegen als inline-Anlage daneben. Löst das niemand auf, steht im
// Lesebereich ein kaputtes Bild-Symbol — gemessen bei 9 von 25 Mails im
// Posteingang, also der Normalfall und nicht der Sonderfall.

/**
 * Content-Id auf eine vergleichbare Form bringen. BEIDE Seiten sind unsauber,
 * und niemand räumt vorher auf: der email-service reicht Graphs ``contentId``
 * ungefiltert durch (``api/sb_mail.py:1434``), ``app/routers/mail.py:735``
 * ebenso. Damit landet hier die rohe MIME-Kopfzeile — je nach Absendersystem
 * ``<image001@…>`` ODER ``image001@…``. Der ``cid:``-Wert im HTML wiederum darf
 * laut RFC 2392 prozent-kodiert sein.
 *
 * Reihenfolge ist wichtig: ERST dekodieren, DANN die Klammern abziehen — bei
 * ``%3Cimage001%40x%3E`` stünden die Klammern sonst noch kodiert da und
 * blieben stehen.
 */
function cidSchluessel(roh: string): string {
  let s = (roh ?? "").trim();
  try {
    s = decodeURIComponent(s);
  } catch {
    // Kaputte %-Sequenz: lieber ungenau vergleichen als gar nicht auflösen.
  }
  return s.trim().replace(/^<+/, "").replace(/>+$/, "").trim().toLowerCase();
}

/**
 * Die ``cid:``-Referenzen EINES (bereits sanitisierten) Bodys, normalisiert.
 * Der Regex-Vortest ist die Kosten-Bremse: Mails ohne ``cid:`` — die große
 * Mehrheit — verlassen die Funktion, bevor ein DOM gebaut wird, und lösen
 * damit später keinen einzigen Abruf aus.
 */
function cidRefsAus(html: string | null): string[] {
  if (!html || !/cid:/i.test(html)) return [];
  if (typeof DOMParser === "undefined") return [];
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const raus = new Set<string>();
    for (const img of Array.from(doc.querySelectorAll("img"))) {
      const src = img.getAttribute("src") || "";
      if (/^cid:/i.test(src)) raus.add(cidSchluessel(src.slice(4)));
    }
    return [...raus].filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Schreibt ``cid:``-Referenzen auf die geholten Blob-URLs um.
 *
 * REIHENFOLGE — der sicherheitsrelevante Punkt: diese Funktion läuft
 * AUSSCHLIESSLICH auf dem Ergebnis von ``sanitizeBody`` (Mail.tsx:557), nie auf
 * dem Rohbody. Sie kann die Bereinigung also nicht umgehen. Andersherum ginge
 * es auch technisch nicht: DOMPurifys ``IS_ALLOWED_URI`` kennt ``cid`` (das
 * Attribut überlebt), aber NICHT ``blob`` — eine vor der Bereinigung gesetzte
 * Blob-URL würde DOMPurify wieder entfernen.
 *
 * Was hier hineingeschrieben wird, ist ausnahmslos App-eigen: eine Blob-URL aus
 * Bytes, die über die eigene Bearer-API kamen, oder ein fester Hinweistext. Der
 * einzige fremdbestimmte Wert — die Content-Id — dient NUR als Map-Schlüssel
 * und wird nie ins DOM geschrieben.
 */
function mitCidBildern(
  html: string,
  bilder: Map<string, string>,
  fertig: boolean
): string {
  if (!/cid:/i.test(html) || typeof DOMParser === "undefined") return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    for (const img of Array.from(doc.querySelectorAll("img"))) {
      const src = img.getAttribute("src") || "";
      if (!/^cid:/i.test(src)) continue;
      const url = bilder.get(cidSchluessel(src.slice(4)));
      if (url) {
        img.setAttribute("src", url);
        img.setAttribute("data-cid-bild", "aufgeloest");
      } else if (!fertig) {
        // Noch am Laden. ``src`` abziehen statt stehen lassen, sonst blitzt für
        // die Dauer des Abrufs genau das kaputte Symbol auf, das wir beheben.
        img.removeAttribute("src");
        img.setAttribute("data-cid-bild", "laedt");
      } else {
        // Endgültig nicht auflösbar (Anlage gelöscht, Abruf gescheitert). Ein
        // kaputtes Bild-Symbol wäre wieder der Ausgangszustand — also ein
        // ruhiger, sichtbarer Hinweis an seiner Stelle.
        const hinweis = doc.createElement("span");
        hinweis.setAttribute("data-cid-bild", "fehlt");
        hinweis.setAttribute(
          "class",
          "inline-flex items-center rounded border border-border bg-surface-2 " +
            "px-1.5 py-0.5 text-[11px] text-text-3 align-middle"
        );
        hinweis.textContent = katalog().mail.lesen.bildNichtVerfuegbar;
        img.replaceWith(hinweis);
      }
    }
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

/**
 * Holt die eingebetteten Bilder EINER Mail und gibt ``Content-Id → Blob-URL``
 * zurück.
 *
 * KOSTEN: der Abruf hängt allein daran, ob im Körper überhaupt ein ``cid:``
 * steht — nicht an ``has_attachments``. Zwei Gründe, beide gemessen:
 *  - ``has_attachments`` ist bei inline-only Mails FALSE (Graph zählt
 *    eingebettete Bilder nicht mit): 8 der 9 cid-Mails im Posteingang. Auf
 *    dieses Flag gegated würde nie ein Logo erscheinen.
 *  - Umgekehrt wiegt eine ungefilterte Anlagenliste bis zu 1,3 MB. Eine Mail
 *    ohne ``cid:`` darf sie deshalb nicht anfassen.
 * Der Abruf ist bewusst getrennt von dem der Chip-Leiste (Mail.tsx:6281): der
 * dortige läuft OHNE ``include_inline``, damit Signatur-Logos nicht als
 * „Anhänge" in der Leiste auftauchen. Diese Trennung bleibt bestehen.
 */
function useInlineBilder(
  imid: string | null,
  mailbox: string | null,
  clean: string | null
): { bilder: Map<string, string>; fertig: boolean } {
  const cids = useMemo(() => cidRefsAus(clean), [clean]);
  const cidKey = cids.join("|");
  const [bilder, setBilder] = useState<Map<string, string>>(new Map());
  const [fertig, setFertig] = useState(false);

  useEffect(() => {
    if (!cidKey || !imid) {
      setBilder(new Map());
      setFertig(false);
      return;
    }
    let abgebrochen = false;
    const erzeugte: string[] = [];
    const aufraeumen = () => {
      for (const u of erzeugte) URL.revokeObjectURL(u);
      erzeugte.length = 0;
    };

    (async () => {
      const neu = new Map<string, string>();
      try {
        const p = new URLSearchParams({ include_inline: "1" });
        if (mailbox) p.set("mailbox", mailbox);
        const liste = await api.get<MessageAttachment[]>(
          `/mail/attachments/${encodeURIComponent(imid)}?${p}`
        );
        const gesucht = new Set(cidKey.split("|"));
        for (const a of liste) {
          if (!a.content_id) continue;
          const key = cidSchluessel(a.content_id);
          if (!gesucht.has(key) || neu.has(key)) continue;
          const q = new URLSearchParams({ attachment_id: a.attachment_id });
          if (mailbox) q.set("mailbox", mailbox);
          // Bytes brauchen den Bearer — ein blankes <img src="/mail/attachment…">
          // hätte keinen Token dabei und liefe in 401.
          const r = await fetch(
            apiUrl(`/mail/attachment/${encodeURIComponent(imid)}?${q}`),
            { headers: { Authorization: `Bearer ${authToken() ?? ""}` } }
          );
          if (!r.ok) continue;
          const url = URL.createObjectURL(await r.blob());
          erzeugte.push(url);
          neu.set(key, url);
        }
      } catch {
        // Still: die Mail selbst steht da und ist lesbar. Was nicht aufgelöst
        // werden konnte, sagt gleich der Hinweis an Ort und Stelle — ein
        // Fehlerbanner über dem Text wäre lauter als der Schaden.
      }
      if (abgebrochen) {
        aufraeumen();
        return;
      }
      setBilder(neu);
      setFertig(true);
    })();

    // Nachricht gewechselt oder Ansicht zu: die Blob-URLs dieser Mail freigeben.
    // Ohne das wächst der Speicher mit jeder geöffneten Mail — die Bytes hängen
    // am Dokument, bis sie widerrufen werden.
    return () => {
      abgebrochen = true;
      aufraeumen();
      setBilder(new Map());
      setFertig(false);
    };
  }, [imid, mailbox, cidKey]);

  return { bilder, fertig };
}

// ---- Kleine Bausteine ------------------------------------------------------

function CtxBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-surface-3 text-text-2 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap">
      {children}
    </span>
  );
}

// Ordner-Chip (P1.5): dezente Pill für die Scope-Navigation (Posteingang /
// Gesendet / Entwürfe / eigene Ordner). Rein lesend — Ordner sind Outlook-
// Wahrheit, KEIN Auto-Move/Auto-Sort (Doktrin). `badge` = dezenter Ungelesen-
// Zähler.
function FolderChip({
  label,
  active,
  onClick,
  badge,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
        active
          ? "bg-accent-500 text-white"
          : "bg-surface-3 text-text-2 hover:bg-surface-2 hover:text-text"
      )}
    >
      {label}
      {badge != null && badge > 0 && (
        <span
          className={clsx(
            "rounded-full px-1.5 text-[10px] tabular-nums leading-tight",
            active ? "bg-white/20 text-white" : "bg-accent-500/15 text-accent-600"
          )}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

// Postfach-Umschalter (P2.13): dezentes Dropdown im PageHeader neben
// „Aktualisieren". NUR sichtbar bei >1 Eintrag (eigenes + ≥1 freigegebenes
// Funktionspostfach). Reine Klick-Aktion — KEIN Tastenkürzel (Doc 04). Auswahl
// eines Funktionspostfachs schaltet die Ansicht READ-ONLY (Lese-Postfach).
function MailboxSwitcher({
  mailboxes,
  activeUpn,
  onSelect,
}: {
  mailboxes: MailboxOut[];
  activeUpn: string;
  onSelect: (upn: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const mt = useT().mail;
  // „Mein Postfach" ist eine Beschriftung der OBERFLÄCHE und gehört damit in den
  // Katalog; der Server sagt über ``display_key`` nur, WELCHER Eintrag das ist
  // (app/schemas/mail.py::MailboxOut). Die Namen der Funktionspostfächer sind
  // Daten und bleiben, wie sie kommen — sie werden nicht übersetzt.
  const label = (m: MailboxOut) =>
    m.display_key === "own_mailbox" ? mt.postfach.eigenes : m.display;
  // Nur ein Postfach → kein Umschalter (der Normalfall ohne Funktionspostfach).
  if (mailboxes.length < 2) return null;
  const active =
    mailboxes.find((m) => m.upn.toLowerCase() === activeUpn.toLowerCase()) ??
    mailboxes[0];
  return (
    <div className="relative" data-mailbox-switcher>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={mt.postfach.wechseln}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-text-2 transition-colors hover:bg-surface-2 hover:text-text"
      >
        <InboxIcon className="w-4 h-4 shrink-0" />
        <span className="max-w-[10rem] truncate">{label(active)}</span>
        <ChevronDown
          className={clsx("w-3.5 h-3.5 transition-transform", open && "rotate-180")}
        />
      </button>
      {open && (
        <>
          {/* Klick außerhalb schließt (kein Doc-Listener). */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div
            role="listbox"
            className="absolute right-0 z-20 mt-1 min-w-[14rem] rounded-lg border border-border bg-surface py-1 shadow-lg"
          >
            {mailboxes.map((m) => {
              const isActive = m.upn.toLowerCase() === active.upn.toLowerCase();
              return (
                <button
                  key={m.upn}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  data-mailbox-option={m.upn}
                  onClick={() => {
                    setOpen(false);
                    if (!isActive) onSelect(m.upn);
                  }}
                  className={clsx(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors",
                    isActive
                      ? "bg-surface-3 text-text font-medium"
                      : "text-text-2 hover:bg-surface-2 hover:text-text"
                  )}
                >
                  <InboxIcon className="w-3.5 h-3.5 shrink-0 text-text-3" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{label(m)}</span>
                    <span className="block truncate text-[10px] text-text-3">
                      {m.upn}
                    </span>
                  </span>
                  {isActive && (
                    <Check className="w-3.5 h-3.5 shrink-0 text-accent-500" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---- E1: die beiden Kopf-Menüs ---------------------------------------------
//
// Der Seitenkopf trug acht Knöpfe nebeneinander (Kürzel-Chip, drei Dichte-
// Stufen, Postfach, Aktualisieren, Abwesenheitsnotiz, Neue Mail). Gemessen am
// 19.08.2026: 68 Bedienelemente auf dem Posteingang. Es fällt NICHTS weg —
// die Dichte zieht ins „Ansicht"-Menü, der Rest in den ···-Überlauf. Die
// TASTEN bleiben unverändert funktionsfähig; ihre SICHTBARKEIT wandert in
// kbd-Chips im Menü (Outlook-Muster, docs/mail-parity-research/04). F1 öffnet
// die Übersicht weiterhin — der Menüeintrag ist der zweite, sichtbare Weg.
//
// Der Rahmen selbst (`KopfMenu`/`KopfMenuItem`) liegt seit Etappe 2 in
// `components/KopfMenu.tsx` — der Kalender benutzt dieselbe Bauform, und der
// Esc-Fix darin darf nicht in zwei Kopien auseinanderlaufen. Am gerenderten
// Markup des Posteingangs ändert der Umzug nichts.

function ListRow({
  item,
  active,
  onSelect,
  tracked,
  onArchive,
  onDelete,
  onOpenMenu,
  menuEnabled = true,
  readOnly = false,
  allowTrack = true,
  zeigeEmpfaenger = false,
}: {
  item: InboxItem;
  active: boolean;
  onSelect: (imid: string) => void;
  tracked: boolean;
  onArchive: (imid: string) => void;
  onDelete: (imid: string) => void;
  /** Kontextmenü öffnen (Rechtsklick · Kontextmenü-Taste/Umschalt+F10 ·
   *  Langdruck). `trigger` = die Zeile selbst; dorthin geht der Fokus zurück. */
  onOpenMenu: (
    item: InboxItem,
    x: number,
    y: number,
    trigger: HTMLElement
  ) => void;
  /** false = für diese Zeile gibt es gar keine Aktion (fremdes Funktionspostfach).
   *  Dann wird auch der native Rechtsklick NICHT unterdrückt — ein leeres Menü
   *  anzubieten wäre schlechter als das Browser-Menü zu lassen. */
  menuEnabled?: boolean;
  // Ordner-Ansicht (P1.5): TRIAGE-FREI → keine Hover-Aktionen (kein Archiv/
  // Verschieben/Löschen/Read-Toggle). Ordner sind Outlook-Lese-Wahrheit.
  readOnly?: boolean;
  // F-04: der Nachverfolgungs-Marker ist als EINZIGE Aktion aus `readOnly`
  // herausgelöst — er ändert nichts am Postfach, sondern legt einen Atlas-Track
  // an. Der Melder markiert Mails aus „Gesendet"; bis heute ging das nur über
  // den Umweg Suchtreffer. Das Postfach-Gate bleibt davon UNBERÜHRT: in einem
  // fremden Funktionspostfach ist auch dieser Marker weg (`allowTrack={!sharedScope}`).
  allowTrack?: boolean;
  // F-27: Diese Zeile ist eine AUSGEHENDE Mail (Absender = eigenes Postfach) →
  // vorne steht der Empfänger, nicht der Absender. Sonst liest im Ordner
  // „Gesendet" jede Zeile den eigenen Namen und die Liste sagt nichts aus.
  zeigeEmpfaenger?: boolean;
}) {
  const mt = useT().mail.liste;
  const ct = useT().common;
  const unread = !item.is_read;
  // Empfänger-Anzeige (nur bei `zeigeEmpfaenger`): der erste Empfänger trägt
  // die Zeile, die übrigen stehen als „+n" daneben — ein Verteiler mit 20
  // Adressen darf die Zeile nicht sprengen. Das „+n" sitzt BEWUSST außerhalb
  // des abschneidenden Namens-Elements, sonst verschluckt ein langer erster
  // Name genau die Information, dass da noch mehr Empfänger sind.
  const empfaenger = item.to_recipients ?? [];
  const ersterEmpfaenger =
    empfaenger[0]?.name || empfaenger[0]?.address || mt.keinEmpfaenger;
  const weitereEmpfaenger = Math.max(0, empfaenger.length - 1);
  const longPress = useRef<number | null>(null);
  // Nach einem Langdruck folgt auf Touch trotzdem ein `click`. Ohne diese Sperre
  // öffnet der Fingerdruck das Menü UND die Mail — die Mail-Ansicht schöbe sich
  // sofort über das gerade geöffnete Menü. Gemerkt wird der ZEITPUNKT, nicht ein
  // Ja/Nein-Schalter: ein Schalter, der nie zurückgesetzt wird (Langdruck ohne
  // folgenden Klick), verschluckt sonst irgendwann einen echten Klick.
  const longPressAt = useRef(0);
  const cancelLongPress = () => {
    if (longPress.current !== null) {
      window.clearTimeout(longPress.current);
      longPress.current = null;
    }
  };
  return (
    <button
      type="button"
      data-mail-row={item.internet_message_id}
      onClick={() => {
        if (Date.now() - longPressAt.current < 800) return;
        onSelect(item.internet_message_id);
      }}
      // Der native Rechtsklick wird NUR hier unterdrückt — nicht global und
      // nicht im Lesebereich (dort will man Text markieren und kopieren).
      onContextMenu={(e) => {
        if (!menuEnabled) return;
        e.preventDefault();
        onOpenMenu(item, e.clientX, e.clientY, e.currentTarget);
      }}
      // Tastaturweg: Kontextmenü-Taste bzw. Umschalt+F10 auf der fokussierten
      // Zeile. `preventDefault` unterdrückt das daraus folgende `contextmenu`-
      // Ereignis, sonst ginge das Menü zweimal (an zwei Stellen) auf. Position:
      // linke obere Ecke der Zeile, leicht eingerückt — dort, wo bei der Maus
      // der Zeiger stünde.
      onKeyDown={(e) => {
        if (!menuEnabled) return;
        if (e.key === "ContextMenu" || (e.shiftKey && e.key === "F10")) {
          e.preventDefault();
          const r = e.currentTarget.getBoundingClientRect();
          onOpenMenu(item, r.left + 24, r.top + 24, e.currentTarget);
        }
      }}
      // Touch: langer Fingerdruck öffnet dieselbe Liste. Auf Android feuert
      // zusätzlich `contextmenu` (oben abgefangen), auf iOS nicht — deshalb der
      // eigene Zeitgeber. Jede Bewegung/jedes Loslassen bricht ihn ab, damit
      // Scrollen kein Menü öffnet.
      onTouchStart={(e) => {
        if (!menuEnabled) return;
        const t = e.touches[0];
        if (!t) return;
        const { clientX, clientY } = t;
        const el = e.currentTarget;
        cancelLongPress();
        longPress.current = window.setTimeout(() => {
          longPress.current = null;
          longPressAt.current = Date.now();
          onOpenMenu(item, clientX, clientY, el);
        }, 500);
      }}
      onTouchMove={cancelLongPress}
      onTouchEnd={cancelLongPress}
      onTouchCancel={cancelLongPress}
      className={clsx(
        "group w-full text-left px-3 py-2.5 border-b border-border transition-colors",
        active ? "bg-surface-3" : "hover:bg-surface-2"
      )}
    >
      <div className="flex items-center gap-2">
        {unread ? (
          <span
            className="w-2 h-2 rounded-full bg-accent-500 shrink-0"
            aria-label={mt.ungelesen}
          />
        ) : (
          <span className="w-2 h-2 shrink-0" />
        )}
        {/* Das „An:" bleibt sichtbar und wird nicht impliziert: Trefferlisten
            der Postfach-Suche mischen ein- und ausgehende Mails in EINER Liste
            — ohne die Beschriftung stünde in der einen Zeile ein Absender und
            in der nächsten ein Empfänger, beide gleich aussehend. */}
        {zeigeEmpfaenger && (
          <span className="shrink-0 text-xs text-text-3">{mt.an}</span>
        )}
        <span
          data-mail-row-name
          className={clsx(
            "flex-1 min-w-0 truncate text-sm",
            unread ? "font-semibold text-text" : "text-text"
          )}
          title={zeigeEmpfaenger ? empfaenger[0]?.address : item.from_address}
        >
          {zeigeEmpfaenger
            ? ersterEmpfaenger
            : item.from_name || item.from_address || mt.unbekannt}
        </span>
        {zeigeEmpfaenger && weitereEmpfaenger > 0 && (
          <span
            data-mail-row-mehr
            className="shrink-0 text-xs text-text-3"
            title={empfaenger
              .slice(1)
              .map((r) => r.name || r.address)
              .join(", ")}
          >
            +{weitereEmpfaenger}
          </span>
        )}
        {item.has_attachments && (
          <Paperclip className="w-3.5 h-3.5 text-text-3 shrink-0" aria-label={mt.anhang} />
        )}
        {/* Der Wecker ist hier NUR noch ZUSTANDS-Anzeige: „diese Mail steht unter
            Nachverfolgung". Genau deshalb bleibt er in der Zeile — wäre er mit
            ins Kontextmenü gewandert, sähe niemand mehr, welche Zeile verfolgt
            wird. Die AKTION „zur Nachverfolgung markieren" (früher der
            Hover-Zustand desselben Icons) liegt im Kontextmenü; ein Icon, das
            erst beim Hover auftaucht, war ohnehin nicht auffindbar. Kein
            `role="button"` mehr: ein Klick tat im markierten Zustand schon
            vorher nichts.
            F-04 gilt unverändert: der Marker steht BEWUSST außerhalb des
            `readOnly`-Blocks. Er schreibt nichts ins Postfach, sondern zeigt
            einen Atlas-Track — die Begründung „Ordner sind Outlook-Lese-
            Wahrheit" trägt für ihn nicht. Das `sharedScope`-Gate bleibt über
            `allowTrack` vollständig, und im Kontextmenü hängt die zugehörige
            Aktion an derselben Bedingung. */}
        {allowTrack && tracked && (
          <span
            data-mail-track
            aria-label={mt.inNachverfolgung}
            title={mt.inNachverfolgung}
            className="shrink-0 rounded p-0.5 text-accent-500"
          >
            <AlarmClockCheck className="w-3.5 h-3.5" />
          </span>
        )}
        {/* Ordner-Ansicht (P1.5): TRIAGE-FREI → die Postfach-ändernden Aktionen
            (Archiv / Löschen; früher auch Read-Toggle und Verschieben) entfallen.
            Ordner sind Outlook-Lese-Wahrheit, kein Triage-Weg. AUSGENOMMEN seit
            F-04: der Nachverfolgungs-Marker (Block oben) — er fasst das Postfach
            nicht an. Das Kontextmenü hängt an DEMSELBEN Gate (`rowMenuItems`). */}
        {!readOnly && (
          <>
        {/* Zero-Inbox (P0.3): Archiv + Löschen als Hover-Schnellaktionen in
            DERSELBEN Span-Gruppe wie der Nachverfolgungs-Marker (eine Leiste,
            kein zweiter Cluster). <span role=button> wegen <button>-Verschachtelung,
            e.stopPropagation() gegen das Row-onSelect. KEIN Tastenkürzel in R1
            (Doc 04) — reine Hover-Aktion; Sichtbarkeit über title mit Klartext.
            Es sind BEWUSST GENAU DIESE ZWEI und keine dritte: beide sind
            Ein-Klick-Ziele ohne Dialog und treffen fast jede Mail — dafür lohnt
            ein Symbol. Alles andere liegt im Kontextmenü: „In Ordner
            verschieben" öffnet ohnehin einen Dialog, und „Als (un)gelesen
            markieren" ist eine Aktion, keine Anzeige — der ZUSTAND steht im
            Ungelesen-Punkt am Zeilenanfang, der ohne Hover sichtbar ist. */}
        <span
          role="button"
          tabIndex={-1}
          data-mail-archive
          aria-label={mt.archivieren}
          title={mt.archivieren}
          onClick={(e) => {
            e.stopPropagation();
            onArchive(item.internet_message_id);
          }}
          className="shrink-0 rounded p-0.5 text-text-3 opacity-0 group-hover:opacity-100 hover:text-accent-500 hover:bg-surface-3 focus-visible:opacity-100 transition-colors cursor-pointer"
        >
          <Archive className="w-3.5 h-3.5" />
        </span>
        {/* Löschen: Graph soft-delete → „Gelöschte Elemente" (in Outlook
            wiederherstellbar) → bewusst KEIN Bestätigungs-Dialog. Entf-Bindung
            besitzt X1 (nicht hier). */}
        <span
          role="button"
          tabIndex={-1}
          data-mail-delete
          aria-label={ct.loeschen}
          title={ct.loeschen}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.internet_message_id);
          }}
          className="shrink-0 rounded p-0.5 text-text-3 opacity-0 group-hover:opacity-100 hover:text-danger hover:bg-surface-3 focus-visible:opacity-100 transition-colors cursor-pointer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </span>
          </>
        )}
        {/* F-30 — sichtbarer Griff zum Zeilen-Menü. Seit die Zeile auf zwei
            Symbole reduziert wurde (5de7e43), lag ALLES Übrige nur noch hinter
            dem Rechtsklick; der Hinweisstreifen dazu erscheint genau einmal und
            nie wieder. Der Melder hat es so beschrieben: „funktioniert, wenn
            man weiß, dass es das gibt — war ja vorher über das Dreipunkte-Menü
            zu finden" (Bentler, 18.08.2026). Also bekommt es einen Griff, und
            zwar den, nach dem gesucht wurde.
            Bewusst AUSSERHALB des `readOnly`-Blocks: in der Ordner-Ansicht gibt
            es keine Triage-Symbole, aber sehr wohl Menü-Einträge (Antworten,
            Weiterleiten, Nachverfolgen) — genau dort fehlte der Zugang.
            `role="button"` statt `<button>`, weil die Zeile selbst ein
            `<button>` ist (keine Verschachtelung), und der Fokus-Rückgabe-Anker
            ist die ZEILE, nicht dieses Symbol: das Symbol ist beim Schließen
            eines Dialogs längst nicht mehr unter dem Zeiger.
            Der Name ist BEWUSST „Aktionen" und nicht „Weitere Aktionen" — so
            heißt das …-Menü der GEÖFFNETEN Mail (`:4680`). Zwei Bedienelemente
            mit demselben zugänglichen Namen in einer Ansicht sind mehrdeutig,
            für Screenreader wie für jede Suche danach: im Walk griff
            `getByRole("button", {name:"Weitere Aktionen"}).first()` prompt die
            Zeile statt des Detail-Menüs. */}
        {menuEnabled && (
          <span
            role="button"
            tabIndex={-1}
            data-mail-more
            aria-label={mt.aktionen}
            title={mt.aktionenTitel}
            onClick={(e) => {
              e.stopPropagation();
              const griff = e.currentTarget as HTMLElement;
              const r = griff.getBoundingClientRect();
              const zeile = griff.closest("[data-mail-row]") as HTMLElement | null;
              onOpenMenu(item, r.right, r.bottom, zeile ?? griff);
            }}
            className="shrink-0 rounded p-0.5 text-text-3 opacity-0 group-hover:opacity-100 hover:text-accent-500 hover:bg-surface-3 focus-visible:opacity-100 transition-colors cursor-pointer"
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </span>
        )}
        <span className="text-[11px] tabular-nums text-text-3 shrink-0">
          {fmtListTime(item.received_at)}
        </span>
      </div>
      <div
        className={clsx(
          "mt-0.5 truncate text-sm",
          unread ? "font-medium text-text" : "text-text-2"
        )}
      >
        {item.subject || mt.keinBetreff}
      </div>
      <div className="mt-0.5 truncate text-xs text-text-3">{item.body_preview}</div>
      {(item.enrichment.thread_category ||
        item.enrichment.account_name ||
        item.enrichment.status === "sf_pending" ||
        item.importance === "high") && (
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          {item.importance === "high" && (
            <StatusBadge status="warning">{mt.wichtig}</StatusBadge>
          )}
          {item.enrichment.thread_category && (
            <CtxBadge>{item.enrichment.thread_category}</CtxBadge>
          )}
          {item.enrichment.account_name && (
            <CtxBadge>{item.enrichment.account_name}</CtxBadge>
          )}
          {/* Chip NUR im transienten Fall. Bei `no_match`/`internal` waere ein
              Hinweis eine Luege — und ein Chip „nicht in Salesforce" auf vielen
              Zeilen macht die Liste unruhig; die fehlende Kontext-Angabe sagt
              dort schon alles, die Erklaerung steht im Panel rechts.
              Text nennt die Ursache (SF nicht erreichbar) statt „folgt" — sonst
              sieht ein Salesforce-Ausfall in der Liste aus wie normales Laden. */}
          {item.enrichment.status === "sf_pending" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-surface-2 text-text-3 border border-border px-2 py-0.5 text-[11px] whitespace-nowrap">
              <AlertTriangle className="w-3 h-3 text-warning" />
              {mt.sfNichtErreichbar}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

function ListSkeleton() {
  return (
    <div className="divide-y divide-border">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="px-3 py-2.5 space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="w-2 h-2 rounded-full" />
            <Skeleton className="h-3.5 flex-1 max-w-[55%]" />
            <Skeleton className="h-3 w-10" />
          </div>
          <Skeleton className="h-3.5 w-3/4" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

// ---- Detail-Pane -----------------------------------------------------------

/**
 * Entfernt das angehaengte Salesforce-Referenz-Token ` [SF:<id>]` aus einem
 * Anzeige-String. Es wird AUSSCHLIESSLICH das bracketed [SF:…]-Token entfernt
 * (SF-id = alphanumerisch, i.d.R. 15-18 Zeichen) — der uebrige Text bleibt
 * unveraendert und vollstaendig. Trailing whitespace wird getrimmt.
 * Exportiert fuer Unit-Belegbarkeit (kein Frontend-Testrunner vorhanden).
 */
export function stripSfId(text: string): string {
  return text.replace(/\s*\[SF:[A-Za-z0-9]+\]/g, "").replace(/\s+$/, "");
}

/**
 * Zerlegt eine thread_summary-Zeile in optionalen Prefix `[YYYY-MM-DD|IN|OUT]`
 * + Rest-Text. Robust: matcht der Prefix nicht, kommt die (bereits SF-id-freie)
 * Zeile komplett als `text` zurueck (date/dir = null).
 */
function parseThreadLine(rawLine: string): {
  date: string | null;
  dir: string | null;
  text: string;
} {
  const clean = stripSfId(rawLine).trim();
  // Richtung |IN|OUT ist optional: ältere Summaries haben nur `[YYYY-MM-DD]`.
  // Ohne Richtung wird das Datum trotzdem abgesetzt (nur ohne Pfeil).
  const m = clean.match(/^\[(\d{4}-\d{2}-\d{2})(?:\|(IN|OUT))?\]\s*(.*)$/);
  if (m) return { date: m[1], dir: m[2] ?? null, text: m[3] };
  return { date: null, dir: null, text: clean };
}

/** Die drei Nicht-ready-Zustände, jeder mit eigenem Wortlaut. Vorher liefen alle
 *  drei unter „die Anreicherung ist noch unterwegs" — ein Ladezustand, der bei
 *  `no_match` nie endet: der Nutzer wartet auf Daten, die nie kommen, und kann
 *  nicht erkennen, dass der Absender in Salesforce gar nicht existiert. */
function ContextMissCard({ status }: { status: EnrichmentStatus }) {
  const mt = useT().mail.kontext;
  const { Icon, text } =
    status === "sf_pending"
      ? {
          // NICHT „ist noch unterwegs": `enrich_emails` läuft synchron im
          // Request (app/routers/mail.py) — es ist nichts unterwegs. Wahr ist:
          // Salesforce hat nicht geantwortet, der nächste Versuch kommt beim
          // Aktualisieren. Wortlaut wie `ModuleError`, damit ein SF-Ausfall
          // im Panel überall gleich klingt und nicht wie Laden aussieht.
          Icon: AlertTriangle,
          text: mt.missPending,
        }
      : status === "internal"
        ? {
            Icon: UserRound,
            text: mt.missIntern,
          }
        : {
            // Terminal: als ERGEBNIS formuliert, nicht als Wartezustand. Sagt,
            // was der Fall ist, ohne zum Anlegen aufzufordern.
            Icon: SearchX,
            text: mt.missKeinTreffer,
          };
  return (
    <Card padding="sm" className="bg-surface-2" data-sf-context-miss={status}>
      <div className="flex items-start gap-2 text-sm text-text-2">
        <Icon className="mt-0.5 w-4 h-4 text-text-3 shrink-0" />
        <span>{text}</span>
      </div>
    </Card>
  );
}

function ContextCard({ enr }: { enr: MessageEnrichment }) {
  // D4: Thread-Historie initial ZUGEKLAPPT (Toggle). Hook VOR dem Miss-Return,
  // damit die Hook-Reihenfolge über alle Renderpfade stabil bleibt.
  const [threadOpen, setThreadOpen] = useState(false);
  const mt = useT().mail.kontext;
  if (enr.status !== "ready") {
    return <ContextMissCard status={enr.status} />;
  }

  const showThreadSummary =
    enr.thread_summary && enr.thread_summary !== enr.summary;
  const threadLines = showThreadSummary
    ? enr
        .thread_summary!.split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .map(parseThreadLine)
        .filter((l) => l.text.length > 0)
    : [];
  const kat = [enr.thread_category, enr.thread_sub_category]
    .filter(Boolean)
    .join(" › ");

  return (
    <Card padding="sm" data-sf-context className="bg-surface-2 space-y-3">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-text-3">
        <Link2 className="w-3.5 h-3.5" />
        {mt.sfKontext}
      </div>

      {enr.summary && (
        <p className="text-sm text-text leading-relaxed">{enr.summary}</p>
      )}
      {threadLines.length > 0 && (
        <div className="rounded-lg border border-border bg-surface">
          {/* D4: Thread-Historie initial ZUGEKLAPPT — oben bleibt nur die
              Zusammenfassung (enr.summary) sichtbar. Klick klappt die Zeilen auf.
              Gate: thread_summary !== summary (in threadLines eingebaut). */}
          <button
            type="button"
            data-ctx-thread-toggle
            onClick={() => setThreadOpen((o) => !o)}
            aria-expanded={threadOpen}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left"
          >
            <MessageSquare className="shrink-0 w-3.5 h-3.5 text-text-3" aria-hidden="true" />
            <span className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-text-3">
              {mt.threadVerlauf}
            </span>
            <span className="shrink-0 text-[11px] text-text-3 tabular-nums" aria-hidden="true">
              {threadLines.length}
            </span>
            <span className="min-w-0 flex-1 truncate text-xs text-text-2">
              {threadOpen ? "" : enr.thread_subject ?? ""}
            </span>
            <ChevronDown
              className={clsx(
                "shrink-0 w-3.5 h-3.5 text-text-3 transition-transform",
                threadOpen && "rotate-180"
              )}
              aria-hidden="true"
            />
          </button>
          {threadOpen && (
            <ul data-ctx-thread-lines className="px-2.5 pb-2 space-y-1">
              {threadLines.map((line, i) => (
                <li
                  key={i}
                  className="flex items-baseline gap-2 text-sm text-text"
                  title={
                    line.date
                      ? `${line.date} · ${line.dir} — ${line.text}`
                      : line.text
                  }
                >
                  {line.date && (
                    <span className="shrink-0 flex items-baseline gap-1 font-mono text-[11px] text-text-3 tabular-nums">
                      {line.date}
                      {line.dir === "IN" ? (
                        <ArrowDownLeft className="w-3 h-3 text-text-3" aria-label={mt.eingehend} />
                      ) : line.dir === "OUT" ? (
                        <ArrowUpRight className="w-3 h-3 text-text-3" aria-label={mt.ausgehend} />
                      ) : null}
                    </span>
                  )}
                  <span className="min-w-0 flex-1 break-words">{line.text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {kat && <CtxBadge>{kat}</CtxBadge>}
        {enr.account_name && <CtxBadge>{mt.account(enr.account_name)}</CtxBadge>}
        {enr.direction && <CtxBadge>{enr.direction}</CtxBadge>}
        {enr.urgency && <CtxBadge>{mt.dringlichkeit(enr.urgency)}</CtxBadge>}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-text-3 mb-1.5">
          {mt.verknuepfteDatensaetze}
        </div>
        <RecordLinkGroups
          links={enr.record_links}
          degraded={enr.record_links_degraded}
        />
      </div>
    </Card>
  );
}

// ---- W3: Account-Kontext-Panel --------------------------------------------

interface RevenueYear {
  year: number;
  revenue: number;
  order_count: number;
}
interface RevenueData {
  years: RevenueYear[];
  total_revenue: number;
}
interface OrderItem {
  name: string | null;
  brandname: string | null;
  product: string | null;
  quantity: number | null;
  units: string | null;
  value: number | null;
  expected_delivery_date: string | null;
  stage: string | null;
}
interface OrdersData {
  count: number;
  orders: OrderItem[];
}
interface RegistrationItem {
  name: string | null;
  product_brand_name: string | null;
  brandname: string | null;
  country: string | null;
  type: string | null;
  status: string | null;
  launch_date: string | null;
  launch_year: number | null;
  is_launch: boolean;
}
interface RegistrationsData {
  count: number;
  registrations: RegistrationItem[];
}
interface AccountModule {
  key: string;
  title: string;
  data?: unknown;
  error?: string | null;
}
interface AccountContextResponse {
  account_id: string;
  account_name: string | null;
  modules: AccountModule[];
}

const MODULE_ORDER = [
  "revenue_by_year",
  "open_orders",
  "processing_orders",
  "registrations",
] as const;

// Nur noch das Symbol je Modul — Beschriftung und Beschreibung stehen im
// Katalog (`mail.kontext.modul`), s. docs/i18n.md („Nur Text in die Kataloge").
const MODULE_ICONS: Record<string, typeof BarChart3> = {
  revenue_by_year: BarChart3,
  open_orders: Package,
  processing_orders: ClipboardList,
  registrations: Rocket,
};

const PANEL_MODULES_KEY = "sb_mail_panel_modules";

// Ein-/Ausblend-Zustand des seitlichen Intelligence-Panels im Mail-Detail.
// Merkt sich die Wahl über Navigation/Mail-Wechsel hinweg.
//
// E1 (19.08.2026): der Default ist ZU — vorher stand das 440-px-Panel auf
// jedem Desktop dauerhaft offen und drückte die Lesespur bei 1280 px auf
// ~340 px zusammen. Wer es benutzt, klappt es einmal auf; ab dann trägt der
// localStorage-Wert die Entscheidung. Die freigegebene Wahl ist bewusst, dass
// auch bestehende Nutzer ohne gemerkten Wert zugeklappt starten.
const INTEL_OPEN_KEY = "sb_mail_intel_open";

// Breite des Intelligence-Slots im Mail-Detail, wenn OFFEN. Breit genug für
// die SF-Inhalte (Account + verknüpfte Datensätze), damit dort nichts hart
// umbricht / abgeschnitten wird. Beim Zuklappen wird dieser Slot NICHT mehr
// reserviert — nur eine schmale Toggle-Rail bleibt, und die Email-Spalte
// dehnt sich in die freigewordene Breite aus (Reflow beim Toggle ist gewollt).
const INTEL_PANEL_W = 440;

function readIntelOpen(): boolean {
  try {
    const v = localStorage.getItem(INTEL_OPEN_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    /* localStorage blockiert (Privatmodus) — dann gilt der Default unten. */
  }
  return false;
}

function writeIntelOpen(open: boolean): void {
  try {
    localStorage.setItem(INTEL_OPEN_KEY, open ? "1" : "0");
  } catch {
    /* still schlucken — Zustand bleibt für die Sitzung im React-State. */
  }
}

// Vom User einstellbare Breite des SF-Panels — gemerkt JE VIEWPORT-Breite
// (Bucket = auf 100px gerundet), damit sich an großem und kleinem Monitor
// unterschiedliche Breiten merken. Min 300, Max 55% des Viewports.
const INTEL_W_KEY = "sb_mail_intel_w";
const INTEL_W_MIN = 300;

function intelViewportBucket(): string {
  const w = typeof window !== "undefined" ? window.innerWidth : 1280;
  return String(Math.round(w / 100) * 100);
}

function clampIntelWidth(w: number): number {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const max = Math.max(360, Math.round(vw * 0.55));
  return Math.min(max, Math.max(INTEL_W_MIN, Math.round(w)));
}

function readIntelWidth(): number {
  try {
    const raw = localStorage.getItem(INTEL_W_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    const v = map[intelViewportBucket()];
    if (typeof v === "number" && Number.isFinite(v)) return clampIntelWidth(v);
  } catch {
    /* Fallback auf Default unten. */
  }
  return INTEL_PANEL_W;
}

function writeIntelWidth(w: number): void {
  try {
    const raw = localStorage.getItem(INTEL_W_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    map[intelViewportBucket()] = w;
    localStorage.setItem(INTEL_W_KEY, JSON.stringify(map));
  } catch {
    /* still schlucken — Breite bleibt für die Sitzung im React-State. */
  }
}

const nfEuro0 = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

function fmtEuroFull(v: number | null | undefined): string {
  if (v == null) return "—";
  return nfEuro0.format(v);
}
function fmtEuroCompact(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000)
    return `${(v / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} M€`;
  if (abs >= 10_000)
    return `${(v / 1_000).toLocaleString("de-DE", { maximumFractionDigits: 0 })} k€`;
  return `${v.toLocaleString("de-DE", { maximumFractionDigits: 0 })} €`;
}
function fmtDateDe(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
function fmtQty(q: number | null, units: string | null): string | null {
  if (q == null) return null;
  const n = q.toLocaleString("de-DE", { maximumFractionDigits: 0 });
  return units ? `${n} ${units}` : n;
}

function ModuleError() {
  const mt = useT().mail.kontext;
  return (
    <div className="flex items-center gap-2 text-xs text-text-3 bg-surface-2 border border-border rounded-md px-2.5 py-2">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-warning" />
      {mt.modulFehler}
    </div>
  );
}

// ---- Verknüpfte SF-Datensätze: Gruppen + Sachfelder je Objekt --------------
//
// Das Backend liefert je Verknüpfung `fields` mit den handlungsrelevanten
// Werten des Ziel-Datensatzes (Schlüssel = `_LINK_TARGETS` in
// app/mail/context.py). Hier steht, WELCHE davon in welcher Reihenfolge
// erscheinen. Reihenfolge der Gruppen ist fix, damit das Panel bei jeder Mail
// gleich aussieht. Die BESCHRIFTUNGEN liegen im Katalog
// (`mail.kontext.gruppe` / `.feld` / `.stufe`), nachgeschlagen über denselben
// Schlüssel, der hier steht.

type LinkFieldFormat = "text" | "date" | "euro" | "qty";

interface LinkFieldSpec {
  key: string;
  format?: LinkFieldFormat;
}

interface RecordGroupSpec {
  kind: string;
  /** Feld, das als Status-Chip rechts steht. */
  statusKey?: string;
  /** Status-Werte über `mail.kontext.stufe` übersetzen (nur Sales Positions). */
  translateStatus?: boolean;
  fields: LinkFieldSpec[];
}

const RECORD_GROUPS: RecordGroupSpec[] = [
  {
    kind: "Batch_Record",
    statusKey: "status",
    fields: [
      { key: "batch_no" },
      // SF-Feld ist Text im Format „2024-10" → NICHT als Datum formatieren.
      { key: "manufacturing_date" },
      { key: "expiry_date", format: "date" },
    ],
  },
  {
    kind: "Registration",
    statusKey: "status",
    fields: [
      { key: "country" },
      { key: "type" },
      { key: "launch_date", format: "date" },
    ],
  },
  {
    kind: "Sales_Position",
    statusKey: "stage",
    translateStatus: true,
    fields: [
      { key: "quantity", format: "qty" },
      { key: "value", format: "euro" },
      { key: "expected_delivery_date", format: "date" },
    ],
  },
  {
    kind: "Artwork",
    statusKey: "status",
    fields: [
      { key: "phase" },
      { key: "valid_from", format: "date" },
    ],
  },
  {
    kind: "Formulation_Group",
    statusKey: "status",
    fields: [],
  },
];

function fmtLinkField(
  spec: LinkFieldSpec,
  fields: Record<string, string | number | null>
): string | null {
  const raw = fields[spec.key];
  if (raw == null || raw === "") return null;
  switch (spec.format) {
    case "date":
      return fmtDateDe(String(raw));
    case "euro":
      return typeof raw === "number" ? fmtEuroFull(raw) : String(raw);
    case "qty": {
      const units = fields.units;
      return typeof raw === "number"
        ? fmtQty(raw, units == null ? null : String(units))
        : String(raw);
    }
    default:
      return String(raw);
  }
}

/**
 * Langen Text MITTIG kürzen: Anfang füllt die Breite (CSS-Ellipse), das Ende
 * bleibt IMMER stehen.
 *
 * Warum nicht `truncate`: SF-Namen unterscheiden sich am Ende. Drei echte
 * Batch-Records eines Threads heißen
 * ``bitop_2026/7_bitop EED02_OSD_MDD_IIa_1x10ML_Pharmaster DE FG_00920/_00921/
 * _00922`` — am Ende gekürzt sehen alle drei identisch aus, und drei nicht
 * unterscheidbare Karten sind schlimmer als eine.
 *
 * Bewusst CSS statt Zeichen-Rechnerei: das Panel ist breiten-verstellbar, eine
 * feste Zeichenzahl wäre bei jeder Breite falsch. Der Kopf schrumpft mit,
 * der Schwanz ist ``shrink-0``.
 */
function MiddleTruncate({
  text,
  tailChars = 9,
  className,
  title,
}: {
  text: string;
  tailChars?: number;
  className?: string;
  /** Voller Text als Tooltip (die Kürzung nimmt ja etwas weg). */
  title?: string;
}) {
  // Kurze Namen (oder kürzere als der Schwanz + etwas Kopf) NICHT zerlegen.
  const split = text.length > tailChars + 8;
  const head = split ? text.slice(0, text.length - tailChars) : text;
  const tail = split ? text.slice(text.length - tailChars) : "";
  return (
    <span
      title={title}
      className={clsx("inline-flex min-w-0 max-w-full items-baseline", className)}
    >
      <span className="min-w-0 truncate">{head}</span>
      {tail && (
        <span data-truncate-tail className="shrink-0 whitespace-pre">
          {tail}
        </span>
      )}
    </span>
  );
}

/** Eine Verknüpfung: Name + Konfidenz oben, Status-Chip + Sachfelder darunter. */
function RecordLinkCard({ link, spec }: { link: RecordLink; spec: RecordGroupSpec }) {
  const mt = useT().mail.kontext;
  const feldLabel = mt.feld as Record<string, string | undefined>;
  const stufeLabel = mt.stufe as Record<string, string | undefined>;
  // Ein Backend vor diesem Branch liefert `fields` nicht (s. RecordLink). Ohne
  // diesen Default warf der Zugriff, und mit ihm starb die komplette Mail-Seite.
  // Karte rendert dann eben nur Name + Konfidenz — weniger Inhalt, aber sichtbar.
  const fields = link.fields ?? {};
  const status = spec.statusKey ? fields[spec.statusKey] : null;
  const statusText =
    status == null || status === ""
      ? null
      : spec.translateStatus
        ? (stufeLabel[String(status)] ?? String(status))
        : String(status);
  const meta = spec.fields
    .map((f) => ({ label: feldLabel[f.key] ?? f.key, value: fmtLinkField(f, fields) }))
    .filter((m): m is { label: string; value: string } => m.value != null);
  // Voller Name im Tooltip — die Karte kürzt lange SF-Namen MITTIG (s.
  // MiddleTruncate): SF-Namen unterscheiden sich am ENDE (…_00920/_00921).
  const label = link.name ?? link.target_id ?? "—";
  const tooltip = link.url ? mt.inSfOeffnen(label) : label;

  return (
    <li className="rounded-lg bg-surface border border-border px-2.5 py-1.5">
      <div className="flex items-baseline justify-between gap-2">
        {link.url ? (
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            data-sf-record-link
            className="group min-w-0 flex-1 flex items-baseline gap-1 text-sm font-medium text-text hover:text-accent-600"
            title={tooltip}
          >
            <MiddleTruncate text={label} className="group-hover:underline" />
            <ExternalLink className="shrink-0 w-3 h-3 text-text-3 group-hover:text-accent-600" />
          </a>
        ) : (
          <MiddleTruncate
            text={label}
            title={tooltip}
            className="min-w-0 flex-1 text-sm font-medium text-text"
          />
        )}
        {link.confidence != null && (
          <span
            className="shrink-0 text-[11px] font-medium tabular-nums text-text-3"
            title={mt.konfidenz}
          >
            {/* SF Match_Confidence__c ist ein Percent-Feld auf Skala 0-100 —
                der Wert wird NICHT mit 100 multipliziert. */}
            {Math.round(link.confidence)} %
          </span>
        )}
      </div>
      {(statusText || meta.length > 0) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          {statusText && (
            <span className="inline-flex items-center rounded-full bg-surface-3 text-text-2 px-2 py-0.5 text-[11px] font-medium">
              {statusText}
            </span>
          )}
          {meta.map((m) => (
            <span key={m.label} className="text-[11px] text-text-3">
              <span className="text-text-3/80">{m.label}</span>{" "}
              <span className="text-text-2 tabular-nums">{m.value}</span>
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

/** Verknüpfte Datensätze, nach Objekttyp gruppiert (Überschrift + Anzahl).
 *  `degraded` = die SF-Abfrage scheiterte: dann ist die Liste UNBEKANNT, und das
 *  Panel darf nicht „keine verknüpften Datensätze" behaupten. Wortlaut bewusst
 *  wie `ModuleError` weiter unten, damit die Sprache im Panel konsistent ist. */
function RecordLinkGroups({
  links,
  degraded = false,
}: {
  links: RecordLink[];
  degraded?: boolean;
}) {
  const mt = useT().mail.kontext;
  const gruppeLabel = mt.gruppe as Record<string, string | undefined>;
  const groups = useMemo(() => {
    const byKind = new Map<string, RecordLink[]>();
    for (const l of links) {
      const k = l.target_kind ?? "unbekannt";
      const cur = byKind.get(k);
      if (cur) cur.push(l);
      else byKind.set(k, [l]);
    }
    const out: { spec: RecordGroupSpec; items: RecordLink[] }[] = [];
    for (const spec of RECORD_GROUPS) {
      const items = byKind.get(spec.kind);
      if (items?.length) out.push({ spec, items });
      byKind.delete(spec.kind);
    }
    // Defensiv: unbekannte Objekttypen (neues Lookup im Backend) fallen nicht
    // unter den Tisch, sondern hängen mit rohem Namen hinten dran.
    for (const [kind, items] of byKind) {
      out.push({ spec: { kind, fields: [] }, items });
    }
    return out;
  }, [links]);

  if (degraded) {
    return (
      <div
        data-sf-links-degraded
        className="flex items-center gap-2 text-xs text-text-3 bg-surface-2 border border-border rounded-md px-2.5 py-2"
      >
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-warning" />
        {mt.linksDegradiert}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <p className="text-[13px] italic text-text-3">{mt.keineLinks}</p>
    );
  }

  return (
    <div className="space-y-2.5" data-sf-record-groups>
      {groups.map((g) => (
        <div key={g.spec.kind} data-sf-record-group={g.spec.kind}>
          <div className="mb-1 flex items-baseline gap-1.5">
            <span className="text-[11px] font-semibold text-text-2">
              {gruppeLabel[g.spec.kind] ?? g.spec.kind.replace(/_/g, " ")}
            </span>
            <span className="text-[11px] tabular-nums text-text-3">
              {g.items.length}
            </span>
          </div>
          <ul className="space-y-1.5">
            {g.items.map((l, i) => (
              <RecordLinkCard
                key={`${l.target_id ?? l.name ?? "rl"}-${i}`}
                link={l}
                spec={g.spec}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** Balken-Chart (selbst gebaut, nur divs + Theme-Tokens). Content-First:
 *  min-width je Balken, horizontal scrollbar bei vielen Jahren. */
function RevenueChart({ data }: { data: RevenueData }) {
  const mt = useT().mail.kontext;
  const years = data.years ?? [];
  const currentYear = new Date().getFullYear();
  const max = Math.max(1, ...years.map((y) => y.revenue || 0));
  const MAX_BAR = 116; // px, lässt oben Platz für das Wert-Label

  if (years.length === 0) {
    return <div className="text-xs text-text-3">{mt.keineUmsatzdaten}</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="text-sm text-text">
          {mt.gesamt}{" "}
          <span className="font-semibold tabular-nums">
            {fmtEuroFull(data.total_revenue)}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-text-3">
          <span className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm bg-accent-500" />
            {mt.inklAuftragsbestand(currentYear)}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm bg-accent-200 dark:bg-accent-900/50" />
            {mt.istUmsatz}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto pb-1">
        <div className="flex items-stretch gap-3 min-w-min">
          {years.map((y) => {
            const barPx = Math.max(4, Math.round(((y.revenue || 0) / max) * MAX_BAR));
            const future = y.year >= currentYear;
            return (
              <div key={y.year} className="flex flex-col items-center min-w-[46px]">
                <div
                  className="flex flex-col justify-end items-center w-full"
                  style={{ height: MAX_BAR + 22 }}
                >
                  <div className="text-[10px] tabular-nums text-text-2 mb-0.5 whitespace-nowrap">
                    {fmtEuroCompact(y.revenue || 0)}
                  </div>
                  <div
                    className={clsx(
                      "w-9 rounded-t-md",
                      future
                        ? "bg-accent-500"
                        : "bg-accent-200 dark:bg-accent-900/50"
                    )}
                    style={{ height: barPx }}
                    title={mt.balkenTitel(y.year, fmtEuroFull(y.revenue), y.order_count)}
                  />
                </div>
                <div className="mt-1.5 text-[11px] tabular-nums text-text-3">
                  {y.year}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StageBadge({ stage }: { stage: string | null }) {
  const stufeLabel = useT().mail.kontext.stufe as Record<string, string | undefined>;
  if (!stage) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-surface-3 text-text-2 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap">
      {stufeLabel[stage] ?? stage}
    </span>
  );
}

function OrderRow({ o }: { o: OrderItem }) {
  const qty = fmtQty(o.quantity, o.units);
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-text truncate" title={o.name ?? undefined}>
          {o.name || "—"}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-3">
          {(o.brandname || o.product) && (
            <span className="truncate max-w-[220px]">
              {o.brandname || o.product}
            </span>
          )}
          {qty && <span className="tabular-nums">· {qty}</span>}
          {o.expected_delivery_date && (
            <span className="tabular-nums">· {fmtDateDe(o.expected_delivery_date)}</span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className="text-sm font-medium text-text tabular-nums">
          {fmtEuroFull(o.value)}
        </span>
        <StageBadge stage={o.stage} />
      </div>
    </div>
  );
}

function RegistrationRow({ r }: { r: RegistrationItem }) {
  const mt = useT().mail.kontext;
  const name = r.product_brand_name || r.brandname || r.name || "—";
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-text truncate" title={name}>
          {name}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-3">
          {r.country && (
            <span className="inline-flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {r.country}
            </span>
          )}
          {r.type && <span className="truncate">· {r.type}</span>}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {r.status && (
          <span className="inline-flex items-center rounded-full bg-surface-3 text-text-2 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap">
            {r.status}
          </span>
        )}
        {r.is_launch && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-accent-500 text-white px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap"
            title={
              r.launch_date ? mt.launchTitel(fmtDateDe(r.launch_date)) : mt.launch
            }
          >
            <Rocket className="w-3 h-3" />
            {mt.launch} {r.launch_date ? fmtDateDe(r.launch_date) : ""}
          </span>
        )}
      </div>
    </div>
  );
}

const LIST_VISIBLE = 8;

function ExpandableList<T>({
  items,
  render,
  emptyLabel,
  keyOf,
}: {
  items: T[];
  render: (item: T) => React.ReactNode;
  emptyLabel: string;
  keyOf: (item: T, i: number) => string;
}) {
  const [showAll, setShowAll] = useState(false);
  const mt = useT().mail.kontext;
  if (items.length === 0) {
    return <div className="text-xs text-text-3 py-1">{emptyLabel}</div>;
  }
  const visible = showAll ? items : items.slice(0, LIST_VISIBLE);
  return (
    <div>
      <div>{visible.map((it, i) => <div key={keyOf(it, i)}>{render(it)}</div>)}</div>
      {items.length > LIST_VISIBLE && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 text-xs text-accent-600 hover:underline"
        >
          {showAll ? mt.wenigerAnzeigen : mt.alleAnzeigen(items.length)}
        </button>
      )}
    </div>
  );
}

function ModuleBody({ mod }: { mod: AccountModule }) {
  const mt = useT().mail.kontext;
  if (mod.error) return <ModuleError />;
  if (mod.data == null)
    return <div className="text-xs text-text-3">{mt.keineDaten}</div>;

  if (mod.key === "revenue_by_year") {
    return <RevenueChart data={mod.data as RevenueData} />;
  }
  if (mod.key === "open_orders" || mod.key === "processing_orders") {
    const d = mod.data as OrdersData;
    return (
      <ExpandableList
        items={d.orders ?? []}
        keyOf={(o, i) => `${o.name ?? "o"}-${i}`}
        emptyLabel={
          mod.key === "open_orders"
            ? mt.keineOffenenBestellungen
            : mt.keineAuftraegeAbwicklung
        }
        render={(o) => <OrderRow o={o} />}
      />
    );
  }
  if (mod.key === "registrations") {
    const d = mod.data as RegistrationsData;
    return (
      <ExpandableList
        items={d.registrations ?? []}
        keyOf={(r, i) => `${r.name ?? "r"}-${i}`}
        emptyLabel={mt.keineRegistrierungen}
        render={(r) => <RegistrationRow r={r} />}
      />
    );
  }
  return null;
}

function ModuleSection({ mod }: { mod: AccountModule }) {
  const [open, setOpen] = useState(true);
  const module = useT().mail.kontext.modul as Record<
    string,
    { label: string; beschreibung: string } | undefined
  >;
  const Icon = MODULE_ICONS[mod.key] ?? BarChart3;
  const title = module[mod.key]?.label ?? mod.title;
  // Zähler-Badge, wenn das Modul eine Liste ist.
  let count: number | null = null;
  if (!mod.error && mod.data && typeof mod.data === "object") {
    const c = (mod.data as { count?: number }).count;
    if (typeof c === "number") count = c;
  }
  return (
    <div className="rounded-lg border border-border bg-surface" data-account-module={mod.key}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
      >
        <Icon className="w-4 h-4 text-text-3 shrink-0" />
        <span className="text-sm font-medium text-text flex-1 min-w-0 truncate">
          {title}
        </span>
        {count != null && (
          <span className="text-xs tabular-nums text-text-3">{count}</span>
        )}
        <ChevronDown
          className={clsx(
            "w-4 h-4 text-text-3 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-0.5">
          <ModuleBody mod={mod} />
        </div>
      )}
    </div>
  );
}

function readPanelSelection(): string[] | null {
  try {
    const raw = localStorage.getItem(PANEL_MODULES_KEY);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr) && arr.every((x) => typeof x === "string")) return arr;
  } catch {
    /* ignore */
  }
  return null;
}

function PanelConfigModal({
  current,
  onClose,
  onSaved,
}: {
  current: string[];
  onClose: () => void;
  onSaved: (modules: string[]) => void;
}) {
  const toast = useToast();
  const mt = useT().mail.kontext;
  const ct = useT().common;
  const module = mt.modul as Record<
    string,
    { label: string; beschreibung: string } | undefined
  >;
  const [sel, setSel] = useState<string[]>(current);
  const [saving, setSaving] = useState(false);

  const toggle = (key: string) =>
    setSel((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const save = async () => {
    // Reihenfolge nach MODULE_ORDER stabilisieren.
    const ordered = MODULE_ORDER.filter((k) => sel.includes(k));
    setSaving(true);
    try {
      // Bestehenden payload NICHT überschreiben: erst lesen, dann mail_panel mergen.
      let base: Record<string, unknown> = {};
      try {
        base = (await api.get<Record<string, unknown>>("/settings")) ?? {};
      } catch {
        /* GET-Fehler ist unkritisch — wir mergen dann auf leerem payload. */
      }
      await api.put("/settings", {
        ...base,
        mail_panel: { modules: ordered },
      });
      localStorage.setItem(PANEL_MODULES_KEY, JSON.stringify(ordered));
      toast.ok(katalog().mail.kontext.panelGespeichert);
      onSaved(ordered);
      onClose();
    } catch (e) {
      // Server-seitige Persistenz ist optional (Abteilungs-Default bleibt gültig);
      // die lokale Auswahl greift trotzdem sofort.
      localStorage.setItem(PANEL_MODULES_KEY, JSON.stringify(ordered));
      toast.err(
        katalog().mail.kontext.panelNichtGespeichert,
        e instanceof ApiError ? e.message : undefined
      );
      onSaved(ordered);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      icon={<SettingsIcon className="w-4 h-4" />}
      title={mt.panelKonfigurieren}
      description={mt.panelKonfigurierenBeschreibung}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {ct.abbrechen}
          </Button>
          <Button variant="primary" size="sm" onClick={() => void save()} loading={saving}>
            {ct.speichern}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        {MODULE_ORDER.map((key) => {
          const meta = module[key];
          const on = sel.includes(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              className={clsx(
                "w-full flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
                on ? "border-accent-300 bg-accent-50/50" : "border-border hover:bg-surface-2"
              )}
            >
              <span
                className={clsx(
                  "mt-0.5 w-5 h-5 rounded-md border grid place-items-center shrink-0",
                  on ? "bg-accent-500 border-accent-500 text-white" : "border-border"
                )}
              >
                {on && <Check className="w-3.5 h-3.5" />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-text">{meta?.label}</span>
                <span className="block text-xs text-text-3">{meta?.beschreibung}</span>
              </span>
            </button>
          );
        })}
        <div className="flex items-start gap-2 rounded-md bg-surface-2 border border-border px-3 py-2 text-xs text-text-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-text-3" />
          <span>
            {mt.panelStandardHinweisA}
            <span className="font-medium">{mt.panelStandardHinweisB}</span>
            {mt.panelStandardHinweisC}
          </span>
        </div>
      </div>
    </Modal>
  );
}

/** Absender-Steckbrief: WER schreibt hier, in welcher Funktion, für welchen
 *  Account. Steht ganz oben im Account-Modul, noch vor den Kennzahlen. */
function SenderContactRow({
  contact,
  hideOrg = false,
}: {
  contact: SenderContact;
  /** Firma weglassen, wenn der Karten-Kopf schon exakt dieselbe zeigt. */
  hideOrg?: boolean;
}) {
  const mt = useT().mail.kontext;
  const name = contact.name || contact.email;
  const org = hideOrg ? null : contact.account_name;
  return (
    <div
      data-sf-sender-contact
      className="flex items-start gap-2 rounded-lg border border-border bg-surface px-2.5 py-2"
    >
      <UserRound className="mt-0.5 w-4 h-4 shrink-0 text-text-3" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <span className="min-w-0 truncate text-sm font-medium text-text" title={name ?? undefined}>
            {name ?? mt.unbekannterAbsender}
          </span>
          {contact.kind === "lead" && (
            <span className="shrink-0 rounded-full bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium text-text-3">
              {mt.lead}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-text-2">
          {contact.title || (
            <span className="text-text-3">{mt.funktionNichtHinterlegt}</span>
          )}
        </div>
        {org && (
          <div className="mt-0.5 truncate text-xs text-text-3" title={org}>
            {org}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountPanel({
  accountId,
  accountName,
  senderContact,
}: {
  /** null = kein Account in SF verknüpft (dann nur der Absender-Steckbrief). */
  accountId: string | null;
  accountName: string | null;
  senderContact: SenderContact | null;
}) {
  const [data, setData] = useState<AccountContextResponse | null>(null);
  const [loading, setLoading] = useState(!!accountId);
  const [error, setError] = useState<ApiError | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [selection, setSelection] = useState<string[] | null>(() => readPanelSelection());
  const mt = useT().mail.kontext;

  const load = useCallback(async () => {
    if (!accountId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<AccountContextResponse>(
        `/mail/account-context/${encodeURIComponent(accountId)}`
      );
      setData(res);
    } catch (e) {
      setError(e as ApiError);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Angezeigte Module: Response, ggf. lokal gefiltert/geordnet nach der Auswahl.
  const modules = useMemo(() => {
    const all = data?.modules ?? [];
    if (!selection || selection.length === 0) return all;
    const byKey = new Map(all.map((m) => [m.key, m]));
    return selection.map((k) => byKey.get(k)).filter((m): m is AccountModule => !!m);
  }, [data, selection]);

  const name = data?.account_name ?? accountName;

  // OHNE SF-Account ist das keine Account-Karte: sie darf nicht „ACCOUNT" über
  // einen Namen schreiben, den Salesforce so nicht kennt (Lead-`Company` steht
  // in `account_name`, ohne dass ein Account existiert). Dann Kopf = Absender.
  const hasAccount = !!accountId;
  const HeaderIcon = hasAccount ? Building2 : UserRound;
  const headerTitle = hasAccount
    ? name || accountId
    : senderContact?.account_name || senderContact?.name || mt.ohneZuordnung;
  // Doppelung vermeiden: zeigt der Kopf exakt dieselbe Firma, lässt die
  // Absender-Zeile sie weg. Weicht sie ab (Kontakt gehört zu einem ANDEREN
  // Account als der Thread), bleibt sie stehen — das ist dann Information.
  const hideSenderOrg =
    !!senderContact?.account_name && senderContact.account_name === headerTitle;

  return (
    <Card padding="sm" className="bg-surface-2 space-y-3" data-sf-account-card>
      <div className="flex items-center gap-2">
        <HeaderIcon className="w-4 h-4 text-text-3 shrink-0" />
        <div className="min-w-0 flex-1">
          <div
            data-sf-account-label
            className="text-[11px] uppercase tracking-wide text-text-3"
          >
            {hasAccount ? mt.account_ : mt.absender}
          </div>
          <div className="text-sm font-semibold text-text truncate" title={headerTitle ?? undefined}>
            {headerTitle}
          </div>
        </div>
        {hasAccount && (
          <button
            type="button"
            onClick={() => setConfigOpen(true)}
            aria-label={mt.panelKonfigurieren}
            title={mt.panelKonfigurieren}
            className="p-1.5 rounded-md text-text-3 hover:text-text hover:bg-surface-3"
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Absender zuerst: mit WEM habe ich es zu tun, bevor die Account-Zahlen
          kommen. Bleibt auch während Ladevorgang/Fehler des Account-Kontexts
          stehen (der Kontakt kommt aus der Mail-Anreicherung, nicht aus diesem
          Fetch) — dadurch springt das Panel beim Laden nicht. */}
      {senderContact && (
        <SenderContactRow contact={senderContact} hideOrg={hideSenderOrg} />
      )}

      {!hasAccount && (
        <div className="text-xs text-text-3">{mt.keinAccount}</div>
      )}

      {loading && (
        <div className="space-y-2">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg border border-border bg-surface p-3 flex items-center justify-between gap-2">
          <span className="text-xs text-text-2">
            {error.status === 422
              ? mt.accountFehlerReferenz
              : mt.accountFehlerLaden}
          </span>
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw className="w-3.5 h-3.5" />}
            onClick={() => void load()}
          >
            {mt.erneut}
          </Button>
        </div>
      )}

      {!loading && !error && data && (
        <div className="space-y-2">
          {modules.length === 0 ? (
            <div className="text-xs text-text-3">{mt.keineModule}</div>
          ) : (
            modules.map((m) => <ModuleSection key={m.key} mod={m} />)
          )}
        </div>
      )}

      {configOpen && (
        <PanelConfigModal
          current={
            selection && selection.length > 0
              ? selection
              : (data?.modules ?? []).map((m) => m.key)
          }
          onClose={() => setConfigOpen(false)}
          onSaved={(mods) => {
            setSelection(mods);
            void load();
          }}
        />
      )}
    </Card>
  );
}

// ---- Zitat-Kette: neuer Text vs. Historie ----------------------------------
//
// Outlook/Word zitieren OHNE ``<blockquote>`` (in echten Prod-Bodies: 0 Tags) —
// die Grenze ist ein Zitat-KOPF (``From:``/``Von:`` + ``Sent:``/``Gesendet:``/
// ``An:``/``Betreff:``) bzw. der OWA-Anker ``#divRplyFwdMsg``/``#appendonsend``.
// Verifiziert an zwei echten Ketten (509.387 / 425.588 Zeichen body_html):
// die erste Grenze ist dort je ein ``div`` direkt unter ``div.WordSection1``.
//
// Doktrin: fehlerhaft WENIGER zeigen ist schlimmer als zu viel. Deshalb faltet
// diese Funktion nur, wenn sie es BEWEISEN kann — Invariante
// ``nonWs(neu) + nonWs(historie) === nonWs(gesamt)`` (exakt, ohne Whitespace).
// Schlägt sie fehl, gibt es keine Faltung und der Body bleibt vollständig.

/** Erster Zitat-Kopf-Token am Anfang des Elementtexts. */
const QUOTE_HEAD_START = /^\s*(?:Von|From|Gesendet|Sent)\s*:/i;
/** Bestätigende Tokens — ein einzelnes „From:" reicht als Beweis NICHT. */
const QUOTE_HEAD_TOKENS = [
  /\bVon\s*:/i,
  /\bFrom\s*:/i,
  /\bGesendet\s*:/i,
  /\bSent\s*:/i,
  /\bAn\s*:/i,
  /\bTo\s*:/i,
  /\bBetreff\s*:/i,
  /\bSubject\s*:/i,
];

const nonWs = (s: string) => s.replace(/\s+/g, "");

/** Tags, deren Anfang und Ende eine Zeilen-Grenze sind (wie ``innerText``). */
const BLOCK_TAGS = new Set([
  "ADDRESS", "ARTICLE", "ASIDE", "BLOCKQUOTE", "DD", "DIV", "DL", "DT", "FIELDSET",
  "FIGCAPTION", "FIGURE", "FOOTER", "FORM", "H1", "H2", "H3", "H4", "H5", "H6",
  "HEADER", "HR", "LI", "MAIN", "NAV", "OL", "P", "PRE", "SECTION", "TABLE",
  "TBODY", "TD", "TFOOT", "TH", "THEAD", "TR", "UL",
]);

/**
 * Anfangs-Text eines Elements MIT Zeilen-Separator an ``br``- und Block-Grenzen
 * (``innerText``-artig), abgebrochen nach ``limit`` Rohzeichen.
 *
 * Warum nicht ``textContent``: Outlook trennt die Zeilen des Zitat-Kopfs oft nur
 * durch ``<br>`` OHNE umgebenden Whitespace. ``textContent`` klebt sie dann
 * zusammen (``…a@b.deGesendet:``), die ``\b``-Wortgrenzen der
 * ``QUOTE_HEAD_TOKENS`` greifen nicht mehr, der Kopf bleibt unerkannt und die
 * Kette ungefaltet. ``innerText`` selbst ist hier keine Option: das Element
 * hängt in einem geparsten, nicht gerenderten Dokument (kein Layout).
 */
function headText(el: Element, limit: number): string {
  const walker = el.ownerDocument.createTreeWalker(
    el,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT
  );
  const open: Element[] = [];
  let out = "";
  for (let n = walker.nextNode(); n && out.length < limit; n = walker.nextNode()) {
    // Verlassene Blöcke schließen (``…</p>Text`` ist ebenfalls eine Grenze).
    while (open.length && !open[open.length - 1].contains(n)) {
      if (BLOCK_TAGS.has((open.pop() as Element).tagName)) out += " ";
    }
    if (n.nodeType === 3 /* Text */) {
      out += n.nodeValue || "";
      continue;
    }
    const e = n as Element;
    open.push(e);
    if (e.tagName === "BR" || BLOCK_TAGS.has(e.tagName)) out += " ";
  }
  return out;
}

/** Erste Zitat-Grenze in Dokument-Reihenfolge (äußerstes passendes Element). */
function findQuoteBoundary(root: HTMLElement): Element | null {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const el = n as Element;
    if (el.id === "divRplyFwdMsg" || el.id === "appendonsend") return el;
    // 4000 Rohzeichen sind nach dem Zusammenfalten des Whitespace sicher mehr
    // als die 500 Zeichen, in denen die Kopf-Tokens gesucht werden.
    const text = headText(el, 4000).replace(/\s+/g, " ").trim();
    if (!text || !QUOTE_HEAD_START.test(text)) continue;
    const head = text.slice(0, 500);
    const tokens = QUOTE_HEAD_TOKENS.filter((re) => re.test(head)).length;
    if (tokens >= 2) return el;
  }
  return null;
}

/**
 * Sichtbarer Text eines HTML-Schnipsels, whitespace-frei — Vergleichsschlüssel
 * für die Frage „steht das hier schon?".
 *
 * Verglichen wird TEXT, nicht HTML: ein fortgesetzter Entwurf kommt aus Graph
 * zurück und trägt dort fremdes Markup um denselben Wortlaut (aus
 * ``<p>A<br>B</p>`` werden gern zwei Absätze). Und der Whitespace fällt ganz
 * weg, weil er dabei WANDERT — ``textContent`` sieht das ``<br>`` nicht, wohl
 * aber den Zeilenumbruch, den Outlook zwischen zwei Tags schreibt. Gleiches
 * Vorgehen wie ``isBlankHtml`` (``mailRichText.ts:126``).
 */
function textKey(html: string): string {
  if (!html) return "";
  try {
    if (typeof DOMParser !== "undefined") {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return nonWs(doc.body.textContent ?? "");
    }
  } catch {
    /* kaputtes HTML → grobe Tag-Entfernung reicht als Vergleichsschlüssel */
  }
  return nonWs(html.replace(/<[^>]*>/g, " "));
}

/** Vergleichs-Schlüssel für „steht dieser Gruß schon da?": ohne Leerraum UND
 *  ohne die Satzzeichen, in denen sich zwei Schreibweisen desselben Grußes
 *  unterscheiden dürfen (Komma nach der Formel, Punkt am Zeilenende). Erwartet
 *  TEXT, nicht HTML — bei HTML erst ``textKey``. Bewusst NUR hier und nicht in
 *  ``textKey`` selbst: der ist der Schlüssel für „ist das IDENTISCH"
 *  (``bodyIsOnlyClosing``) und muss streng bleiben. */
const grussKey = (s: string) => nonWs(s).replace(/[.,;:!?]/g, "");

/**
 * Trägt der EIGENE Teil des Entwurfs die Verabschiedung schon?
 *
 * Ein fortgesetzter Entwurf hat beim letzten Öffnen bereits einen Block
 * bekommen und trägt ihn seither in seinem ``body_html``. Ohne diese Frage
 * bekäme er bei JEDEM Öffnen einen weiteren — gemessen stand nach dem zweiten
 * Öffnen „Mit freundlichen Grüßen Wolfgang Hansen" zweimal im Editor, und der
 * Autosave schrieb es prompt zurück.
 *
 * Nur der eigene Teil zählt, also alles VOR der Zitat-Grenze: im zitierten
 * Verlauf eines Threads steht dieselbe Verabschiedung aus einer früheren
 * EIGENEN Nachricht — die darf die neue nicht unterdrücken.
 *
 * Verglichen wird OHNE Satzzeichen (``grussKey``). Atlas erzeugt „Mit
 * freundlichen Grüßen**,**" (``app/mail/signature.py:72``), ein Mensch tippt in
 * Outlook üblicherweise ohne Komma (DIN 5008 kennt dort keins). Zählte das Komma
 * mit, erkennte die Frage den vorhandenen Gruß nicht und der fortgesetzte
 * Entwurf bekäme einen zweiten — gemessen, s. Abschnitt I2 in
 * ``frontend/abschlussblock-walk.mjs``.
 */
function ownPartHasClosing(
  root: HTMLElement,
  boundary: Element | null,
  blockHtml: string
): boolean {
  const closing = grussKey(textKey(blockHtml));
  if (!closing) return false;
  let own = root.textContent ?? "";
  if (boundary) {
    const range = root.ownerDocument.createRange();
    range.setStart(root, 0);
    range.setEndBefore(boundary);
    own = range.toString();
  }
  return grussKey(own).includes(closing);
}

/**
 * Abschlussblock (Task 57) in den Composer-Body legen — VOR eine ggf. vorhandene
 * Zitat-Historie, nie darunter. Unter dem Zitat wäre er praktisch unsichtbar
 * (der Nutzer müsste an der ganzen Kette vorbeiscrollen) und im gesendeten
 * Ergebnis falsch: eine Verabschiedung gehört an das Ende der EIGENEN Nachricht,
 * nicht an das Ende des zitierten Verlaufs.
 *
 * Die Grenze findet ``findQuoteBoundary`` — dieselbe Erkennung, mit der die
 * Leseansicht die Kette faltet, statt einer zweiten Wahrheit daneben.
 *
 * Steht die Verabschiedung im eigenen Teil schon, passiert NICHTS
 * (``ownPartHasClosing``) — ein fortgesetzter Entwurf bekommt keine zweite.
 *
 * Über dem Block steht IMMER ein leerer Absatz. Er ist beim leeren Entwurf das
 * Ziel der Schreibmarke ("wo fange ich an zu schreiben") und in jedem anderen
 * Fall die Zeile, auf der man weiterschreibt — der Editor setzt ``p``-Absätze
 * ohne Abstand (``[&_p]:my-0``), ohne diese Zeile klebte die Verabschiedung
 * direkt am letzten Satz.
 */
function insertClosingIntoBody(bodyHtml: string, blockHtml: string): string {
  if (!blockHtml) return bodyHtml;
  const block = `<p><br></p>${blockHtml}`;
  if (isBlankHtml(bodyHtml)) return block;
  try {
    if (typeof DOMParser !== "undefined") {
      const root = new DOMParser().parseFromString(bodyHtml, "text/html").body;
      const boundary = findQuoteBoundary(root);
      if (ownPartHasClosing(root, boundary, blockHtml)) return bodyHtml;
      if (boundary && boundary.parentNode) {
        const holder = root.ownerDocument.createElement("div");
        holder.innerHTML = block;
        while (holder.firstChild) {
          boundary.parentNode.insertBefore(holder.firstChild, boundary);
        }
        return root.innerHTML;
      }
    }
  } catch {
    /* kaputtes/ungeparstes HTML → anhängen ist der sichere Ausgang */
  }
  return `${bodyHtml}${block}`;
}

/**
 * ``Name <adresse@x>`` → ``Name``. Der Zitat-Kopf trägt beides in einem String;
 * ungekürzt frisst er die halbe Zeile und bricht mitten in der Adresse ab
 * (gemessen: „Bert Muster <b…"). Die vollständige Angabe bleibt im ``title``.
 * Steht nur eine Adresse da, bleibt sie stehen — dann ist sie der Name.
 */
function absenderName(s: string): string {
  const m = /^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/.exec(s);
  const name = m?.[1]?.trim();
  return name || (m?.[2] ?? s).trim();
}

/** Betreff ohne Antwort-/Weiterleitungs-Präfixe, für den Vergleich „ist das noch
 *  dasselbe Thema?". ``AW:``/``Re:``/``WG:``/``Fwd:`` stapeln sich über eine
 *  Kette hinweg beliebig tief, deshalb wiederholt abschneiden. */
const betreffKern = (s: string | null): string =>
  (s ?? "")
    .replace(/^\s*(?:re|aw|antw|wg|fw|fwd|tr|rv)\s*(?:\[\d+\])?\s*:\s*/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/** Ein Abschnitt, wie die Ansicht ihn braucht — HTML- und Klartext-Zerleger
 *  liefern dieselbe Form, nur das Renderfeld unterscheidet sich. */
type Abschnitt = { head: QuoteHead | null; preview: string; html?: string; text?: string };

/**
 * Body-Renderer für EINE Nachricht: sanitisieren, in die mitgeschickten
 * Iterationen zerlegen (``splitQuotedSegments``), den neuen Text normal zeigen
 * und die früheren als einzelne, zugeklappte Einträge darunter.
 *
 * Warum keine zweite Abfrage mehr: die Iterationen stehen bereits IN dieser
 * Mail. Der frühere Weg lud dieselben Nachrichten ein zweites Mal einzeln aus
 * dem Postfach (``GET /mail/conversation``) und zeigte sie zusätzlich zur
 * Zitat-Kette derselben Mail — derselbe Inhalt zweimal. Eine Mail, ein Abruf.
 *
 * Trust (``sfVerified`` + ``trustedDomain``) kommt von außen; ``subjectOfMail``
 * dient nur dem Vergleich, ob ein Abschnitt das Thema gewechselt hat.
 */
function RenderedBody({
  bodyHtml,
  bodyText,
  trustedDomain,
  sfVerified,
  subjectOfMail,
  imid,
  mailbox,
}: {
  bodyHtml: string | null;
  bodyText: string | null;
  trustedDomain: string | null;
  sfVerified: boolean;
  subjectOfMail?: string | null;
  imid?: string | null;
  mailbox?: string | null;
}) {
  const mt = useT().mail.lesen;
  const { clean, blockedImages } = useMemo(() => {
    if (bodyHtml && bodyHtml.trim()) {
      return sanitizeBody(bodyHtml, trustedDomain, sfVerified);
    }
    return { clean: null as string | null, blockedImages: false };
  }, [bodyHtml, trustedDomain, sfVerified]);

  // #84: eingebettete Bilder. Läuft NACH ``sanitizeBody`` und ausschließlich
  // auf dessen Ergebnis — siehe ``mitCidBildern``.
  const { bilder: cidBilder, fertig: cidFertig } = useInlineBilder(
    imid ?? null,
    mailbox ?? null,
    clean
  );
  const angezeigt = useMemo(
    () => (clean == null ? null : mitCidBildern(clean, cidBilder, cidFertig)),
    [clean, cidBilder, cidFertig]
  );

  // Zerlegung in ``[neuer Text, Iteration₁, …]``. Ist nichts beweisbar zu
  // trennen, kommt genau ein Abschnitt zurück — dann sieht die Ansicht aus wie
  // eh und je.
  const segments = useMemo(
    () => (angezeigt == null ? null : splitQuotedSegments(angezeigt).segments),
    [angezeigt]
  );
  // Klartext-Mails (kein ``body_html``) haben keinen DOM, den man zerlegen
  // könnte — sie laufen über den ``<pre>``-Zweig. Derselbe Verlauf, zweiter
  // Zerleger: zeilenbasiert, gleiche Kopf-Erkennung, gleiche Regel.
  const textSegments = useMemo(
    () => (clean != null || !bodyText ? null : splitQuotedTextSegments(bodyText).segments),
    [clean, bodyText]
  );
  // Beide Zerleger auf EINE Anzeige-Form bringen: der Unterschied ist nur, ob
  // ein Abschnitt als HTML oder als Text gerendert wird.
  const alle = useMemo<Abschnitt[] | null>(() => {
    if (segments) return segments.map((s) => ({ head: s.head, preview: s.preview, html: s.html }));
    if (textSegments) return textSegments.map((s) => ({ head: s.head, preview: s.preview, text: s.text }));
    return null;
  }, [segments, textSegments]);
  // Der neue Text ist der erste Abschnitt OHNE Kopf. Fehlt er (reine
  // Weiterleitung ohne eigenen Kommentar), gibt es keinen.
  const fresh = alle && !alle[0].head ? alle[0] : null;
  const quoted = useMemo(() => (alle ?? []).filter((s) => s.head), [alle]);

  // Zugeklappt starten — außer es gibt keinen neuen Text: dann steht der erste
  // vorhandene Abschnitt offen, damit beim Öffnen Text dasteht und nicht bloß
  // eine zugeklappte Zeile.
  const initialOpen = useCallback(
    () => (fresh ? new Set<number>() : new Set<number>(quoted.length ? [0] : [])),
    [fresh, quoted.length]
  );
  const [openIdx, setOpenIdx] = useState<Set<number>>(initialOpen);
  useEffect(() => {
    setOpenIdx(initialOpen());
  }, [clean, initialOpen]);
  const toggle = (i: number) =>
    setOpenIdx((prev) => {
      const next = new Set(prev);
      if (!next.delete(i)) next.add(i);
      return next;
    });

  const bodyCls = clsx(
    "text-sm text-text leading-relaxed",
    "[&_a]:text-accent-600 [&_a]:underline [&_a]:break-words",
    "[&_p]:my-2 [&_h1]:font-semibold [&_h1]:text-base [&_h2]:font-semibold [&_h3]:font-semibold",
    "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2",
    "[&_blockquote]:border-l-2 [&_blockquote]:border-border-strong [&_blockquote]:pl-3 [&_blockquote]:text-text-2",
    "[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded",
    "[&_table]:text-xs [&_table]:border-collapse",
    "[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1",
    "[&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1"
  );

  return (
    <div className="space-y-2">
      {blockedImages && (
        <div className="flex items-center gap-2 text-xs text-text-3 bg-surface-2 border border-border rounded-md px-2.5 py-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          {mt.externeBilderAusgeblendet}
        </div>
      )}
      <div className="max-w-3xl overflow-x-auto">
        {alle != null ? (
          <>
            {fresh && (fresh.html != null ? (
              <div
                data-mail-body-content
                className={bodyCls}
                dangerouslySetInnerHTML={{ __html: fresh.html }}
              />
            ) : (
              <pre data-mail-body-content className="text-sm text-text leading-relaxed whitespace-pre-wrap font-sans">
                {fresh.text}
              </pre>
            ))}
            {quoted.length > 0 && (
              <ol
                className={clsx("space-y-2", fresh && "mt-3")}
                aria-label={mt.fruehereNachrichten}
              >
                {quoted.map((seg, i) => {
                  const head = seg.head!;
                  const open = openIdx.has(i);
                  // Der Betreff steht nur da, wo er etwas sagt: wenn der
                  // Abschnitt das Thema gewechselt hat. In einer normalen Kette
                  // trägt jeder Kopf denselben Betreff wie die Mail selbst —
                  // dreizehnmal „AW: …" untereinander ist Lärm, kein Inhalt.
                  const themaGewechselt =
                    !!head.subject &&
                    betreffKern(head.subject) !== betreffKern(subjectOfMail ?? null);
                  return (
                    <li
                      key={i}
                      data-quote-segment={i}
                      className="rounded-lg border border-border bg-surface overflow-hidden"
                    >
                      <button
                        type="button"
                        data-quote-toggle
                        onClick={() => toggle(i)}
                        aria-expanded={open}
                        className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-surface-2 transition-colors"
                      >
                        <ChevronDown
                          className={clsx(
                            "mt-0.5 shrink-0 w-3.5 h-3.5 text-text-3 transition-transform",
                            open && "rotate-180"
                          )}
                          aria-hidden="true"
                        />
                        {/* ZWEI Zeilen, nicht eine. Die Mail-Spalte ist schmal
                            (neben Liste und Kontext-Panel bleiben ~300 px), und
                            einzeilig verlor als Erstes das Datum seine Uhrzeit —
                            „Mittwoch, 8. Juli 2026 1…". Genau die unterscheidet
                            aber zwei Iterationen desselben Tages. Also: Absender
                            und Datum oben, Vorschau darunter über die volle
                            Breite. Das Datum ist kurz und fest, es behält seinen
                            Platz; gekürzt wird der Absender. */}
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline gap-2">
                            <span
                              className="min-w-0 flex-1 truncate text-sm text-text-2"
                              title={head.sender}
                              data-quote-sender
                            >
                              {absenderName(head.sender)}
                            </span>
                            {themaGewechselt && (
                              <span
                                className="shrink-0 max-w-[40%] truncate text-xs text-text-2 italic"
                                title={head.subject ?? undefined}
                                data-quote-subject
                              >
                                {head.subject}
                              </span>
                            )}
                            {head.date && (
                              <span
                                className="shrink-0 text-[11px] text-text-3"
                                data-quote-date
                              >
                                {head.date}
                              </span>
                            )}
                          </span>
                          {/* Vorschau nur im zugeklappten Zustand — offen steht
                              der Inhalt ohnehin darunter. Fehlt sie, entsteht
                              kein Loch: die Zeile ist dann schlicht einzeilig. */}
                          {!open && seg.preview && (
                            <span
                              className="block truncate text-xs text-text-3 mt-0.5"
                              data-quote-preview
                            >
                              {seg.preview}
                            </span>
                          )}
                        </span>
                      </button>
                      {open && (seg.html != null ? (
                        <div
                          data-quote-block
                          data-mail-body-content
                          className={clsx(bodyCls, "px-3 pb-3 pt-2 border-t border-border")}
                          dangerouslySetInnerHTML={{ __html: seg.html }}
                        />
                      ) : (
                        <pre
                          data-quote-block
                          data-mail-body-content
                          className="px-3 pb-3 pt-2 border-t border-border text-sm text-text leading-relaxed whitespace-pre-wrap font-sans"
                        >
                          {seg.text}
                        </pre>
                      ))}
                    </li>
                  );
                })}
              </ol>
            )}
          </>
        ) : (
          <pre data-mail-body-content className="text-sm text-text leading-relaxed whitespace-pre-wrap font-sans">
            {bodyText || mt.keinInhalt}
          </pre>
        )}
      </div>
    </div>
  );
}

function MessageBody({
  msg,
  mailbox,
}: {
  msg: MessageResponse;
  mailbox?: string | null;
}) {
  // SF-verifizierter Absender = echter Account-Match (account_id gesetzt). Dann
  // ist die Absender-Identität durch Salesforce gewährleistet → alle Bilder
  // anzeigen. Ohne Match bleibt es konservativ (nur Absender-Domain, Rest geblockt).
  const sfVerified = !!msg.enrichment.account_id;
  const trustedDomain = sfVerified ? msg.enrichment.mail_domain : null;
  return (
    <RenderedBody
      bodyHtml={msg.body_html}
      bodyText={msg.body_text}
      trustedDomain={trustedDomain}
      sfVerified={sfVerified}
      subjectOfMail={msg.subject}
      // #84: die Kennung, unter der die eingebetteten Bilder abrufbar sind.
      imid={msg.internet_message_id}
      mailbox={mailbox}
    />
  );
}

/**
 * D3 — Zusammenfassung der (neuesten/geöffneten) Mail, dezent ÜBER dem Verlauf
 * in der Mitte. Rein aus ``enrichment.summary`` (SF, bereits geladen — kein
 * Egress). Fehlt/leer → ``null`` (kein leerer Rahmen). Kein ``prose``
 * (Typography-Plugin nicht installiert) —
 * Stil-Tokens konsistent mit den übrigen DetailPane-Elementen.
 */
function DetailSummary({ summary }: { summary: string | null }) {
  const mt = useT().mail.lesen;
  if (!summary) return null;
  return (
    <div
      data-mail-detail-summary
      className="mb-3 rounded-lg border border-border bg-surface-2 px-3 py-2"
    >
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-text-3">
        <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
        {mt.zusammenfassung}
      </div>
      <p className="text-sm text-text leading-relaxed">{summary}</p>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="p-5 space-y-4">
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-24 w-full rounded-xl" />
      <div className="space-y-2 pt-2">
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-5/6" />
        <Skeleton className="h-3.5 w-4/6" />
      </div>
    </div>
  );
}

// ---- W2: Antwort-Vorschläge-Panel ------------------------------------------

/**
 * Body-Markdown „light": kein prose-Plugin, keine HTML-Injektion — nur
 * Absätze (Leerzeile) + Zeilenumbrüche via whitespace-pre-wrap. Bewusst
 * schlicht (Spec §3: „einfach: Zeilenumbrüche + Absätze, KEIN prose").
 */
function BodyPreview({ md, className }: { md: string; className?: string }) {
  return (
    <div
      className={clsx(
        "whitespace-pre-wrap break-words text-sm text-text leading-relaxed",
        className
      )}
    >
      {md.trim()}
    </div>
  );
}

function ReplyContextChips({ ctx }: { ctx: ReplyContext }) {
  const [open, setOpen] = useState(false);
  const mt = useT().mail.composer;
  const hasSources = ctx.sources.length > 0;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {ctx.category && (
          <CtxBadge>
            {ctx.category}
            {ctx.sub_category ? ` › ${ctx.sub_category}` : ""}
          </CtxBadge>
        )}
        <span className="inline-flex items-center gap-1 rounded-full bg-surface-3 text-text-2 px-2 py-0.5 text-[11px] font-medium">
          <FileText className="w-3 h-3" />
          {mt.vorlagen(ctx.templates_used.length)}
        </span>
        {ctx.ectoin_used && (
          <button
            type="button"
            onClick={() => hasSources && setOpen((v) => !v)}
            className={clsx(
              "inline-flex items-center gap-1 rounded-full bg-accent-100 text-accent-700 px-2 py-0.5 text-[11px] font-medium",
              hasSources && "hover:bg-accent-100/70"
            )}
          >
            <FlaskConical className="w-3 h-3" />
            {mt.ectoinWissen}
            {hasSources && (
              <ChevronDown
                className={clsx("w-3 h-3 transition-transform", open && "rotate-180")}
              />
            )}
          </button>
        )}
      </div>
      {open && hasSources && (
        <ul className="space-y-1 rounded-md border border-border bg-surface-2 px-3 py-2">
          {ctx.sources.map((s) => (
            <li key={s.n} className="text-xs text-text-2 flex gap-1.5">
              <span className="text-text-3 tabular-nums shrink-0">[{s.n}]</span>
              <span className="min-w-0">
                <span className="text-text">{s.title || mt.ectoinQuelle}</span>
                {s.locator && (
                  <span className="text-text-3 break-all"> · {s.locator}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface MailSignature {
  html: string;
  text: string;
  complete: boolean;
  name: string;
}

/** Antwort von ``POST /mail/signature-block`` (Task 57) — Verabschiedung UND
 *  Signatur in einem Stück. Der Composer nimmt daraus die ``closing``-Zeilen für
 *  den Editor; ``html``/``text`` bleiben ungenutzt, weil die Signatur die
 *  Editor-Whitelist nicht überlebt (s. ``closingToHtml``). */
interface SignatureBlock {
  language: "de" | "en";
  closing: string;
  html: string;
  text: string;
  complete: boolean;
}

/** Griff auf den Editor für Aufrufer, die den ECHTEN Cursor brauchen (Task 57:
 *  Abschlussblock beim Öffnen → Schreibmarke über den Block setzen). Ein
 *  ``value``-Umweg ginge nicht: der Editor seedet die DOM nur bei externer
 *  ``value``-Änderung und weiß nichts über die Schreibmarke des Aufrufers. */
interface RichTextHandle {
  el: () => HTMLDivElement | null;
  caretToStart: () => void;
  /** Bis zur ersten eigenen Eingabe die Schreibmarke oben halten — auch, wenn
   *  der Nutzer irgendwo in die leere Fläche klickt (s. ``caretHomeArmed``). */
  armCaretHome: () => void;
}

// ---- P1.10: Rich-Text-Composer — HTML-Serializer (DOMPurify-Wrapper) --------
// Der Composer-``body``-State ist gewhitelistetes Editor-HTML. Diese Wrapper sind
// die EINZIGE Egress-Grenze: kein ungefiltertes contentEditable-innerHTML verlässt
// je den Editor. Reine (DOM-freie) Bausteine liegen in ``./mailRichText``.

/** Rohes Editor-HTML → gewhitelistetes, deterministisch normalisiertes body_html.
 *  Pipeline: div→<br> (Umbrüche retten) → DOMPurify-Whitelist (strong,b,em,i,ul,
 *  ol,li,a,p,br; nur href) → <b>/<i>→<strong>/<em> → trim. Leer → "". */
function sanitizeEditorHtml(raw: string): string {
  const clean = DOMPurify.sanitize(preNormalizeBlocks(raw), {
    ALLOWED_TAGS: EDITOR_ALLOWED_TAGS,
    ALLOWED_ATTR: EDITOR_ALLOWED_ATTR,
  });
  const out = normalizeInlineTags(clean)
    .replace(/^(?:\s*<br\s*\/?>\s*)+/i, "")
    .trim();
  return isBlankHtml(out) ? "" : out;
}

// ---- Signatur schon im Body? (Nacharbeit Task 57, Blocker 1) ----------------
//
// Ein in OUTLOOK verfasster Entwurf bringt die gebrandete Signatur im Body mit.
// Der Send-Pfad hängt seine trotzdem an → beim Empfänger stand sie ZWEIMAL:
// einmal entkernt (die Whitelist wirft Logo, Tabelle und Schriftgrößen weg,
// LÖSCHT die Signatur aber nicht) und einmal intakt aus dem Splice. Gemessen
// ohne einen einzigen Tastendruck: Entwurf öffnen → „Senden".

/** Mindestlänge (ohne Leerraum) einer Signatur-Zeile, die als Marker taugt. */
const SIG_KERNZEILE_MIN = 12;
/** So viele Kernzeilen müssen zusammentreffen, damit „Signatur steht schon da"
 *  gilt. Siehe ``signaturKernzeilen`` — eine einzelne Zeile reicht NICHT. */
const SIG_MIN_TREFFER = 3;

/**
 * „Kernzeilen" einer Signatur — die Zeilen, an denen man sie im Fließtext
 * wiedererkennt (Leerraum entfernt, damit ``Mail:&nbsp;&nbsp;&nbsp;x`` und
 * ``Mail: x`` derselbe Marker sind).
 *
 * Warum MEHRERE Zeilen und nicht ein einzelner Marker: es gibt keinen einzelnen,
 * der trägt.
 *   * Der **Name** steht auch in der Verabschiedung („Mit freundlichen Grüßen,
 *     Wolfgang Hansen") — als Marker hielte er JEDE Atlas-Mail für „Signatur
 *     schon vorhanden" und hängte nie wieder eine an.
 *   * **Adress- oder Mail-Zeile** allein kommen im Fließtext vor („schreiben Sie
 *     mir an …", „unsere Anschrift lautet …").
 * Erst das Zusammentreffen von ``SIG_MIN_TREFFER`` Zeilen ist ein Beleg. Kurze
 * Zeilen („bitop AG", „Vorstand") fallen raus: die stehen in normaler
 * Geschäftskorrespondenz.
 *
 * Gelesen wird aus dem HTML der ECHTEN Signatur des Nutzers (``GET
 * /mail/signature``) — keine Kopie im Frontend, die beim ersten Signatur-Wechsel
 * ins Leere liefe.
 */
function signaturKernzeilen(sigHtml: string): string[] {
  if (!sigHtml || typeof DOMParser === "undefined") return [];
  try {
    const doc = new DOMParser().parseFromString(sigHtml, "text/html");
    const zeilen = new Set<string>();
    for (const roh of linearizeEditor(doc.body, null).split("\n")) {
      const key = nonWs(roh);
      if (key.length >= SIG_KERNZEILE_MIN) zeilen.add(key);
    }
    return Array.from(zeilen);
  } catch {
    return [];
  }
}

/**
 * Text des EIGENEN Teils (alles VOR der Zitat-Grenze), ohne Leerraum.
 *
 * Die Grenze ist der Kern der Sache: der zitierte Verlauf trägt regelmäßig
 * DIESELBE Signatur — aus einer früher von uns gesendeten Mail. Wer das ganze
 * Dokument ansieht, hängt bei jeder Antwort in einem eigenen Thread keine
 * Signatur mehr an. Gleiche Grenze, gleiche Erkennung wie bei
 * ``ownPartHasClosing`` — keine zweite Wahrheit daneben.
 *
 * Ohne DOM/bei kaputtem HTML: ``""`` → kein Treffer → es wird angehängt wie
 * bisher. Bewusst in diese Richtung: eine doppelte Signatur ist sichtbar und
 * korrigierbar, eine still fehlende nicht.
 */
function ownPartTextKey(bodyHtml: string): string {
  if (!bodyHtml || typeof DOMParser === "undefined") return "";
  try {
    const root = new DOMParser().parseFromString(bodyHtml, "text/html").body;
    const boundary = findQuoteBoundary(root);
    if (!boundary) return nonWs(root.textContent ?? "");
    const range = root.ownerDocument.createRange();
    range.setStart(root, 0);
    range.setEndBefore(boundary);
    return nonWs(range.toString());
  } catch {
    return "";
  }
}

/** ``true``, wenn der eigene Teil des Bodys die Signatur bereits trägt. */
function ownPartHasSignature(bodyHtml: string, sigHtml: string): boolean {
  const kern = signaturKernzeilen(sigHtml);
  if (kern.length < SIG_MIN_TREFFER) return false; // keine belastbare Grundlage
  const own = ownPartTextKey(bodyHtml);
  if (!own) return false;
  let treffer = 0;
  for (const zeile of kern) {
    if (own.includes(zeile) && ++treffer >= SIG_MIN_TREFFER) return true;
  }
  return false;
}

/** Editor-HTML (Body) → gesendetes body_html: whitelisten, dann Signatur an das
 *  erste literale ``[Name]`` spleißen (sonst anhängen). Signatur bleibt
 *  vertrauenswürdig (nicht gewhitelistet), s. Driver-Entscheidung #1.
 *
 *  Trägt der EIGENE Teil die Signatur schon, wird KEINE zweite angehängt —
 *  gesendet wird dann, was der Nutzer im Editor sieht. Das ist bewusst die
 *  Anhänge-Bremse und kein Zuschnitt am Body:
 *    * VERWORFEN „bekannte Signatur-Marker vor dem Splice aus dem Body
 *      entfernen": Löschen braucht eine Grenze („wo beginnt die Signatur"), die
 *      niemand zuverlässig kennt. Ein zu großzügiger Schnitt frisst Nutzertext,
 *      ein zu kleiner lässt Reste stehen — und im zitierten Verlauf schnitte er
 *      in fremde Mails. Nicht-Anhängen kann per Bauart nichts verlieren.
 *    * VERWORFEN „mitgebrachte Signatur herausschneiden und die intakte hinten
 *      anhängen": das VERSCHIEBT sie, sobald der Nutzer sie absichtlich mitten
 *      im Text stehen hat (Weiterleitung mit Kommentar darunter). Was im Editor
 *      steht, soll ankommen — an derselben Stelle.
 *    * VERWORFEN „die Signatur beim Fortsetzen aus dem Entwurf entfernen, damit
 *      sie wie bei Atlas-Mails erst beim Senden dazukommt": nimmt dem Nutzer
 *      sichtbar Text aus dem Entwurf und braucht dieselbe Bremse trotzdem, für
 *      den Fall der Signatur mitten im Text. Zwei Eingriffe für ein Problem. */
function spliceSignatureHtml(bodyHtml: string, sig: MailSignature | null): string {
  const anhaengen = sig && !ownPartHasSignature(bodyHtml, sig.html) ? sig.html : null;
  return spliceSignature(sanitizeEditorHtml(bodyHtml), anhaengen);
}

// Dev-only Test-Hook (von Vite in Prod-Builds entfernt): erlaubt dem Playwright-
// Walk, die ECHTEN DOMPurify-Wrapper via page.evaluate zu prüfen (script/style/
// span/div-Müll entfernt, Splice, mdToHtml) — ohne Prod-Code zu verändern.
if (import.meta.env.DEV) {
  (window as unknown as { __mailRichText?: unknown }).__mailRichText = {
    sanitizeEditorHtml,
    spliceSignatureHtml,
    ownPartHasSignature,
    mdToHtml,
  };
}

// ---- Formulierhilfe: DOM-Hälfte (Task 57) ----------------------------------
// Die reine Hälfte (Auslöser-Erkennung) liegt in ``mailComposerAssist.ts``.
// Hier steht nur, was ohne echten Editor nicht geht: Text mit Zeilengrenzen
// lesen, rückwärts selektieren, an der Schreibmarke ersetzen.

/**
 * Editor-DOM → Text MIT Zeilengrenzen (``\n`` an ``br``- und Block-Grenzen).
 *
 * Warum nicht ``textContent``: der klebt Zeilen zusammen (``…Grüßen>>`` statt
 * ``…Grüßen\n>>``). Dann sähe die Auslöser-Erkennung den falschen „Absatz" und
 * das Modell bekäme einen Entwurf ohne Absatzstruktur. Gleiche Begründung wie
 * bei ``headText`` weiter oben, nur mit ``\n`` statt Leerzeichen.
 *
 * ``stopAt`` (die Zitat-Grenze) schneidet die zitierte Kette ab: sie ist NICHT
 * Teil des Entwurfs, und im Prompt stünde sie doppelt — die Bezugsmail steckt
 * ohnehin im Server-Kontext.
 * ``caret`` begrenzt zusätzlich auf den Text VOR der Schreibmarke.
 */
function linearizeEditor(
  root: HTMLElement,
  stopAt: Element | null,
  caret?: { node: Node; offset: number }
): string {
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT
  );
  const open: Element[] = [];
  let out = "";
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (stopAt && (n === stopAt || stopAt.contains(n))) break;
    while (open.length && !open[open.length - 1].contains(n)) {
      if (BLOCK_TAGS.has((open.pop() as Element).tagName)) out += "\n";
    }
    if (n.nodeType === 3 /* Text */) {
      const v = n.nodeValue || "";
      if (caret && n === caret.node) return out + v.slice(0, caret.offset);
      out += v;
      continue;
    }
    const e = n as Element;
    open.push(e);
    if (e.tagName === "BR" || BLOCK_TAGS.has(e.tagName)) out += "\n";
  }
  return out;
}

/** Letzter Textknoten innerhalb von ``n`` (oder ``n`` selbst). */
function lastTextNodeIn(n: Node): Text | null {
  if (n.nodeType === 3) return n as Text;
  for (let i = n.childNodes.length - 1; i >= 0; i--) {
    const found = lastTextNodeIn(n.childNodes[i]);
    if (found) return found;
  }
  return null;
}

/** Vorheriger Textknoten in Dokument-Reihenfolge, innerhalb von ``root``. */
function prevTextNode(root: HTMLElement, from: Node): Text | null {
  let cur: Node | null = from;
  while (cur && cur !== root) {
    let sib: Node | null = cur.previousSibling;
    while (sib) {
      const found = lastTextNodeIn(sib);
      if (found) return found;
      sib = sib.previousSibling;
    }
    cur = cur.parentNode;
  }
  return null;
}

/**
 * ``count`` Zeichen rückwärts ab der Schreibmarke selektieren — der Auslöser,
 * der gleich ersetzt wird.
 *
 * Über Knotengrenzen hinweg, weil die Anweisung formatierte Teile enthalten
 * kann (``<sag <b>Termin</b> ab>>>`` sind drei Textknoten).
 */
function selectBackwards(root: HTMLElement, count: number, at?: Range): boolean {
  const sel = window.getSelection();
  if (!sel) return false;
  const src = at ?? (sel.rangeCount > 0 ? sel.getRangeAt(0) : null);
  if (!src) return false;
  const base = src.cloneRange();
  base.collapse(false);
  let endNode: Node = base.endContainer;
  let endOffset = base.endOffset;
  if (endNode.nodeType !== 3) {
    const kids = endNode.childNodes;
    let cand: Text | null = null;
    for (let i = Math.min(endOffset, kids.length) - 1; i >= 0; i--) {
      cand = lastTextNodeIn(kids[i]);
      if (cand) break;
    }
    if (!cand) return false;
    endNode = cand;
    endOffset = (cand.nodeValue || "").length;
  }
  let startNode: Node = endNode;
  let startOffset = endOffset;
  let remaining = count;
  while (remaining > 0) {
    if (startOffset >= remaining) {
      startOffset -= remaining;
      remaining = 0;
      break;
    }
    remaining -= startOffset;
    const prev = prevTextNode(root, startNode);
    if (!prev) return false;
    startNode = prev;
    startOffset = (prev.nodeValue || "").length;
  }
  const range = root.ownerDocument.createRange();
  range.setStart(startNode, startOffset);
  range.setEnd(endNode, endOffset);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
}

/** Zustand der Formulierhilfe — genau die vier, die der Nutzer sehen muss. */
type AssistPhase =
  | { art: "ruhe" }
  | { art: "laeuft"; gefuehrt: boolean }
  | { art: "fertig"; gebremst: number }
  | { art: "fehler"; grund: string };

/** Ein Auftrag an die Formulierhilfe — identisch für Erstlauf und „Nochmal". */
interface AssistAuftrag {
  draftText: string;
  instruction: string | null;
  /** Text unmittelbar VOR der Einfügestelle (Wiederholungs-Bremse + Leerzeichen). */
  davor: string;
  /** Was das Ergebnis ersetzt: den Auslöser (Erstlauf) oder den zuletzt
   *  eingefügten Text („Nochmal" nach Erfolg). */
  ziel:
    | { art: "ausloeser"; range: Range; laenge: number }
    | { art: "eingefuegt"; range: Range };
}

/** Einen Bereich zur aktuellen Auswahl machen (Ziel „eingefuegt"). */
function setzeAuswahl(range: Range): boolean {
  const sel = window.getSelection();
  if (!sel) return false;
  sel.removeAllRanges();
  sel.addRange(range);
  return !sel.isCollapsed;
}

/**
 * Fehler → Satz, der dem Nutzer sagt, WAS los ist. „Fehlgeschlagen" allein
 * schickt ihn auf die Suche nach einem Fehler bei sich selbst; die Statuscodes
 * des Endpunkts sind bekannt (``app/routers/mail.py:1051``) und benennbar.
 */
function assistGrund(e: unknown): string {
  const mt = katalog().mail.composer;
  if (e instanceof ApiError) {
    if (e.status === 502) return mt.assistGrund502;
    if (e.status === 503) return mt.assistGrund503;
    if (e.status === 403) return mt.assistGrund403;
    if (e.status === 404) return mt.assistGrund404;
    if (e.status === 0 || !e.status) return mt.assistGrundOffline;
    return mt.assistGrundStatus(e.status);
  }
  if (e instanceof Error && e.message) return e.message;
  return mt.assistGrundUnbekannt;
}

// Kleiner Toolbar-Button für den Rich-Text-Editor (Fett/Kursiv/Liste/Link).
function RtBtn({
  onClick,
  active = false,
  disabled = false,
  title,
  label,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      // Fokus/Selektion im Editor NICHT stehlen (sonst greift execCommand ins Leere).
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={clsx(
        "inline-flex items-center justify-center rounded p-1.5 transition-colors",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        active
          ? "bg-accent-100 text-accent-700"
          : "text-text-2 hover:bg-surface-3 hover:text-text"
      )}
    >
      {children}
    </button>
  );
}

/**
 * Leichter WYSIWYG-Editor (P1.10): contentEditable-Div + Toolbar (Fett/Kursiv/
 * Aufzählung/Link). ``value`` = gewhitelistetes HTML (geteilte Composer-Quelle);
 * ``onChange`` liefert bei jeder Eingabe das sanitisierte HTML zurück. Seeds
 * (KI/Vorlage/Resume) setzen die DOM nur, wenn sich ``value`` EXTERN ändert (nicht
 * beim eigenen onInput-Echo) → kein Cursor-Sprung beim Tippen.
 * Kürzel (Doc 04): Strg+B/Strg+I = Fett/Kursiv, Strg+K = Hyperlink — ausschließlich
 * im Editor (stopPropagation), Strg+Umschalt+P (Palette, X1) NICHT abgefangen.
 */
function RichTextEditor({
  value,
  onChange,
  disabled = false,
  placeholder,
  ariaLabel,
  handleRef,
  assist,
}: {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /** Wird mit dem Editor-Griff befüllt (s. ``RichTextHandle``). */
  handleRef?: React.MutableRefObject<RichTextHandle | null>;
  /** Formulierhilfe (Task 57). Fehlt sie, verhält sich der Editor wie bisher —
   *  die Weiterleiten-Notiz z.B. bekommt keine. Der Aufrufer liefert nur den
   *  Netz-Aufruf; Auslöser, Einfügen und Rückmeldung liegen hier. */
  assist?: (req: { draftText: string; instruction: string | null }) => Promise<string>;
}) {
  const mt = useT().mail.composer;
  const ct = useT().common;
  const ref = useRef<HTMLDivElement | null>(null);
  const lastEmitted = useRef<string | null>(null);
  const savedRange = useRef<Range | null>(null);
  const [isEmpty, setIsEmpty] = useState(() => isBlankHtml(value));
  const [active, setActive] = useState({ bold: false, italic: false, ul: false });
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const linkInputRef = useRef<HTMLInputElement | null>(null);

  // DOM nur bei EXTERNER value-Änderung neu seeden (Mount + KI/Vorlage/Resume),
  // nie beim eigenen onInput-Echo (gegen ``lastEmitted`` geprüft).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (value !== lastEmitted.current) {
      el.innerHTML = value || "";
      lastEmitted.current = value;
      setIsEmpty(isBlankHtml(value));
    }
  }, [value]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const clean = sanitizeEditorHtml(el.innerHTML);
    lastEmitted.current = clean;
    setIsEmpty(isBlankHtml(clean));
    onChange(clean);
  }, [onChange]);

  // „Schreibmarke oben halten, bis der Nutzer selbst tippt."
  //
  // Nötig, weil der Abschlussblock beim Öffnen der EINZIGE Inhalt ist und der
  // Editor 240 px hoch: wer in die leere Fläche darunter klickt — das Nächste,
  // was man tut — landet HINTER „Wolfgang Hansen" und schreibt seine Mail unter
  // die Verabschiedung. Gemessen, nicht vermutet. Ein einmaliges Zurückholen
  // beim ersten Klick löst genau diesen Fall und nimmt dem Nutzer danach nichts
  // mehr aus der Hand: mit dem ersten Tastendruck ist die Sperre weg.
  const caretHomeArmed = useRef(false);
  const caretToStart = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;
    const r = document.createRange();
    r.selectNodeContents(el);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  }, []);

  // Editor-Griff für Aufrufer, die die echte Schreibmarke setzen müssen.
  useEffect(() => {
    if (!handleRef) return;
    handleRef.current = {
      el: () => ref.current,
      caretToStart,
      armCaretHome: () => {
        caretHomeArmed.current = true;
      },
    };
    return () => {
      handleRef.current = null;
    };
  }, [handleRef, caretToStart]);

  const syncActive = useCallback(() => {
    try {
      setActive({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        ul: document.queryCommandState("insertUnorderedList"),
      });
    } catch {
      /* queryCommandState nicht unterstützt → Aktiv-Markierung entfällt still */
    }
  }, []);

  // ── Formulierhilfe (Task 57) ───────────────────────────────────────────────
  // Auslöser ist das Tippen selbst: endet der Absatz auf ``>>``, läuft sie los.
  // Kein Knopf, kein Menüeintrag — man lernt es einmal und kann es dann.
  const [assistPhase, setAssistPhase] = useState<AssistPhase>({ art: "ruhe" });
  const assistBusy = useRef(false);
  // Zuletzt von SELBST ausgelöster Absatz. Nötig, weil der Auslöser bei einem
  // Fehler STEHEN BLEIBT (Zusage: die Anweisung wird nicht gefressen): der
  // Absatz endet dann weiter auf ``>>``, und ohne diese Sperre würde jeder
  // weitere Tastendruck denselben Aufruf erneut starten — eine Endlosschleife
  // aus 6-Sekunden-Aufrufen. Ein erneuter Versuch ist Sache von „Nochmal".
  const assistLastFired = useRef<string | null>(null);
  // „Nochmal" — derselbe Auftrag ohne Neutippen. Nach einem FEHLER ersetzt er
  // wieder den (stehengebliebenen) Auslöser, nach einem ERFOLG den zuletzt
  // eingefügten Text. Beides derselbe Weg, nur ein anderes Ziel.
  const assistRedo = useRef<(() => void) | null>(null);

  /**
   * Einen Auftrag ausführen und das Ergebnis über ``ziel`` einsetzen.
   *
   * EIN Weg für Erstlauf und „Nochmal": beide rufen mit demselben ``draftText``
   * und derselben ``instruction``, sie ersetzen nur etwas anderes.
   */
  const fuehreAus = useCallback(
    async (auftrag: AssistAuftrag) => {
      const el = ref.current;
      if (!el || !assist || assistBusy.current) return;
      assistBusy.current = true;
      setAssistPhase({ art: "laeuft", gefuehrt: auftrag.instruction != null });
      try {
        const roh = await assist({
          draftText: auftrag.draftText,
          instruction: auftrag.instruction,
        });
        let text = stripInlineMarkers(roh).trim();
        if (!text) throw new Error(katalog().mail.composer.assistLeer);
        // Wiederholungs-Bremse: sie sitzt hier und nicht im Prompt, weil die
        // Prompt-Regel nachweislich in rund einem Drittel der Fälle durchrutscht
        // (s. ``stripLeadingRepeat``).
        const gebremst = stripLeadingRepeat(text, auftrag.davor);
        text = gebremst.text.trim();
        if (!text) {
          throw new Error(katalog().mail.composer.assistNurWiederholt);
        }
        // Schreibmarke klebt an einem Wort → Leerzeichen davor, sonst entsteht
        // „…das anund melde mich" (gemessen). Wie im Vorbild (word.ts:843-851).
        const zeilenrest = auftrag.davor.slice(auftrag.davor.lastIndexOf("\n") + 1);
        if (zeilenrest && !/\s$/.test(zeilenrest)) text = ` ${text}`;

        el.focus();
        const gewaehlt =
          auftrag.ziel.art === "ausloeser"
            ? selectBackwards(el, auftrag.ziel.laenge, auftrag.ziel.range)
            : setzeAuswahl(auftrag.ziel.range);
        if (!gewaehlt) {
          throw new Error(katalog().mail.composer.assistStelleWeg);
        }
        // EIN ``insertText`` auf der Selektion = EIN Schritt auf dem nativen
        // Rückgängig-Stapel. Deshalb holt ein Strg+Z die Anweisung zurück.
        document.execCommand("insertText", false, text);
        emit();
        syncActive();

        // Bereich des gerade Eingefügten merken — „Nochmal" ersetzt genau ihn.
        // Ein Range wandert bei späteren Änderungen im Dokument mit.
        let neuerBereich: Range | null = null;
        if (selectBackwards(el, text.length)) {
          const s2 = window.getSelection();
          neuerBereich = s2 && s2.rangeCount > 0 ? s2.getRangeAt(0).cloneRange() : null;
          s2?.collapseToEnd(); // Schreibmarke wieder ans Ende → weitertippen
        }
        assistRedo.current = neuerBereich
          ? () =>
              void fuehreAusRef.current?.({
                ...auftrag,
                ziel: { art: "eingefuegt", range: neuerBereich as Range },
              })
          : null;
        setAssistPhase({ art: "fertig", gebremst: gebremst.entfernt });
      } catch (e) {
        // Der getippte Text steht noch da — entfernt wird er NUR bei Erfolg.
        // Das Word-Vorbild macht es andersherum (word.ts:71-98 räumt VOR dem
        // Aufruf ab) und verlässt sich auf Words natives Strg+Z; hier soll der
        // Nutzer nichts zurückholen müssen, was er gerade getippt hat.
        assistRedo.current = () => void fuehreAusRef.current?.(auftrag);
        setAssistPhase({ art: "fehler", grund: assistGrund(e) });
      } finally {
        assistBusy.current = false;
      }
    },
    [assist, emit, syncActive]
  );
  // Selbstbezug für „Nochmal" ohne Ringschluss in den Abhängigkeiten.
  const fuehreAusRef = useRef(fuehreAus);
  fuehreAusRef.current = fuehreAus;

  /** Auslöser im Absatz an der Schreibmarke lesen und ggf. ausführen. */
  const runAssist = useCallback(async () => {
    const el = ref.current;
    if (!el || !assist || assistBusy.current) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const caretRange = sel.getRangeAt(0);
    if (!caretRange.collapsed || !el.contains(caretRange.endContainer)) return;

    // HTML-Zitate tragen keine ``>``-Präfixe (Outlook zitiert ohne) — dagegen
    // hilft nur die DOM-Grenze. Steht die Schreibmarke in der zitierten Kette,
    // ist das Historie und kein Auftrag.
    const quote = findQuoteBoundary(el);
    if (
      quote &&
      (quote.contains(caretRange.endContainer) ||
        (quote.compareDocumentPosition(caretRange.endContainer) &
          Node.DOCUMENT_POSITION_FOLLOWING) !== 0)
    ) {
      return;
    }

    const bisCaret = linearizeEditor(el, quote, {
      node: caretRange.endContainer,
      offset: caretRange.endOffset,
    });
    const absatz = bisCaret.slice(bisCaret.lastIndexOf("\n") + 1);
    const hit = detectTrigger(absatz);
    if (!hit.fired) {
      assistLastFired.current = null;
      return;
    }
    // Ohne Leerraum am Ende vergleichen: nach einem Fehler steht ``<…>>>`` noch
    // da; tippt der Nutzer als Nächstes ein Leerzeichen, endet der Absatz auf
    // ``>>> `` — das ist derselbe Auftrag und darf nicht neu starten.
    const kennung = absatz.replace(/\s+$/, "");
    if (assistLastFired.current === kennung) return;
    assistLastFired.current = kennung;

    // Der GANZE Entwurf geht in den Prompt (ohne die zitierte Kette), nicht nur
    // der letzte Absatz wie im Word-Vorbild: die Anrede steht ganz oben. Mit
    // „nur letzter Absatz" sähe das Modell sie nie und setzte systematisch eine
    // zweite darunter. Der Auslöser selbst gehört nicht in den Prompt.
    const ganzerEntwurf = linearizeEditor(el, quote);
    const draftText = ganzerEntwurf
      .replace(hit.trigger, "")
      .replace(/[ \t]+$/gm, "")
      .trim();

    await fuehreAus({
      draftText,
      instruction: hit.instruction,
      // Text unmittelbar VOR der Einfügestelle: Grundlage der
      // Wiederholungs-Bremse und der Leerzeichen-Frage.
      davor: bisCaret.slice(0, bisCaret.length - hit.trigger.length),
      ziel: { art: "ausloeser", range: caretRange, laenge: hit.trigger.length },
    });
  }, [assist, fuehreAus]);

  const exec = useCallback(
    (cmd: string, val?: string) => {
      ref.current?.focus();
      // Tag-basierte Formatierung erzwingen (<b>/<i> statt <span style>) — sonst
      // würde die Whitelist span-Formatierung verwerfen (Fett/Kursiv ginge verloren).
      try {
        document.execCommand("styleWithCSS", false, "false");
      } catch {
        /* nicht überall unterstützt → ignorieren */
      }
      document.execCommand(cmd, false, val);
      emit();
      syncActive();
    },
    [emit, syncActive]
  );

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    } else {
      savedRange.current = null;
    }
  };
  const restoreSelection = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const openLink = () => {
    if (disabled) return;
    saveSelection();
    setLinkUrl("");
    setLinkOpen(true);
  };
  useEffect(() => {
    if (linkOpen) linkInputRef.current?.focus();
  }, [linkOpen]);

  const applyLink = () => {
    const url = normalizeUrl(linkUrl);
    setLinkOpen(false);
    if (!url) return;
    restoreSelection();
    try {
      document.execCommand("styleWithCSS", false, "false");
    } catch {
      /* ignorieren */
    }
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) {
      // Keine Selektion → Link mit URL als sichtbarem Text einfügen.
      document.execCommand(
        "insertHTML",
        false,
        `<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`
      );
    } else {
      document.execCommand("createLink", false, url);
    }
    emit();
    syncActive();
  };

  // Kürzel NUR im Editor; stopPropagation, damit kein globaler Handler/keine
  // Palette greift; preventDefault auch gegen Browser-Strg+K (Adressleiste).
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
    const k = e.key.toLowerCase();
    if (k === "b") {
      e.preventDefault();
      e.stopPropagation();
      exec("bold");
    } else if (k === "i") {
      e.preventDefault();
      e.stopPropagation();
      exec("italic");
    } else if (k === "k") {
      e.preventDefault();
      e.stopPropagation();
      openLink();
    }
    // Strg+Umschalt+P (Palette, Owner X1) bewusst NICHT abgefangen.
  };

  // Eingabe = Auslöser-Prüfung. Nach ``emit``, damit der Editor-Zustand steht,
  // bevor die Formulierhilfe den Entwurf liest.
  const onInput = () => {
    caretHomeArmed.current = false; // ab jetzt bestimmt der Nutzer die Marke
    emit();
    if (assist) void runAssist();
  };

  // Erster Klick in den noch unbeschriebenen Editor → zurück nach oben.
  const onMouseUp = () => {
    if (caretHomeArmed.current) {
      caretHomeArmed.current = false;
      caretToStart();
    }
    syncActive();
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-0.5 rounded-md border border-border bg-surface-2 px-1.5 py-1">
        <RtBtn
          title={mt.fettTitel}
          label={mt.fett}
          active={active.bold}
          disabled={disabled}
          onClick={() => exec("bold")}
        >
          <Bold className="w-4 h-4" />
        </RtBtn>
        <RtBtn
          title={mt.kursivTitel}
          label={mt.kursiv}
          active={active.italic}
          disabled={disabled}
          onClick={() => exec("italic")}
        >
          <Italic className="w-4 h-4" />
        </RtBtn>
        <RtBtn
          title={mt.aufzaehlung}
          label={mt.aufzaehlung}
          active={active.ul}
          disabled={disabled}
          onClick={() => exec("insertUnorderedList")}
        >
          <List className="w-4 h-4" />
        </RtBtn>
        <div className="mx-0.5 h-4 w-px bg-border" />
        <RtBtn
          title={mt.linkEinfuegenTitel}
          label={mt.linkEinfuegen}
          disabled={disabled}
          onClick={openLink}
        >
          <Link2 className="w-4 h-4" />
        </RtBtn>
        {/* Das Kürzel muss SICHTBAR sein, nicht nur funktionieren (Doc 04).
            Es ersetzt den Knopf, den es hier bewusst NICHT gibt. */}
        {assist && (
          <span className="ml-auto pr-1 text-[11px] text-text-3" data-assist-hinweis>
            {mt.assistHinweisA}
            <code className="text-text-2">
              {mt.assistHinweisText}
              {">>"}
            </code>
            {mt.assistHinweisB}
            <code className="text-text-2">{mt.assistHinweisAnweisung}</code>
            {mt.assistHinweisC}
          </span>
        )}
      </div>

      {/* Rückmeldung der Formulierhilfe. Der Aufruf dauert im Median gut 6 s —
          ohne sichtbaren Ladezustand sieht das aus wie „nichts passiert".
          ``role="status"`` + Text, damit auch Sprachausgaben es mitbekommen. */}
      {assist && assistPhase.art !== "ruhe" && (
        <div
          role="status"
          aria-live="polite"
          data-assist-status
          data-assist-art={assistPhase.art}
          className={clsx(
            "flex flex-wrap items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs",
            assistPhase.art === "fehler"
              ? "border-danger/40 bg-danger/10 text-text"
              : "border-accent-300 bg-accent-50 text-text-2"
          )}
        >
          {assistPhase.art === "laeuft" && (
            <>
              <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-accent-500" />
              <span>
                {assistPhase.gefuehrt
                  ? mt.assistLaeuftGefuehrt
                  : mt.assistLaeuftFrei}{" "}
                {mt.assistDauer}
              </span>
            </>
          )}
          {assistPhase.art === "fertig" && (
            <span>
              {mt.assistFertig}
              {assistPhase.gebremst > 0 && (
                // Sichtbar machen, DASS die Bremse greift — sonst weiß niemand,
                // ob sie noch arbeitet (dieselbe Absicht wie im Vorbild-Panel).
                <span data-assist-bremse>
                  {" "}
                  {mt.assistGebremst(assistPhase.gebremst)}
                </span>
              )}
            </span>
          )}
          {assistPhase.art === "fehler" && (
            <span>
              <span className="font-medium">{mt.assistFehlerTitel}</span>{" "}
              {assistPhase.grund} {mt.assistFehlerNachsatz}
            </span>
          )}
          {(assistPhase.art === "fertig" || assistPhase.art === "fehler") &&
            assistRedo.current && (
              // Kein dauerhafter Knopf in der Oberfläche: diese Zeile erscheint
              // nur nach einem Lauf und verschwindet mit dem nächsten. Ohne sie
              // müsste der Nutzer die Anweisung neu tippen, um sie zu wiederholen.
              <button
                type="button"
                data-assist-nochmal
                onMouseDown={(e) => e.preventDefault()} // Auswahl im Editor halten
                onClick={() => assistRedo.current?.()}
                className="ml-auto inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-0.5 font-medium text-text-2 hover:bg-surface-2 hover:text-text transition-colors"
              >
                <RefreshCw className="w-3 h-3" />
                {mt.assistNochmal}
              </button>
            )}
        </div>
      )}

      <div className="relative">
        <div
          ref={ref}
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={onInput}
          onBlur={emit}
          onKeyDown={onKeyDown}
          onKeyUp={syncActive}
          onMouseUp={onMouseUp}
          onFocus={syncActive}
          className={clsx(
            "min-h-[240px] w-full overflow-y-auto whitespace-pre-wrap break-words rounded-md",
            "border border-border bg-surface px-3 py-2 text-sm text-text",
            "focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500",
            "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
            "[&_a]:text-accent-600 [&_a]:underline [&_p]:my-0",
            disabled && "cursor-not-allowed opacity-50"
          )}
        />
        {isEmpty && !disabled && (
          <div className="pointer-events-none absolute left-3 top-2 select-none text-sm text-text-3">
            {placeholder}
          </div>
        )}
        {linkOpen && (
          <div className="absolute left-2 top-2 z-10 w-[min(360px,92%)] space-y-2 rounded-md border border-accent-300 bg-surface p-2 shadow-lg">
            <div className="text-xs font-medium text-text-2">{mt.linkEinfuegen}</div>
            <div className="flex items-center gap-2">
              <Input
                ref={linkInputRef}
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    applyLink();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setLinkOpen(false);
                  }
                }}
                placeholder="https://…"
              />
              <Button variant="primary" size="sm" onClick={applyLink}>
                {mt.linkOk}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setLinkOpen(false)}>
                {ct.abbrechen}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- P1.8: Entwürfe (Draft-Sync) -------------------------------------------

/** Ein Graph-Entwurf für die Entwürfe-Ansicht (Kontrakt app/schemas/mail.py). */
interface DraftOut {
  draft_id: string;
  subject: string;
  to: string[];
  cc: string[];
  bcc: string[];
  body_html: string;
  last_modified: string | null;
  has_attachments: boolean;
}

/** Debounce für den Composer-Autosave (~1.5 s nach dem letzten Anschlag). */
const DRAFT_DEBOUNCE_MS = 1500;

/** Inverse zu ``mdToHtml`` — HTML → der markdown-ish Composer-Text. Symmetrisch
 *  zu ``mdToHtml`` erweitert (P1.10): Links, ``<em>``→``*``, ``<li>``→``- `` neben
 *  ``<p>``/``<strong>``/``<br>``. Genutzt für die Entwürfe-Vorschau (fmtDraftPreview)
 *  UND das KI-Feedback (``final_md`` markdown-ish, damit suggested_md↔final_md
 *  vergleichbar bleibt — Driver-Entscheidung #5). Fremd-Entwürfe (echtes Outlook)
 *  degradieren sauber auf reinen Text. */
function htmlToComposerText(html: string): string {
  if (!html) return "";
  let s = html;
  // Inline zuerst (Links/Fett/Kursiv), bevor Tags gestrippt werden.
  s = s.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gis, "[$2]($1)");
  s = s.replace(/<strong>(.*?)<\/strong>/gis, "**$1**");
  s = s.replace(/<em>(.*?)<\/em>/gis, "*$1*");
  // Listen → ``- ``-Zeilen; Wrapper als Umbruch-Grenze.
  s = s.replace(/<li[^>]*>(.*?)<\/li>/gis, "- $1\n");
  s = s.replace(/<\/?(?:ul|ol)[^>]*>/gi, "\n");
  s = s.replace(/<\/p>/gi, "\n\n").replace(/<p[^>]*>/gi, "");
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<[^>]+>/g, "");
  s = s
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return s.replace(/\n{3,}/g, "\n\n").trim();
}

/** „Zuletzt geändert" für die Entwürfe-Liste. */
function fmtDraftPreview(html: string): string {
  return htmlToComposerText(html).replace(/\s+/g, " ").trim().slice(0, 120);
}

/** Eine Entwurfs-Zeile (Slice B) — visuell konsistent mit ``ListRow``, aber
 *  eigens gebaut (DraftOut ist kein InboxItem: ausgehend, kein Absender/is_read).
 *  Klick öffnet den EINEN Composer vorbefüllt (kein Lese-Detail). */
function DraftRow({
  draft,
  onOpen,
  onDelete,
}: {
  draft: DraftOut;
  onOpen: (d: DraftOut) => void;
  onDelete: (draftId: string) => void;
}) {
  const mt = useT().mail.entwuerfe;
  const recipients = draft.to.join(", ");
  const preview = fmtDraftPreview(draft.body_html);
  const when = draft.last_modified ? fmtListTime(draft.last_modified) : "";
  return (
    <button
      type="button"
      onClick={() => onOpen(draft)}
      className="group w-full text-left px-3 py-2.5 border-b border-border transition-colors hover:bg-surface-2"
    >
      <div className="flex items-center gap-2">
        <Pencil className="w-3.5 h-3.5 text-accent-500 shrink-0" aria-label={mt.entwurf} />
        <span className="flex-1 min-w-0 truncate text-sm text-text">
          {recipients || <span className="text-text-3">{mt.keinEmpfaenger}</span>}
        </span>
        {draft.has_attachments && (
          <Paperclip
            className="w-3.5 h-3.5 shrink-0 text-text-3"
            aria-label={mt.enthaeltAnhaenge}
          />
        )}
        {/* Löschen: Graph-Soft-Delete → „Gelöschte Elemente" (in Outlook
            wiederherstellbar) → wie bei der Mail-Zeile BEWUSST kein
            Bestätigungs-Dialog. <span role=button>, weil die Zeile selbst ein
            <button> ist; stopPropagation, damit der Klick den Entwurf nicht
            zugleich zum Weiterschreiben öffnet.
            Mit der TASTATUR erreichbar (tabIndex 0 + Enter/Leertaste) und dabei
            sichtbar (focus-visible), sonst wäre der einzige Weg zum Löschen die
            Maus — Doktrin „Tastatur + Sichtbarkeit". preventDefault ist Pflicht:
            dieselbe Taste öffnet sonst zusätzlich die Zeile (und die Leertaste
            scrollt). */}
        <span
          role="button"
          tabIndex={0}
          data-mail-draft-delete
          aria-label={mt.loeschen}
          title={mt.loeschen}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(draft.draft_id);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            e.stopPropagation();
            onDelete(draft.draft_id);
          }}
          // Den Fokus-Ring selbst setzt die globale Regel in index.css (bewusst
          // mit hoher Spezifität) — hier steht nur, was der versteckte Griff im
          // Fokus zusätzlich tun muss: sichtbar werden, und zwar wie beim Hover.
          className="shrink-0 rounded p-0.5 text-text-3 opacity-0 group-hover:opacity-100 hover:text-danger hover:bg-surface-3 focus-visible:opacity-100 focus-visible:text-danger focus-visible:bg-surface-3 transition-colors cursor-pointer"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </span>
        {when && <span className="shrink-0 text-xs text-text-3 tabular-nums">{when}</span>}
      </div>
      <div className="mt-0.5 pl-6 flex items-baseline gap-2 min-w-0">
        <span className="truncate text-sm text-text">
          {draft.subject || <span className="text-text-3 italic">{mt.keinBetreff}</span>}
        </span>
      </div>
      {preview && (
        <div className="mt-0.5 pl-6 truncate text-xs text-text-3">{preview}</div>
      )}
    </button>
  );
}

/** "Re: …" voranstellen, wenn nicht bereits vorhanden. */
function reSubject(s: string): string {
  const t = (s || "").trim();
  if (!t) return "";
  return /^re:/i.test(t) ? t : `Re: ${t}`;
}

/** "Fwd: …" voranstellen, wenn nicht bereits vorhanden. */
function fwdSubject(s: string): string {
  const t = (s || "").trim();
  if (!t) return "";
  return /^(fw|fwd|wg):/i.test(t) ? t : `Fwd: ${t}`;
}

// ---- P0.4: Composer-Verfassen-Fälle + Empfänger-Autocomplete + Upload -------

/** Verfassen-Fall des EINEN Composers (ReplyPanel). */
type ComposeKind = "reply" | "reply-all" | "forward" | "new";

interface RawAttachment {
  name: string;
  content_type: string;
  content_bytes: string; // base64 (ohne data:-Präfix)
  /** Graph-Id AM Entwurf, sobald der Anhang dorthin synchronisiert ist. Solange
   *  sie fehlt, liegt die Datei nur im Composer — der Sende-Pfad holt sie dann
   *  vor dem Absenden nach. Bei einem fortgesetzten Entwurf ist es umgekehrt:
   *  Id gesetzt, ``content_bytes`` leer (die Bytes bleiben bei Graph). */
  attachment_id?: string | null;
}

/** Ein Vorlagen-Anhang (SF-``ContentVersion``) im Composer — dieselbe
 *  Sync-Markierung wie bei den rohen Dateien. */
interface QueuedAttachment extends MailAttachment {
  attachment_id?: string | null;
}

/** Eine Datei AM Entwurf (GET/POST /mail/drafts/{id}/attachments). */
interface DraftAttachmentOut {
  attachment_id: string;
  name: string;
  content_type: string;
  size: number;
}

/** Datei → base64 (Data-URL-Präfix gestrippt, exakt was der email-service
 *  als contentBytes an Graph relayt). */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result);
      resolve(s.slice(s.indexOf(",") + 1)); // "data:...;base64,XXXX" → "XXXX"
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/** Obergrenze für Datei-Anhänge einer Mail, in BASE64-ZEICHEN. Dieselbe Zahl,
 *  die der email-service als ``_MAX_ATTACH_B64`` durchsetzt (Graph deckelt eine
 *  Nachricht inkl. Anhängen bei ~4 MB) — hier vorgezogen, damit die Meldung beim
 *  ANHÄNGEN kommt und nicht erst, wenn der Versand nach dem Rückhol-Fenster
 *  scheitert. Base64 bläht die Bytes um ~4/3 auf, ~3,5 Mio. Zeichen sind also
 *  rund 2,5 MB Dateigröße. */
const MAX_ANHANG_B64 = 3_500_000;

/** Enthält der Zug Dateien aus dem Dateisystem? Nur dann reagieren Wächter und
 *  Drop-Zone — der interne Anhang-Zug ins Dateisystem (``anlageZiehStart``)
 *  trägt ausschließlich ``DownloadURL`` und bleibt damit unberührt. */
function ziehtDateien(dt: DataTransfer | null): boolean {
  return Array.from(dt?.types ?? []).includes("Files");
}

/** Vorbelegung An: reply-all = Original-Absender + An-Verteiler (eigenes Postfach
 *  raus, case-insensitiv dedupliziert); reply = nur Absender; new/forward leer. */
function prefillTo(
  kind: ComposeKind,
  replyTo: string,
  toRecipients: MailRecipient[],
  ownerEmail: string
): string {
  if (kind === "reply") return replyTo;
  if (kind !== "reply-all") return ""; // new / forward
  return dedupeAddrs([replyTo, ...toRecipients.map((r) => r.address)], ownerEmail);
}

/** Vorbelegung Cc: nur reply-all (Original-Cc, eigenes Postfach raus). */
function prefillCc(
  kind: ComposeKind,
  ccRecipients: MailRecipient[],
  ownerEmail: string
): string {
  if (kind !== "reply-all") return "";
  return dedupeAddrs(ccRecipients.map((r) => r.address), ownerEmail);
}

function dedupeAddrs(addrs: string[], ownerEmail: string): string {
  return dedupeAddrList(addrs, ownerEmail).join(", ");
}

/** Kern von `dedupeAddrs`: Reihenfolge erhalten, Leeres und das EIGENE Postfach
 *  raus, case-insensitiv dedupliziert.
 *
 *  Als Liste herausgezogen, weil „Termin aus dieser Mail" denselben Verteiler
 *  braucht, daraus aber Teilnehmer-OBJEKTE baut statt eines Feld-Strings. Eine
 *  zweite Selbst-Entfernung daneben wäre die Stelle, an der die eigene Adresse
 *  irgendwann doch im Verteiler landet — deshalb genau EINE Mechanik. */
function dedupeAddrList(addrs: string[], ownerEmail: string): string[] {
  const owner = ownerEmail.trim().toLowerCase();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of addrs) {
    const addr = (a || "").trim();
    const key = addr.toLowerCase();
    if (!addr || key === owner || seen.has(key)) continue;
    seen.add(key);
    out.push(addr);
  }
  return out;
}

/** Freitext-Empfänger-Feld mit @bitop.de-Autocomplete-Dropdown. Externe Adressen
 *  bleiben möglich (reiner Freitext); das Dropdown schlägt beim Tippen matchende
 *  Kollegen vor, Klick hängt die Adresse an das letzte (kommagetrennte) Token an. */
function RecipientField({
  label,
  hint,
  optional,
  value,
  onChange,
  colleagues,
  placeholder,
}: {
  label: string;
  hint?: string;
  optional?: boolean;
  value: string;
  onChange: (v: string) => void;
  colleagues: Colleague[];
  placeholder?: string;
}) {
  const [focused, setFocused] = useState(false);
  // Letztes (durch , oder ; getrenntes) Token = aktuelle Sucheingabe.
  const sepIdx = Math.max(value.lastIndexOf(","), value.lastIndexOf(";"));
  const needle = value.slice(sepIdx + 1).trim();
  const matches =
    focused && needle.length >= 2 ? rankColleagues(colleagues, needle, 6) : [];
  const append = (email: string) => {
    const prefix = value.slice(0, sepIdx + 1);
    onChange((prefix ? prefix + " " : "") + email + ", ");
  };
  return (
    <Field label={label} hint={hint} optional={optional}>
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => window.setTimeout(() => setFocused(false), 150)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {matches.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-surface shadow-lg">
            {matches.map((c) => (
              <button
                key={c.email}
                type="button"
                // mousedown-preventDefault: Klick feuert VOR dem Blur → Feld
                // verliert den Fokus nicht, bevor die Adresse angehängt ist.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => append(c.email)}
                className="block w-full px-3 py-1.5 text-left hover:bg-accent-50"
              >
                <div className="truncate text-sm text-text">{c.name}</div>
                <div className="truncate text-[11px] text-text-3">
                  {c.email}
                  {c.department ? ` · ${c.department}` : ""}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}

/**
 * KI-Antwort-Flow (Spec §5B): Laden → 2 Varianten → Editor → senden/verwerfen.
 * Gesendet wird direkt aus dem eigenen Postfach über POST /mail/send (email-service).
 * Trainings-Feedback: action=sent beim Senden, dismissed beim Verwerfen.
 * Die Mitarbeiter-Signatur (GET /mail/signature) wird an den Body angehängt.
 */
// Anker-Kinds, die der Merge-Resolver aus verknüpften Datensätzen auflösen kann.
const RESOLVER_ANCHOR_KINDS = ["Sales_Position", "Batch_Record", "Registration", "Account"];

/** "Vorlage einfügen"-Trigger mit direkt anliegender Dropdown-Liste: kompakt,
 *  nach Kategorie gruppiert (kleine Überschrift), darunter die Vorlagen-Namen.
 *  Pick → (bei >1 verknüpftem Datensatz Anker-Auswahl) → SF-Auflösung → onPicked.
 *  Ersetzt das frühere Vollbild-Modal. */
function TemplatePickerButton({
  recordLinks,
  onPicked,
  emailText,
  variant = "secondary",
  align = "left",
  className,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: {
  recordLinks: RecordLink[];
  onPicked: (r: ResolveTemplateResult, name: string) => void;
  /** Text der eingegangenen Mail → LLM finalisiert den Entwurf in deren Sprache. */
  emailText?: string;
  variant?: "primary" | "secondary";
  align?: "left" | "right";
  className?: string;
  /** Extern gesteuerter Offen-Zustand (P2.18: aus dem …-Menü geöffnet, ohne
   *  Nested-Dropdown). Fehlen die Props, bleibt der Button selbst-gesteuert. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Panel ohne eigenen Trigger rendern (headless) — Öffner ist das …-Menü. */
  hideTrigger?: boolean;
}) {
  const toast = useToast();
  const mt = useT().mail.vorlagen;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean | ((o: boolean) => boolean)) => {
      const value = typeof next === "function" ? next(open) : next;
      if (onOpenChange) onOpenChange(value);
      else setUncontrolledOpen(value);
    },
    [onOpenChange, open]
  );
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [anchorFor, setAnchorFor] = useState<Template | null>(null);
  const [resolving, setResolving] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Vorlagen beim ersten Öffnen laden.
  useEffect(() => {
    if (!open || templates !== null) return;
    let alive = true;
    api
      .get<Template[]>("/mail/templates")
      .then((t) => alive && setTemplates(t))
      .catch(() => alive && setTemplates([]));
    return () => {
      alive = false;
    };
  }, [open, templates]);

  // Klick außerhalb schließt die Dropdown.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setAnchorFor(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const anchors = useMemo(
    () =>
      recordLinks.filter(
        (r) => r.target_id && RESOLVER_ANCHOR_KINDS.includes(r.target_kind ?? "")
      ),
    [recordLinks]
  );

  const resolve = useCallback(
    async (tpl: Template, anchor: RecordLink | null) => {
      setResolving(true);
      try {
        const payload: Record<string, unknown> = { template_id: tpl.id };
        if (anchor?.target_id) {
          payload.anchor = { kind: anchor.target_kind, id: anchor.target_id };
        }
        if (emailText && emailText.trim()) payload.email_text = emailText;
        const res = await api.post<ResolveTemplateResult>(
          "/mail/resolve-template",
          payload
        );
        onPicked(res, tpl.name);
        setOpen(false);
        setAnchorFor(null);
      } catch (e) {
        const err = e as ApiError;
        const v = katalog().mail.vorlagen;
        toast.err(
          v.aufloesenFehlgeschlagen,
          err.status === 404 ? v.aufloesenNichtGefunden : v.aufloesenErneut
        );
      } finally {
        setResolving(false);
      }
    },
    [onPicked, toast, emailText]
  );

  const pick = (tpl: Template) => {
    if (anchors.length <= 1) void resolve(tpl, anchors[0] ?? null);
    else setAnchorFor(tpl);
  };

  const grouped = useMemo(() => {
    const g: Record<string, Template[]> = {};
    for (const t of templates ?? []) (g[t.category] ??= []).push(t);
    return g;
  }, [templates]);

  return (
    <div ref={wrapRef} className={clsx("relative inline-flex", className)}>
      {!hideTrigger && (
        <Button
          variant={variant}
          size="sm"
          icon={<FileText className="w-4 h-4" />}
          onClick={() => setOpen((o) => !o)}
        >
          {mt.einfuegen}
        </Button>
      )}
      {open && (
        <div
          className={clsx(
            "absolute top-full z-30 mt-1 max-h-[60vh] w-72 overflow-y-auto rounded-lg border border-border bg-surface p-1.5 shadow-lg",
            align === "right" ? "right-0" : "left-0"
          )}
        >
          {resolving ? (
            <div className="flex items-center gap-2 px-2 py-3 text-sm text-text-2">
              <Loader2 className="w-4 h-4 animate-spin" /> {mt.wirdAufgeloest}
            </div>
          ) : anchorFor ? (
            <div className="space-y-0.5">
              <div className="px-2 py-1 text-[11px] text-text-3">
                {mt.datensatzWaehlen(anchorFor.name)}
              </div>
              {anchors.map((a, i) => (
                <button
                  key={`${a.target_id}-${i}`}
                  type="button"
                  onClick={() => void resolve(anchorFor, a)}
                  className="w-full rounded-md px-2 py-1.5 text-left text-sm text-text hover:bg-accent-50"
                >
                  <span className="text-[10px] uppercase text-text-3">
                    {a.target_kind}
                  </span>{" "}
                  {a.name ?? a.target_id}
                </button>
              ))}
              <button
                type="button"
                onClick={() => void resolve(anchorFor, null)}
                className="w-full rounded-md px-2 py-1.5 text-left text-[13px] text-text-2 hover:bg-accent-50"
              >
                {mt.ohneDatensatz}
              </button>
            </div>
          ) : templates === null ? (
            <div className="px-2 py-3 text-sm text-text-2">
              <Loader2 className="mr-1 inline w-4 h-4 animate-spin" /> {mt.laedt}
            </div>
          ) : templates.length === 0 ? (
            <div className="px-2 py-3 text-[13px] italic text-text-3">{mt.keine}</div>
          ) : (
            Object.entries(grouped).map(([cat, tpls]) => (
              <div key={cat} className="mb-1">
                <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-text-3">
                  {cat}
                </div>
                {tpls.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => pick(t)}
                    className="block w-full truncate rounded-md px-2 py-1.5 text-left text-sm text-text hover:bg-accent-50"
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Dock-API des Detail-Kopf-Overflow-Menüs (P2.18). BINDEND: P0.3/P0.4/X1 hängen
 *  ihre Aktionen an dieses Item-Shape an — Shape + Reihenfolge nicht ändern.
 *  Reihenfolge-Konvention: reply-all · reply-blank · forward · template · archive ·
 *  delete(danger, immer zuletzt). */
type DetailAction = {
  key: string; // stabile id: reply-all|reply-blank|forward|template|schedule-from-mail|archive|delete
  label: string;
  icon: React.ReactNode; // lucide w-4 h-4
  onSelect: () => void;
  variant?: "default" | "danger"; // danger = Löschen (rot, immer zuletzt)
  disabled?: boolean;
  // Tooltip am Menü-Item (P2.16 Sichtbarkeits-Hook, Doc 04): z.B. der
  // Tastenkürzel-Hinweis „(Strg+Umschalt+A)" — X1 verdrahtet die Bindung.
  title?: string;
};

// X1 Dock-Brücke: DetailPane exponiert seine (readOnly-gegateten) `detailActions`
// + den Primär-CTA `openReplySuggest` an MailPage per Ref — der zentrale keydown-
// Effect und der `?do=`-Effect greifen darüber per Key zu (schedule-from-mail /
// template / delete-Gating), OHNE CustomEvent. Shape + Reihenfolge der Liste sind
// der Andock-Vertrag (siehe `detailActions`, Mail.tsx). Bei geschlossenem Detail
// ist die Ref `null` → alle mail-offen-Aktionen no-op.
type MailDetailActionsBridge = {
  actions: DetailAction[];
  openReplySuggest: () => void;
};

/** …-Overflow-Menü im Detail-Kopf (P2.18). Data-driven aus `items` (DetailAction[]).
 *  Dropdown-Muster wie TemplatePickerButton (absolutes Panel, Outside-Click-mousedown)
 *  + NEU: Escape schließt (Standard-Dismiss, kein Aktions-Kürzel). Kein Side-Drawer,
 *  kein globaler Key-Handler (Escape nur bei offenem Menü aktiv, Doc 04). */
function DetailActionMenu({
  items,
  align = "right",
  className,
}: {
  items: DetailAction[];
  align?: "left" | "right";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const mt = useT().mail.lesen;
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className={clsx("relative inline-flex", className)}>
      <Button
        variant="secondary"
        size="sm"
        icon={<MoreHorizontal className="w-4 h-4" />}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={mt.weitereAktionen}
        onClick={() => setOpen((o) => !o)}
      />
      {open && (
        <div
          role="menu"
          className={clsx(
            "absolute top-full z-30 mt-1 w-56 rounded-lg border border-border bg-surface p-1.5 shadow-lg",
            align === "right" ? "right-0" : "left-0"
          )}
        >
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              disabled={it.disabled}
              title={it.title}
              onClick={() => {
                setOpen(false);
                it.onSelect();
              }}
              className={clsx(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
                it.variant === "danger"
                  ? // Löschen optisch abgesetzt zuletzt (Trennlinie) + rot (text-danger
                    // wie die bestehenden Löschen-Buttons).
                    "mt-0.5 border-t border-border pt-2 text-danger hover:bg-danger/10"
                  : "text-text hover:bg-accent-50"
              )}
            >
              <span className="shrink-0">{it.icon}</span>
              <span className="truncate">{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Kontextmenü der Listenzeile (Rechtsklick am Desktop, Langdruck auf Touch).
 *
 *  Dieselbe Item-Form wie `DetailActionMenu` (`DetailAction[]`) und dieselbe
 *  Optik — nur nicht an einem Knopf verankert, sondern am ZEIGER. Zwei Dinge
 *  sind hier bewusst anders als im …-Menü:
 *
 *  1. Portal + `position: fixed`. Die Liste ist ein scrollendes <aside> mit
 *     fester Breite; ein `absolute`-Panel darin wird beschnitten — derselbe
 *     Grund, aus dem schon der Ordner-Aufklapper portalisiert ist (Mail.tsx,
 *     Kommentar am `folders-menu`).
 *  2. Umklappen statt Abschneiden: die Position wird nach dem Messen gesetzt
 *     (useLayoutEffect, also vor dem Paint — kein Springen). Passt das Panel
 *     rechts/unten vom Zeiger nicht mehr in den Viewport, geht es zur anderen
 *     Seite auf; danach wird hart in den sichtbaren Bereich geklemmt. Das ist
 *     der Fall „unterste Zeile einer langen Liste".
 *
 *  Tastatur: beim Öffnen liegt der Fokus auf dem ersten Eintrag, Pfeile/Pos1/Ende
 *  wandern, Escape schließt (der Aufrufer gibt den Fokus an die Zeile zurück),
 *  Tab schließt ebenfalls (kein Fokus-Käfig). KEIN globaler Key-Handler — die
 *  Listener leben nur, solange das Menü offen ist (Doc 04).
 */
function MailRowContextMenu({
  x,
  y,
  label,
  items,
  onClose,
}: {
  x: number;
  y: number;
  /** Betreff/Absender der Zeile — gibt dem Menü einen Namen für Screenreader. */
  label: string;
  items: DetailAction[];
  onClose: (returnFocus: boolean) => void;
}) {
  const mt = useT().mail.liste;
  const panelRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const M = 8; // Mindestabstand zum Viewport-Rand
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let left = x + w + M > window.innerWidth ? x - w : x;
    let top = y + h + M > window.innerHeight ? y - h : y;
    left = Math.max(M, Math.min(left, window.innerWidth - w - M));
    top = Math.max(M, Math.min(top, window.innerHeight - h - M));
    setBox({ left, top });
  }, [x, y, items.length]);

  // `preventScroll` ist hier NICHT Kosmetik: das Panel liegt `fixed` und immer
  // vollständig im Bild, ein Scrollen dorthin ist also nie nötig. Ohne das Flag
  // rollt der Browser den nächsten scrollbaren Vorfahren trotzdem an — und das
  // ausgelöste `scroll` schließt das Menü über den Listener unten sofort wieder.
  // Gemessen: der erste Rechtsklick nach einem geschlossenen Dialog öffnete gar
  // nichts, der zweite ging auf.
  useEffect(() => {
    panelRef.current
      ?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')
      ?.focus({ preventScroll: true });
  }, []);

  // Außen-Klick + Scrollen/Resize schließen. Bewusst OHNE Backdrop-Element: ein
  // Overlay würde den nativen Rechtsklick auch außerhalb der Zeile abfangen —
  // und im Lesebereich will man kopieren. So schließt der `mousedown` das Menü,
  // und das native Menü des Browsers geht ganz normal an seinem Ziel auf.
  const geoeffnetAm = useRef(performance.now());
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) onClose(false);
    };
    // Scrollen schließt: das Menü hängt am Zeiger, die Zeile darunter wandert
    // weg. ABER erst nach einer kurzen Schonfrist — ein Scrollvorgang, der VOR
    // dem Rechtsklick lief (Browser stellt die Zeile ins Bild, Trägheitsscroll
    // auf Touch), liefert sein `scroll`-Ereignis erst danach aus und schlösse
    // das Menü im selben Atemzug wieder. Gemessen: das Menü ging auf und war
    // sofort wieder weg, der zweite Rechtsklick funktionierte.
    const onAway = () => {
      if (performance.now() - geoeffnetAm.current < 250) return;
      onClose(false);
    };
    document.addEventListener("mousedown", onDown);
    // capture: die Liste scrollt in einem inneren Container, nicht am window.
    window.addEventListener("scroll", onAway, true);
    window.addEventListener("resize", onAway);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onAway, true);
      window.removeEventListener("resize", onAway);
    };
  }, [onClose]);

  const focusAt = (delta: number, absolute?: "first" | "last") => {
    const el = panelRef.current;
    if (!el) return;
    const all = [
      ...el.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])'),
    ];
    if (!all.length) return;
    const cur = all.indexOf(document.activeElement as HTMLElement);
    const next =
      absolute === "first"
        ? 0
        : absolute === "last"
          ? all.length - 1
          : (cur + delta + all.length) % all.length;
    all[next]?.focus();
  };

  return createPortal(
    <div
      ref={panelRef}
      data-mail-rowmenu
      role="menu"
      aria-label={mt.menuAria(label)}
      tabIndex={-1}
      style={{ left: box?.left ?? -9999, top: box?.top ?? -9999 }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          onClose(true);
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          focusAt(1);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          focusAt(-1);
        } else if (e.key === "Home") {
          e.preventDefault();
          focusAt(0, "first");
        } else if (e.key === "End") {
          e.preventDefault();
          focusAt(0, "last");
        } else if (e.key === "Tab") {
          onClose(false);
        }
      }}
      // Breiter als das …-Menü (w-56): dort sind die Beschriftungen kürzer.
      // Hier steht „Zur Nachverfolgung markieren" — bei w-60 schnitt `truncate`
      // sie zu „Zur Nachverfolgung marki…" ab, und ein abgeschnittener
      // Menü-Eintrag ist keine Auffindbarkeit.
      className="fixed z-40 w-72 max-h-[80vh] overflow-y-auto rounded-lg border border-border bg-surface p-1.5 shadow-lg"
    >
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          role="menuitem"
          data-mail-rowmenu-item={it.key}
          disabled={it.disabled}
          title={it.title}
          onClick={() => {
            onClose(false);
            it.onSelect();
          }}
          className={clsx(
            "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
            it.variant === "danger"
              ? "mt-0.5 border-t border-border pt-2 text-danger hover:bg-danger/10 focus-visible:bg-danger/10 focus-visible:outline-none"
              : "text-text hover:bg-accent-50 focus-visible:bg-accent-50 focus-visible:outline-none"
          )}
        >
          <span className="shrink-0">{it.icon}</span>
          <span className="truncate">{it.label}</span>
        </button>
      ))}
    </div>,
    document.body
  );
}

/** Undo-Send (P2.15): der GESAMTE beim „Senden"-Klick EINGEFRORENE Send. Spätere
 *  Edits während des Rückhol-Fensters ändern diesen Puffer NICHT — nach
 *  „Rückgängig" editiert der Nutzer weiter und löst einen NEUEN Send aus. */
interface FrozenSend {
  kind: ComposeKind;
  imid?: string;
  recipients: string[];
  ccList: string[];
  bccList: string[];
  subject: string;
  /** gebrandeter HTML-Body inkl. Signatur (new/reply/reply-all). */
  html: string;
  /** gewhitelistete Notiz für Weiterleiten (Original zitiert Graph selbst). */
  forwardComment: string;
  /** Gibt es einen Autosave-Entwurf, geht der Versand IMMER über ihn (er wird
   *  zur gesendeten Mail → keine Waise in „Entwürfe"). ``noExtras`` gibt es
   *  nicht mehr: Bcc und Anhänge liegen inzwischen selbst am Entwurf. */
  draftId: string | null;
  attachments: { content_version_id: string; filename: string | null }[];
  rawAttachments: RawAttachment[];
  /** für sendFeedback("sent") NUR bei kind==="reply". */
  feedbackBody: string;
}

/** Undo-Send (P2.15, Slice B): kleines ruhiges Fenster-<select> im Composer-Footer.
 *  Persistiert rein in localStorage (keine Migration). Zeigt den gespeicherten Wert,
 *  wenn er in der Whitelist liegt, sonst den Default-Eintrag (7 s). */
function UndoWindowSelect() {
  const mt = useT().mail.composer;
  const [val, setVal] = useState<number>(() => {
    const cur = getUndoWindowSeconds();
    return UNDO_SEND_OPTIONS.some((o) => o.value === cur) ? cur : UNDO_SEND_DEFAULT_S;
  });
  return (
    <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-text-3">
      <Undo2 className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{mt.rueckholzeit}</span>
      <select
        value={val}
        onChange={(e) => {
          const v = Number(e.target.value);
          setVal(v);
          setUndoWindowSeconds(v);
        }}
        aria-label={mt.rueckholzeitAria}
        className="rounded-md border border-border bg-surface px-1.5 py-1 text-xs text-text-2 focus:outline-none focus:border-accent-500"
      >
        {UNDO_SEND_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {mt.rueckholzeitOption(o.value)}
          </option>
        ))}
      </select>
    </label>
  );
}

function ReplyPanel({
  imid,
  kind = "reply",
  category,
  subCategory,
  mode = "ai",
  replyTo,
  replySubject,
  recordLinks,
  emailText,
  initialBody,
  initialAttachments,
  draftId: initialDraftId,
  initialSubject,
  initialTo,
  initialCc,
  initialBcc,
  colleagues = [],
  ownerEmail = "",
  toRecipients = [],
  ccRecipients = [],
  onClose,
}: {
  /** internetMessageId der Bezugsnachricht. Bei ``kind="new"`` (Neue Mail)
   *  gibt es keine — dann bleibt es undefined (kein Auto-Mark/Thread-Anker). */
  imid?: string;
  /** Verfassen-Fall: reply (Default) · reply-all · forward · new. */
  kind?: ComposeKind;
  category: string | null;
  subCategory: string | null;
  /** "ai" = KI-Vorschläge laden (nur bei kind=reply), "blank" = leerer Editor. */
  mode?: "ai" | "blank";
  /** Vorbelegter Empfänger (Absender der Original-Mail). Editierbar. */
  replyTo: string;
  /** Betreff der Original-Mail (für "Re:"/"Fwd:"). */
  replySubject: string;
  /** Verknüpfte SF-Datensätze des Threads — Anker für die Vorlagen-Auflösung. */
  recordLinks: RecordLink[];
  /** Text der eingegangenen Mail → LLM finalisiert die Vorlage in deren Sprache. */
  emailText?: string;
  /** Vorbefüllter Body als HTML (P1.10: der ``body``-State IST Editor-HTML).
   *  Markdown-Quellen (Vorlage/KI) werden vom Aufrufer via ``mdToHtml`` konvertiert;
   *  ein fortgesetzter Entwurf liefert sein ``body_html`` direkt. */
  initialBody?: string;
  initialAttachments?: MailAttachment[];
  /** Entwurf fortsetzen (Slice B): bestehende Graph-Entwurfs-Id + vorbefüllte
   *  Felder. Weiter-Tippen PATCHt denselben Entwurf statt einen neuen anzulegen. */
  draftId?: string;
  initialSubject?: string;
  initialTo?: string;
  initialCc?: string;
  initialBcc?: string;
  /** @bitop.de-Kollegen für das Empfänger-Autocomplete-Dropdown. */
  colleagues?: Colleague[];
  /** Eigenes Postfach (owner_ref) — wird bei "Allen antworten" aus dem
   *  vorbelegten Verteiler entfernt (kein Selbst-Empfang). */
  ownerEmail?: string;
  /** Original-An/-Cc-Verteiler — Grundlage der reply-all-Vorbelegung. */
  toRecipients?: MailRecipient[];
  ccRecipients?: MailRecipient[];
  onClose: () => void;
}) {
  const toast = useToast();
  const mt = useT().mail.composer;
  const mtAnh = useT().mail.anhaenge;
  const ct = useT().common;
  // KI-Vorschläge nur im Antworten-Fall (reply) + ai-Modus; new/reply-all/forward
  // starten immer direkt im Editor (kein KI-Call, brauchen keine imid dafür).
  const aiFlow = kind === "reply" && mode === "ai";
  const usesSendPath = kind === "new" || kind === "reply"; // → POST /mail/send
  // ── Wer schreibt einen Autosave-Entwurf? (P1.8 + Tester-Feedback 19.08.) ────
  // NICHT dasselbe wie ``usesSendPath``: der steuert den VERSANDWEG (und damit
  // das Betreff-Feld, das es bei reply-all/forward bewusst nicht gibt — Graph
  // setzt „RE:"/„FW:" selbst). Ob halbfertiger Text gesichert wird, ist eine
  // andere Frage — und die Antwort war für „Allen antworten" schlicht falsch:
  // wer dort tippte und das Fenster schloss, verlor alles.
  //
  // „Weiterleiten" bleibt bewusst DRAUSSEN. Es ist derselbe Schalter, aber
  // nicht derselbe Inhalt: der Versand schickt nur die Notiz (``forwardComment``),
  // Zitat und Original-Anhänge erzeugt Graph erst beim Senden (createForward).
  // Ein Entwurf trüge also NUR die Notiz — und beim Fortsetzen (das ist immer
  // ``kind="new"``) käme eine Mail ohne das weitergeleitete Original zurück.
  // Ein Entwurf, der die Hälfte verschweigt, ist schlechter als kein Entwurf.
  const speichertEntwurf = usesSendPath || kind === "reply-all";
  const [loading, setLoading] = useState(aiFlow);
  const [error, setError] = useState<ApiError | null>(null);
  const [data, setData] = useState<ReplySuggestResponse | null>(null);
  const [chosen, setChosen] = useState<number | null>(null);
  // Beim Fortsetzen eines Entwurfs (Slice B) gewinnen die vorbefüllten Werte;
  // sonst die verfassen-fall-abhängige Standard-Vorbelegung.
  const [subject, setSubject] = useState(
    initialSubject ??
      (aiFlow || kind === "new"
        ? ""
        : kind === "forward"
          ? fwdSubject(replySubject)
          : reSubject(replySubject))
  );
  const [body, setBody] = useState(initialBody ?? "");
  const [sig, setSig] = useState<MailSignature | null>(null);
  // Signatur für DIESE eine Mail abschalten (Tester-Wunsch: „Gibt es eine
  // Möglichkeit, die Signatur nicht anzuhängen?"). Bewusst als gewöhnlicher
  // Komponenten-State und NICHT in localStorage/user_settings:
  //   * Der Standard bleibt „an" — niemandes Gewohnheit ändert sich ungefragt.
  //     Eine gemerkte Einstellung würde nach EINER abgeschalteten Mail still
  //     jede weitere ohne Signatur rausschicken; das merkt man erst beim
  //     Empfänger.
  //   * Das Zurücksetzen fällt damit gratis an und braucht keinen eigenen
  //     Effekt: ``ReplyPanel`` wird an allen drei Stellen KONDITIONAL gerendert
  //     (``{replyMode && …}`` in der Detailspalte, ``{composeNew && …}`` und
  //     ``{resumeDraft && …}`` in der Seite). Schließen heißt Unmount, das
  //     nächste Verfassen-Fenster ist ein frischer Mount mit ``true``. Ein
  //     Reset-Effekt daneben wäre eine zweite Wahrheit über denselben Zustand.
  // Wirkt an GENAU EINER Stelle (dem eingefrorenen Send-Payload weiter unten),
  // damit alle drei Versandwege — /mail/send, /mail/reply-all und der
  // Entwurfs-Pfad — dieselbe Antwort geben.
  const [signaturAnhaengen, setSignaturAnhaengen] = useState(true);
  // Steht die Signatur im eigenen Teil schon (in Outlook verfasster Entwurf),
  // hängt der Send-Pfad KEINE zweite an (``spliceSignatureHtml``). Dann darf die
  // Vorschau darunter auch nicht weiter „wird beim Senden angehängt" behaupten —
  // sonst sagt die UI etwas zu, das der Versand nicht mehr tut.
  const sigSchonImText = useMemo(
    () => (sig ? ownPartHasSignature(body, sig.html) : false),
    [body, sig]
  );
  // ── Abschlussblock (Task 57) ───────────────────────────────────────────────
  // Verabschiedung + Signatur sollen SICHTBAR dastehen, sobald der Composer
  // aufgeht — der Nutzer soll sehen, dass unten nichts mehr zu tun ist.
  // Aufgeteilt an genau der Naht, die das Backend selbst zieht
  // (``SignatureBlockResponse.closing`` vs. Signatur-HTML):
  //   * Die VERABSCHIEDUNG steht im Editor. Sie gehört dem Nutzer — er kann sie
  //     umformulieren oder löschen, und sie ist whitelist-rein.
  //   * Die SIGNATUR steht als Vorschau darunter, weil sie beim Senden ohnehin
  //     deterministisch angehängt wird (``spliceSignatureHtml``) UND weil von
  //     ihrem gebrandeten HTML nichts die Editor-Whitelist überlebt — gemessen,
  //     s. ``docs/evidence/task-57/whitelist-sonde.md``. Im Editor wäre sie eine
  //     Zusage, die der erste Tastendruck bricht.
  const editorHandle = useRef<RichTextHandle | null>(null);
  // Zähler statt direktem Aufruf: die Schreibmarke darf erst gesetzt werden,
  // NACHDEM der Editor sein DOM neu geseedet hat. Ein Aufruf direkt im
  // ``.then()`` landet vor dem Re-Seed — ``el.innerHTML = value`` wirft die
  // Selektion danach weg, und die Tastendrücke des Nutzers laufen ins Leere
  // (gemessen: sie lösen dann globale Kürzel aus). Als State-Zähler läuft er
  // im Eltern-Effekt, der nach den Kind-Effekten und damit nach dem Seed liegt.
  const [caretHomeTick, setCaretHomeTick] = useState(0);
  // Das eingefügte Verabschiedungs-HTML — Anker für die Vorlagen-Einfügung
  // ("Inhalt kommt DARÜBER"). Findet sie es nicht mehr, hat der Nutzer den Block
  // bearbeitet oder gelöscht; dann bleibt er weg (er kommt NICHT zurück).
  const closingHtmlRef = useRef<string>("");
  const closingSeededRef = useRef(false);
  // Empfänger vorbelegt je Verfassen-Fall, editierbar; Cc/Bcc optional.
  const [to, setTo] = useState(
    () => initialTo ?? prefillTo(kind, replyTo, toRecipients, ownerEmail)
  );
  const [cc, setCc] = useState(
    () => initialCc ?? prefillCc(kind, ccRecipients, ownerEmail)
  );
  // Ein fortgesetzter Entwurf bringt sein Bcc mit — dann steht das Feld auch
  // offen, sonst wäre die Blindkopie unsichtbar im Versand.
  const [bcc, setBcc] = useState(initialBcc ?? "");
  const [showBcc, setShowBcc] = useState(!!(initialBcc ?? "").trim());
  const [rawAttachments, setRawAttachments] = useState<RawAttachment[]>([]);
  // Abgelehnte Datei (zu groß) — als Inline-Meldung am Anhang-Block, NICHT als
  // Toast: der Nutzer steht in diesem Moment genau dort und muss entscheiden,
  // welche Datei weichen soll.
  const [anhangFehler, setAnhangFehler] = useState<string | null>(null);
  // Anhang-Sync zum Entwurf fehlgeschlagen. Steht in DERSELBEN Meldezeile: der
  // Anhang bleibt im Composer und geht beim Senden trotzdem mit (der Sende-Pfad
  // holt ihn nach) — der Nutzer soll nur wissen, dass er gerade NICHT im
  // zwischengeparkten Entwurf liegt.
  const [anhangSyncFehler, setAnhangSyncFehler] = useState<string | null>(null);
  // Datei-Zug über der Composer-Karte (Overlay). ``dragTiefe`` zählt
  // enter/leave: jedes Kind-Element löst beim Überfahren ein eigenes Paar aus,
  // ohne Zähler flackerte das Overlay über jeder Feldgrenze.
  const [dragUeber, setDragUeber] = useState(false);
  const dragTiefe = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [queuedAttachments, setQueuedAttachments] = useState<QueuedAttachment[]>(
    initialAttachments ?? []
  );
  const [sending, setSending] = useState(false);
  // Undo-Send (P2.15): Rückhol-Fenster nach „Senden". ``pendingSend`` = Fenster
  // aktiv (Footer zeigt die Undo-Zeile, Editor gesperrt); ``undoLeft`` = Countdown.
  const [pendingSend, setPendingSend] = useState(false);
  const [undoLeft, setUndoLeft] = useState(0);
  const feedbackSent = useRef(false);

  // ── Autosave-Entwurf (P1.8) ────────────────────────────────────────────────
  // NUR auf dem Send-Pfad (new/reply). reply-all/forward erzeugen Graph-seitig
  // eigene Drafts (createReplyAll/createForward) → ein paralleler Autosave würde
  // Dubletten anlegen. Der Composer legt bei der ersten sinnvollen Änderung EINEN
  // Graph-Entwurf an (POST) und aktualisiert ihn debounced (PATCH), sodass
  // halbfertiger Text in den Outlook-Entwürfen liegt — unsichtbar, kein Button.
  const [draftId, setDraftId] = useState<string | null>(initialDraftId ?? null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [savingNow, setSavingNow] = useState(false);
  // Refs, damit Flush-on-Unmount + der (leer-Deps) Debounce-Callback stale-closure-
  // frei auf den frischesten Stand zugreifen.
  const draftIdRef = useRef<string | null>(initialDraftId ?? null);
  const creatingRef = useRef(false); // Create-in-flight-Lock (genau EIN Create)
  const sendingRef = useRef(false); // Autosave während des Sendens aussetzen
  const aliveRef = useRef(true); // kein setState nach Unmount
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Undo-Send-Fenster (P2.15): Fire-Timer + Countdown-Intervall + eingefrorener
  // Payload. ``pendingSendRef`` spiegelt ``pendingSend`` stale-closure-frei für den
  // Unmount-Cleanup (fire-and-forget).
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const undoIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingSendRef = useRef(false);
  const frozenRef = useRef<FrozenSend | null>(null);
  const draftSnapshot = (
    s: string, b: string, t: string, c: string, bc: string
  ) => `${s}\u0000${b}\u0000${t}\u0000${c}\u0000${bc}`;
  // Der beim Öffnen eingesetzte Abschlussblock (Task 57) ist KEINE Eingabe des
  // Nutzers und zählt deshalb nicht als Inhalt. Ohne diese Herausrechnung wäre
  // der Vorsatz „leerer Composer → NIE ein Create" (s. ``saveDraftRef``) tot:
  // jedes bloße Öffnen des Composers legte nach ~1.5 s einen Outlook-Entwurf an,
  // ohne dass jemand eine Taste gedrückt hat — und weil der Block mitgespeichert
  // wird, käme dieser Entwurf beim Fortsetzen mit einem Gruß zurück. Gemessen:
  // „Neue Mail" geöffnet, 4 s gewartet, nichts getippt → POST /mail/drafts = 1;
  // danach geschlossen → 2, denn der Flush-on-Unmount stellt dieselbe Frage.
  //
  // Verglichen wird whitespace-freier Text (``textKey``), nicht HTML: der Editor
  // serialisiert den Block beim ersten ``onInput`` neu.
  //
  // Die Alternative „den Block in die Baseline aufnehmen" wäre riskanter: die
  // Baseline entscheidet auch, ob der Flush-on-Unmount noch etwas rausschickt —
  // wer sie nachträglich überschreibt, verschluckt Text, den der Nutzer während
  // der asynchronen Lücke getippt hat.
  const bodyIsOnlyClosing = (b: string) => {
    if (!b.trim()) return true;
    const closing = textKey(closingHtmlRef.current);
    if (!closing) return false;
    return textKey(b).split(closing).join("") === "";
  };
  const draftIsEmpty = (s: string, b: string, t: string, c: string, bc: string) =>
    !s.trim() && !t.trim() && !c.trim() && !bc.trim() && bodyIsOnlyClosing(b);
  // Baseline = Stand beim Öffnen (Reply-Prefill ODER fortgesetzter Entwurf) →
  // kein Autosave, bis der Nutzer WIRKLICH etwas ändert.
  const lastSavedRef = useRef<string>("");
  const baselineInitRef = useRef(false);
  if (!baselineInitRef.current) {
    lastSavedRef.current = draftSnapshot(subject, body, to, cc, bcc);
    baselineInitRef.current = true;
  }
  // Immer den frischesten Stand für den Unmount-Flush + den Save-Callback halten.
  const latest = useRef({ subject, body, to, cc, bcc, sig });
  latest.current = { subject, body, to, cc, bcc, sig };
  const speichertEntwurfRef = useRef(speichertEntwurf);
  speichertEntwurfRef.current = speichertEntwurf;
  // Der AKTUELLE Anhang-Stand für den Sende-Pfad. Bewusst ein Ref und NICHT der
  // eingefrorene Payload: der Anhang-Sync kann während des Rückhol-Fensters noch
  // fertig werden, und ein eingefrorenes „ist noch nicht am Entwurf" würde die
  // Datei danach ein ZWEITES Mal anhängen.
  const anhaengeRef = useRef({ raw: rawAttachments, queued: queuedAttachments });
  anhaengeRef.current = { raw: rawAttachments, queued: queuedAttachments };

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const added: RawAttachment[] = [];
    const zuGross: string[] = [];
    // Die SCHON angehängten Bytes zählen mit — sonst käme man in drei Schritten
    // über eine Grenze, die ein Schritt verweigert. Die Vorlagen-Anhänge
    // (``queuedAttachments``, SF) fehlen in dieser Summe zwangsläufig: ihre
    // Bytes liegen erst serverseitig vor. Dafür gibt es das Server-Gate, dessen
    // 413 der Sende-Pfad unten auf Deutsch anzeigt.
    let summe = rawAttachments.reduce((n, a) => n + a.content_bytes.length, 0);
    for (const f of Array.from(files)) {
      try {
        const content_bytes = await readFileAsBase64(f);
        if (summe + content_bytes.length > MAX_ANHANG_B64) {
          zuGross.push(f.name);
          continue;
        }
        summe += content_bytes.length;
        added.push({
          name: f.name,
          content_type: f.type || "application/octet-stream",
          content_bytes,
        });
      } catch {
        toast.err(mtAnh.leseFehler, mtAnh.leseFehlerText(f.name));
      }
    }
    if (added.length) {
      setRawAttachments((q) => [...q, ...added]);
      // Eine angehängte Datei IST eine sinnvolle Änderung — sie legt den Entwurf
      // an, falls es noch keinen gibt, und wandert sofort an ihn.
      void anhaengeSynchronisieren(added, []);
    }
    setAnhangFehler(zuGross.length ? mtAnh.zuGross(zuGross) : null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Entwurf fortsetzen: die Dateien, die schon AM Entwurf hängen, als Chips
  // holen. Ohne das behauptete der wieder geöffnete Composer einen Entwurf ohne
  // Anhänge — und beim Senden gingen kommentarlos Dateien mit, die niemand mehr
  // sah. ``content_bytes`` bleibt leer: die Bytes liegen bei Graph, der Chip
  // trägt nur Namen und ``attachment_id`` (= der Griff zum Entfernen).
  useEffect(() => {
    if (!initialDraftId) return;
    let alive = true;
    api
      .get<DraftAttachmentOut[]>(
        `/mail/drafts/${encodeURIComponent(initialDraftId)}/attachments`
      )
      .then((atts) => {
        if (!alive || !atts.length) return;
        setRawAttachments((q) => [
          ...atts.map((a) => ({
            name: a.name,
            content_type: a.content_type,
            content_bytes: "",
            attachment_id: a.attachment_id,
          })),
          ...q,
        ]);
      })
      .catch(() => {
        /* best-effort: ohne Liste bleibt der Composer wie bisher */
      });
    return () => {
      alive = false;
    };
  }, [initialDraftId]);

  // Mitarbeiter-Signatur einmalig laden (best-effort; scheitert der Call,
  // fällt das Kopieren auf reine [Name]-Entfernung zurück).
  useEffect(() => {
    let alive = true;
    api
      .get<MailSignature>("/mail/signature")
      .then((s) => {
        if (alive) setSig(s);
      })
      .catch(() => {
        /* best-effort */
      });
    return () => {
      alive = false;
    };
  }, []);

  // Abschlussblock beim Öffnen einfügen (Task 57). Deterministisch und ohne
  // LLM (Sprache per ``detect_language`` im Backend) → er steht sofort da.
  //
  // NICHT im KI-Voll-Vorschlag (``aiFlow``): der ist eine komplette Antwort und
  // endet laut Prompt selbst auf einem Gruß (``app/mail/reply.py:368``) — ein
  // zweiter darunter wäre ein Fehler, kein Service.
  //
  // NICHT beim Weiterleiten. Das ist KEINE Bauentscheidung („nur eine Notiz"),
  // sondern eine ausdrückliche Entscheidung des Auftraggebers (2026-07-30) gegen
  // seine eigene frühere Ansage „unabhängig davon welcher Modus": eine
  // Weiterleitung an eine Kollegin („schau mal") mit voller Grußformel und
  // Firmensignatur darunter wirkt fremd — und die weitergeleitete Mail trägt
  // darunter ohnehin schon eine Signatur. Wer das für ein Versehen hält und
  // „mitfixt", dreht eine getroffene Entscheidung zurück; die Zusagen dazu
  // stehen in ``frontend/abschlussblock-walk.mjs`` (Abschnitt G) und decken
  // Editor UND gesendeten ``comment`` ab.
  useEffect(() => {
    if (aiFlow || kind === "forward") return;
    let alive = true;
    api
      .post<SignatureBlock>("/mail/signature-block", {
        source_text: emailText ?? null,
      })
      .then((blk) => {
        if (!alive || closingSeededRef.current) return;
        const html = closingToHtml(blk.closing);
        if (!html) return;
        closingSeededRef.current = true;
        closingHtmlRef.current = html;
        // Leer-Prüfung auf ``latest`` (wird bei jedem Render gesetzt) und NICHT
        // im ``setBody``-Updater: der läuft erst in der Render-Phase, sein
        // Seiteneffekt wäre hier noch nicht passiert.
        const blankBefore = isBlankHtml(latest.current.body);
        setBody((prev) => insertClosingIntoBody(prev, html));
        // Schreibmarke ÜBER den Block — aber nur, wenn oben noch nichts steht.
        // Bei einem fortgesetzten Entwurf würde ein Sprung an den Anfang den
        // Nutzer aus seinem Text reißen.
        if (blankBefore) setCaretHomeTick((n) => n + 1);
      })
      .catch(() => {
        /* best-effort: ohne Block schreibt der Nutzer wie bisher weiter */
      });
    return () => {
      alive = false;
    };
  }, [aiFlow, kind, emailText]);

  // Läuft NACH dem Re-Seed des Editors (Kind-Effekte zuerst) → die Selektion
  // überlebt. Sonst schreibt der Nutzer beim ersten Tastendruck ins Nichts.
  useEffect(() => {
    if (caretHomeTick === 0) return;
    editorHandle.current?.caretToStart();
    // …und sie bleibt oben, bis der Nutzer selbst tippt — sonst landet der
    // erste Klick in die leere Fläche unter dem Block.
    editorHandle.current?.armCaretHome();
  }, [caretHomeTick]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setChosen(null);
    try {
      const res = await api.post<ReplySuggestResponse>("/mail/reply-suggestions", {
        internet_message_id: imid,
      });
      setData(res);
      // Ein Vorschlag → direkt in den Editor (kein Varianten-Auswahlschritt).
      if (res.variants.length === 1) {
        setChosen(0);
        setSubject(res.variants[0].subject);
        setBody(mdToHtml(res.variants[0].body_md));
      }
    } catch (e) {
      setError(e as ApiError);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [imid]);

  useEffect(() => {
    // Nur der Antworten-Fall mit ai-Modus lädt KI-Vorschläge (und braucht eine
    // imid); new/reply-all/forward/blank zeigen den Editor direkt.
    if (!aiFlow) return;
    void load();
  }, [load, aiFlow]);

  const pick = (idx: number) => {
    const v = data?.variants[idx];
    if (!v) return;
    setChosen(idx);
    setSubject(v.subject);
    setBody(mdToHtml(v.body_md));
  };

  // Netz-Hälfte der Formulierhilfe (Task 57). Derselbe Endpunkt wie der
  // Voll-Vorschlag; die Anwesenheit von draft_text/instruction schaltet ihn in
  // den Composer-Modus (app/schemas/mail.py ReplySuggestRequest.is_composer).
  // Ohne Bezugsnachricht (Neue Mail) entfällt die imid — der Vertrag lässt das
  // im Composer-Modus ausdrücklich zu.
  const runComposerAssist = useCallback(
    async (req: { draftText: string; instruction: string | null }) => {
      // Der Abschlussblock (Task 57) ist KEIN vom Nutzer geschriebener Text — er
      // steht ab dem Öffnen im Editor. Ginge er als ``draft_text`` mit, wäre der
      // „bisherige Text" NIE leer, und im Backend kippte damit dauerhaft die
      // Anrede-Weiche (``app/mail/reply.py``): statt „der bisherige Text ist
      // leer — beginne mit einer passenden Anrede" bekäme das Modell „prüfe, ob
      // schon eine Anrede dasteht" — vorgelegt mit einem Text, in dem „Grüßen"
      // steht. Damit hätte die Zusage „es muss auch eine Begrüßungs-Formel
      // eingefügt werden" keine Stelle mehr, an der sie feuern kann.
      //
      // Gefragt wird mit ``bodyIsOnlyClosing`` — demselben Hebel, der schon
      // „leerer Composer legt keinen Entwurf an" beantwortet. Zwei eigene
      // Antworten auf dieselbe Frage liefen garantiert auseinander.
      //
      // BEKANNTE GRENZE (benannt statt still): ``bodyIsOnlyClosing`` erwartet
      // HTML, hier kommt der linearisierte TEXT an. ``textKey`` darin schickt
      // ihn durch den HTML-Parser — steht im Entwurf ausser der Verabschiedung
      // NICHTS ausser einem Ausdruck in spitzen Klammern (``<meier@kunde.de>``),
      // schluckt der ihn und der Entwurf gilt als leer. Der Preis ist eine
      // Anrede, die das Modell in diesem Randfall zusätzlich setzt; ein zweiter,
      // eigener Vergleich für dieselbe Frage wäre der teurere Fehler.
      const draftText = bodyIsOnlyClosing(req.draftText) ? "" : req.draftText;
      const res = await api.post<ReplySuggestResponse>("/mail/reply-suggestions", {
        internet_message_id: imid ?? null,
        draft_text: draftText,
        instruction: req.instruction,
      });
      return res.variants[0]?.body_md ?? "";
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imid]
  );

  // Aufgelöste Vorlage in den Editor übernehmen + CoA-Anhang vormerken.
  const handleInsert = (r: ResolveTemplateResult, name: string) => {
    // Die Vorlage ersetzt den Entwurf, aber NICHT den Abschlussblock: der steht
    // weiter unten (Task 57), die Vorlage kommt darüber. Ist der Block nicht
    // mehr da (gelöscht/bearbeitet), holt ihn auch die Vorlage nicht zurück.
    setBody((prev) => {
      const tpl = mdToHtml(r.preview_md);
      const blk = closingHtmlRef.current;
      return blk && prev.includes(blk) ? `${tpl}${blk}` : tpl;
    });
    // Die Vorlage ERSETZT die bisherigen Vorlagen-Anhänge. Was davon schon am
    // Entwurf hing, muss dort auch weg — sonst schickte der Entwurfs-Versand
    // eine Datei mit, die im Composer längst nicht mehr steht.
    for (const alt of queuedAttachments) anhangAmEntwurfEntfernen(alt);
    setQueuedAttachments(r.attachments);
    void anhaengeSynchronisieren([], r.attachments);
    const v = katalog().mail.vorlagen;
    const parts = [v.felderGefuellt(Object.keys(r.filled).length)];
    if (r.gaps.length) parts.push(v.offen(r.gaps.length));
    if (r.attachments.length) parts.push(v.anhang(r.attachments.length));
    if (r.sf_error) parts.push(v.sfNichtErreichbar);
    toast.ok(v.eingefuegt(name), parts.join(" · "));
  };

  const sendFeedback = useCallback(
    async (action: "copied" | "dismissed" | "sent", finalMd: string | null) => {
      if (!data) return;
      const suggested =
        chosen != null
          ? data.variants[chosen]?.body_md ?? ""
          : data.variants[0]?.body_md ?? "";
      try {
        await api.post("/mail/reply-feedback", {
          internet_message_id: imid,
          variant_count: data.variants.length,
          chosen_variant: chosen,
          suggested_md: suggested,
          final_md: finalMd,
          action,
          category,
          sub_category: subCategory,
        });
      } catch {
        /* Feedback ist best-effort — Fehler darf den UX-Fluss nicht kippen. */
      }
    },
    [data, chosen, imid, category, subCategory]
  );

  const parseAddrs = (s: string): string[] =>
    s.split(/[,;]/).map((a) => a.trim()).filter(Boolean);

  // Legt GENAU EINEN Entwurf an und liefert dessen Id — auch wenn Autosave und
  // Anhang-Sync im selben Moment danach fragen. Beide teilen sich denselben
  // in-flight-Promise; zwei eigene Create-Wege hätten zwei Entwürfe angelegt und
  // damit genau die Dublette erzeugt, die diese Runde beseitigen soll.
  const createInFlightRef = useRef<Promise<string | null> | null>(null);
  const createDraftOnce = (): Promise<string | null> => {
    if (draftIdRef.current) return Promise.resolve(draftIdRef.current);
    if (createInFlightRef.current) return createInFlightRef.current;
    const { subject: s, body: b, to: t, cc: c, bcc: bc } = latest.current;
    const snap = draftSnapshot(s, b, t, c, bc);
    creatingRef.current = true;
    const p = api
      .post<{ draft_id: string }>("/mail/drafts", {
        subject: s,
        body_html: sanitizeEditorHtml(b),
        to: parseAddrs(t),
        cc: parseAddrs(c),
        bcc: parseAddrs(bc),
      })
      .then((res) => {
        draftIdRef.current = res.draft_id;
        lastSavedRef.current = snap;
        if (aliveRef.current) {
          setDraftId(res.draft_id);
          setSavedAt(new Date());
        }
        return res.draft_id;
      })
      .catch(() => null) // still — lastSavedRef unverändert → Retry beim Debounce
      .finally(() => {
        creatingRef.current = false;
        createInFlightRef.current = null;
        // Während des Create eingegangene Änderungen nachziehen. Steht HIER und
        // nicht beim Aufrufer, weil inzwischen zwei Wege einen Create auslösen
        // (Autosave und Anhang-Sync) — beim Aufrufer wäre es zweimal dieselbe
        // Regel, und die zweite bliebe irgendwann zurück.
        const cur = latest.current;
        if (
          draftIdRef.current &&
          !sendingRef.current &&
          draftSnapshot(cur.subject, cur.body, cur.to, cur.cc, cur.bcc) !==
            lastSavedRef.current
        ) {
          void saveDraftRef.current();
        }
      });
    createInFlightRef.current = p;
    return p;
  };

  // ── Anhänge AM Entwurf ─────────────────────────────────────────────────────
  // Eine Datei, die nur hier im State liegt, ist beim Schließen des Composers
  // weg — das war der gemeldete Fehler. Deshalb wandert sie SOFORT (nicht
  // debounced, ein Anhang ist kein Tastendruck) an den Graph-Entwurf, und die
  // zurückkommende ``attachment_id`` bleibt am Chip stehen.
  //
  // Alle Anhang-Ops laufen in EINER Promise-Kette: Add und Delete auf denselben
  // Entwurf dürfen sich nicht überholen (sonst löscht ein Delete eine Id, die
  // ein noch laufendes Add gerade erst vergibt).
  const anhangKetteRef = useRef<Promise<void>>(Promise.resolve());
  const anhangReihen = (op: () => Promise<void>): Promise<void> => {
    const next = anhangKetteRef.current.then(op, op);
    anhangKetteRef.current = next.catch(() => {});
    return next;
  };
  // Id-Merkzettel je Chip-Objekt. Ein Chip kann entfernt werden, WÄHREND sein
  // Add noch läuft — dann steht seine Graph-Id nirgends im State, und ohne
  // diesen Zettel bliebe die Datei unsichtbar am Entwurf hängen und ginge beim
  // Senden doch mit raus. Weil alle Anhang-Ops in EINER Kette laufen, ist der
  // Zettel gefüllt, wenn das Delete an die Reihe kommt.
  const anhangIdsRef = useRef(new WeakMap<object, string>());
  // Anhänge, deren Entfernen am Entwurf FEHLGESCHLAGEN ist. Der Chip ist aus dem
  // Composer verschwunden, die Datei hängt aber noch am Graph-Entwurf — und der
  // Entwurf ist es, der sendet. Ohne diese Liste ginge genau die Datei mit raus,
  // die der Nutzer weggeklickt hat. Der Sende-Pfad arbeitet sie zuerst ab und
  // bricht ab, wenn sie sich nicht räumen lässt.
  const offeneLoeschungenRef = useRef(new Set<string>());

  /** Dateien an den Entwurf hängen und die Graph-Ids an den Chips vermerken.
   *  Scheitert das, BLEIBT der Anhang im Composer (er geht beim Senden trotzdem
   *  mit — der Sende-Pfad holt ihn nach); nur die Meldezeile sagt es. */
  const anhaengeSynchronisieren = (
    neueRoh: RawAttachment[],
    neueRefs: QueuedAttachment[]
  ): Promise<void> =>
    anhangReihen(async () => {
      if (!speichertEntwurfRef.current) return; // kein Autosave-Pfad → kein Entwurf
      if (!neueRoh.length && !neueRefs.length) return;
      const id = draftIdRef.current ?? (await createDraftOnce());
      if (!id) {
        setAnhangSyncFehler(katalog().mail.anhaenge.entwurfNichtAngelegt);
        return;
      }
      const res = await api.post<{ attachments: DraftAttachmentOut[] }>(
        `/mail/drafts/${encodeURIComponent(id)}/attachments`,
        {
          attachments: neueRefs.map((a) => ({
            content_version_id: a.content_version_id,
            filename: a.filename,
          })),
          raw_attachments: neueRoh.map((a) => ({
            name: a.name,
            content_type: a.content_type,
            content_bytes: a.content_bytes,
          })),
        }
      );
      // Reihenfolge der Antwort = Reihenfolge des Bodys (erst die SF-Referenzen,
      // dann die rohen Dateien) — der Server hängt sie genau so aneinander.
      const ids = res.attachments.map((a) => a.attachment_id);
      const refIds = ids.slice(0, neueRefs.length);
      const rohIds = ids.slice(neueRefs.length);
      // Merkzettel ZUERST (und unabhängig von ``aliveRef``): ein inzwischen
      // entfernter Chip muss seine Id noch finden, sonst bleibt seine Datei am
      // Entwurf zurück.
      neueRefs.forEach((a, i) => refIds[i] && anhangIdsRef.current.set(a, refIds[i]));
      neueRoh.forEach((a, i) => rohIds[i] && anhangIdsRef.current.set(a, rohIds[i]));
      if (!aliveRef.current) return;
      // Zuordnung über Objekt-Identität: die Einträge in ``neueRoh``/``neueRefs``
      // SIND die Objekte im State (Entfernen filtert, es ersetzt sie nicht).
      setRawAttachments((q) =>
        q.map((a) => {
          const i = neueRoh.indexOf(a);
          return i >= 0 && rohIds[i] ? { ...a, attachment_id: rohIds[i] } : a;
        })
      );
      setQueuedAttachments((q) =>
        q.map((a) => {
          const i = neueRefs.indexOf(a);
          return i >= 0 && refIds[i] ? { ...a, attachment_id: refIds[i] } : a;
        })
      );
      setAnhangSyncFehler(null);
    }).catch((e) => {
      if (!aliveRef.current) return;
      const err = e as ApiError;
      const a = katalog().mail.anhaenge;
      setAnhangSyncFehler(err.status === 413 ? a.syncZuGross : a.syncFehler);
    });

  /** Einen Anhang wieder vom Entwurf nehmen (Chip-x). Id und Entwurf werden ERST
   *  beim Ausführen aufgelöst — beim Klick kann beides noch unterwegs sein. */
  const anhangAmEntwurfEntfernen = (chip: { attachment_id?: string | null }) => {
    void anhangReihen(async () => {
      const id = chip.attachment_id ?? anhangIdsRef.current.get(chip);
      if (!id || !draftIdRef.current) return; // war nie am Entwurf → nichts zu tun
      try {
        await api.delete(
          `/mail/drafts/${encodeURIComponent(draftIdRef.current)}/attachments/${encodeURIComponent(id)}`
        );
      } catch (e) {
        // Vormerken statt vergessen: der Versand holt das Entfernen nach und
        // sendet NICHT, solange die Datei noch am Entwurf hängt.
        offeneLoeschungenRef.current.add(id);
        throw e;
      }
      anhangIdsRef.current.delete(chip);
      offeneLoeschungenRef.current.delete(id);
    }).catch(() => {
      if (!aliveRef.current) return;
      // Der Chip ist weg (der Nutzer hat entschieden) — aber am Entwurf liegt
      // die Datei noch. Das MUSS er erfahren, sonst geht sie beim nächsten
      // Fortsetzen wieder mit raus.
      setAnhangSyncFehler(katalog().mail.anhaenge.entfernenFehler);
    });
  };

  // Autosave-Kern: liest den frischesten Stand aus Refs (stale-closure-frei),
  // best-effort (Fehler still — Retry beim nächsten Debounce). Genau EIN Create
  // (Lock), danach PATCH; kein Create bei leerem Composer; kein redundanter PATCH
  // ohne Änderung seit dem letzten Speichern.
  const saveDraftRef = useRef<() => Promise<void>>(async () => {});
  saveDraftRef.current = async () => {
    if (!speichertEntwurfRef.current || sendingRef.current) return;
    const { subject: s, body: b, to: t, cc: c, bcc: bc } = latest.current;
    const snap = draftSnapshot(s, b, t, c, bc);
    if (snap === lastSavedRef.current) return; // nichts geändert → kein Egress
    const payload = {
      subject: s,
      // body ist bereits Editor-HTML; hier gewhitelistet (idempotent), damit der
      // Entwurf sauberes body_html trägt (kein doppel-kodiertes &lt;p&gt;).
      body_html: sanitizeEditorHtml(b),
      to: parseAddrs(t),
      cc: parseAddrs(c),
      bcc: parseAddrs(bc),
    };
    if (!draftIdRef.current) {
      if (draftIsEmpty(s, b, t, c, bc)) return; // leerer Composer → NIE ein Create
      if (creatingRef.current) return; // Create läuft → dessen Nachzug patcht
      if (aliveRef.current) setSavingNow(true);
      try {
        await createDraftOnce();
      } finally {
        if (aliveRef.current) setSavingNow(false);
      }
      return;
    }
    if (aliveRef.current) setSavingNow(true);
    lastSavedRef.current = snap;
    try {
      await api.patch(`/mail/drafts/${encodeURIComponent(draftIdRef.current)}`, payload);
      if (aliveRef.current) setSavedAt(new Date());
    } catch {
      lastSavedRef.current = ""; // still — Retry beim nächsten Debounce erzwingen
    } finally {
      if (aliveRef.current) setSavingNow(false);
    }
  };

  // Debounce (~1.5 s): nur planen, wenn sich seit dem letzten Speichern etwas
  // geändert hat und nicht gerade gesendet wird. Cleanup verwirft den Timer bei
  // jeder weiteren Eingabe (klassisches Debounce).
  useEffect(() => {
    if (!speichertEntwurf || sendingRef.current) return;
    if (draftSnapshot(subject, body, to, cc, bcc) === lastSavedRef.current) return;
    const timer = setTimeout(() => {
      debounceTimerRef.current = null;
      void saveDraftRef.current();
    }, DRAFT_DEBOUNCE_MS);
    debounceTimerRef.current = timer;
    return () => clearTimeout(timer);
  }, [subject, body, to, cc, bcc, speichertEntwurf]);

  // Flush-on-Unmount: eine ausstehende (noch nicht gespeicherte) Änderung wird
  // beim Schließen/Navigieren als letzter fire-and-forget-Save rausgeschickt —
  // halbfertiger Text darf NICHT verloren gehen. KEIN setState nach Unmount.
  // ``aliveRef`` beim (Re-)Mount wieder auf true setzen: React-StrictMode (Dev)
  // spielt Mount→Cleanup→Mount durch — ohne dieses Reset bliebe der Ref nach dem
  // simulierten Cleanup dauerhaft false und würde alle Status-setState blocken.
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (!speichertEntwurfRef.current || sendingRef.current) return; // Senden hat es erledigt
      const { subject: s, body: b, to: t, cc: c, bcc: bc } = latest.current;
      if (draftSnapshot(s, b, t, c, bc) === lastSavedRef.current) return; // nichts offen
      const payload = {
        subject: s,
        body_html: sanitizeEditorHtml(b),
        to: parseAddrs(t),
        cc: parseAddrs(c),
        bcc: parseAddrs(bc),
      };
      if (draftIdRef.current) {
        void api
          .patch(`/mail/drafts/${encodeURIComponent(draftIdRef.current)}`, payload)
          .catch(() => {});
      } else if (!creatingRef.current && !draftIsEmpty(s, b, t, c, bc)) {
        void api.post("/mail/drafts", payload).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Undo-Send (P2.15) ───────────────────────────────────────────────────────
  // Der GESAMTE bisherige Netzwerk-Dispatch (alle 4 kind-Branches + kind-abhängiger
  // toast.ok + sendFeedback-nur-reply-NACH-POST + catch/finally). Bekommt den beim
  // „Senden"-Klick EINGEFRORENEN Payload → spätere Edits im Fenster ändern ihn NICHT.
  // Aufgerufen entweder vom Fenster-Timer (Fenster abgelaufen) ODER vom Unmount-
  // Cleanup (fire-and-forget). ``aliveRef``-Guard: der Unmount-Fire darf kein
  // setState mehr absetzen (Komponente ist dann weg).
  /** Der Entwurf muss vor dem Senden GENAU das tragen, was im Composer steht:
   *  alles Sichtbare dran, alles Weggeklickte weg.
   *
   *  Liest den AKTUELLEN Stand (``anhaengeRef``) statt des eingefrorenen — ein
   *  Sync, der während des Rückhol-Fensters fertig geworden ist, würde sonst
   *  doppelt angehängt. Wirft weiter, wenn etwas davon scheitert: eine Mail ohne
   *  die sichtbar angehängte Datei — oder MIT einer entfernten — rauszuschicken
   *  wäre der schlimmere Ausgang. */
  const draftAnhaengeNachholen = async (draftId: string) => {
    await anhangKetteRef.current; // laufenden Sync abwarten (keine Dublette)
    // ZUERST das Entfernen nachholen. Eine Datei, die der Nutzer weggeklickt
    // hat, deren DELETE aber scheiterte, hängt noch am Entwurf — und der
    // Entwurf ist es, der sendet.
    for (const id of [...offeneLoeschungenRef.current]) {
      try {
        await api.delete(
          `/mail/drafts/${encodeURIComponent(draftId)}/attachments/${encodeURIComponent(id)}`
        );
        offeneLoeschungenRef.current.delete(id);
      } catch (e) {
        const err = e as ApiError;
        if (err.status === 404) {
          // Schon weg (oder der erste Versuch kam doch durch) — das Ziel ist
          // erreicht. Sonst bliebe der Versand für immer blockiert.
          offeneLoeschungenRef.current.delete(id);
          continue;
        }
        throw Object.assign(new Error("anhang-sync"), {
          status: err.status,
          anhangSync: true,
        });
      }
    }
    const { raw, queued } = anhaengeRef.current;
    const offenRoh = raw.filter((a) => !a.attachment_id && a.content_bytes);
    const offenRefs = queued.filter((a) => !a.attachment_id);
    if (!offenRoh.length && !offenRefs.length) return;
    try {
      await api.post(`/mail/drafts/${encodeURIComponent(draftId)}/attachments`, {
        attachments: offenRefs.map((a) => ({
          content_version_id: a.content_version_id,
          filename: a.filename,
        })),
        raw_attachments: offenRoh.map((a) => ({
          name: a.name,
          content_type: a.content_type,
          content_bytes: a.content_bytes,
        })),
      });
    } catch (e) {
      const err = e as ApiError;
      throw Object.assign(new Error("anhang-sync"), {
        status: err.status,
        anhangSync: true,
      });
    }
  };

  const fireSend = async (f: FrozenSend) => {
    try {
      if (f.kind === "forward") {
        // Weiterleiten: Original + dessen Anhänge zitiert Graph createForward
        // selbst; wir schicken die (optionale) Notiz, den neuen Empfänger und
        // die im Composer ZUSÄTZLICH angehängten Dateien.
        await api.post("/mail/forward", {
          internet_message_id: f.imid,
          to: f.recipients,
          comment: f.forwardComment,
          attachments: f.attachments,
          raw_attachments: f.rawAttachments,
        });
      } else if (f.kind === "reply-all") {
        // Gebrandeter HTML-Body inkl. Signatur; to/cc/bcc = editierter Verteiler.
        await api.post("/mail/reply-all", {
          internet_message_id: f.imid,
          body_html: f.html,
          to: f.recipients,
          cc: f.ccList,
          bcc: f.bccList,
          attachments: f.attachments,
          raw_attachments: f.rawAttachments,
        });
        // Den Autosave-Entwurf wegräumen. Er ist die PARKPOSITION für
        // halbfertigen Text, nicht die Mail: gesendet hat Graph seinen EIGENEN
        // Entwurf (createReplyAll → PATCH → send, in EINEM Aufruf des
        // email-service). Bliebe unserer liegen, stünde nach jedem „Allen
        // antworten" eine Dublette in „Entwürfe" — in Atlas UND in Outlook.
        //
        // Die Id wird HIER aufgelöst und nicht nur aus dem eingefrorenen
        // Payload gelesen: beim „Senden"-Klick kann der Create noch unterwegs
        // gewesen sein (derselbe Grund wie im Entwurfs-Sendepfad unten) — sonst
        // liefe das Aufräumen an einem gerade erst entstandenen Entwurf vorbei.
        //
        // Fehler bleiben still: die Mail IST raus, und ein Fehler-Toast über
        // einem geglückten Versand würde das Gegenteil behaupten. Der Entwurf
        // ist über „Entwürfe" sichtbar und von Hand löschbar.
        const autosave =
          f.draftId ?? (await createInFlightRef.current) ?? draftIdRef.current;
        if (autosave) {
          draftIdRef.current = null;
          await api
            .delete(`/mail/drafts/${encodeURIComponent(autosave)}`)
            .catch(() => {});
        }
      } else {
        // Beim „Senden"-Klick kann der Entwurf noch im Anlegen gewesen sein (der
        // Anhang-Sync stößt ihn an). Deshalb wird die Id hier aufgelöst und
        // nicht nur aus dem eingefrorenen Payload gelesen — sonst liefe der
        // Versand an einem gerade entstandenen Entwurf VORBEI und ließe ihn als
        // Dublette in „Entwürfe" liegen. Genau der gemeldete Fehler.
        const draftId =
          f.draftId ?? (await createInFlightRef.current) ?? draftIdRef.current;
        if (draftId) {
          // Der Entwurf sendet SELBST — der einzige Weg ohne Waise: Graph macht
          // aus dem Entwurf die gesendete Mail. Noch nicht synchronisierte
          // Anhänge werden vorher nachgeholt; scheitert das, bricht der Versand
          // ab (unten im catch) — lieber gar nicht senden als ohne die Datei,
          // die im Composer sichtbar dranhängt.
          await draftAnhaengeNachholen(draftId);
          await api.patch(`/mail/drafts/${encodeURIComponent(draftId)}`, {
            subject: f.subject,
            body_html: f.html,
            to: f.recipients,
            cc: f.ccList,
            bcc: f.bccList,
          });
          // Anhänge NICHT mitschicken: sie hängen bereits am Entwurf, ein
          // zweiter Weg würde sie doppelt an die Mail heften.
          await api.post(`/mail/drafts/${encodeURIComponent(draftId)}/send`, {
            internet_message_id: f.imid ?? null,
          });
        } else {
          // Kein Entwurf (Autosave-Create ist gescheitert o.ä.) → regulärer Pfad
          // mit allem im Payload.
          await api.post("/mail/send", {
            to: f.recipients,
            cc: f.ccList,
            bcc: f.bccList,
            subject: f.subject,
            body_html: f.html,
            attachments: f.attachments,
            raw_attachments: f.rawAttachments,
            internet_message_id: f.imid,
          });
        }
      }
      const c = katalog().mail.composer;
      toast.ok(
        f.kind === "forward" ? c.weitergeleitet : c.gesendet,
        f.recipients.length
          ? c.gesendetAn(f.recipients.join(", "))
          : c.gesendetAnVerteiler
      );
      feedbackSent.current = true;
      // final_md markdown-ish (body ist HTML) → suggested_md↔final_md vergleichbar.
      if (f.kind === "reply") void sendFeedback("sent", f.feedbackBody);
      onClose();
    } catch (e) {
      // Senden fehlgeschlagen → Autosave + Composer wieder freigeben (der Nutzer
      // editiert weiter). Das Undo-Fenster ist zu diesem Zeitpunkt bereits vorbei.
      sendingRef.current = false;
      pendingSendRef.current = false;
      if (aliveRef.current) {
        setPendingSend(false);
        setUndoLeft(0);
      }
      const err = e as ApiError;
      const c = katalog().mail.composer;
      toast.err(
        c.sendenFehlgeschlagen,
        (e as { anhangSync?: boolean }).anhangSync && err.status !== 413
          ? // Der Entwurf trägt nicht genau das, was im Composer steht (eine
            // Datei fehlt noch oder eine entfernte hängt noch dran) → NICHTS
            // wurde gesendet. Falsch zu senden wäre der teurere Fehler.
            c.sendenFehlerAnhangSync
          : err.status === 403
          ? c.sendenFehler403
          : err.status === 413
            ? // Der einzige Sendefehler, den der Nutzer selbst beheben kann —
              // also nennt die Meldung die Ursache statt „später erneut".
              c.sendenFehler413
            : err.status === 404
              ? c.sendenFehler404
              : err.status === 502
                ? c.sendenFehler502
                : c.sendenFehlerSonst
      );
    } finally {
      if (aliveRef.current) setSending(false);
    }
  };

  // Fire-Timer + Countdown-Intervall des Undo-Fensters freigeben (idempotent).
  const clearUndoTimers = () => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    if (undoIntervalRef.current) {
      clearInterval(undoIntervalRef.current);
      undoIntervalRef.current = null;
    }
  };

  // „Senden"-Klick: EIN Klick (das 2-Schritt-Confirm ist entfallen — Nordstern
  // one-click). Empfänger-Guard SYNCHRON (sonst käme die Fehlermeldung erst nach
  // Fensterablauf). Danach Autosave anhalten + Debounce canceln, Payload EINFRIEREN
  // und je nach Slice-B-Fenster entweder das Rückhol-Fenster öffnen oder — bei
  // Fenster 0 — sofort feuern.
  const beginSend = () => {
    const recipients = parseAddrs(to);
    // reply-all darf leer sein (Graph createReplyAll füllt den Verteiler); alle
    // anderen Fälle brauchen mindestens einen Empfänger.
    if (kind !== "reply-all" && recipients.length === 0) {
      toast.err(mt.keinEmpfaenger, mt.keinEmpfaengerText);
      return;
    }
    // Ab hier ist „ein Send unterwegs": Autosave aussetzen + pending Debounce
    // canceln. sendingRef=true seit Fensterstart ist zugleich der Schalter, der den
    // P1.8-Flush-on-Unmount abschaltet → beim Unmount feuert NUR der Undo-Fire, kein
    // paralleler Draft-Flush (kein Doppel-Egress).
    sendingRef.current = true;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    // Payload EINMALIG einfrieren (Signatur/HTML/Empfänger/noExtras/Anhänge).
    const bccList = parseAddrs(bcc);
    const frozen: FrozenSend = {
      kind,
      imid,
      recipients,
      ccList: parseAddrs(cc),
      bccList,
      subject,
      // „Signatur aus" heißt: es gibt für diesen Versand keine — also ``null``
      // statt ``sig``. Kein Zuschnitt am Body: was der Nutzer im Editor stehen
      // hat, geht unverändert raus (die Doppel-Anhänge-Bremse in
      // ``spliceSignatureHtml`` arbeitet auf demselben Prinzip).
      html: spliceSignatureHtml(body, signaturAnhaengen ? sig : null),
      forwardComment: sanitizeEditorHtml(body),
      draftId,
      attachments: queuedAttachments.map((a) => ({
        content_version_id: a.content_version_id,
        filename: a.filename,
      })),
      // Nur der Fallback-Pfad (/mail/send, kein Entwurf) trägt Bytes im Body —
      // und der braucht die reine Wire-Form ohne die Sync-Markierung.
      rawAttachments: rawAttachments.map((a) => ({
        name: a.name,
        content_type: a.content_type,
        content_bytes: a.content_bytes,
      })),
      feedbackBody: htmlToComposerText(body),
    };
    frozenRef.current = frozen;

    const windowMs = getUndoWindowSeconds() * 1000;
    if (windowMs <= 0) {
      // Slice B: Fenster aus → sofort senden (1 Klick = senden, keine Undo-Zeile).
      setSending(true);
      void fireSend(frozen);
      return;
    }
    // Rückhol-Fenster öffnen: Editor/Aktionen sperren, Countdown starten, nach
    // Ablauf GENAU EINMAL feuern.
    pendingSendRef.current = true;
    setPendingSend(true);
    const totalS = Math.max(1, Math.round(windowMs / 1000));
    setUndoLeft(totalS);
    undoIntervalRef.current = setInterval(() => {
      setUndoLeft((n) => (n > 1 ? n - 1 : 1));
    }, 1000);
    undoTimerRef.current = setTimeout(() => {
      clearUndoTimers();
      pendingSendRef.current = false;
      setPendingSend(false);
      setSending(true);
      void fireSend(frozen);
    }, windowMs);
  };

  // „Rückgängig": Fenster stornieren → 0 Send-Calls. ``sendingRef`` MUSS zurück auf
  // false, sonst bleibt der Autosave DAUERHAFT suspendiert (Guard in saveDraftRef +
  // Debounce-Effekt). Ein evtl. bereits angelegter Autosave-Entwurf bleibt bestehen
  // (über „Entwürfe" wiederherstellbar) — nur der explizite Klick storniert.
  const undoSend = () => {
    clearUndoTimers();
    pendingSendRef.current = false;
    frozenRef.current = null;
    setPendingSend(false);
    setUndoLeft(0);
    setSending(false);
    sendingRef.current = false;
  };

  // Unmount während eines laufenden Rückhol-Fensters ⇒ den gepufferten Send NICHT
  // still verwerfen, sondern SOFORT feuern (fire-and-forget). Dieser Effekt wird
  // NACH dem P1.8-Flush-on-Unmount registriert → sein Cleanup läuft danach; da
  // ``sendingRef`` seit Fensterstart true ist, hat der Flush bereits early-returnt
  // (kein Draft-Flush) ⇒ genau 1 Send. Timer/Intervall immer freigeben.
  useEffect(() => {
    return () => {
      clearUndoTimers();
      if (pendingSendRef.current && frozenRef.current) {
        pendingSendRef.current = false;
        void fireSend(frozenRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const discard = () => {
    if (!feedbackSent.current)
      void sendFeedback("dismissed", chosen != null ? htmlToComposerText(body) : null);
    onClose();
  };

  // ── Datei-Drop auf die Composer-Karte ──────────────────────────────────────
  // Melder-Wunsch: „Anhänge per Drag&Drop". Ziel ist die GANZE Karte und nicht
  // ein kleines Feld — wer eine Datei aus dem Explorer zieht, zielt auf „das
  // Fenster". Angenommen wird nur, solange das Formular (und mit ihm der
  // Anhang-Block) wirklich dasteht: bei den KI-Vorschlägen erst NACH der
  // Variantenwahl. Sonst verspräche das Overlay etwas, das niemand einlöst.
  const formularSteht =
    !loading &&
    !error &&
    (kind !== "reply" || mode === "blank" || !!(data && chosen != null));
  const dropAktiv = formularSteht && !pendingSend && !sending;
  const dragRein = (e: React.DragEvent) => {
    if (!dropAktiv || !ziehtDateien(e.dataTransfer)) return;
    e.preventDefault();
    dragTiefe.current += 1;
    setDragUeber(true);
  };
  const dragUeberKarte = (e: React.DragEvent) => {
    if (!dropAktiv || !ziehtDateien(e.dataTransfer)) return;
    // Ohne dieses preventDefault lehnt der Browser den Drop ab und öffnet die
    // Datei stattdessen selbst — genau der gemeldete Fehler.
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const dragRaus = (e: React.DragEvent) => {
    if (!dropAktiv || !ziehtDateien(e.dataTransfer)) return;
    dragTiefe.current = Math.max(0, dragTiefe.current - 1);
    if (dragTiefe.current === 0) setDragUeber(false);
  };
  const dateiFallenLassen = (e: React.DragEvent) => {
    if (!dropAktiv || !ziehtDateien(e.dataTransfer)) return;
    e.preventDefault();
    dragTiefe.current = 0;
    setDragUeber(false);
    void onFiles(e.dataTransfer.files);
  };

  return (
    <Card
      padding="sm"
      data-mail-composer
      className="relative space-y-3 border-accent-300"
      onDragEnter={dragRein}
      onDragOver={dragUeberKarte}
      onDragLeave={dragRaus}
      onDrop={dateiFallenLassen}
    >
      {dragUeber && (
        <div
          data-mail-composer-dropzone
          // Deckkraft im Dunkelmodus bewusst so hoch wie im hellen: bei /60
          // schien das Formular durch und die Zeile „Datei hier ablegen" lag
          // sichtbar auf dem Platzhalter des Editors (gemessen,
          // docs/evidence/anhang-dnd/06-overlay-dunkelmodus.png).
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-accent-300 bg-accent-50/95 dark:bg-accent-900/95"
        >
          <span className="inline-flex items-center gap-2 text-sm font-medium text-accent-700 dark:text-accent-200">
            <Paperclip className="w-4 h-4" />
            {mtAnh.ablegen}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-text-3">
          {kind === "new" ? (
            // Fortgesetzter Entwurf ist derselbe Composer, aber nicht dieselbe
            // Handlung: „Neue Mail" über einem Text, den man vor zwei Tagen
            // angefangen hat, ist schlicht falsch. Unterschieden am mitgegebenen
            // Entwurf (Resume-Modal), nicht am später entstehenden Autosave.
            <>
              {initialDraftId ? (
                <Pencil className="w-3.5 h-3.5 text-accent-500" />
              ) : (
                <MailPlus className="w-3.5 h-3.5 text-accent-500" />
              )}
              {initialDraftId ? mt.kopfEntwurf : mt.kopfNeueMail}
            </>
          ) : kind === "reply-all" ? (
            <>
              <ReplyAll className="w-3.5 h-3.5 text-accent-500" />
              {mt.kopfAllenAntworten}
            </>
          ) : kind === "forward" ? (
            <>
              <Forward className="w-3.5 h-3.5 text-accent-500" />
              {mt.kopfWeiterleiten}
            </>
          ) : aiFlow ? (
            <>
              <Sparkles className="w-3.5 h-3.5 text-accent-500" />
              {mt.kopfKiVorschlaege}
            </>
          ) : (
            <>
              <Pencil className="w-3.5 h-3.5 text-accent-500" />
              {mt.kopfFreiAntworten}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={discard}
          className="p-1 rounded-md text-text-3 hover:text-text hover:bg-surface-3"
          aria-label={ct.schliessen}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* ── AKTIONSLEISTE — OBEN (E1b) ─────────────────────────────────────
          Tester-Feedback (Tim, Outlook-Power-User, bindend): „auf Antworten
          klicken und alles ist direkt da … mit den Buttons OBEN".
          Vorher stand diese Zeile am FUSS der Karte, hinter Editor UND
          Signatur-Vorschau. Gemessen (docs/evidence/ux-e1b-inline-antworten/
          vorher/messwerte-vorher.json): „Senden" lag bei 1280x800 auf
          Dokument-y 1104 in einer 800 px hohen Lesespalte — man musste erst
          scrollen, um überhaupt absenden zu können. Genau das ist der Befund,
          nicht ein Modal (ein Modal war es nie).
          Hier steht jetzt ALLES, was vorher unten oder mitten im Formular lag:
          Senden/Verwerfen/Rückholzeit, Vorlage einfügen, Datei anhängen samt
          Anhang-Chips und der Bcc-Schalter.
          Gilt für alle vier Verfassen-Fälle — es ist EIN Composer, und zwei
          Aufbauten der Aktionsleiste nebeneinander wären zwei Wahrheiten über
          denselben Zustand. Die beiden Modale („Neue Mail"/„Entwurf
          fortsetzen") bleiben Modale; nur ihre Knöpfe wandern mit nach oben. */}
      {formularSteht && (
        <div
          data-mail-composer-actions
          className="space-y-2 border-b border-border pb-3"
        >
          {/* Undo-Send (P2.15): läuft das Rückhol-Fenster, ersetzt die Inline-Zeile
              „Wird gesendet … · Rückgängig (N)" die Buttons KOMPLETT (kein Modal,
              kein 2-Schritt-Confirm mehr). „Rückgängig" bricht ab, BEVOR irgendein
              Netzwerk-Call feuert. */}
          {pendingSend ? (
            <div
              className="flex flex-wrap items-center gap-2 rounded-lg border border-accent-300 bg-accent-50 p-2.5"
              aria-live="polite"
            >
              <span className="inline-flex items-center gap-2 text-sm text-text">
                <Loader2 className="w-4 h-4 animate-spin text-accent-500" />
                {mt.wirdGesendet}
              </span>
              <button
                type="button"
                onClick={undoSend}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-accent-300 bg-surface px-3 py-1.5 text-sm font-medium text-accent-600 hover:bg-accent-100 transition-colors"
              >
                <Undo2 className="w-4 h-4" />
                {mt.rueckgaengig(undoLeft)}
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                icon={<Send className="w-4 h-4" />}
                onClick={beginSend}
                disabled={sending}
              >
                {mt.senden}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={discard}
                disabled={sending}
              >
                {ct.verwerfen}
              </Button>
              {/* Der Bcc-Schalter gehört in die Leiste, nicht mehr unter das
                  Cc-Feld: er blendet ein Feld EIN, ist also eine Aktion. Das
                  Feld selbst steht weiter bei seinen Geschwistern. Deshalb
                  hier die Kehrseite derselben Bedingung — sichtbares Bcc-Feld
                  heißt: kein Einblend-Knopf mehr (der Entwurfs-Walk prüft
                  genau das). */}
              {kind !== "forward" && !(showBcc || bcc.trim()) && (
                <button
                  type="button"
                  onClick={() => setShowBcc(true)}
                  className="inline-flex items-center gap-1 text-xs text-text-2 hover:text-accent-500 transition-colors"
                >
                  <Plus className="w-3 h-3" /> {mt.bccHinzufuegen}
                </button>
              )}
              {/* Slice B: Rückholzeit-Auswahl (0/5/7/10 s), rein localStorage. */}
              <UndoWindowSelect />
            </div>
          )}
          {/* Anhänge + Vorlagen-Einfügen in ALLEN vier Verfassen-Fällen. Bis
              zum Melder-Feedback („beim Weiterleiten kann ich nichts anhängen")
              hing dieser Block an ``usesSendPath`` — bei „Allen antworten" und
              „Weiterleiten" gab es damit gar keinen Weg, eine Datei mitzugeben.
              Graph createForward kopiert weiterhin die ORIGINAL-Anhänge; was
              hier dazukommt, legt der email-service zusätzlich an den Entwurf.
              Knopf UND Chips bleiben EIN Element: die Drag&Drop-Walks ziehen
              auf ``[data-mail-anhang-block]``, ein leerer Block wäre 0 px hoch
              und damit kein Ziel mehr. */}
          <div data-mail-anhang-block className="flex flex-wrap items-center gap-2">
            <TemplatePickerButton
              recordLinks={recordLinks}
              onPicked={handleInsert}
              emailText={emailText}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-text-2 hover:text-text hover:bg-surface-2 transition-colors"
            >
              <Paperclip className="w-3.5 h-3.5" /> {mtAnh.dateiAnhaengen}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => void onFiles(e.target.files)}
            />
            {queuedAttachments.map((a, i) => (
              <span
                key={`${a.content_version_id}-${i}`}
                className="inline-flex items-center gap-1 rounded-full border border-accent-200 bg-accent-50 px-2 py-0.5 text-[11px] text-text-2"
              >
                <Paperclip className="w-3 h-3" />
                <span className="max-w-[180px] truncate">{a.filename ?? mtAnh.anhang}</span>
                <button
                  type="button"
                  aria-label={mtAnh.entfernen}
                  onClick={() => {
                    // Erst vom Entwurf nehmen, dann aus der Anzeige — sonst
                    // bliebe die Datei im zwischengeparkten Entwurf liegen.
                    anhangAmEntwurfEntfernen(a);
                    setQueuedAttachments((q) => q.filter((_, j) => j !== i));
                  }}
                  className="hover:text-text"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {rawAttachments.map((a, i) => (
              <span
                key={`raw-${a.name}-${i}`}
                data-mail-anhang-chip
                className="inline-flex items-center gap-1 rounded-full border border-accent-200 bg-accent-50 px-2 py-0.5 text-[11px] text-text-2"
              >
                <Paperclip className="w-3 h-3" />
                <span className="max-w-[180px] truncate">{a.name}</span>
                <button
                  type="button"
                  aria-label={mtAnh.entfernen}
                  onClick={() => {
                    anhangAmEntwurfEntfernen(a);
                    setRawAttachments((q) => q.filter((_, j) => j !== i));
                  }}
                  className="hover:text-text"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          {/* EINE Meldezeile für beide Anhang-Probleme (abgelehnte Datei +
              fehlgeschlagener Entwurfs-Sync): sie betreffen denselben Block,
              und zwei Kästen übereinander wären Lärm statt Auskunft. */}
          {(anhangFehler || anhangSyncFehler) && (
            <div
              role="alert"
              data-mail-anhang-fehler
              className="flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0">
                {[anhangFehler, anhangSyncFehler].filter(Boolean).join(" ")}
              </span>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-text-2">
            <RefreshCw className="w-4 h-4 animate-spin text-accent-500 shrink-0" />
            {mt.vorschlaegeLaden}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[0, 1].map((i) => (
              <div key={i} className="space-y-2 rounded-lg border border-border p-3">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3.5 w-2/3" />
                <div className="space-y-1.5 pt-1">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-5/6" />
                  <Skeleton className="h-3 w-4/6" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg border border-border bg-surface-2 p-4 text-center space-y-3">
          <p className="text-sm text-text">
            {error.status === 503
              ? mt.fehlerDienst
              : error.status === 502
                ? mt.fehlerGenerierung
                : mt.fehlerAllgemein}
          </p>
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={() => void load()}
          >
            {ct.erneutVersuchen}
          </Button>
        </div>
      )}

      {!loading && !error && data && chosen == null && (
        <div className="space-y-3">
          <ReplyContextChips ctx={data.context} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.variants.map((v, i) => (
              <div
                key={i}
                className="flex flex-col rounded-lg border border-border bg-surface-2 p-3"
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className="inline-flex items-center rounded-full bg-accent-100 text-accent-700 px-2 py-0.5 text-[11px] font-semibold">
                    {v.approach_label}
                  </span>
                </div>
                <div className="text-xs text-text-3 mb-1 truncate" title={v.subject}>
                  {v.subject}
                </div>
                <BodyPreview
                  md={v.body_md}
                  className="flex-1 max-h-56 overflow-y-auto text-[13px]"
                />
                <div className="pt-3">
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<Pencil className="w-3.5 h-3.5" />}
                    onClick={() => pick(i)}
                    className="w-full"
                  >
                    {mt.varianteBearbeiten}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {formularSteht && (
        <div className="space-y-3">
          {aiFlow && data && data.variants.length > 1 && (
            <button
              type="button"
              onClick={() => setChosen(null)}
              className="inline-flex items-center gap-1 text-xs text-text-2 hover:text-text"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {mt.zurueckZuVarianten}
            </button>
          )}
          <RecipientField
            label={mt.an}
            value={to}
            onChange={setTo}
            colleagues={colleagues}
            placeholder={kind === "reply-all" ? mt.anAlle : mt.anPlatzhalter}
          />
          {/* Cc/Bcc: für alle Fälle außer Weiterleiten (dort nur An + Notiz). */}
          {kind !== "forward" && (
            <>
              <RecipientField
                label={mt.cc}
                hint={mt.ccHinweis}
                optional
                value={cc}
                onChange={setCc}
                colleagues={colleagues}
                placeholder={mt.optional}
              />
              {/* Der Einblend-Knopf steht in der Aktionsleiste oben; hier bleibt
                  nur das Feld selbst, bei seinen Geschwistern An/Cc. */}
              {(showBcc || bcc.trim()) && (
                <RecipientField
                  label={mt.bcc}
                  hint={mt.bccHinweis}
                  optional
                  value={bcc}
                  onChange={setBcc}
                  colleagues={colleagues}
                  placeholder={mt.optional}
                />
              )}
            </>
          )}
          {/* Betreff nur bei Neue Mail/Antworten (Graph setzt „RE:"/„FW:" für
              reply-all/forward selbst → dort kein Betreff-Feld). */}
          {usesSendPath && (
            <Field label={mt.betreff}>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </Field>
          )}
          <Field label={kind === "forward" ? mt.notizOptional : mt.nachricht}>
            <RichTextEditor
              value={body}
              onChange={setBody}
              disabled={sending || pendingSend}
              ariaLabel={kind === "forward" ? mt.notiz : mt.nachricht}
              handleRef={editorHandle}
              // Weiterleiten hat nur ein Notizfeld — dort formuliert niemand
              // eine Antwort, also gibt es dort auch keine Formulierhilfe.
              assist={kind === "forward" ? undefined : runComposerAssist}
              placeholder={
                kind === "forward" ? mt.notizPlatzhalter : mt.nachrichtPlatzhalter
              }
            />
          </Field>
          {/* Zweite Hälfte des Abschlussblocks (Task 57): die Signatur SEHEN
              statt sie behauptet zu bekommen. Sie liegt außerhalb des Editors,
              weil ihr gebrandetes HTML die Editor-Whitelist nicht überlebt
              (docs/evidence/task-57/whitelist-sonde.md) — angehängt wird sie
              beim Senden unverändert, sofern der Schalter daneben auf „an"
              steht. Farben werden überschrieben: die Signatur
              trägt inline ``color: black``, das im Dunkelmodus unlesbar wäre. */}
          {kind !== "forward" && sig && (
            <div
              data-mail-sig-preview
              className="rounded-md border border-dashed border-border bg-surface-2 px-3 py-2"
            >
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <div
                  data-mail-sig-preview-label
                  className="min-w-0 text-[11px] font-medium text-text-3"
                >
                  {mt.signatur(sig.name)}
                  {/* Die Bremse steht ZUERST, und zwar unabhängig vom Schalter.
                      Greift sie, steht die Signatur im Text des Nutzers und geht
                      mit ihm raus — „wird nicht angehängt" wäre dort schlicht
                      falsch, egal wie der Schalter steht. Der Schalter
                      entscheidet nur über das ANHÄNGEN; er schneidet nichts aus
                      dem Body. Beide zeigen damit in dieselbe Richtung (nichts
                      Zusätzliches) und können sich nicht gegenseitig aufheben. */}
                  {sigSchonImText
                    ? mt.signaturSchonImText
                    : signaturAnhaengen
                      ? mt.signaturWirdAngehaengt
                      : mt.signaturAbgeschaltet}
                </div>
                {/* Inline-Schalter statt Dialog oder Einstellungs-Seite: die
                    Entscheidung fällt beim Schreiben und gilt nur für diese
                    Mail, also gehört sie an die Vorschau, deren Zusage sie
                    ändert. Bauform + Klassen wortgleich zum „Zeitraum
                    festlegen"-Schalter im Abwesenheits-Dialog — kein neues
                    Design-Vokabular. Bewusst OHNE Tastenkürzel: das
                    Outlook-native Set ist belegt, und ein neues Kürzel für eine
                    Ausnahme wäre den Preis nicht wert. */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={signaturAnhaengen}
                  aria-label={mt.signaturSchalterAria}
                  title={mt.signaturSchalterTitel}
                  data-mail-sig-toggle
                  onClick={() => setSignaturAnhaengen((v) => !v)}
                  className={clsx(
                    "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                    signaturAnhaengen ? "bg-accent-500" : "bg-border"
                  )}
                >
                  <span
                    className={clsx(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      signaturAnhaengen ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>
              <div
                data-mail-sig-preview-body
                className={clsx(
                  "max-h-40 overflow-y-auto text-sm",
                  // Bei „aus" muss die Vorschau die Wirkung SOFORT zeigen — ein
                  // Schalter, dessen Folge man erst beim Empfänger sieht, ist
                  // keiner. Nur am Schalter, nicht an der Bremse: die hat ihren
                  // eigenen Hinweis, und ihr Fall zeigt eine Signatur, die
                  // wirklich mit rausgeht.
                  !signaturAnhaengen && "opacity-40",
                  // ``[&_a_*]`` muss mit: die Signatur setzt die Linkfarbe auf
                  // einem ``span`` INNERHALB des ``a`` — ohne diese Regel bliebe
                  // der Link in der Vorschau grau wie der Fließtext.
                  "[&_*]:!text-text-2 [&_a]:!text-accent-600 [&_a_*]:!text-accent-600",
                  "[&_img]:max-w-full",
                  "[&_table]:w-full [&_td]:!w-auto"
                )}
                dangerouslySetInnerHTML={{ __html: sig.html }}
              />
            </div>
          )}
          {kind !== "forward" && (
            <p className="text-xs text-text-3">{mt.ausDeinemPostfach}</p>
          )}
          {/* Autosave-Status (P1.8): dezent, kein Button/Toast. Fehler bleiben
              still (Retry beim nächsten Debounce). Nur auf dem Send-Pfad. */}
          {speichertEntwurf && (savingNow || savedAt) && (
            <p className="text-xs text-text-3" aria-live="polite">
              {savingNow
                ? mt.entwurfSpeichert
                : savedAt
                  ? mt.entwurfGespeichert(
                      savedAt.toLocaleTimeString("de-DE", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    )
                  : ""}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Anhänge lesen (F-07 + #84) ───────────────────────────────────────────────
//
// Das Büroklammer-Symbol war bis hierhin ein totes <span>: es sagte „Anhang",
// ohne dass irgendwo ein Lesepfad existierte (Melder Bentler: „bekomme ich nur
// das Textbearbeitungs-Icon, keine Verlinkung").
//
// Zuschnitt laut Auftraggeber: BEDARFSGESTEUERT. Ohne Klick gehört der
// Lesebereich vollständig dem Text — kein reservierter Platz, kein leeres
// Pane. Erst der Klick auf einen Chip öffnet die Vorschau NEBEN dem Text
// (der Begleittext erklärt oft, was das PDF ist), und ein einzelner Klick auf
// einen anderen Chip wechselt.

/** Dateigröße menschenlesbar. 0/unbekannt → leer statt „0 B" (eine erfundene
 *  Null ist schlechter als keine Angabe). */
function fmtDateiGroesse(n: number): string {
  if (!n || n < 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function istPdf(a: MessageAttachment): boolean {
  return (
    a.content_type === "application/pdf" ||
    a.name.toLowerCase().endsWith(".pdf")
  );
}

function istBild(a: MessageAttachment): boolean {
  return a.content_type.startsWith("image/");
}

/** Seitenzahl aus den BEREITS geladenen PDF-Bytes.
 *
 *  Bewusst KEIN Vorab-Abruf: eine Seitenzahl für jeden Chip zu zeigen würde
 *  bedeuten, beim Öffnen jeder Mail JEDE Datei komplett herunterzuladen —
 *  genau der Aufwand, den die Metadaten-Abfrage gerade vermeidet. Deshalb
 *  erscheint die Seitenzahl an dem Chip, dessen PDF man geöffnet hat.
 *
 *  ``/Count n`` im Seitenbaum ist der Wert, den der Erzeuger selbst angibt;
 *  der größte davon ist die Gesamtzahl. Findet sich keiner (z.B. weil der
 *  Baum in einem Objekt-Stream komprimiert ist), wird NICHTS behauptet. */
function pdfSeitenzahl(bytes: ArrayBuffer): number | null {
  try {
    const kopf = new TextDecoder("latin1").decode(new Uint8Array(bytes));
    let max = 0;
    for (const m of kopf.matchAll(/\/Count\s+(\d+)/g)) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > max) max = n;
    }
    return max > 0 ? max : null;
  } catch {
    return null;
  }
}

/** Die Anhang-Leiste: ein Chip je Datei. Immer sichtbar, sobald es Anlagen
 *  gibt — sie ist der Überblick („welches PDF ist eigentlich was"). Das
 *  Vorschau-Pane erscheint dagegen erst auf Klick. */
function AnhangLeiste({
  atts,
  aktivId,
  seiten,
  onWaehlen,
  onHerunterladen,
  onVorladen,
  onZiehStart,
  onAlleHerunterladen,
  sammelLaeuft,
}: {
  atts: MessageAttachment[];
  aktivId: string | null;
  seiten: Record<string, number>;
  onWaehlen: (a: MessageAttachment) => void;
  onHerunterladen: (a: MessageAttachment) => void;
  onVorladen: (a: MessageAttachment) => void;
  onZiehStart: (a: MessageAttachment, e: React.DragEvent<HTMLElement>) => void;
  onAlleHerunterladen: () => void;
  sammelLaeuft: boolean;
}) {
  const mt = useT().mail.anhaenge;
  if (!atts.length) return null;
  return (
    <div
      data-mail-attachment-bar
      className="mt-2 flex flex-wrap items-center gap-2"
    >
      {/* „Alle herunterladen (n)" — Melder-Fall: eine Mail mit 13 Anlagen und
          13 Klicks. Es entstehen n EINZELNE Dateien, KEIN Archiv: „sonst muss
          er wieder ent-zippen" (Auflage, wörtlich).

          Ab DREI Anlagen. Bei ein bis zwei ist der Sammel-Weg kein Gewinn — er
          wäre nur ein zweiter Knopf neben zwei Knöpfen und nähme die Kontrolle
          darüber, WELCHE Datei kommt. Die Schwelle steht hier und nicht im
          Katalog: sie ist Verhalten, keine Sprache.

          Steht VOR den Chips: er ist die Antwort auf die ganze Leiste, nicht
          auf ihren letzten Eintrag — und am Ende einer über mehrere Zeilen
          umbrechenden Leiste wäre er nicht mehr auffindbar. */}
      {atts.length >= 3 && (
        <button
          type="button"
          data-mail-attachments-download-all
          onClick={onAlleHerunterladen}
          disabled={sammelLaeuft}
          title={mt.alleHerunterladenTitel(atts.length)}
          className="inline-flex items-center gap-1.5 rounded-md border border-accent-500/40 bg-accent-500/10 px-2 py-1 text-xs font-medium text-accent-600 transition-colors hover:bg-accent-500/20 disabled:opacity-60"
        >
          <Download
            className={clsx("w-3.5 h-3.5 shrink-0", sammelLaeuft && "animate-pulse")}
          />
          <span>
            {sammelLaeuft
              ? mt.alleHerunterladenLaeuft
              : mt.alleHerunterladen(atts.length)}
          </span>
        </button>
      )}
      {atts.map((a) => {
        const aktiv = a.attachment_id === aktivId;
        const vorschaubar = istPdf(a) || istBild(a);
        const s = seiten[a.attachment_id];
        const groesse = fmtDateiGroesse(a.size);
        const beschriftung = [groesse, s ? mt.seiten(s) : ""]
          .filter(Boolean)
          .join(" · ");
        return (
          <div
            key={a.attachment_id}
            data-mail-attachment-chip={a.attachment_id}
            // F-12: der Chip ist die Ziehquelle. Kein zusätzliches Dauer-Symbol
            // — die Optik im Ruhezustand bleibt, nur der Zeiger sagt beim
            // Darüberfahren „das kannst du nehmen". `select-none`, damit der Zug
            // keine Textmarkierung hinter sich herzieht.
            draggable
            onDragStart={(e) => onZiehStart(a, e)}
            onMouseEnter={() => onVorladen(a)}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-md border pl-2 pr-1 py-1 text-xs transition-colors",
              "cursor-grab active:cursor-grabbing select-none",
              aktiv
                ? "border-accent-500 bg-accent-500/10 text-text"
                : "border-border bg-surface text-text-2"
            )}
          >
            {/* Ein Klick = Vorschau. Die Zusage lautet „so wenige Clicks wie
                möglich" — deshalb ist der Chip SELBST der Auslöser und nicht
                erst ein Menü darin. */}
            <button
              type="button"
              onClick={() => vorschaubar && onWaehlen(a)}
              disabled={!vorschaubar}
              title={
                vorschaubar ? mt.ansehen(a.name) : mt.keineVorschauTitel(a.name)
              }
              className={clsx(
                // Der Zeiger bleibt auch über der Beschriftung „grab": sie ist
                // die Fläche, an der man den Chip anfasst (der Klick öffnet
                // zusätzlich die Vorschau, das sagt die Hover-Farbe).
                "inline-flex items-center gap-1.5 max-w-[15rem] cursor-grab",
                vorschaubar && "hover:text-accent-500"
              )}
            >
              <FileText className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{a.name}</span>
              {beschriftung && (
                <span className="shrink-0 text-text-3">({beschriftung})</span>
              )}
            </button>
            <button
              type="button"
              onClick={() => onHerunterladen(a)}
              title={mt.herunterladenTitel(a.name)}
              aria-label={mt.herunterladenTitel(a.name)}
              className="shrink-0 rounded p-0.5 text-text-3 hover:text-accent-500 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Das Vorschau-Pane. Existiert im DOM NUR, solange eine Anlage gewählt ist —
 *  „andernfalls bleibt das Pane 100% beim Text" (Auftraggeber, wörtlich).
 *
 *  Gerendert wird mit BORDMITTELN des Browsers über eine Blob-URL: kein CDN,
 *  keine externe Ressource (EU-Hosting). Die Bytes kommen per Bearer-Auth
 *  durch die eigene API — keine presigned URL, weil MinIO Docker-intern ist. */
function AnhangVorschau({
  att,
  imid,
  mailbox,
  onClose,
  onSeitenzahl,
  onHerunterladen,
}: {
  att: MessageAttachment;
  imid: string;
  mailbox?: string;
  onClose: () => void;
  onSeitenzahl: (attId: string, seiten: number) => void;
  onHerunterladen: (a: MessageAttachment) => void;
}) {
  const mt = useT().mail.anhaenge;
  const ct = useT().common;
  const [url, setUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  useEffect(() => {
    let abgebrochen = false;
    let objectUrl: string | null = null;
    setLaedt(true);
    setFehler(null);
    setUrl(null);

    (async () => {
      try {
        const p = new URLSearchParams({ attachment_id: att.attachment_id });
        if (mailbox) p.set("mailbox", mailbox);
        const r = await fetch(
          apiUrl(`/mail/attachment/${encodeURIComponent(imid)}?${p}`),
          { headers: { Authorization: `Bearer ${authToken() ?? ""}` } }
        );
        if (!r.ok) {
          // Serverfehler LOKAL übersetzen: der globale Client verstümmelt ein
          // 422 app-weit zu „[object Object]" (frontend/src/api.ts) — das ist
          // ein eigener Auftrag und wird hier nur umgangen, nicht behoben.
          const a = katalog().mail.anhaenge;
          throw new Error(
            r.status === 404
              ? a.fehler404
              : r.status === 403
                ? a.fehler403
                : a.fehlerStatus(r.status)
          );
        }
        const buf = await r.arrayBuffer();
        if (abgebrochen) return;
        if (istPdf(att)) {
          const s = pdfSeitenzahl(buf);
          if (s) onSeitenzahl(att.attachment_id, s);
        }
        objectUrl = URL.createObjectURL(
          new Blob([buf], { type: att.content_type || "application/octet-stream" })
        );
        setUrl(objectUrl);
      } catch (e) {
        if (!abgebrochen)
          setFehler(
            e instanceof Error ? e.message : katalog().mail.anhaenge.fehlerAllgemein
          );
      } finally {
        if (!abgebrochen) setLaedt(false);
      }
    })();

    return () => {
      abgebrochen = true;
      // Ohne Freigabe hält jeder Wechsel PDF→PDF die alte Datei im Speicher.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [att.attachment_id, att.content_type, imid, mailbox, onSeitenzahl]);

  return (
    <div
      data-mail-attachment-pane
      className={clsx(
        "flex flex-col min-h-0 border-border bg-surface",
        // Desktop: fester Anteil NEBEN dem Text, der Text bleibt lesbar.
        "md:w-[46%] md:min-w-[320px] md:max-w-[720px] md:border-l",
        // Mobile (390 px): das Pane klappt UNTER den Text statt daneben.
        // 45vh statt mehr, damit über der Vorschau noch ein paar Zeilen Text
        // stehen bleiben — der Begleittext erklärt oft, was das PDF ist. Bei
        // 55vh blieb davon auf einem 390×844-Gerät nichts Lesbares übrig.
        "w-full border-t md:border-t-0 h-[45vh] md:h-auto shrink-0"
      )}
    >
      <div className="shrink-0 flex items-center gap-2 border-b border-border px-3 py-2">
        <FileText className="w-4 h-4 shrink-0 text-text-3" />
        <span className="truncate text-sm font-medium text-text" title={att.name}>
          {att.name}
        </span>
        <span className="ml-auto shrink-0 flex items-center gap-1">
          <button
            type="button"
            onClick={() => onHerunterladen(att)}
            title={ct.herunterladen}
            aria-label={mt.anlageHerunterladen}
            className="rounded p-1 text-text-3 hover:text-accent-500 transition-colors"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            title={mt.vorschauSchliessen}
            aria-label={mt.vorschauSchliessen}
            data-mail-attachment-close
            className="rounded p-1 text-text-3 hover:text-accent-500 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </span>
      </div>

      <div className="flex-1 min-h-0 overflow-auto bg-canvas">
        {laedt && (
          <div className="flex h-full items-center justify-center gap-2 p-6 text-sm text-text-3">
            <Loader2 className="w-4 h-4 animate-spin" />
            {mt.laedt}
          </div>
        )}
        {!laedt && fehler && (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
            <AlertTriangle className="w-5 h-5 text-warning" />
            <div className="text-sm text-text-2">{fehler}</div>
          </div>
        )}
        {!laedt && !fehler && url && istPdf(att) && (
          // Browser-eigene PDF-Darstellung. title = Barrierefreiheit; der
          // iframe-Inhalt ist eine Blob-URL, kein fremder Ursprung.
          <iframe
            src={url}
            title={mt.vorschauTitel(att.name)}
            data-mail-attachment-frame
            className="h-full w-full border-0"
          />
        )}
        {!laedt && !fehler && url && !istPdf(att) && istBild(att) && (
          <div className="flex h-full items-center justify-center p-3">
            <img
              src={url}
              alt={att.name}
              data-mail-attachment-image
              className="max-h-full max-w-full object-contain"
            />
          </div>
        )}
        {!laedt && !fehler && url && !istPdf(att) && !istBild(att) && (
          // Kein Office-Viewer — herunterladen ist hier die ehrliche Antwort.
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <div className="text-sm text-text-2">{mt.keineVorschau}</div>
            <Button
              variant="secondary"
              size="sm"
              icon={<Download className="w-4 h-4" />}
              onClick={() => onHerunterladen(att)}
            >
              {ct.herunterladen}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Termin-Einladung beantworten (F-11) ──────────────────────────────────────
//
// Die Antwort geht an denselben Endpunkt wie im Kalender
// (POST /calendar/events/{id}/respond); die Wortwahl der Schaltflächen ist
// bewusst identisch zu ``CalendarMs.tsx`` — dieselbe Handlung soll nicht an
// zwei Stellen anders heißen.
function TerminAntwort({
  eventId,
  bereits,
}: {
  eventId: string;
  bereits: string | null;
}) {
  const toast = useToast();
  const mt = useT().mail.termin;
  const antwortLabel = mt.label as Record<string, string | undefined>;
  const [laeuft, setLaeuft] = useState<string | null>(null);
  // Optimistische Anzeige: was zuletzt in DIESER Sitzung verschickt wurde.
  const [gesendet, setGesendet] = useState<string | null>(null);

  const antworten = async (response: string) => {
    setLaeuft(response);
    try {
      await api.post(
        `/calendar/events/${encodeURIComponent(eventId)}/respond`,
        { response }
      );
      setGesendet(response);
      const k = katalog().mail.termin;
      const kl = k.label as Record<string, string | undefined>;
      toast.ok(kl[response] ?? k.antwortVerschickt);
    } catch (e) {
      const s = (e as ApiError)?.status;
      const k = katalog().mail.termin;
      toast.err(
        k.antwortFehlgeschlagen,
        s === 503 ? k.fehlerDienst : s === 403 ? k.fehlerFreigabe : undefined
      );
    } finally {
      setLaeuft(null);
    }
  };

  // ``notResponded`` ist keine Antwort, sondern deren Abwesenheit.
  const vorstand =
    gesendet ??
    (bereits && bereits !== "notResponded" && bereits !== "none"
      ? bereits
      : null);

  return (
    <div
      data-mail-invitation-actions
      className="mt-3 rounded-md border border-border bg-surface px-3 py-2"
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-text">
        <CalendarClock className="w-3.5 h-3.5 text-text-3" />
        {mt.ueberschrift}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          icon={<Check className="w-4 h-4" />}
          disabled={laeuft !== null}
          onClick={() => void antworten("accept")}
          data-mail-invitation-accept
        >
          {mt.zusagen}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon={<HelpCircle className="w-4 h-4" />}
          disabled={laeuft !== null}
          onClick={() => void antworten("tentativelyAccept")}
        >
          {mt.mitVorbehalt}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon={<X className="w-4 h-4" />}
          disabled={laeuft !== null}
          onClick={() => void antworten("decline")}
        >
          {mt.absagen}
        </Button>
        {vorstand && (
          <span className="text-xs text-text-3">
            {antwortLabel[vorstand] ?? mt.status(vorstand)}
          </span>
        )}
      </div>
    </div>
  );
}

/** Wieviele Empfänger im Kopf ohne Aufklappen dastehen (F-27). Ein Verteiler
 *  mit 20 Adressen darf den Kopf nicht auf halbe Bildschirmhöhe treiben — der
 *  Rest steht hinter „+n weitere" und ist einen Klick entfernt, nicht weg. */
const EMPFAENGER_SICHTBAR = 5;

/**
 * Eine Empfänger-Zeile im Kopf der geöffneten Mail — „An:" bzw. „Kopie:" (F-27).
 *
 * Der Kopf zeigte bis hierher NUR den Absender, und zwar in jedem Ordner. Damit
 * stand nirgends, an wen eine Mail ging oder wer mitgelesen hat; im Ordner
 * „Gesendet" las der Melder folglich immer nur den eigenen Namen.
 *
 * Content-First: keine feste Breite, die abschneidet. Jeder Name ist ein
 * eigenes Flex-Element, die Zeile bricht um, und die Höhe folgt dem Inhalt.
 * Der `title` trägt die Adresse nach — sichtbar steht der Name, denn „Tim
 * Bentler" beantwortet die Frage, `tim.bentler@…` beantwortet sie zweimal.
 */
function EmpfaengerZeile({
  label,
  empfaenger,
  testId,
}: {
  label: string;
  empfaenger: MailRecipient[];
  testId: string;
}) {
  const [offen, setOffen] = useState(false);
  const mt = useT().mail.lesen;
  if (!empfaenger.length) return null;
  const sichtbar = offen ? empfaenger : empfaenger.slice(0, EMPFAENGER_SICHTBAR);
  const rest = empfaenger.length - sichtbar.length;
  return (
    <div
      data-testid={testId}
      className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-xs text-text-3"
    >
      <span className="shrink-0 font-medium text-text-2">{label}</span>
      {sichtbar.map((r, i) => (
        <span key={`${r.address}-${i}`} title={r.address} className="break-all">
          {r.name || r.address}
          {i < sichtbar.length - 1 ? "," : ""}
        </span>
      ))}
      {rest > 0 && (
        <button
          type="button"
          data-testid={`${testId}-mehr`}
          onClick={() => setOffen(true)}
          className="shrink-0 rounded text-accent-600 hover:underline"
        >
          {mt.weitereEmpfaenger(rest)}
        </button>
      )}
      {offen && empfaenger.length > EMPFAENGER_SICHTBAR && (
        <button
          type="button"
          onClick={() => setOffen(false)}
          className="shrink-0 rounded text-accent-600 hover:underline"
        >
          {mt.wenigerEmpfaenger}
        </button>
      )}
    </div>
  );
}

function DetailPane({
  imid,
  onBack,
  isRead,
  onToggleRead,
  onArchive,
  onMove,
  onDelete,
  colleagues,
  ownerEmail,
  readOnly = false,
  mailbox = "",
  actionsRef,
  pendingAction = null,
  onPendingActionDone,
}: {
  imid: string;
  onBack: () => void;
  isRead: boolean;
  onToggleRead: (imid: string, read: boolean) => void;
  onArchive: (imid: string) => void;
  /** `trigger` = das auslösende Bedienelement; dorthin geht der Fokus zurück.
   *  `fromAddress` = Absender der Mail; daraus holt der Dialog den Zielordner-
   *  Vorschlag (F-15). Fehlt er, gibt es schlicht keinen Vorschlag. */
  onMove: (
    imid: string,
    trigger?: HTMLElement | null,
    fromAddress?: string | null
  ) => void;
  onDelete: (imid: string) => void;
  colleagues: Colleague[];
  ownerEmail: string;
  // X1: MailPage reicht eine Ref herein; DetailPane spiegelt seine `detailActions`
  // + `openReplySuggest` hinein, damit die zentralen Kürzel (Termin/KI/Vorlage +
  // das Löschen-Gating) daran andocken. Optional → Detail läuft auch ohne.
  actionsRef?: React.MutableRefObject<MailDetailActionsBridge | null>;
  // Aus dem Zeilen-Kontextmenü angestoßene Detail-Aktion (Antworten/Weiterleiten/
  // Vorlage/Termin). Die brauchen die GELADENE Mail — die Zeile hat nur den Kopf.
  // Der Menü-Eintrag öffnet deshalb die Mail und hinterlegt hier den Key; sobald
  // der Body da ist, löst DetailPane genau diesen Eintrag aus. Dieselbe Liste
  // (`detailActions`), derselbe readOnly-Filter — kein zweiter Aktions-Pfad.
  pendingAction?: { imid: string; key: string } | null;
  onPendingActionDone?: () => void;
  // Ordner-Ansicht (P1.5) / Shared-Postfach (P2.13): Lese-Ansicht ohne Archiv/
  // Löschen/Read-Toggle — Antworten-Buttons bleiben. Outlook-Lese-Wahrheit.
  readOnly?: boolean;
  // Aktives Funktionspostfach (P2.13): leer = eigenes Postfach. Wird an den
  // Detail-/Conversation-Fetch als `?mailbox=` gehängt, damit Body + Thread aus
  // demselben (freigegebenen) Postfach kommen wie die Liste.
  mailbox?: string;
}) {
  // Shared-Postfach (P2.13): non-leeres `mailbox` = fremdes Funktionspostfach →
  // die Ansicht ist strikt LESEND. Zusätzlich zu `readOnly` (Archiv/Löschen/
  // Read-Toggle) entfallen auch ALLE Verfassen-Einstiege (Antworten/Allen/
  // Weiterleiten/Vorlage/Frei) — „Senden ALS Funktionspostfach" ist die offene
  // §7.1-Entscheidung (Send-As), nicht Teil von R1.
  const noCompose = !!mailbox;
  const [msg, setMsg] = useState<MessageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  // null = kein Reply-Editor offen; "ai" = KI-Vorschläge; "blank" = Freitext.
  const [replyMode, setReplyMode] = useState<"ai" | "blank" | null>(null);
  // Verfassen-Fall des offenen Composers (reply/reply-all/forward).
  const [replyKind, setReplyKind] = useState<ComposeKind>("reply");
  // Über "Vorlage einfügen" im Detail aufgelöst → Startinhalt für den Composer.
  const [pendingDraft, setPendingDraft] = useState<
    { body: string; attachments: MailAttachment[] } | null
  >(null);
  const toast = useToast();
  const mt = useT().mail.lesen;
  const ml = useT().mail.liste;
  const ct = useT().common;

  // P2.18: TemplatePicker-Open-State ins DetailPane hochgezogen → das …-Menü öffnet
  // die Vorlagen-Auswahl ohne Nested-Dropdown (die Resolve-Logik bleibt in
  // TemplatePickerButton, hier nur der Offen-Zustand).
  const [templateOpen, setTemplateOpen] = useState(false);

  // P2.16: „Termin aus dieser Mail" — öffnet den (wiederverwendeten) Kalender-
  // Wizard prefilled. `openScheduleFromMail` ist ein BENANNTER, programmatisch
  // aufrufbarer Handler (X1 bindet ihn später an Strg+Umschalt+A — hier KEIN
  // Key-Handler, Doc 04). Der Menü-Eintrag trägt den Kürzel-Hinweis als Tooltip.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const openScheduleFromMail = useCallback(() => setScheduleOpen(true), []);

  // P2.18/Slice B: Breite der EMAIL-LANE (nicht des Viewports — das SF-Panel ist
  // resizable, breiter Viewport ≠ breite Lane). Breite Lane hebt Sekundär-Aktionen
  // als Inline-Buttons aus dem …-Menü heraus. Callback-Ref, damit der Observer auch
  // beim Loading→Loaded-Remount der Lane sauber (neu) andockt. Hysterese
  // (900/935 px) gegen Flackern am Grenzwert.
  const laneRoRef = useRef<ResizeObserver | null>(null);
  // Der Knoten selbst (E1b): der Composer steht oben in dieser Spalte, also
  // muss die Spalte auch oben stehen, wenn er aufgeht.
  const laneElRef = useRef<HTMLDivElement | null>(null);
  // laneWide (>935): Sekundär-Aktionen inline herausgehoben. Der Wert ist
  //   gemessen (Cluster 635 px + Betreff-Boden 288 px + Abstand), Begründung
  //   unten am Observer.
  // laneNarrow (<440): der Primär-CTA schrumpft auf icon-only (ohne Text-Label),
  //   sonst quetscht selbst "Antwort vorschlagen" (≈160 px) den Betreff wieder auf
  //   wortweisen Umbruch — die Kern-Acceptance ist cluster.clientWidth <
  //   h2.clientWidth bei ~400 px Lane, und das ist mit einem Text-Primärbutton bei
  //   400 px nicht erfüllbar. Der CTA bleibt inline erreichbar (aria-label/title).
  const [laneWide, setLaneWide] = useState(false);
  const [laneNarrow, setLaneNarrow] = useState(false);
  const setLaneNode = useCallback((node: HTMLDivElement | null) => {
    laneRoRef.current?.disconnect();
    laneRoRef.current = null;
    laneElRef.current = node;
    if (!node || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setLaneWide((prev) => {
        // Der Schwellwert ist an die GEMESSENE Breite des vollen Clusters
        // gebunden, nicht geschätzt: 635 px (sechs Knöpfe, deutsche Labels,
        // `frontend/mail-kopf-messung.mjs`) + 288 px Betreff-Boden + 12 px
        // Abstand = 935 px. Bei 640 px schaltete die Schwelle die drei
        // Sekundär-Aktionen frei, obwohl sie zusammen breiter waren als die
        // ganze Lane — daraus entstand der zerquetschte Betreff. Darunter
        // stehen sie im …-Menü, vollständig und funktionsgleich.
        // Was der Wert NICHT verspricht: eine einzige Kopfzeile. Ob Betreff
        // und Cluster nebeneinander passen, entscheidet die INHALTS-Breite
        // (Lane minus 40 px Innenabstand) — zwischen 935 und ~975 px steht der
        // Cluster deshalb rechts in einer zweiten Zeile. Das ist der gewollte
        // Ausweg, nicht der Fehlerfall.
        // Hysterese 900/935 gegen Flackern an der Kante.
        if (prev && w < 900) return false;
        if (!prev && w > 935) return true;
        return prev;
      });
      setLaneNarrow((prev) => {
        if (prev && w > 480) return false;
        if (!prev && w < 440) return true;
        return prev;
      });
    });
    ro.observe(node);
    laneRoRef.current = ro;
  }, []);

  // Detail-Kopf-Aktionen (P2.18): gemeinsame Öffner für Inline-Buttons UND Menü-
  // Items, damit beide Darstellungen funktionsgleich sind (Setter sind stabil).
  const openReplySuggest = useCallback(() => {
    setPendingDraft(null);
    setReplyKind("reply");
    setReplyMode("ai");
  }, []);
  const openReplyBlank = useCallback(() => {
    setPendingDraft(null);
    setReplyKind("reply");
    setReplyMode("blank");
  }, []);
  const openReplyAll = useCallback(() => {
    setPendingDraft(null);
    setReplyKind("reply-all");
    setReplyMode("blank");
  }, []);
  const openForward = useCallback(() => {
    setPendingDraft(null);
    setReplyKind("forward");
    setReplyMode("blank");
  }, []);

  // E1b: Beim Öffnen des Composers an den Anfang der Lesespalte springen.
  // Der Composer steht dort — wer eine lange Mail bis ans Ende gelesen hat und
  // dann antwortet (Kopf-Knopf, …-Menü, Zeilen-Kontextmenü oder Kürzel),
  // bekäme sonst ein Formular, das OBERHALB seines Sichtfelds aufgeht: „alles
  // ist direkt da" wäre dann genau nicht wahr. Bewusst ohne `behavior:smooth`
  // (Outlook springt) und nur beim Öffnen — beim Schließen bleibt der Blick,
  // wo er ist.
  useEffect(() => {
    if (replyMode && laneElRef.current) laneElRef.current.scrollTop = 0;
  }, [replyMode]);

  // Menü-Items für das …-Overflow-Menü (P2.18 Dock-API). BINDENDE Reihenfolge:
  // reply-all · reply-blank · forward · template · archive · delete(danger,last).
  // Ordner-Ansicht (readOnly): archive/delete ganz WEGLASSEN (nicht disabled) —
  // konsistent mit P1.5. Der Primär-CTA "Antwort vorschlagen" (reply-suggest)
  // bleibt separat inline (NICHT in dieser Liste). X1 indexiert später GENAU diese
  // Liste — Shape + Reihenfolge sind der Andock-Vertrag.
  const detailActions = useMemo<DetailAction[]>(() => {
    const actions: DetailAction[] = [
      { key: "reply-all", label: ml.menuAllenAntworten, icon: <ReplyAll className="w-4 h-4" />, onSelect: openReplyAll },
      { key: "reply-blank", label: ml.menuFreiAntworten, icon: <Pencil className="w-4 h-4" />, onSelect: openReplyBlank },
      { key: "forward", label: ml.menuWeiterleiten, icon: <Forward className="w-4 h-4" />, onSelect: openForward },
      { key: "template", label: ml.menuVorlage, icon: <FileText className="w-4 h-4" />, onSelect: () => setTemplateOpen(true) },
      // P2.16 — nach `template`, vor `archive` (Driver-Reihenfolge). Tooltip mit
      // Kürzel-Hinweis (Sichtbarkeit, Doc 04); X1 bindet den Handler an das Kürzel.
      { key: "schedule-from-mail", label: ml.menuTermin, icon: <CalendarPlus className="w-4 h-4" />, onSelect: openScheduleFromMail, title: ml.menuTerminTitel(shortcutHint("termin")) },
    ];
    if (!readOnly) {
      actions.push({ key: "archive", label: ml.menuArchivieren, icon: <Archive className="w-4 h-4" />, onSelect: () => onArchive(imid) });
      // F-05 — direkt nach `archive`: Archivieren IST das Verschieben mit festem
      // Ziel, die beiden gehören nebeneinander. Bewusst OHNE Kürzel-Hinweis im
      // `title` (anders als `schedule-from-mail`/`delete`) — es gibt keines, und
      // ein Hinweis auf ein nicht gebundenes Kürzel wäre eine Lüge.
      actions.push({ key: "move", label: ml.menuVerschieben, icon: <FolderInput className="w-4 h-4" />, onSelect: () => onMove(imid, null, msg?.from_address) });
      actions.push({ key: "delete", label: ct.loeschen, icon: <Trash2 className="w-4 h-4" />, onSelect: () => onDelete(imid), variant: "danger", title: ml.menuLoeschenTitel(shortcutHint("delete")) });
    }
    return actions;
    // `msg?.from_address` in den Deps: die Aktionen entstehen, bevor das Detail
    // geladen ist. Ohne die Dep bliebe der Absender in der geschlossenen Hülle
    // `undefined` — der Vorschlag käme im Menü NIE, im Kopfleisten-Knopf aber
    // schon, und niemand fände den Grund.
  }, [imid, readOnly, onArchive, onMove, onDelete, openReplyAll, openReplyBlank, openForward, openScheduleFromMail, msg?.from_address, ml]);

  // Slice B: breite Lane → reply-all + reply-blank + template stehen inline, das
  // Menü zeigt den Rest. Items-API bleibt identisch, nur die Darstellung
  // entscheidet — kein Eintrag steht doppelt (inline UND im Menü).
  const menuActions = useMemo(
    () =>
      laneWide
        ? detailActions.filter(
            (a) =>
              a.key !== "reply-all" &&
              a.key !== "reply-blank" &&
              a.key !== "template"
          )
        : detailActions,
    [detailActions, laneWide]
  );

  // X1 Dock-Brücke: die (readOnly-gegateten) `detailActions` + den Antwort-CTA in
  // die MailPage-Ref spiegeln, damit die zentralen Kürzel (Strg+Umschalt+A,
  // ?do=termin/ki/vorlage) und das Entf-Gating per Key andocken. Beim Unmount
  // auf null → mail-offen-Kürzel werden zu no-ops.
  useEffect(() => {
    if (!actionsRef) return;
    actionsRef.current = { actions: detailActions, openReplySuggest };
    return () => {
      actionsRef.current = null;
    };
  }, [actionsRef, detailActions, openReplySuggest]);

  // Nachzügler aus dem Zeilen-Kontextmenü. Erst wenn `msg` steht — „Termin aus
  // dieser Mail" zieht Betreff/Verteiler aus der geladenen Mail, ein zu früher
  // Aufruf öffnete den Wizard ohne Vorbelegung. Der Key wird über dieselbe
  // (readOnly-gefilterte) `detailActions`-Liste aufgelöst: was das Menü der
  // offenen Mail nicht anbietet, geht auch hierüber nicht.
  useEffect(() => {
    if (!pendingAction || pendingAction.imid !== imid || !msg) return;
    detailActions.find((a) => a.key === pendingAction.key)?.onSelect();
    onPendingActionDone?.();
  }, [pendingAction, imid, msg, detailActions, onPendingActionDone]);

  // P2.16 — Prefill für „Termin aus dieser Mail": Betreff = Mail-Betreff;
  // erkanntes Datum (parseGermanDate über Betreff+Body) → 07:00–19:00-Fenster;
  // und der VERTEILER der Mail als vorausgewählte Teilnehmer — intern wie EXTERN.
  //
  // Verteiler-Regel wie Outlook: Absender + An → PFLICHT, Cc → OPTIONAL. Das
  // eigene Postfach steht in KEINER von beiden (man lädt sich nicht selbst ein;
  // Organizer ist man ohnehin serverseitig über das Token). Eine Adresse, die in
  // An UND Cc steht, ist genau EINMAL Teilnehmer — als Pflicht: „darf fehlen"
  // wäre die schädlichere der beiden Lesarten, wenn die Mail beides sagt.
  //
  // Gerechnet wird mit `dedupeAddrList` — demselben Kern, aus dem „Allen
  // antworten" seine An-/Cc-Vorbelegung zieht (prefillTo/prefillCc). Damit gibt
  // es EINE Stelle, die das eigene Postfach entfernt und case-insensitiv
  // dedupliziert, statt zweier, die auseinanderlaufen können.
  //
  // Externe waren hier ausgenommen mit der Begründung, `_validate_attendees`
  // weise sie mit 422 ab. Das trifft nur den VERFÜGBARKEITS-Pfad: der läuft mit
  // `allow_external=False` (app/routers/calendar.py:141-148, Aufruf :201), weil Graph
  // `getSchedule` für Adressen außerhalb der Organisation kein belastbares
  // Free/Busy liefert (ein leeres availabilityView läse sich als „frei") — dort
  // hat eine externe Adresse zu Recht nichts verloren, und der Wizard filtert
  // sie über `internalUpns` heraus (CalendarMs.tsx:2346). Der EVENT-Pfad läuft
  // dagegen mit `allow_external=True` (calendar.py:235, Regel :135-136/:154) und verlangt nur,
  // dass die Adresse ein SF-`Contact` ist: externe Teilnehmer sind unterstützt,
  // ein Kunde gehört also in die Einladung. Ob der Kontakt existiert, gleicht
  // der Wizard beim Öffnen gegen `/calendar/contacts` ab und sagt es VOR dem
  // Senden (CalendarMs.tsx `UnknownContactWarning`) — statt den Nutzer in den
  // 422 laufen zu lassen.
  //
  // Memoisiert, damit `initialWindow`/`initialAttendees` als stabile Objekte in
  // den Wizard gehen (der prüft den Prefill genau einmal beim Öffnen).
  const schedulePrefill = useMemo(() => {
    if (!msg) return null;
    const fromAddr = (msg.from_address ?? "").trim();
    const to = msg.to_recipients ?? [];
    const cc = msg.cc_recipients ?? [];

    const requiredAddrs = dedupeAddrList(
      [fromAddr, ...to.map((r) => r.address)],
      ownerEmail
    );
    // Pflicht gewinnt: Cc-Adressen, die schon Pflicht sind, fallen hier raus —
    // sonst stünde derselbe Mensch in beiden Gruppen.
    const requiredKeys = new Set(requiredAddrs.map((a) => a.toLowerCase()));
    const optionalAddrs = dedupeAddrList(cc.map((r) => r.address), ownerEmail).filter(
      (a) => !requiredKeys.has(a.toLowerCase())
    );

    // Anzeigename: Kollegen-Verzeichnis schlägt den Namen aus der Mail (dort
    // steht bei Verteilern oft nur die Adresse), die Mail schlägt die nackte
    // Adresse. Der Chip soll einen Menschen zeigen, keinen Header-Rest.
    const mailNames = new Map<string, string>();
    if (fromAddr && msg.from_name) mailNames.set(fromAddr.toLowerCase(), msg.from_name);
    for (const r of [...to, ...cc])
      if (r.name) mailNames.set((r.address || "").trim().toLowerCase(), r.name);
    const toParticipant = (addr: string): Colleague => {
      const key = addr.toLowerCase();
      return (
        colleagues.find((c) => c.email.toLowerCase() === key) ?? {
          name: mailNames.get(key) || addr,
          email: addr,
          department: null,
        }
      );
    };

    const detected = parseGermanDate(
      `${msg.subject ?? ""} ${msg.body_text ?? ""}`,
      new Date()
    );
    let initialWindow: { start: Date; end: Date } | undefined;
    if (detected) {
      const start = new Date(detected);
      start.setHours(7, 0, 0, 0);
      const end = new Date(detected);
      end.setHours(19, 0, 0, 0);
      initialWindow = { start, end };
    }
    return {
      initialSubject: msg.subject ?? "",
      initialAttendees: requiredAddrs.map(toParticipant),
      initialOptionalAttendees: optionalAddrs.map(toParticipant),
      initialWindow,
      contextNote: fromAddr ? `Absender: ${fromAddr}` : undefined,
    };
  }, [msg, colleagues, ownerEmail]);

  // Seitliches Intelligence-Panel: ein-/ausblendbar, Zustand gemerkt
  // (localStorage, Fallback nach Viewport). Bleibt über Mail-Wechsel/
  // Navigation erhalten → nur beim Mount aus dem Speicher gelesen.
  const [intelOpen, setIntelOpen] = useState<boolean>(() => readIntelOpen());
  const [intelTab, setIntelTab] = useState<"kontext" | "fragen">("kontext");
  const [intelWidth, setIntelWidth] = useState<number>(() => readIntelWidth());
  const toggleIntel = useCallback(() => {
    setIntelOpen((o) => {
      const next = !o;
      writeIntelOpen(next);
      return next;
    });
  }, []);
  const resizeIntel = useCallback((w: number) => {
    const c = clampIntelWidth(w);
    setIntelWidth(c);
    writeIntelWidth(c);
  }, []);

  // Aufgelöste Vorlage → Composer öffnen und mit dem Entwurf starten.
  const handleTemplatePick = (r: ResolveTemplateResult, name: string) => {
    setPendingDraft({ body: r.preview_md, attachments: r.attachments });
    setReplyMode("blank");
    const v = katalog().mail.vorlagen;
    const parts = [v.felderGefuellt(Object.keys(r.filled).length)];
    if (r.gaps.length) parts.push(v.offen(r.gaps.length));
    if (r.attachments.length) parts.push(v.anhang(r.attachments.length));
    if (r.sf_error) parts.push(v.sfNichtErreichbar);
    toast.ok(v.eingefuegt(name), parts.join(" · "));
  };

  // Reply-Panel schließen, sobald eine andere Mail geöffnet wird.
  useEffect(() => {
    setReplyMode(null);
    setReplyKind("reply");
    setPendingDraft(null);
    // F-07: auch die Anhang-Auswahl gehört zur alten Mail. Ohne diesen Reset
    // stünde die Vorschau der vorigen Nachricht über der neuen.
    setAtts([]);
    setPreviewId(null);
    setSeiten({});
  }, [imid]);

  // ── Anlagen (F-07) ────────────────────────────────────────────────────────
  // Metadaten-Abruf, sobald feststeht, dass die Mail überhaupt Anhänge hat.
  // Das ist eine zweite, LEICHTE Abfrage (Namen/Größen, keine Bytes) — die
  // Dateien selbst holt erst die Vorschau auf Klick.
  const [atts, setAtts] = useState<MessageAttachment[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [seiten, setSeiten] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!msg?.has_attachments) return;
    let abgebrochen = false;
    (async () => {
      try {
        const suffix = mailbox ? `?mailbox=${encodeURIComponent(mailbox)}` : "";
        const res = await api.get<MessageAttachment[]>(
          `/mail/attachments/${encodeURIComponent(imid)}${suffix}`
        );
        if (!abgebrochen) setAtts(res);
      } catch {
        // Still: eine leere Leiste ist ehrlicher als ein Fehlerbanner über
        // einer Mail, die man gerade lesen will. Der Fehler ist nicht
        // handlungsrelevant — die Mail selbst steht ja da.
        if (!abgebrochen) setAtts([]);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [imid, mailbox, msg?.has_attachments]);

  const merkeSeitenzahl = useCallback((attId: string, n: number) => {
    setSeiten((s) => (s[attId] === n ? s : { ...s, [attId]: n }));
  }, []);

  const anlageHerunterladen = useCallback(
    (a: MessageAttachment) => {
      const p = new URLSearchParams({
        attachment_id: a.attachment_id,
        download: "1",
      });
      if (mailbox) p.set("mailbox", mailbox);
      // Bytes gehen per Bearer-Auth durch die API; ein direkter <a href> hätte
      // keinen Token dabei. Deshalb Blob holen und lokal anklicken.
      void (async () => {
        try {
          const r = await fetch(
            apiUrl(`/mail/attachment/${encodeURIComponent(imid)}?${p}`),
            { headers: { Authorization: `Bearer ${authToken() ?? ""}` } }
          );
          if (!r.ok) throw new Error(String(r.status));
          const url = URL.createObjectURL(await r.blob());
          const el = document.createElement("a");
          el.href = url;
          el.download = a.name || "anlage";
          document.body.appendChild(el);
          el.click();
          el.remove();
          URL.revokeObjectURL(url);
        } catch {
          toast.err(katalog().mail.lesen.downloadFehlgeschlagen, a.name);
        }
      })();
    },
    [imid, mailbox, toast]
  );

  // ── „Alle herunterladen (n)" ──────────────────────────────────────────────
  // EIN Klick, n einzelne Dateien im Download-Ordner. KEIN ZIP: „sonst muss er
  // wieder ent-zippen" (Auflage des Auftraggebers, wörtlich).
  //
  // Die Bytes kommen in EINER Antwort (``/mail/attachments-bundle/…``, dahinter
  // EIN Graph-Aufruf für alle Anlagen). Der frühere Weg — n-mal denselben
  // Einzel-Download auslösen — hätte bei 13 Anlagen 26 Graph-Aufrufe für einen
  // Klick bedeutet und wäre bei der ersten Drosselung halb fertig geworden.
  //
  // Was der Browser daraus macht, bleibt dieselbe Mechanik wie beim einzelnen
  // Knopf: Blob + ``<a download>``-Klick. Nur eben n-mal. Der kleine Abstand
  // dazwischen ist Absicht — mehrere Downloads im selben Tick lässt der Browser
  // zusammenfallen, und dann fehlen Dateien, ohne dass ein Fehler auftaucht.
  const [sammelLaeuft, setSammelLaeuft] = useState(false);
  const alleAnlagenHerunterladen = useCallback(() => {
    void (async () => {
      setSammelLaeuft(true);
      try {
        const p = new URLSearchParams();
        if (mailbox) p.set("mailbox", mailbox);
        const suffix = p.toString() ? `?${p}` : "";
        const res = await api.get<AttachmentBundleResponse>(
          `/mail/attachments-bundle/${encodeURIComponent(imid)}${suffix}`
        );
        for (const a of res.attachments) {
          const url = URL.createObjectURL(b64ZuBlob(a.content_bytes, a.content_type));
          const el = document.createElement("a");
          el.href = url;
          el.download = a.name || "anlage";
          document.body.appendChild(el);
          el.click();
          el.remove();
          URL.revokeObjectURL(url);
          await new Promise((r) => setTimeout(r, 120));
        }
      } catch (e) {
        const t = katalog().mail.anhaenge;
        const status = (e as ApiError)?.status;
        toast.err(
          t.alleFehlgeschlagen,
          // 413 ist der einzige Fall, den der Nutzer selbst lösen kann — also
          // nennt die Meldung den Ausweg statt „später erneut".
          status === 413 ? t.alleZuGross : t.alleFehlgeschlagenText
        );
      } finally {
        setSammelLaeuft(false);
      }
    })();
  }, [imid, mailbox, toast]);

  // ── Ziehen ins Dateisystem (F-12) ─────────────────────────────────────────
  // Melder Bentler: „Könnte auch eingestellt werden, dass sie per drag&drop
  // direkt ins richtige Verzeichnis gezogen werden kann?"
  //
  // Aus dem Browser in den Explorer führt genau EIN Weg: ein
  // ``DownloadURL``-Eintrag „<mime>:<name>:<url>" im ``dragstart``. Beim
  // Fallenlassen holt der BROWSER diese URL — ohne die Header der App. Ein
  // Verweis auf ``/mail/attachment/…`` liefe deshalb in 401 (Bearer aus dem
  // localStorage, api.ts), und einen Token in die Query zu hängen ist keine
  // Option. Also zeigt der Eintrag auf eine ``blob:``-URL, deren Bytes schon
  // im Browser liegen.
  //
  // ``dragstart`` ist SYNCHRON und erlaubt kein await — die Bytes müssen vor
  // dem Zug da sein. Geholt werden sie beim ``mouseenter`` auf dem Chip, NICHT
  // beim Öffnen der Mail: sonst zahlte jede geöffnete Mail mit Anhang die
  // Bytes, ohne dass sie jemand haben will.
  const ziehBlobs = useRef<Map<string, string>>(new Map());
  const ziehLaufend = useRef<Set<string>>(new Set());
  const ziehStand = useRef(0);

  useEffect(() => {
    const karte = ziehBlobs.current;
    const laufend = ziehLaufend.current;
    return () => {
      // Nachricht gewechselt: die Bytes der alten Mail freigeben — sonst hängen
      // sie bis zum Reload am Dokument. Der hochgezählte Stand lässt noch
      // laufende Abrufe leer ausgehen, statt eine Blob-URL anzulegen, die
      // hinterher niemand mehr widerruft.
      ziehStand.current += 1;
      for (const u of karte.values()) URL.revokeObjectURL(u);
      karte.clear();
      laufend.clear();
    };
  }, [imid, mailbox]);

  const anlageVorladen = useCallback(
    (a: MessageAttachment) => {
      const id = a.attachment_id;
      if (ziehBlobs.current.has(id) || ziehLaufend.current.has(id)) return;
      ziehLaufend.current.add(id);
      const stand = ziehStand.current;
      void (async () => {
        try {
          const p = new URLSearchParams({ attachment_id: id });
          if (mailbox) p.set("mailbox", mailbox);
          const r = await fetch(
            apiUrl(`/mail/attachment/${encodeURIComponent(imid)}?${p}`),
            { headers: { Authorization: `Bearer ${authToken() ?? ""}` } }
          );
          if (!r.ok) return;
          const bytes = await r.blob();
          if (ziehStand.current !== stand) return; // Mail schon gewechselt
          ziehBlobs.current.set(
            id,
            URL.createObjectURL(
              new Blob([bytes], {
                type: a.content_type || "application/octet-stream",
              })
            )
          );
        } catch {
          // Still: der Download-Knopf bleibt der Weg, der immer funktioniert.
        } finally {
          ziehLaufend.current.delete(id);
        }
      })();
    },
    [imid, mailbox]
  );

  const anlageZiehStart = useCallback(
    (a: MessageAttachment, e: React.DragEvent<HTMLElement>) => {
      const url = ziehBlobs.current.get(a.attachment_id);
      if (!url) {
        // Noch keine Bytes (gerade erst gehovert, langsame Leitung): lieber gar
        // kein Zug als ein Eintrag, der im Zielordner eine kaputte Datei ablegt.
        // Der Download-Knopf bleibt unverändert der Weg, der immer trägt.
        e.preventDefault();
        return;
      }
      const typ = a.content_type || "application/octet-stream";
      // Der Doppelpunkt trennt die drei Felder — ein Dateiname, der selbst
      // einen enthält, würde das Format sprengen (Windows verbietet ihn im
      // Dateinamen ohnehin).
      const name = (a.name || "anlage").replace(/[:\r\n]/g, "_");
      e.dataTransfer.setData("DownloadURL", `${typ}:${name}:${url}`);
      e.dataTransfer.effectAllowed = "copy";
    },
    []
  );

  const previewAtt = atts.find((a) => a.attachment_id === previewId) ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const suffix = mailbox ? `?mailbox=${encodeURIComponent(mailbox)}` : "";
      const res = await api.get<MessageResponse>(
        `/mail/messages/${encodeURIComponent(imid)}${suffix}`
      );
      setMsg(res);
    } catch (e) {
      setError(e as ApiError);
      setMsg(null);
    } finally {
      setLoading(false);
    }
  }, [imid, mailbox]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="h-full min-h-0 flex flex-col">
      {/* Mobile-Zurück-Leiste (nur <md, weil Desktop die Liste danebenlässt). */}
      <div className="md:hidden shrink-0 border-b border-border px-2 py-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-text-2 hover:text-text min-h-11 px-1"
        >
          <ArrowLeft className="w-4 h-4" />
          {ct.zurueck}
        </button>
      </div>

      {loading && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <DetailSkeleton />
        </div>
      )}

      {!loading && error && (
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          {error.status === 404 ? (
            <EmptyState
              icon={<MailIcon className="w-6 h-6" />}
              title={mt.nichtGefundenTitel}
              hint={mt.nichtGefundenHinweis}
              action={
                <Button variant="secondary" onClick={onBack}>
                  {mt.zurueckZurListe}
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<ServerCrash className="w-6 h-6" />}
              title={mt.nichtErreichbarTitel}
              hint={mt.nichtErreichbarHinweis}
              action={
                <Button variant="secondary" icon={<RefreshCw className="w-4 h-4" />} onClick={() => void load()}>
                  {ct.erneutVersuchen}
                </Button>
              }
            />
          )}
        </div>
      )}

      {!loading && msg && (
        <div
          className={clsx(
            "flex-1 min-h-0 flex relative overflow-hidden",
            // F-07: mit offener Vorschau klappt das Pane auf Mobile UNTER den
            // Text (Spalte) und steht auf Desktop DANEBEN (Zeile). Ohne
            // Vorschau bleibt es bei der bisherigen Zeile — die Lane ist dann
            // ohnehin das einzige sichtbare Kind.
            previewAtt ? "flex-col md:flex-row" : "flex-row"
          )}
        >
          {/* ---- EMAIL-Spalte (primär) --------------------------------------
              Eigene Scroll-Region, flex-1 → dehnt sich in die freigewordene
              Breite aus, wenn das Intel-Panel rechts zugeklappt/schmaler wird.
              Header/Meta/Composer nutzen die volle Lane-Breite; nur der
              Mail-BODY selbst bleibt (in MessageBody) auf lesbare Breite gecappt.
              md:min-w-[340px] hält die Email-Lane lesbar, wenn der User das
              SF-Panel breit zieht — das Panel gibt dann nach (shrink), statt
              über den Viewport hinauszulaufen. */}
          <div ref={setLaneNode} data-mail-body-scroll className="flex-1 min-w-0 md:min-w-[340px] overflow-y-auto">
            <div className="p-5 space-y-4">
              {/* ── ANTWORTEN: der Composer steht GANZ OBEN in der Lesespalte
                  (E1b, Tester-Feedback Tim). Er war schon vorher inline — nur
                  stand er UNTER dem Kopf der Originalmail (gemessen: y 290 vs.
                  Kopf y 154), und seine Knöpfe lagen ganz unten. Outlook macht
                  es umgekehrt: Composer oben, die Originalmail bleibt darunter
                  vollständig lesbar (Kopf + Body, gemeinsame Scrollspur).
                  Bewusst KEIN Modal/Overlay, kein position:fixed — der
                  Lesebereich soll nicht verdeckt werden.
                  „Neue Mail"/„Entwurf fortsetzen" bleiben Modale (unten). */}
              {replyMode && (
                <ReplyPanel
                  imid={msg.internet_message_id}
                  kind={replyKind}
                  category={msg.enrichment.thread_category}
                  subCategory={msg.enrichment.thread_sub_category}
                  mode={replyMode}
                  replyTo={msg.from_address}
                  replySubject={msg.subject}
                  recordLinks={msg.enrichment.record_links}
                  emailText={msg.body_text ?? ""}
                  initialBody={pendingDraft ? mdToHtml(pendingDraft.body) : undefined}
                  initialAttachments={pendingDraft?.attachments}
                  colleagues={colleagues}
                  ownerEmail={ownerEmail}
                  toRecipients={msg.to_recipients}
                  ccRecipients={msg.cc_recipients}
                  onClose={() => {
                    setPendingDraft(null);
                    setReplyMode(null);
                  }}
                />
              )}

              {/* Verdichteter Thread-Header: Betreff-Zeile fuellt den vormals
                  leeren Raum rechts mit der Kernaktion (Antwort vorschlagen);
                  die Meta-Zeile buendelt Absender + Datum + Account/Kategorie
                  (frueher nur im Panel verstreut). Nur vorhandene Felder werden
                  gerendert (kein leeres Label). */}
              <div>
                {/* Boden unter dem Betreff + Umbruch des ganzen Clusters.
                    Vorher teilten sich beide EINE nicht umbrechende Zeile: die
                    Überschrift mit `flex-1 min-w-0` (Basis 0, schrumpft bis auf
                    null, bricht mit `break-words` auch INNERHALB von Wörtern),
                    der Cluster mit seiner vollen Inhaltsbreite. Gemessen
                    (`frontend/mail-kopf-messung.mjs`): bei 690 px Lane fiel der
                    Betreff auf 3 px Breite und 86 Zeilen — ein Buchstabe je
                    Zeile, 2365 px hoch.
                    Jetzt hat der Betreff einen Boden (18rem, per `min()` auf die
                    Container-Breite gedeckelt, damit er auf schmalen Geräten
                    keinen Überlauf erzwingt), und wenn der Cluster daneben nicht
                    mehr passt, weicht ER in die zweite Zeile aus. Das ist die
                    strukturelle Hälfte: auch ein längeres Label oder ein
                    sechster Knopf kann den Betreff nicht mehr zerquetschen. */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h2 className="min-w-[min(18rem,100%)] flex-1 break-words font-display text-xl font-semibold text-text leading-snug">
                    {msg.subject || mt.keinBetreff}
                  </h2>
                  {/* P2.18: Der Aktions-Cluster ist auf den Primär-CTA + das
                      …-Overflow-Menü verdichtet → der Betreff bekommt die volle
                      Umbruch-Lane (kein wortweiser Umbruch mehr). Bei breiter Lane
                      (Slice B) stehen "Frei antworten" + "Vorlage einfügen"
                      zusätzlich inline; das Menü zeigt dann nur den Rest. */}
                  {/* `shrink-0` statt `min-w-0`: der Cluster gibt seine Breite
                      nicht mehr her, sondern wandert im Zweifel eine Zeile
                      tiefer (`flex-wrap` am Elternteil). Sein eigenes
                      `flex-wrap` bleibt als letzte Reserve für den Fall, dass
                      er auch allein nicht auf eine Zeile passt.
                      `ml-auto` ist NICHT Kosmetik: `justify-between` wirkt je
                      Zeile, und auf einer Zeile mit nur EINEM Element heisst
                      das linksbündig. Der Cluster stünde dann links in der
                      Lane — und das …-Menü (`absolute right-0`, 224 px breit)
                      klappte nach links aus der Lane heraus über die
                      Nachrichtenliste, wo der Hinweisstreifen darüber malt und
                      Klicks abfängt (gemessen: Menü bei x 498-722 statt
                      736-960). `ml-auto` hält den Cluster rechts, damit das
                      Menü im eigenen Bereich bleibt. */}
                  <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
                    {/* Desktop: Primär-Aktion + …-Menü belegen den Leerraum rechts
                        oben. Auf Mobile ausgeblendet (dort stehen sie voll unter
                        der Meta-Zeile). */}
                    {!replyMode && !noCompose && (
                      <>
                        {/* Slice B — breite Lane: Sekundär-Aktionen inline
                            herausgehoben (funktionsgleich mit den Menü-Items). */}
                        {laneWide && (
                          <>
                            {/* W8: „Allen antworten" stand auf dem Desktop
                                ausschließlich im …-Menü — auf Mobile dagegen als
                                sichtbarer Knopf. Reiner Auffindbarkeits-Befund,
                                die Funktion selbst war vollständig. Steht jetzt
                                inline vor „Frei antworten" (Menü-Reihenfolge
                                reply-all · reply-blank · …) und fällt bei
                                schmaler Lane in dasselbe …-Menü zurück wie seine
                                Geschwister — dieselbe Mechanik, die den Cluster
                                schon gegen Überlauf schützt. */}
                            <Button
                              variant="secondary"
                              size="sm"
                              icon={<ReplyAll className="w-4 h-4" />}
                              onClick={openReplyAll}
                              className="hidden sm:inline-flex"
                            >
                              {mt.allenAntworten}
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              icon={<Pencil className="w-4 h-4" />}
                              onClick={openReplyBlank}
                              className="hidden sm:inline-flex"
                            >
                              {mt.freiAntworten}
                            </Button>
                            <TemplatePickerButton
                              recordLinks={msg.enrichment.record_links}
                              onPicked={handleTemplatePick}
                              emailText={msg.body_text ?? ""}
                              open={templateOpen}
                              onOpenChange={setTemplateOpen}
                              align="right"
                              className="hidden sm:inline-flex"
                            />
                          </>
                        )}
                        {/* Primär-CTA — immer inline sichtbar. Bei sehr schmaler
                            Lane icon-only (Label via aria-label/title), damit der
                            Betreff die Lane zurückbekommt (kein wortweiser Umbruch). */}
                        <Button
                          variant="primary"
                          size="sm"
                          icon={<Sparkles className="w-4 h-4" />}
                          onClick={openReplySuggest}
                          aria-label={mt.antwortVorschlagen}
                          title={mt.antwortVorschlagen}
                          className="hidden sm:inline-flex"
                        >
                          {!laneNarrow && mt.antwortVorschlagen}
                        </Button>
                        {/* Overflow-…-Menü (Dock-API detailActions). Bei breiter
                            Lane ohne reply-blank/template (die stehen dann inline). */}
                        <DetailActionMenu
                          items={menuActions}
                          align="right"
                          className="hidden sm:inline-flex"
                        />
                        {/* Headless TemplatePicker-Panel für den Menü-Weg (schmale
                            Lane): kein eigener Trigger, geöffnet über das
                            template-Menü-Item; Panel rechtsbündig wie das …-Menü.
                            Nur montiert, wenn die Lane NICHT breit ist (sonst steht
                            der Picker bereits inline). */}
                        {!laneWide && (
                          <TemplatePickerButton
                            recordLinks={msg.enrichment.record_links}
                            onPicked={handleTemplatePick}
                            emailText={msg.body_text ?? ""}
                            open={templateOpen}
                            onOpenChange={setTemplateOpen}
                            hideTrigger
                            align="right"
                            className="hidden sm:inline-flex"
                          />
                        )}
                      </>
                    )}
                    {/* Panel-Toggle (Mobile) — öffnet den Drawer. Auf Desktop
                        liegt die Ein-/Ausblend-Steuerung im Panel bzw. in der
                        schmalen Rail rechts (Toggle klappt den breiten Slot
                        auf/zu). */}
                    <button
                      type="button"
                      onClick={toggleIntel}
                      className="md:hidden shrink-0 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-text-2 hover:text-text transition-colors"
                      aria-label={mt.kontextEinblenden}
                    >
                      <PanelRight className="w-4 h-4" />
                      {mt.kontext}
                    </button>
                  </div>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text-3">
                  <span className="font-medium text-text">{msg.from_name}</span>
                  <span className="break-all">&lt;{msg.from_address}&gt;</span>
                  <span aria-hidden="true" className="hidden sm:inline text-border">
                    ·
                  </span>
                  <span className="text-text-2 whitespace-nowrap">
                    {fmtFullDate(msg.received_at)}
                  </span>
                  {/* Gelesen/Ungelesen-Toggle (Slice B): subtiler State-Toggle in
                      der Meta-Zeile (NICHT im Primär-Aktions-Cluster — der ist der
                      P2.18-Crowding-Hotspot). Kein Kürzel in R1 (Doc 04). Status =
                      optimistische Eltern-Wahrheit (isRead), nicht das evtl. noch
                      ungelesene Detail-Fetch-msg.is_read. Ordner-Ansicht (P1.5):
                      entfällt (Lese-Ansicht, Outlook-Lese-Wahrheit unverändert). */}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => onToggleRead(imid, !isRead)}
                      title={isRead ? mt.alsUngelesenMarkieren : mt.alsGelesenMarkieren}
                      aria-label={isRead ? mt.alsUngelesenMarkieren : mt.alsGelesenMarkieren}
                      className="inline-flex shrink-0 items-center gap-1 rounded p-0.5 text-text-3 hover:text-accent-500 transition-colors"
                    >
                      {isRead ? (
                        <CircleDot className="w-3.5 h-3.5" />
                      ) : (
                        <CircleCheck className="w-3.5 h-3.5" />
                      )}
                      <span className="text-[11px]">
                        {isRead ? mt.zustandUngelesen : mt.zustandGelesen}
                      </span>
                    </button>
                  )}
                  {/* Das Büroklammer-Symbol bleibt NUR als Fallback stehen: es
                      sagt „hier hängt etwas dran", kann aber nichts öffnen.
                      Sobald die Anhang-Leiste darunter die echten Chips zeigt,
                      wäre es ein zweites, totes Versprechen — genau das, was
                      der Melder beanstandet hat. */}
                  {msg.has_attachments && atts.length === 0 && (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <Paperclip className="w-3.5 h-3.5" /> {mt.anhang}
                    </span>
                  )}
                  {msg.importance === "high" && (
                    <StatusBadge status="warning">{mt.wichtig}</StatusBadge>
                  )}
                  {msg.enrichment.account_name && (
                    <CtxBadge>{msg.enrichment.account_name}</CtxBadge>
                  )}
                  {msg.enrichment.thread_category && (
                    <CtxBadge>{msg.enrichment.thread_category}</CtxBadge>
                  )}
                </div>

                {/* F-27: Empfänger. Gilt in JEDEM Ordner — auch im Posteingang
                    stand bis hierher nirgends, wer eine Mail mitbekommen hat.
                    „Kopie:" und nicht „CC:", weil der Rest der Oberfläche
                    deutsch beschriftet ist; die Zeile fehlt still, wenn es
                    keine Kopie-Empfänger gibt (kein leerer Rahmen). */}
                <div className="mt-1 space-y-0.5">
                  <EmpfaengerZeile
                    label={mt.empfaengerAn}
                    empfaenger={msg.to_recipients ?? []}
                    testId="mail-detail-an"
                  />
                  <EmpfaengerZeile
                    label={mt.empfaengerKopie}
                    empfaenger={msg.cc_recipients ?? []}
                    testId="mail-detail-cc"
                  />
                </div>

                {/* F-07: die Anhang-Leiste. Immer da, sobald es Anlagen gibt —
                    sie ist der Überblick. Das Vorschau-Pane erscheint erst auf
                    Klick (siehe unten). */}
                <AnhangLeiste
                  atts={atts}
                  aktivId={previewId}
                  seiten={seiten}
                  onWaehlen={(a) => setPreviewId(a.attachment_id)}
                  onHerunterladen={anlageHerunterladen}
                  onVorladen={anlageVorladen}
                  onZiehStart={anlageZiehStart}
                  onAlleHerunterladen={alleAnlagenHerunterladen}
                  sammelLaeuft={sammelLaeuft}
                />

                {/* F-11: Termin-Einladung direkt aus der Mail beantworten.
                    Melderin Teubner-Mayle: „das kann man erst im Termin … aber
                    dann muss man den Termin erst finden."
                    Bedingung bewusst eng: NUR eine offene Einladung
                    (``meetingRequest``) mit bekannter Termin-Id. Eine bereits
                    verschickte Zu-/Absage (meetingAccepted/-Declined) ist keine
                    offene Einladung und bekommt die Knöpfe nicht. */}
                {msg.meeting_message_type === "meetingRequest" &&
                  msg.event_id && (
                    <TerminAntwort
                      eventId={msg.event_id}
                      bereits={msg.event_response ?? null}
                    />
                  )}
                {!replyMode && !noCompose && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 sm:hidden">
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<Sparkles className="w-4 h-4" />}
                      onClick={() => {
                        setPendingDraft(null);
                        setReplyKind("reply");
                        setReplyMode("ai");
                      }}
                    >
                      {mt.antwortVorschlagen}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<ReplyAll className="w-4 h-4" />}
                      onClick={() => {
                        setPendingDraft(null);
                        setReplyKind("reply-all");
                        setReplyMode("blank");
                      }}
                    >
                      {mt.allenAntworten}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Forward className="w-4 h-4" />}
                      onClick={() => {
                        setPendingDraft(null);
                        setReplyKind("forward");
                        setReplyMode("blank");
                      }}
                    >
                      {mt.weiterleiten}
                    </Button>
                    <TemplatePickerButton
                      recordLinks={msg.enrichment.record_links}
                      onPicked={handleTemplatePick}
                      emailText={msg.body_text ?? ""}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil className="w-4 h-4" />}
                      onClick={() => {
                        setPendingDraft(null);
                        setReplyKind("reply");
                        setReplyMode("blank");
                      }}
                    >
                      {mt.freiAntworten}
                    </Button>
                    {/* Zero-Inbox (P0.3): Archiv + Verschieben + Löschen auch auf
                        Mobile im Detail — dieselben kompakten Icon-Buttons wie
                        Desktop (Parität; die Row-Hover-Aktionen sind auf Touch
                        nicht erreichbar). Ordner-Ansicht (P1.5): entfällt. */}
                    {!readOnly && (
                      <>
                        <button
                          type="button"
                          title={mt.archivieren}
                          aria-label={mt.archivieren}
                          onClick={() => onArchive(imid)}
                          className="inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-surface p-1.5 text-text-2 hover:text-accent-500 transition-colors"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                        {/* F-05 gehört hierher, nicht nur ins …-Menü: das Menü ist
                            `hidden sm:inline-flex` und auf Mobile gar nicht da.
                            Ohne diesen Knopf wäre Verschieben auf dem Telefon
                            unerreichbar — genau der Grund, aus dem Archiv und
                            Löschen hier schon stehen. */}
                        <button
                          type="button"
                          data-mail-move-mobile
                          title={mt.verschieben}
                          aria-label={mt.verschieben}
                          onClick={(e) => onMove(imid, e.currentTarget, msg?.from_address)}
                          className="inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-surface p-1.5 text-text-2 hover:text-accent-500 transition-colors"
                        >
                          <FolderInput className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          title={ct.loeschen}
                          aria-label={ct.loeschen}
                          onClick={() => onDelete(imid)}
                          className="inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-surface p-1.5 text-text-2 hover:text-danger transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-4">
                {/* D3: Zusammenfassung der (neuesten/geöffneten) Mail ÜBER dem
                    Verlauf. Fehlt still, wenn keine Summary da ist (kein leerer
                    Rahmen). Der SF-Thread-Verlauf lebt NUR noch rechts
                    (ContextCard).

                    Darunter GENAU EIN Zweig: der Body der geöffneten Mail.
                    ``MessageBody``/``RenderedBody`` zerlegt ihn in die
                    mitgeschickten Iterationen. Es gibt keine zweite Abfrage des
                    Postfachs mehr — die frühere Thread-Liste zeigte dieselben
                    Nachrichten ein zweites Mal neben der Zitat-Kette. */}
                <DetailSummary summary={msg.enrichment.summary} />
                <MessageBody msg={msg} mailbox={mailbox} />
              </div>
            </div>
          </div>

          {/* ---- ANHANG-Vorschau (F-07) --------------------------------------
              Steht NUR im DOM, wenn ein Chip gewählt ist. Genau das ist die
              Zusage des Auftraggebers: „nur erscheint wenn man auf das
              jeweilige PDF klickt, andernfalls bleibt das Pane 100% beim
              Text." Der Wechsel PDF→PDF ist EIN Klick auf einen anderen Chip —
              der ``key`` zwingt einen frischen Blob-Abruf. */}
          {previewAtt && (
            <AnhangVorschau
              key={previewAtt.attachment_id}
              att={previewAtt}
              imid={imid}
              mailbox={mailbox}
              onClose={() => setPreviewId(null)}
              onSeitenzahl={merkeSeitenzahl}
              onHerunterladen={anlageHerunterladen}
            />
          )}

          {/* ---- INTELLIGENCE-Panel (sekundär, seitlich, collapsible) --------
              Desktop: offen = breiter Slot rechts (INTEL_PANEL_W); zu = nur
              schmale Toggle-Rail, die Email-Spalte gewinnt die Breite zurück.
              Mobile: Slide-over-Drawer über der Email. */}
          <IntelPanel
            open={intelOpen}
            tab={intelTab}
            onTab={setIntelTab}
            onClose={toggleIntel}
            width={intelWidth}
            onResize={resizeIntel}
            msg={msg}
          />
        </div>
      )}

      {/* P2.16 — „Termin aus dieser Mail": der wiederverwendete Kalender-Wizard,
          prefilled (Betreff/Attendee/Fenster/contextNote), Teams default an.
          Legt über die bestehende Kette (/calendar/availability + /events,
          Organizer = Token-User) an — kein zweiter Kalender-Flow. */}
      {scheduleOpen && schedulePrefill && (
        <FindMeetingWizard
          colleagues={colleagues}
          initialSubject={schedulePrefill.initialSubject}
          initialAttendees={schedulePrefill.initialAttendees}
          initialOptionalAttendees={schedulePrefill.initialOptionalAttendees}
          initialWindow={schedulePrefill.initialWindow}
          onlineMeetingDefault
          contextNote={schedulePrefill.contextNote}
          onClose={() => setScheduleOpen(false)}
          onCreated={() => {}}
        />
      )}
    </div>
  );
}

// ---- Seitliches Intelligence-Panel (Kontext + Fragen) ----------------------

function IntelPanel({
  open,
  tab,
  onTab,
  onClose,
  width,
  onResize,
  msg,
}: {
  open: boolean;
  tab: "kontext" | "fragen";
  onTab: (t: "kontext" | "fragen") => void;
  onClose: () => void;
  width: number;
  onResize: (w: number) => void;
  msg: MessageResponse;
}) {
  const mt = useT().mail.kontext;
  // „Kontakt anlegen": das Tor entscheidet das Backend (Account-Treffer,
  // Freemail/intern, schon bekannt). Der Aufruf ist billig (kein LLM, kein
  // SF-Call) und faellt still aus — geht er schief, erscheint die Funktion
  // einfach nicht.
  const [contactCandidate, setContactCandidate] =
    useState<ContactCandidate | null>(null);
  const imid = msg.internet_message_id;
  const loadCandidate = useCallback(() => {
    let alive = true;
    setContactCandidate(null);
    api
      .get<ContactCandidate>(
        `/mail/contact-candidate?internet_message_id=${encodeURIComponent(imid)}`
      )
      .then((c) => {
        if (alive) setContactCandidate(c);
      })
      .catch(() => {
        if (alive) setContactCandidate(null);
      });
    return () => {
      alive = false;
    };
  }, [imid]);
  useEffect(() => loadCandidate(), [loadCandidate]);

  // Drag am linken Panel-Rand → Breite ändern (nach links ziehen = breiter).
  // Obergrenze = tatsächliche Row-Breite minus Mindest-Email-Lane (340), damit
  // das Panel nie über den Viewport hinausläuft (unabhängig von Sidebar/Inbox).
  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const handle = e.currentTarget as HTMLElement;
    const rowEl = handle.parentElement?.parentElement as HTMLElement | null;
    const rowW = rowEl ? rowEl.getBoundingClientRect().width : width + 340;
    const maxW = Math.max(INTEL_W_MIN, Math.round(rowW - 340));
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev: PointerEvent) =>
      onResize(Math.min(maxW, Math.max(INTEL_W_MIN, startW + (startX - ev.clientX))));
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };
  // Geschlossen (Desktop): Der Panel gibt seinen Content-Slot FREI — es bleibt
  // nur eine schmale Toggle-Rail (w-10) zum Wiederöffnen. Die Email-Spalte
  // (flex-1 min-w-0) dehnt sich in die freigewordene Breite nach rechts aus
  // (Reflow beim Toggle ist gewollt). Auf Mobile ist das Panel ein Overlay
  // und rendert geschlossen nichts.
  if (!open) {
    return (
      <aside className="hidden md:flex shrink-0 min-h-0 w-9 border-l border-border bg-surface">
        {/* E1: Seit der Default ZU ist, muss die Leiste SAGEN, was hinter ihr
            liegt — ein nacktes Symbol hätte das Panel für alle unsichtbar
            gemacht, die es nie offen gesehen haben. Deshalb die senkrechte
            Beschriftung („Kontext · Fragen") unter dem Symbol. Ein Klick
            irgendwo auf die Leiste öffnet das volle Panel. */}
        <button
          type="button"
          data-intel-rail
          onClick={onClose}
          className="flex w-full flex-col items-center gap-2 pt-3 text-text-2 transition-colors hover:bg-surface-2 hover:text-text"
          aria-label={mt.panelEinblenden}
          title={mt.panelEinblenden}
        >
          <PanelRight className="w-4 h-4 shrink-0" />
          <span
            className="text-[11px] font-medium tracking-wide whitespace-nowrap"
            style={{ writingMode: "vertical-rl" }}
          >
            {mt.reiterKontext} · {mt.reiterFragen}
          </span>
        </button>
      </aside>
    );
  }

  const inner = (
    <div className="h-full min-h-0 w-full min-w-0 flex flex-col bg-surface">
      {/* Panel-Kopf: Sub-Reiter Kontext / Fragen + Schließen. */}
      <div className="shrink-0 flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onTab("kontext")}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
              tab === "kontext"
                ? "bg-surface-2 text-text"
                : "text-text-2 hover:text-text"
            )}
          >
            <Link2 className="w-3.5 h-3.5" />
            {mt.reiterKontext}
          </button>
          <button
            type="button"
            onClick={() => onTab("fragen")}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
              tab === "fragen"
                ? "bg-surface-2 text-text"
                : "text-text-2 hover:text-text"
            )}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {mt.reiterFragen}
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 inline-flex items-center justify-center rounded-md p-1.5 text-text-3 hover:text-text hover:bg-surface-2 transition-colors"
          aria-label={mt.panelAusblenden}
          title={mt.panelAusblenden}
        >
          <PanelRightClose className="w-4 h-4" />
        </button>
      </div>

      {/* Panel-Körper: eigene Scroll-Region. Kontext = ContextCard +
          AccountPanel; Fragen = eingebettetes SfQueryTab (Logik unverändert).
          Beide Sektionen bleiben gemountet (display-toggle), damit der
          Fragen-Verlauf beim Reiter-Wechsel nicht verloren geht. */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div
          data-sf-panel-body
          className={clsx(
            "min-h-0 overflow-y-auto p-3 space-y-3",
            tab === "kontext" ? "flex-1" : "hidden"
          )}
        >
          {/* Zuerst die Kundendaten (Account, offene/laufende Bestellungen …),
              danach der Thread-Kontext. */}
          {(msg.enrichment.account_id || msg.enrichment.sender_contact) && (
            <AccountPanel
              accountId={msg.enrichment.account_id}
              accountName={msg.enrichment.account_name}
              senderContact={msg.enrichment.sender_contact ?? null}
            />
          )}
          {/* Steht bewusst AUSSERHALB von AccountPanel: der interessante Fall
              ist gerade der Absender OHNE Kontakt und ohne Thread-Account —
              dann rendert das Panel oben gar nicht.
              Welcher der drei Faelle (Kontakt / Lead / schon bekannt) sichtbar
              wird, entscheidet die Komponente selbst — sie kennt dafuer auch
              die Lead-Berechtigung. Hier nur noch: haben wir ueberhaupt eine
              Antwort des Tors? */}
          {contactCandidate && (
            <ContactCreate
              candidate={contactCandidate}
              internetMessageId={imid}
              onCreated={loadCandidate}
            />
          )}
          <ContextCard enr={msg.enrichment} />
        </div>
        <div
          className={clsx(
            "min-h-0 flex flex-col",
            tab === "fragen" ? "flex-1" : "hidden"
          )}
        >
          <SfQueryTab />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop: rechte Spalte, Breite vom User per Drag-Griff einstellbar
          (gemerkt je Viewport). Beim Zuklappen weicht der Slot einer schmalen
          Rail (s.o.) und die Email-Spalte dehnt sich nach rechts aus. */}
      <aside
        className="relative hidden md:flex min-w-[300px] min-h-0 border-l border-border"
        style={{ width, flexBasis: width, flexGrow: 0, flexShrink: 1 }}
      >
        <div
          onPointerDown={startResize}
          role="separator"
          aria-orientation="vertical"
          title={mt.breiteZiehen}
          className="absolute left-0 top-0 z-10 h-full w-1.5 -translate-x-1/2 cursor-col-resize hover:bg-accent-300/60 active:bg-accent-400/70 transition-colors"
        />
        {inner}
      </aside>

      {/* Mobile: Slide-over-Drawer über der Email (überdeckt sie, schiebt sie
          NICHT weg). */}
      <div className="md:hidden">
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={onClose}
          aria-hidden="true"
        />
        <aside className="fixed inset-y-0 right-0 z-50 w-[88%] max-w-[380px] border-l border-border shadow-xl">
          {inner}
        </aside>
      </div>
    </>
  );
}

// ---- W2: Vorlagen-Tab ------------------------------------------------------

function SourceBadge({ source }: { source: string }) {
  const mt = useT().mail.vorlagen;
  const isSeed = source === "seed";
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
        isSeed ? "bg-info-soft text-info" : "bg-surface-3 text-text-2"
      )}
    >
      {isSeed ? mt.seed : mt.manuell}
    </span>
  );
}

interface TemplateForm {
  category: string;
  sub_category: string;
  name: string;
  body_md: string;
  usage_notes: string;
}

const EMPTY_FORM: TemplateForm = {
  category: "",
  sub_category: "",
  name: "",
  body_md: "",
  usage_notes: "",
};

function TemplateModal({
  editing,
  categories,
  onClose,
  onSaved,
}: {
  editing: Template | null;
  categories: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const mt = useT().mail.vorlagen;
  const ct = useT().common;
  const [form, setForm] = useState<TemplateForm>(
    editing
      ? {
          category: editing.category,
          sub_category: editing.sub_category ?? "",
          name: editing.name,
          body_md: editing.body_md,
          usage_notes: editing.usage_notes ?? "",
        }
      : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof TemplateForm>(k: K, v: TemplateForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const valid =
    form.category.trim() !== "" &&
    form.name.trim() !== "" &&
    form.body_md.trim() !== "";

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    const payload = {
      category: form.category.trim(),
      sub_category: form.sub_category.trim() || null,
      name: form.name.trim(),
      body_md: form.body_md,
      usage_notes: form.usage_notes.trim() || null,
    };
    try {
      const v = katalog().mail.vorlagen;
      if (editing) {
        await api.put(`/mail/templates/${editing.id}`, payload);
        toast.ok(v.gespeichert);
      } else {
        await api.post("/mail/templates", payload);
        toast.ok(v.angelegt);
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.err(
        katalog().common.speichernFehlgeschlagen,
        e instanceof ApiError ? e.message : undefined
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      icon={<FileText className="w-4 h-4" />}
      title={editing ? mt.modalBearbeiten : mt.modalNeu}
      description={mt.modalBeschreibung}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {ct.abbrechen}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void save()}
            loading={saving}
            disabled={!valid}
          >
            {ct.speichern}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={mt.kategorie} hint={mt.kategorieHinweis}>
            <Input
              list="tpl-categories"
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              placeholder={mt.kategoriePlatzhalter}
            />
            <datalist id="tpl-categories">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
          <Field label={mt.subkategorie} optional>
            <Input
              value={form.sub_category}
              onChange={(e) => set("sub_category", e.target.value)}
              placeholder={mt.subkategoriePlatzhalter}
            />
          </Field>
        </div>
        <Field label={mt.name}>
          <Input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder={mt.namePlatzhalter}
          />
        </Field>
        <Field label={mt.text} hint={mt.textHinweis}>
          <Textarea
            value={form.body_md}
            onChange={(e) => set("body_md", e.target.value)}
            className="min-h-[220px] font-mono text-[13px]"
            placeholder={mt.textPlatzhalter}
          />
        </Field>
        <Field label={mt.notizen} optional hint={mt.notizenHinweis}>
          <Textarea
            value={form.usage_notes}
            onChange={(e) => set("usage_notes", e.target.value)}
            className="min-h-[64px]"
          />
        </Field>
      </div>
    </Modal>
  );
}

function TemplateCard({
  tpl,
  onEdit,
  onDeactivated,
}: {
  tpl: Template;
  onEdit: () => void;
  onDeactivated: () => void;
}) {
  const toast = useToast();
  const mt = useT().mail.vorlagen;
  const ct = useT().common;
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const deactivate = async () => {
    setBusy(true);
    try {
      await api.delete(`/mail/templates/${tpl.id}`);
      toast.ok(katalog().mail.vorlagen.deaktiviert);
      onDeactivated();
    } catch (e) {
      toast.err(
        katalog().mail.vorlagen.deaktivierenFehlgeschlagen,
        e instanceof ApiError ? e.message : undefined
      );
      setBusy(false);
    }
  };

  return (
    <Card padding="sm" className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-sm text-text truncate">{tpl.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {tpl.sub_category && <CtxBadge>{tpl.sub_category}</CtxBadge>}
            <span className="inline-flex items-center rounded-full bg-surface-3 text-text-3 px-2 py-0.5 text-[11px] font-medium uppercase">
              {tpl.language}
            </span>
            <SourceBadge source={tpl.source} />
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 rounded-md text-text-3 hover:text-text hover:bg-surface-3"
            aria-label={ct.bearbeiten}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="p-1.5 rounded-md text-text-3 hover:text-danger hover:bg-surface-3"
            aria-label={mt.deaktivieren}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <BodyPreview
        md={tpl.body_md}
        className="line-clamp-3 text-[13px] text-text-2"
      />
      {tpl.usage_notes && (
        <p className="text-xs text-text-3 italic line-clamp-2">{tpl.usage_notes}</p>
      )}

      {confirming && (
        <div className="mt-1 flex items-center justify-end gap-2 border-t border-border pt-2">
          <span className="text-xs text-text-2 mr-auto">{mt.wirklichDeaktivieren}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirming(false)}
            disabled={busy}
          >
            {ct.abbrechen}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => void deactivate()}
            loading={busy}
          >
            {mt.deaktivieren}
          </Button>
        </div>
      )}
    </Card>
  );
}

function TemplatesTab() {
  const mt = useT().mail.vorlagen;
  const ct = useT().common;
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [modal, setModal] = useState<{ editing: Template | null } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<Template[]>("/mail/templates");
      setTemplates(res);
    } catch (e) {
      setError(e as ApiError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = useMemo(() => {
    const map = new Map<string, Template[]>();
    for (const t of templates ?? []) {
      const list = map.get(t.category) ?? [];
      list.push(t);
      map.set(t.category, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [templates]);

  const categories = useMemo(
    () => Array.from(new Set((templates ?? []).map((t) => t.category))).sort(),
    [templates]
  );

  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <div className="max-w-5xl mx-auto px-6 py-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="text-sm text-text-2">{mt.reiterHinweis}</p>
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setModal({ editing: null })}
          >
            {mt.neu}
          </Button>
        </div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full rounded-xl" />
            ))}
          </div>
        )}

        {!loading && error && (
          <EmptyState
            icon={<ServerCrash className="w-6 h-6" />}
            title={mt.ladefehlerTitel}
            hint={mt.ladefehlerHinweis}
            action={
              <Button
                variant="secondary"
                icon={<RefreshCw className="w-4 h-4" />}
                onClick={() => void load()}
              >
                {ct.erneutVersuchen}
              </Button>
            }
          />
        )}

        {!loading && !error && grouped.length === 0 && (
          <EmptyState
            icon={<FileText className="w-6 h-6" />}
            title={mt.leerTitel}
            hint={mt.leerHinweis}
            action={
              <Button
                variant="primary"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setModal({ editing: null })}
              >
                {mt.leerAktion}
              </Button>
            }
          />
        )}

        {!loading && !error && grouped.length > 0 && (
          <div className="space-y-6">
            {grouped.map(([cat, list]) => (
              <section key={cat}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-text-3 mb-2">
                  {cat}
                  <span className="ml-1.5 text-text-3/70">({list.length})</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {list.map((t) => (
                    <TemplateCard
                      key={t.id}
                      tpl={t}
                      onEdit={() => setModal({ editing: t })}
                      onDeactivated={() => void load()}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <TemplateModal
          editing={modal.editing}
          categories={categories}
          onClose={() => setModal(null)}
          onSaved={() => void load()}
        />
      )}
    </div>
  );
}

// ---- W3: „Fragen"-Tab (NLP2SOQL Stufe 1) ----------------------------------

interface SfQueryResponse {
  success: boolean;
  answer_rows: Record<string, unknown>[];
  truncated: boolean;
  columns: string[];
  labels: Record<string, string> | string[] | null;
  types: Record<string, string> | string[] | null;
  record_count: number | null;
  soql: string | null;
  confidence: number | null;
  detected_object: string | null;
  abstain: boolean;
  abstain_reason: string | null;
  alternative: string | null;
  error: string | null;
}

interface QaPair {
  id: number;
  question: string;
  loading: boolean;
  res?: SfQueryResponse;
  error?: ApiError;
}

const nfEuroCell = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 2,
});

function colLabel(
  labels: SfQueryResponse["labels"],
  key: string,
  idx: number
): string {
  if (Array.isArray(labels)) return labels[idx] ?? key;
  if (labels && typeof labels === "object") return labels[key] ?? key;
  // v4 liefert keine Labels → SOQL-Aggregat-Aliase (expr0, expr1 …) lesbar machen.
  const m = key.match(/^expr(\d+)$/);
  const f = katalog().mail.fragen;
  if (m) return m[1] === "0" ? f.wert : f.wertN(Number(m[1]) + 1);
  return key;
}
function colType(
  types: SfQueryResponse["types"],
  key: string,
  idx: number
): string | null {
  if (Array.isArray(types)) return types[idx] ?? null;
  if (types && typeof types === "object") return types[key] ?? null;
  return null;
}
function fmtQueryCell(value: unknown, type: string | null): string {
  if (value == null || value === "") return "—";
  const t = (type ?? "").toUpperCase();
  if (t === "CURRENCY") {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : nfEuroCell.format(n);
  }
  if (t === "DATE" || t === "DATETIME") {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime())
      ? String(value)
      : d.toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function AnswerTable({ res }: { res: SfQueryResponse }) {
  const mt = useT().mail.fragen;
  const columns = res.columns.length
    ? res.columns
    : res.answer_rows[0]
      ? Object.keys(res.answer_rows[0])
      : [];
  if (res.answer_rows.length === 0) {
    return (
      <div className="text-sm text-text-2 rounded-md bg-surface-2 border border-border px-3 py-2">
        {mt.keineTreffer}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-surface-2">
            {columns.map((c, i) => (
              <th
                key={c}
                className="text-left font-medium text-text-2 px-3 py-2 border-b border-border whitespace-nowrap"
              >
                {colLabel(res.labels, c, i)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {res.answer_rows.map((row, ri) => (
            <tr key={ri} className="odd:bg-surface even:bg-surface-2/40">
              {columns.map((c, ci) => (
                <td
                  key={c}
                  className="px-3 py-1.5 border-b border-border text-text tabular-nums whitespace-nowrap"
                >
                  {fmtQueryCell(row[c], colType(res.types, c, ci))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QueryDetails({ res }: { res: SfQueryResponse }) {
  const toast = useToast();
  const mt = useT().mail.fragen;
  const [open, setOpen] = useState(false);
  if (!res.soql && !res.detected_object && res.confidence == null) return null;
  const copy = async () => {
    if (!res.soql) return;
    try {
      await navigator.clipboard.writeText(res.soql);
      toast.ok(katalog().mail.fragen.soqlKopiert);
    } catch {
      toast.err(katalog().mail.fragen.soqlKopierenFehler);
    }
  };
  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs text-text-3 hover:text-text"
      >
        <ChevronDown className={clsx("w-3.5 h-3.5 transition-transform", open && "rotate-180")} />
        {mt.details}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {res.detected_object && (
              <span className="inline-flex items-center rounded-full bg-surface-3 text-text-2 px-2 py-0.5 text-[11px] font-medium">
                {mt.objekt(res.detected_object)}
              </span>
            )}
            {res.confidence != null && (
              <span className="inline-flex items-center rounded-full bg-info-soft text-info px-2 py-0.5 text-[11px] font-medium">
                {mt.konfidenz(Math.round(res.confidence * 100))}
              </span>
            )}
          </div>
          {res.soql && (
            <div className="relative">
              <pre className="text-[12px] font-mono text-text-2 bg-surface-2 border border-border rounded-md px-3 py-2 pr-9 overflow-x-auto whitespace-pre-wrap break-words">
                {res.soql}
              </pre>
              <button
                type="button"
                onClick={() => void copy()}
                aria-label={mt.soqlKopierenAria}
                title={mt.kopieren}
                className="absolute top-1.5 right-1.5 p-1.5 rounded-md text-text-3 hover:text-text hover:bg-surface-3"
              >
                <ClipboardCopy className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// v4-`abstainReason` (Format "code:Begründung") → freundliche Klartext-Meldung.
function abstainMessage(reason: string | null): string {
  const raw = reason ?? "";
  const idx = raw.indexOf(":");
  const code = idx >= 0 ? raw.slice(0, idx) : raw;
  const detail = idx >= 0 ? raw.slice(idx + 1).trim() : "";
  const mt = katalog().mail.fragen;
  switch (code) {
    case "llm_unanswerable":
      // Die Engine liefert hier eine natürlichsprachliche Begründung → direkt zeigen.
      return detail || mt.abstainUnanswerable;
    case "no_binding":
    case "no_candidates":
      return mt.abstainKeinObjekt;
    case "validation_failed":
      return mt.abstainUngueltig;
    case "llm_timeout":
      return mt.abstainTimeout;
    default:
      return mt.abstainSonst;
  }
}

function AnswerCard({ pair, onAsk }: { pair: QaPair; onAsk: (q: string) => void }) {
  const mt = useT().mail.fragen;
  if (pair.loading) {
    return (
      <div className="rounded-lg border border-border bg-surface p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm text-text-2">
          <Loader2 className="w-4 h-4 animate-spin text-accent-500" />
          {mt.denktNach}
        </div>
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }
  // HTTP-Fehler (Timeout/502).
  if (pair.error) {
    const status = pair.error.status;
    return (
      <div className="rounded-lg border border-danger/40 bg-danger-soft/40 p-3">
        <div className="flex items-start gap-2 text-sm text-text">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-danger" />
          <div>
            <div className="font-medium">
              {status === 504
                ? mt.zeitueberschreitung
                : status === 502
                  ? mt.engineWeg
                  : mt.nichtBeantwortbar}
            </div>
            <div className="text-xs text-text-2 mt-0.5">{mt.konkreter}</div>
          </div>
        </div>
      </div>
    );
  }
  const res = pair.res!;
  // v4-Decline: die Engine antwortet lieber ehrlich nichts als etwas Falsches.
  if (res.abstain) {
    return (
      <div className="rounded-lg border border-border bg-surface-2 p-3">
        <div className="flex items-start gap-2 text-sm text-text">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-text-3" />
          <div className="space-y-2">
            <div>{abstainMessage(res.abstain_reason)}</div>
            {res.alternative && (
              <button
                type="button"
                onClick={() => onAsk(res.alternative!)}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-xs text-text-2 hover:border-accent-300 hover:text-text transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5 text-accent-500" />
                {mt.stattdessenFragen(res.alternative)}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
  // Harter Engine-Fehler (kein abstain).
  if (!res.success || res.error) {
    return (
      <div className="rounded-lg border border-danger/40 bg-danger-soft/40 p-3">
        <div className="flex items-start gap-2 text-sm text-text">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-danger" />
          <div>
            <div className="font-medium">{mt.nichtBeantwortbar}</div>
            <div className="text-xs text-text-2 mt-0.5">
              {res.error ? res.error : mt.konkreter}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-surface p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs text-text-3">
        <span className="tabular-nums">
          {mt.treffer(res.record_count ?? res.answer_rows.length)}
        </span>
        {res.truncated && (
          <span className="inline-flex items-center rounded-full bg-warning-soft text-warning px-2 py-0.5 text-[11px] font-medium">
            {mt.nurErste50}
          </span>
        )}
      </div>
      <AnswerTable res={res} />
      <QueryDetails res={res} />
    </div>
  );
}

function SfQueryTab() {
  const mt = useT().mail.fragen;
  const [pairs, setPairs] = useState<QaPair[]>([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [pairs]);

  const ask = useCallback(async (question: string) => {
    const q = question.trim();
    if (q.length < 3) return;
    const id = Date.now() + Math.random();
    setPairs((prev) => [...prev, { id, question: q, loading: true }]);
    setInput("");
    try {
      const res = await api.post<SfQueryResponse>("/mail/sf-query", { question: q });
      setPairs((prev) =>
        prev.map((p) => (p.id === id ? { ...p, loading: false, res } : p))
      );
    } catch (e) {
      setPairs((prev) =>
        prev.map((p) => (p.id === id ? { ...p, loading: false, error: e as ApiError } : p))
      );
      // Frage bleibt im Input, damit der User sie anpassen kann.
      setInput(q);
    }
  }, []);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="shrink-0 px-6 pt-3">
        <div className="max-w-3xl mx-auto flex items-start gap-2 rounded-md bg-info-soft/50 border border-info/30 px-3 py-2 text-xs text-text-2">
          <FlaskConical className="w-3.5 h-3.5 mt-0.5 shrink-0 text-info" />
          <span>
            <span className="font-medium">{mt.beta}</span>
            {mt.betaHinweis}
          </span>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {pairs.length === 0 ? (
            <div className="pt-6">
              <EmptyState
                icon={<Table2 className="w-6 h-6" />}
                title={mt.leerTitel}
                hint={mt.leerHinweis}
              />
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {mt.beispiele.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => void ask(ex)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-xs text-text-2 hover:border-accent-300 hover:text-text transition-colors"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            pairs.map((p) => (
              <div key={p.id} className="space-y-2">
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-accent-500 text-white px-3.5 py-2 text-sm">
                    {p.question}
                  </div>
                </div>
                <AnswerCard pair={p} onAsk={ask} />
              </div>
            ))
          )}
        </div>
      </div>

      <div className="shrink-0 border-t border-border bg-canvas px-6 py-3">
        <form
          className="max-w-3xl mx-auto flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(input);
          }}
        >
          <div className="relative flex-1">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={mt.platzhalter}
              className="pr-9"
            />
            <CornerDownLeft className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-3 pointer-events-none" />
          </div>
          <Button
            type="submit"
            variant="primary"
            size="md"
            icon={<Send className="w-4 h-4" />}
            disabled={input.trim().length < 3}
          >
            {mt.fragen}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ---- Nachverfolgung (Follow-up / Chasing) ---------------------------------
// UI-Schicht des Kontrakts (app/routers/follow_ups.py): Marking, Tab, Config,
// Fahrplan-Zeile, Scharfschalt-Gate. Der Auto-Versand (Reminder/Holding/OOO/
// Eskalation) läuft nach dem Scharfschalten im Worker (app/mail/follow_up_engine.py).

// "needs_decision" = der Auffangkorb: eine Antwort kam an, die weder Abwesenheit
// noch "kuemmere mich drum" noch eine Terminzusage war. Der Track ist angehalten
// und wartet auf eine Entscheidung des Nutzers (POST .../decide).
type FollowUpState =
  | "draft"
  | "armed"
  | "paused"
  | "answered"
  | "stopped"
  | "needs_decision";
type FollowUpResponse = "none" | "holding" | "real" | "ooo";
// Die vier Auswege aus dem Auffangkorb (POST .../decide). "done"/"continue"
// beantworten eine eingegangene Antwort, "escalate"/"no_escalate" den
// vorgeschlagenen Eskalations-Termin (F-21). Eigene Werte und keine Umdeutung:
// "continue" verankert den Fahrplan neu, "escalate" legt einen Termin an und
// verschickt eine Mail.
type FollowUpDecisionKind = "done" | "continue" | "escalate" | "no_escalate";

interface FollowUpEscalation {
  kind: "meeting";
  bitop_attendees: string[];
  partner_attendees: string[];
  // Aus der Meeting-Einladung einzeln weggeklickte Adressen (nur Meeting, nicht Mails).
  attendee_exclude: string[];
  note: string | null;
}
interface FollowUpConfig {
  interval_days: number;
  escalate_after_iteration: number;
  // Erkannte Sprache der Ursprungsmail ("de"/"en") — nur Anzeige, nicht wählbar.
  language: string | null;
  to: string[];
  cc: string[];
  // Reply-all-Umfang: true = alle Schritte an den ganzen Verteiler (Default).
  reminders_reply_all: boolean;
  escalation: FollowUpEscalation;
}
interface FollowUpStep {
  id: string;
  seq: number;
  kind: "reminder" | "escalation";
  due_date: string; // YYYY-MM-DD
  status: "scheduled" | "sent" | "skipped";
  sent_at: string | null;
  message_ref: string | null;
  // WARUM die Stufe uebersprungen wurde ("holding_reply" = eine verschickte
  // Bestaetigung hat sie ueberholt). Ein durchgestrichenes Symbol ohne Grund
  // war genau das, woraus der Tester am 19.08. „Intervall kaputt" gelesen hat.
  skipped_reason: string | null;
}
interface FollowUpTrack {
  id: string;
  owner_email: string;
  created_at: string;
  updated_at: string;
  internet_message_id: string;
  thread_id: string | null;
  thread_key: string | null;
  subject: string | null;
  account_id: string | null;
  account_name: string | null;
  counterpart_email: string | null;
  counterpart_name: string | null;
  // Gegenseite ist bitop-Kolleg:in (Server leitet es aus counterpart_email ab).
  // Solche Tracks werden NICHT automatisch scharfgeschaltet — der Nutzer bestätigt.
  internal_counterpart: boolean;
  // Im Verteiler stehen NUR bitop-Adressen → die Mahn-Mails sind deutsch
  // (Hausprache). Server leitet es aus config.to/cc ab; nicht hier nachbilden.
  house_language: boolean;
  received_at: string | null;
  body_preview: string | null;
  topic: string | null;
  state: FollowUpState;
  enabled: boolean;
  response_status: FollowUpResponse;
  anchor_date: string | null;
  armed_at: string | null;
  last_eval_at: string | null;
  // Warum der Track eine Entscheidung braucht (Verdikt aus dem letzten
  // decision_needed-Event). Nur bei state === "needs_decision" gesetzt.
  decision_trigger: string | null;
  // Die AUSLOESENDE Antwort — NICHT `internet_message_id`. Letzteres ist die
  // Mail, mit der der Vorgang anfing; gefragt wird wegen der Antwort, die
  // gerade eingegangen ist (am Vorfall-Track liegen fuenf Wochen dazwischen).
  // Beide Mails existieren im Postfach: ein Link auf die falsche oeffnet brav
  // eine Mail und faellt beim Ausprobieren nicht auf.
  decision_message_id: string | null;
  decision_from: string | null;
  decision_at: string | null;
  // Die GESPEICHERTE Einstufungs-Begruendung des Klassifikators (aus dem
  // incoming_eval-Ereignis) — sie wird nicht neu erzeugt.
  decision_reason: string | null;
  // Der VORGESCHLAGENE Eskalations-Termin (F-21) — nur gesetzt, wenn
  // decision_trigger === "escalation_meeting". Ohne Datum/Uhrzeit UND
  // Teilnehmerliste ist "soll ich eskalieren?" keine beantwortbare Frage; beides
  // steht deshalb im Kontrakt und nicht nur im Audit-Log.
  //
  // Die Slots sind naive Berliner Wanduhr-Zeiten ("2026-08-06T10:00:00") — genau
  // so, wie sie beim Anlegen an Graph gehen. NICHT in eine Zone umrechnen: die
  // angezeigte Uhrzeit muss die sein, die im Kalender landet.
  decision_slot_start: string | null;
  decision_slot_end: string | null;
  decision_attendees: string[];
  // ── Was die Engine ZULETZT SELBST getan hat (Transparenz-Paket 20.08.2026) ──
  // Alle vier leitet der Server aus dem Audit-Log ab und raeumt sie mit jeder
  // neuen Runde (verschickter Reminder / Eskalation / neu scharfgeschaltet) auf.
  // Sie sind der ganze Zweck dieses Pakets: die Engine hat am 19.08. alles
  // richtig gemacht und nichts davon gezeigt.
  //
  // `promise_date` = das zugesagte Datum, auf das der Fahrplan eingerastet ist;
  // `promise_at` = wann die Antwort einging, die es genannt hat (die QUELLE).
  promise_date: string | null; // YYYY-MM-DD
  promise_at: string | null;
  // Seit wann der Vorgang abwartet — die Frage, die ein nackter Chip offenlaesst.
  holding_since: string | null;
  // Wann Atlas zuletzt IM NAMEN des Nutzers eine Bestaetigung verschickt hat.
  auto_reply_at: string | null;
  config: Partial<FollowUpConfig>;
  steps: FollowUpStep[];
}

const FU_INTERVAL_DEFAULT = 7;
const FU_ESCALATE_DEFAULT = 3;

/** Zähler neben dem Nachverfolgungs-Tab und über der Entscheidungsliste.
 *
 *  Bewusst NICHT `UnreadPill` (ui/mailUnread): deren `aria-label` ist fest auf
 *  „… ungelesen" verdrahtet, und ein Screenreader läse hier dann etwas Falsches
 *  vor. Die Optik ist absichtlich identisch — nur die Farbe folgt der Warnung
 *  statt dem Akzent, und die Beschriftung sagt, worum es geht. */
function DecisionCountPill({
  count,
  label,
}: {
  count: number;
  /** Vorlesetext. Default ist „… brauchen eine Entscheidung" — am Reiter zaehlt
   *  die Pille seit dem 20.08.2026 aber auch die ABWARTENDEN Vorgaenge mit, und
   *  fuer die waere das schlicht falsch. */
  label?: (n: number) => string;
}) {
  const mt = useT().mail.nachfass;
  if (!count || count <= 0) return null;
  return (
    <span
      data-fu-decision-count
      aria-label={(label ?? mt.entscheidungZaehler)(count)}
      className={clsx(
        "inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1",
        "rounded-full bg-warning text-white text-[10px] font-semibold leading-none tabular-nums"
      )}
    >
      {count > 9 ? "9+" : count}
    </span>
  );
}

/** Klartext für `decision_trigger` — warum liegt der Vorgang hier?
 *
 *  Ein blosses „real_answer" im Interface wäre der Rohwert der Engine; der
 *  Nutzer soll den GRUND lesen, nicht das Verdikt. */
function fuDecisionReason(trigger: string | null | undefined): string {
  const mt = katalog().mail.nachfass;
  switch (String(trigger ?? "")) {
    case "real_answer":
      return mt.grundEchteAntwort;
    case "unrelated":
      return mt.grundUnzuordenbar;
    case "date_promise_unparsed":
      return mt.grundDatumUnklar;
    case "escalation_meeting":
      return mt.grundEskalation;
    default:
      return mt.grundSonst;
  }
}

/** Der vorgeschlagene Eskalations-Termin als lesbare Zeile.
 *
 *  Die Werte sind naive Berliner Wanduhr-Zeiten. `new Date("…T10:00:00")` liest
 *  sie als LOKALE Zeit — also genau die Ziffern, die auch in der Einladung
 *  stehen werden. Eine Umrechnung waere hier ein Fehler und keine Sorgfalt: der
 *  Nutzer soll die Uhrzeit sehen, der er zustimmt. */
function fuSlotLabel(start: string | null, end: string | null): string | null {
  const s = fuParseDate(start);
  if (!s) return null;
  const tag = s.toLocaleDateString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const von = s.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const e = fuParseDate(end);
  const bis = e
    ? e.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : null;
  return `${tag}, ${von}${bis ? `–${bis}` : ""} Uhr`;
}

/** Sprach-Badge-Label aus dem config.language ("de"/"en") — nur Anzeige. */
function fuLangLabel(lang: string | null | undefined): string | null {
  const l = String(lang ?? "").toLowerCase();
  if (l === "de") return "DE";
  if (l === "en") return "EN";
  return null;
}

// Nur die Badge-FARBE — die Beschriftung steht im Katalog
// (`mail.nachfass.zustand` / `.antwort`), s. docs/i18n.md.
const FU_STATE_BADGE: Record<FollowUpState, BadgeStatus> = {
  draft: "neutral",
  armed: "success",
  paused: "warning",
  answered: "info",
  stopped: "neutral",
  needs_decision: "warning",
};
const FU_RESPONSE_BADGE: Record<FollowUpResponse, BadgeStatus> = {
  none: "neutral",
  holding: "warning",
  real: "success",
  ooo: "info",
};

/** ISO-Datum (YYYY-MM-DD oder Datetime) → lokales Date ohne TZ-Verschiebung
 *  für reine Datumsstrings. */
function fuParseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}
function fuAddDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}
/** Sa/So → folgender Montag. Spiegel von
 *  `app/mail/follow_up_schedule.py::roll_to_workday` — gilt AUSSCHLIESSLICH fuer
 *  die Eskalations-Stufe. Die Reminder bleiben liegen, wo sie liegen: das
 *  Sendefenster verzoegert ihren Abflug, es schreibt ihr Fahrplan-Datum nicht um
 *  (eine Holding-Zusage hat es dem Kunden bereits genannt). */
function fuRollToWorkday(d: Date): Date {
  const out = new Date(d);
  while (out.getDay() === 0 || out.getDay() === 6) out.setDate(out.getDate() + 1);
  return out;
}
/** Client-Spiegel von app/mail/follow_up_schedule.py::compute_steps — identische
 *  Formel, damit die Live-Vorschau exakt dem Backend-Fahrplan entspricht. */
function fuComputeSteps(
  anchor: Date,
  intervalDays: number,
  escalateAfter: number
): { seq: number; kind: "reminder" | "escalation"; due: Date }[] {
  const interval = Math.max(1, Math.floor(intervalDays || 0));
  const n = Math.max(1, Math.floor(escalateAfter || 0));
  const out: { seq: number; kind: "reminder" | "escalation"; due: Date }[] = [];
  for (let i = 1; i <= n; i++) {
    out.push({ seq: i, kind: "reminder", due: fuAddDays(anchor, i * interval) });
  }
  out.push({
    seq: n + 1,
    kind: "escalation",
    due: fuRollToWorkday(fuAddDays(anchor, (n + 1) * interval)),
  });
  return out;
}

/** Was die Engine an diesem Vorgang selbst getan hat — als Saetze.
 *
 *  Der Befund vom 19.08.2026 in einer Zeile: die Engine ist auf eine
 *  Datums-Zusage eingerastet, hat in Vertretung eine Bestaetigung verschickt und
 *  einen faelligen Reminder uebersprungen — und NICHTS davon stand irgendwo. Der
 *  Tester diagnostizierte ein kaputtes Intervall und verlor das Vertrauen in die
 *  Automatik.
 *
 *  Bewusst eine LISTE und kein fertiger Absatz: dieselben Saetze stehen in der
 *  kompakten Zeile und im Anpassen-Dialog, und beide setzen sie anders. Leer =
 *  die Engine hat nichts Eigenmaechtiges getan; dann steht auch nichts da (kein
 *  „keine Auffaelligkeiten" — das waere neue Flaeche ohne neuen Inhalt). */
function fuEngineNotes(track: FollowUpTrack): string[] {
  const mt = katalog().mail.nachfass;
  const out: string[] = [];
  const zusage = fuParseDate(track.promise_date);
  if (zusage) {
    const quelle = berlinDayMonth(track.promise_at);
    out.push(
      quelle
        ? mt.eingerastet(fmtDayMonth(zusage), quelle)
        : mt.eingerastetOhneQuelle(fmtDayMonth(zusage))
    );
  }
  const bestaetigt = berlinDayMonth(track.auto_reply_at);
  const uebersprungen = (track.steps ?? []).filter(
    (s) => s.status === "skipped" && s.skipped_reason === "holding_reply"
  );
  // Bestaetigung und ausgefallene Nachfrage sind EIN Vorgang und stehen deshalb
  // in EINEM Satz. Zwei Saetze waeren nicht mehr Information, sondern eine
  // Zeile mehr — und die Zeile ist der Ort, an dem der Melder „cluttered"
  // gesagt hat. Getrennt stehen sie nur, wenn wirklich nur eines von beiden
  // bekannt ist (Altbestand ohne Grund im Log).
  if (bestaetigt && uebersprungen.length > 0) {
    out.push(mt.bestaetigungStattNachfrage(bestaetigt));
  } else if (bestaetigt) {
    out.push(mt.bestaetigungVerschickt(bestaetigt));
  } else {
    // Ohne bekannten Bestaetigungs-Tag wird die Nachfrage mit IHREM
    // Faelligkeitsdatum benannt — gemeint ist die, die ausgefallen ist.
    for (const s of uebersprungen) {
      out.push(mt.nachfrageUebersprungen(fmtDayMonth(fuParseDate(s.due_date))));
    }
  }
  return out;
}

/** Beschriftung des Antwort-Chips — „abwartend" bekommt sein SEIT.
 *
 *  Ein nackter Chip beantwortet die Frage nicht, die man tatsaechlich hat. Ohne
 *  bekannten Zeitpunkt bleibt es beim bisherigen Wort (Altbestand, und die Walks
 *  selektieren darauf). */
function fuAntwortLabel(track: FollowUpTrack): string {
  const mt = katalog().mail.nachfass;
  const wort = mt.antwort[track.response_status];
  if (track.response_status !== "holding") return wort;
  const seit = berlinDayMonth(track.holding_since);
  return seit ? mt.abwartendSeit(seit) : wort;
}
function fuConfigComplete(cfg: Partial<FollowUpConfig>): boolean {
  // Kein Text-Fundament mehr nötig — der Reminder-Ton ist fest (freundlich→
  // schärfer→deutlich). Es braucht nur Intervall, Anzahl Nachfragen und Empfänger.
  const interval = Number(cfg.interval_days ?? 0);
  const escalate = Number(cfg.escalate_after_iteration ?? 0);
  const to = (cfg.to ?? []).filter((t) => String(t).trim());
  return interval >= 1 && escalate >= 1 && to.length > 0;
}
function fuStartOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Feld-Beschriftung zu einem FastAPI-`loc`-Pfad — damit die Meldung sagt, WO
 *  die beanstandete Adresse steht. `loc` sieht so aus:
 *  `["body","config","to",1]` bzw. `["body","config","escalation","partner_attendees",0]`. */
/** Server-Antwort → EIN Satz, der die beanstandete Adresse nennt.
 *
 *  Ohne das läuft ein 422 ins Leere: `api.ts::request` baut die Meldung aus
 *  `body.detail`, und bei einem Validierungsfehler ist das eine LISTE von
 *  Objekten — `String([{…}])` ergibt „[object Object]". Der Nutzer sähe also
 *  „Speichern fehlgeschlagen — [object Object]" und müsste seine Chips einzeln
 *  durchprobieren. Die Adresse steht in `input`, der Klartext in `msg`
 *  (Pydantic stellt „Value error, " voran), das Feld in `loc`. */
function fuSaveFehlerText(err: ApiError): string {
  const mt = katalog().mail.nachfass;
  const feldLabel = mt.feldLabel as Record<string, string | undefined>;
  const detail = (err.body as { detail?: unknown } | undefined)?.detail;
  if (Array.isArray(detail)) {
    const zeilen = detail
      .map((d) => {
        const e = d as { loc?: unknown[]; msg?: string; input?: unknown };
        const feld = (e.loc ?? [])
          .map((p) => feldLabel[String(p)])
          .filter(Boolean)
          .pop();
        const grund =
          (e.msg ?? "").replace(/^Value error,\s*/, "") ||
          (e.input != null ? mt.feldAbgelehnt(String(e.input)) : "");
        if (!grund) return null;
        return feld ? `${feld}: ${grund}` : grund;
      })
      .filter(Boolean) as string[];
    if (zeilen.length) return zeilen.join(" · ");
  }
  if (typeof detail === "string" && detail) return detail;
  return err.message || mt.unbekannterFehler;
}

/** Externe Salesforce-Kontakte zu einer Eingabe — debounced, mit Frische-Schutz.
 *
 *  Bewusst NICHT die Komponente `ParticipantPicker` (CalendarMs.tsx:3013)
 *  wiederverwendet, sondern deren Such-Bausteine (`ui/participants.ts`,
 *  `ui/recipientRank.ts`). Der Picker trägt drei Zusagen, die im Nachfass-Dialog
 *  FALSCH wären:
 *    • Er kennt keinen Freitext: `Enter` übernimmt ausschließlich einen Treffer
 *      (CalendarMs.tsx:3226-3233). Hier muss jede zustellbare Adresse eintragbar
 *      bleiben — auch eine, die weder in `users` noch in Salesforce steht.
 *    • Ohne Treffer sagt er „ohne Kontakt-Datensatz weist der Server die Adresse
 *      ab" (CalendarMs.tsx:3478-3484). Für eine Nachfass-Mail ist das
 *      nachweislich unwahr: `app/schemas/follow_up.py` prüft nur auf eine
 *      plausible Adresse.
 *    • Er sperrt die EIGENE Adresse hart (der „Riegel" in `take`,
 *      CalendarMs.tsx:3201-3213) und hängt an die Chips die Free/Busy-Warnung
 *      („Outlook sieht keine Kalender außerhalb des Hauses"). Beides gehört zur
 *      Einladbarkeits-Regel des Termins; im CC einer Nachfass-Mail darf man sehr
 *      wohl selbst stehen.
 *  Diese drei Punkte als Props abschaltbar zu machen hieße, in EINE Komponente
 *  zwei gegenläufige Doktrinen zu legen — und dabei genau die Invarianten
 *  anzufassen, an denen die drei Kalender-Einstiege hängen (attendee-walk H1,
 *  M4/M5). Der Termin-Wizard bleibt deshalb unberührt.
 *
 *  Der Frische-Schutz ist von dort übernommen und trägt dieselbe Last: `q` und
 *  `list` werden IMMER gemeinsam gesetzt, und der Verwendungsort prüft
 *  `res.q === needle`. Sonst läuft im Antwort-Fenster eines NEUEN Begriffs die
 *  Liste des alten weiter mit. */
function useContactHits(needle: string): {
  list: Colleague[];
  state: "idle" | "loading" | "error";
} {
  const [res, setRes] = useState<{
    q: string;
    list: Colleague[];
    state: "idle" | "loading" | "error";
  }>({ q: "", list: [], state: "idle" });
  const seq = useRef(0);

  useEffect(() => {
    if (needle.length < MIN_CONTACT_QUERY) {
      setRes({ q: "", list: [], state: "idle" });
      return;
    }
    const mine = ++seq.current;
    setRes({ q: needle, list: [], state: "loading" });
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const hits = await api.get<ContactSuggestion[]>(
            `/calendar/contacts?q=${encodeURIComponent(needle)}`
          );
          if (seq.current !== mine) return;
          setRes({ q: needle, list: hits.map(toParticipant), state: "idle" });
        } catch {
          // Salesforce still weg = Kollegen findet man weiterhin, Freitext auch.
          if (seq.current !== mine) return;
          setRes({ q: needle, list: [], state: "error" });
        }
      })();
    }, CONTACT_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [needle]);

  const fresh = res.q === needle;
  return { list: fresh ? res.list : [], state: fresh ? res.state : "loading" };
}

// ---- Chips-Input (to/cc, Teilnehmer) ---------------------------------------
//
// Tippen → kurze Trefferliste (Kollegen + externe Salesforce-Kontakte) → Klick
// oder Enter übernimmt. Melder Bentler: „Aktuell muss jede einzelne Email
// komplett fehlerfrei ausgeschrieben werden."
//
// Was das Eintragen angeht, ändert sich NICHTS: Enter/Komma/Blur trennen weiter
// am Komma und legen Chips an. Die Trefferliste kommt oben drauf, sie ersetzt
// den Freitext nicht — der Server (`app/schemas/follow_up.py`) bleibt die
// Instanz, die eine Adresse annimmt oder mit Klartext ablehnt.
function ChipsInput({
  value,
  onChange,
  placeholder,
  colleagues = [],
  field,
  label,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Vorschlag-Quelle „intern". Leer = nur Freitext + Salesforce (degradiert). */
  colleagues?: Colleague[];
  /** Haken für den Walk + `aria-label`-Stamm; identifiziert das Feld eindeutig. */
  field: string;
  label: string;
}) {
  const mt = useT().mail.nachfass;
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  // Hat der Nutzer die Auswahl selbst bewegt? Dann gewinnt der markierte Treffer
  // auch dann, wenn die Eingabe schon wie eine vollständige Adresse aussieht.
  const [arrowed, setArrowed] = useState(false);
  const needle = draft.trim();
  const contacts = useContactHits(needle);

  const chosen = useMemo(
    () => new Set(value.map((v) => v.trim().toLowerCase())),
    [value]
  );
  const hits = useMemo(() => {
    if (needle.length < MIN_CONTACT_QUERY) return [];
    const internal = rankColleagues(colleagues, needle, MAX_HITS).filter(
      (c) => !chosen.has(c.email.toLowerCase())
    );
    const internalEmails = new Set(colleagues.map((c) => c.email.toLowerCase()));
    const external = contacts.list.filter(
      (c) =>
        !chosen.has(c.email.toLowerCase()) &&
        !internalEmails.has(c.email.toLowerCase())
    );
    return mergeHits(internal, external);
  }, [chosen, colleagues, contacts.list, needle]);

  useEffect(() => {
    setActive(0);
    setArrowed(false);
  }, [needle, hits.length]);
  const activeIdx = Math.min(active, Math.max(0, hits.length - 1));
  const open = focused && hits.length > 0;

  const add = (raw: string) => {
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const merged = [...value];
    for (const p of parts) if (!merged.includes(p)) merged.push(p);
    onChange(merged);
  };
  const take = (c: Colleague) => {
    add(c.email);
    setDraft("");
    setActive(0);
  };

  return (
    <div className="relative" data-chips-field={field}>
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1.5 focus-within:border-accent-500 focus-within:ring-1 focus-within:ring-accent-500">
        {value.map((v) => (
          <span
            key={v}
            data-chips-chip
            className="inline-flex items-center gap-1 rounded bg-surface-3 px-1.5 py-0.5 text-xs text-text max-w-full"
          >
            <span className="truncate">{v}</span>
            <button
              type="button"
              aria-label={mt.chipEntfernen(v)}
              onClick={() => onChange(value.filter((x) => x !== v))}
              className="text-text-3 hover:text-danger shrink-0"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          data-chips-input={field}
          value={draft}
          role="combobox"
          aria-expanded={open}
          aria-controls={`chips-hits-${field}`}
          aria-autocomplete="list"
          aria-label={mt.chipAria(label)}
          autoComplete="off"
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" && hits.length) {
              e.preventDefault();
              setArrowed(true);
              setActive((a) => Math.min(a + 1, hits.length - 1));
              return;
            }
            if (e.key === "ArrowUp" && hits.length) {
              e.preventDefault();
              setArrowed(true);
              setActive((a) => Math.max(a - 1, 0));
              return;
            }
            if (e.key === "Enter") {
              const hit = open ? hits[activeIdx] : undefined;
              // Eine schon fertig getippte Adresse gewinnt gegen den Vorschlag —
              // sonst überschriebe ein zufälliger Namens-Treffer genau das, was
              // der Nutzer bewusst ausgeschrieben hat. Umgekehrt gewinnt der
              // Treffer, sobald der Nutzer ihn mit ↑↓ selbst angesteuert hat.
              const nimmTreffer =
                !!hit &&
                (arrowed ||
                  !looksLikeEmailAddress(needle) ||
                  isSameAddress(hit.email, needle));
              e.preventDefault();
              if (nimmTreffer && hit) take(hit);
              else {
                add(draft);
                setDraft("");
              }
              return;
            }
            if (e.key === ",") {
              e.preventDefault();
              add(draft);
              setDraft("");
            } else if (e.key === "Backspace" && !draft && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={() => {
            // 150 ms wie im Composer-Feld: der Klick auf einen Treffer feuert
            // erst nach dem Blur, die Liste muss so lange stehen bleiben.
            window.setTimeout(() => setFocused(false), 150);
            if (draft.trim()) {
              add(draft);
              setDraft("");
            }
          }}
          placeholder={value.length ? "" : placeholder}
          className="flex-1 min-w-[10ch] bg-transparent text-sm text-text outline-none placeholder:text-text-3"
        />
      </div>

      {/* Die Liste hängt DIREKT an der Chips-Zeile: der Tasten-Hinweis liegt
          innen als Fußzeile, statt als eigene Zeile zwischen Feld und Liste zu
          stehen (dann sähe die Liste aus, als gehörte sie zum nächsten Feld). */}
      {open && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-md border border-border bg-surface shadow-lg">
          <div
            id={`chips-hits-${field}`}
            role="listbox"
            data-chips-hits={field}
            className="divide-y divide-border"
          >
            {hits.map((c, i) => {
            const external = isExternalParticipant(c);
            const on = i === activeIdx;
            return (
              <button
                key={c.email}
                type="button"
                role="option"
                aria-selected={on}
                data-chips-hit
                data-external={external ? "true" : "false"}
                data-chips-active={on ? "true" : "false"}
                // mousedown-preventDefault: der Klick feuert VOR dem Blur — ohne
                // das committet der Blur den Rohtext und der Treffer geht unter.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => take(c)}
                className={clsx(
                  "flex w-full items-center gap-2.5 px-2.5 py-1.5 text-left transition-colors",
                  on ? "bg-accent-50/60" : "hover:bg-surface-2"
                )}
              >
                <span
                  className={clsx(
                    "grid h-5 w-5 shrink-0 place-items-center rounded-full",
                    external
                      ? "bg-warning-soft text-warning"
                      : "bg-accent-100 text-accent-700"
                  )}
                >
                  {external ? (
                    <Building2 className="h-3 w-3" />
                  ) : (
                    <UserRound className="h-3 w-3" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs text-text">
                    {c.name}
                    {external && (
                      <span className="ml-1.5 rounded bg-warning-soft px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-warning">
                        {mt.extern}
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-[11px] text-text-3">
                    {c.email}
                    {external
                      ? [c.title || null, c.account || null]
                          .filter(Boolean)
                          .map((s) => ` · ${s}`)
                          .join("")
                      : c.department
                        ? ` · ${c.department}`
                        : ""}
                  </span>
                </span>
                {on && (
                  <span
                    aria-hidden
                    className="shrink-0 rounded border border-border px-1 py-0.5 text-[10px] leading-none text-text-3"
                  >
                    ↵
                  </span>
                )}
                </button>
              );
            })}
          </div>
          {/* Die Tastenwege sichtbar machen (Doc 04: ein Weg, den man nicht
              liest, existiert für den Nutzer nicht). */}
          <div
            data-chips-hint={field}
            className="border-t border-border bg-surface-2/60 px-2.5 py-1 text-[11px] text-text-3"
          >
            <span className="font-medium">{mt.chipHinweisA}</span>
            {mt.chipHinweisB}
            <span className="font-medium">{mt.chipHinweisC}</span>
            {mt.chipHinweisD}
          </div>
        </div>
      )}

      {/* Ohne Treffer sagt eine ruhige Zeile, woran man ist — „nichts passiert"
          wäre die schlechteste Antwort auf eine Eingabe. */}
      {!open && focused && needle.length >= MIN_CONTACT_QUERY && (
        <div className="mt-1 text-[11px] text-text-3">
          {contacts.state === "loading" ? (
            <span data-chips-status="loading">{mt.chipSucht}</span>
          ) : (
            <span data-chips-status="frei">{mt.chipKeinVorschlag}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Fahrplan-Symbol-Reihe --------------------------------------------------
function FahrplanRow({
  initialDate,
  steps,
  preview,
}: {
  initialDate: Date | null;
  steps: {
    seq: number;
    kind: "reminder" | "escalation";
    due: Date;
    status?: FollowUpStep["status"];
    /** `sent_at` der Stufe — beschriftet sie, sobald sie gesendet ist (F-13). */
    sentAt?: string | null;
    /** Warum die Stufe uebersprungen wurde — haengt sich an den Tooltip. */
    skippedReason?: string | null;
  }[];
  preview?: boolean;
}) {
  const mt = useT().mail.nachfass;
  const today = fuStartOfToday();
  const cell = "flex flex-col items-center gap-1 shrink-0 min-w-[3.25rem]";
  const dateCls = "text-[10px] tabular-nums text-text-3 leading-none";
  const capCls = "text-[9px] uppercase tracking-wide text-text-3/80 leading-none";
  const connector = <span className="mt-[9px] h-px w-3 bg-border shrink-0" aria-hidden />;

  const reminderIdx = (seq: number) => seq; // seq 1..N = Reminder-Nr.
  return (
    <div className="flex items-start gap-0 overflow-x-auto pb-1">
      {/* Initial / Anker */}
      <div className={cell}>
        <span className={capCls}>{mt.fahrplanStart}</span>
        <CircleDot
          className={clsx("w-4 h-4", preview ? "text-text-3" : "text-accent-500")}
        />
        <span className={dateCls}>{fmtDayMonth(initialDate)}</span>
      </div>
      {steps.map((s, i) => {
        const due = s.due;
        const isDue =
          (s.status ?? "scheduled") === "scheduled" && !preview && due <= today;
        const sent = s.status === "sent";
        const skipped = s.status === "skipped";
        let icon: React.ReactNode;
        let tone: string;
        if (s.kind === "escalation") {
          tone = sent
            ? "text-danger"
            : preview
            ? "text-text-3"
            : isDue
            ? "text-warning"
            : "text-text-3";
          icon = <Triangle className={clsx("w-4 h-4", tone, sent && "fill-current")} />;
        } else if (sent) {
          icon = <CircleCheck className="w-4 h-4 text-success" />;
        } else if (isDue) {
          icon = <Circle className="w-4 h-4 text-accent-500" />;
        } else {
          icon = <Circle className={clsx("w-4 h-4", "text-text-3")} />;
        }
        const caption =
          s.kind === "escalation"
            ? mt.fahrplanEskalation
            : mt.fahrplanReminder(reminderIdx(s.seq));
        // Gesendete Stufen tragen ihr tatsächliches Sendedatum; das geplante
        // bleibt im Tooltip erhalten (F-13). Das Fahrplan-DATUM selbst wird
        // dabei NICHT verschoben — der Kunde kennt es aus der Holding-Zusage.
        const { label, title } = fahrplanStepDate({
          due,
          status: s.status,
          sentAt: s.sentAt,
        });
        // Der GRUND haengt sich hinten an, er ersetzt nichts: das geplante Datum
        // bleibt im Tooltip, wo F-13 es hingelegt hat. Eine durchgestrichene
        // Stufe ohne Erklaerung war der halbe Befund vom 19.08.
        const tooltip =
          skipped && s.skippedReason === "holding_reply"
            ? title + mt.uebersprungenGrund
            : title;
        return (
          <div key={s.seq ?? i} className="flex items-start">
            {connector}
            <div className={cell} data-fahrplan-step={caption} title={tooltip}>
              <span className={capCls}>{caption}</span>
              <span
                className={clsx(
                  "relative inline-flex",
                  isDue && "rounded-full ring-2 ring-accent-500/25"
                )}
              >
                {icon}
                {skipped && (
                  <span className="absolute inset-x-0 top-1/2 h-px bg-text-3 -rotate-12" />
                )}
              </span>
              <span
                data-fahrplan-date
                className={clsx(dateCls, isDue && "text-accent-600 font-medium")}
              >
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Config-Modal -----------------------------------------------------------
function TrackConfigModal({
  track,
  onClose,
  onSaved,
  colleagues = [],
}: {
  track: FollowUpTrack;
  onClose: () => void;
  onSaved: (t: FollowUpTrack) => void;
  /** Kollegen-Verzeichnis für die Vorschläge in den vier Empfänger-Feldern. */
  colleagues?: Colleague[];
}) {
  const toast = useToast();
  const mt = useT().mail.nachfass;
  const ct = useT().common;
  const cfg = track.config ?? {};
  const [interval, setIntervalDays] = useState<number>(
    Number(cfg.interval_days ?? FU_INTERVAL_DEFAULT)
  );
  const [escalate, setEscalate] = useState<number>(
    Number(cfg.escalate_after_iteration ?? FU_ESCALATE_DEFAULT)
  );
  const [to, setTo] = useState<string[]>(cfg.to ?? []);
  const [cc, setCc] = useState<string[]>(cfg.cc ?? []);
  const [replyAll, setReplyAll] = useState<boolean>(cfg.reminders_reply_all ?? true);
  const esc: Partial<FollowUpEscalation> = cfg.escalation ?? {};
  const [bitopAtt, setBitopAtt] = useState<string[]>(esc.bitop_attendees ?? []);
  const [partnerAtt, setPartnerAtt] = useState<string[]>(esc.partner_attendees ?? []);
  const [attendeeExclude, setAttendeeExclude] = useState<string[]>(esc.attendee_exclude ?? []);
  const [note, setNote] = useState<string>(esc.note ?? "");
  const [saving, setSaving] = useState(false);
  // Begründung des Servers zum letzten Speicherversuch (422 = kaputte Adresse).
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveErrorRef = useRef<HTMLDivElement | null>(null);
  // Der Dialog-Rumpf scrollt (Modal.tsx: `flex-1 overflow-y-auto`) und der
  // Speichern-Knopf sitzt in der Fußzeile — eine Meldung am Ende des Rumpfs
  // stünde sonst unter dem sichtbaren Bereich.
  useEffect(() => {
    if (saveError) saveErrorRef.current?.scrollIntoView({ block: "center" });
  }, [saveError]);
  const langLabel = fuLangLabel(cfg.language);
  const st = FU_STATE_BADGE[track.state];
  const resp = FU_RESPONSE_BADGE[track.response_status];
  // Die Erklaerungen der Engine — dieselben wie in der Zeile, hier untereinander
  // unter dem Fahrplan (im Dialog ist Platz fuer den vollen Satz).
  const zusageLabel = track.promise_date
    ? fmtDayMonth(fuParseDate(track.promise_date))
    : null;
  const notizen = fuEngineNotes(track);

  // ── Echter Fahrplan mit Live-Status (aus track.steps) ─────────────────────
  const realSteps = useMemo(
    () =>
      [...(track.steps ?? [])]
        .sort((a, b) => a.seq - b.seq)
        .map((s) => ({
          seq: s.seq,
          kind: s.kind,
          due: fuParseDate(s.due_date) ?? fuStartOfToday(),
          status: s.status,
          sentAt: s.sent_at,
          skippedReason: s.skipped_reason,
        })),
    [track.steps]
  );
  const hasRealSteps = realSteps.length > 0;
  const sentReminders = realSteps.filter(
    (s) => s.kind === "reminder" && s.status === "sent"
  ).length;
  const totalReminders = realSteps.filter((s) => s.kind === "reminder").length;
  const nextOpen = realSteps.find((s) => s.status === "scheduled");
  const escStep = realSteps.find((s) => s.kind === "escalation");
  const escalated = escStep?.status === "sent";

  // Vorschau des NEUEN Fahrplans, während Intervall/Anzahl editiert werden.
  const previewSteps = useMemo(() => {
    const anchor = fuParseDate(track.anchor_date) ?? fuStartOfToday();
    return fuComputeSteps(anchor, interval, escalate).map((s) => ({
      seq: s.seq,
      kind: s.kind,
      due: s.due,
    }));
  }, [track.anchor_date, interval, escalate]);
  const settingsChanged =
    interval !== Number(cfg.interval_days ?? FU_INTERVAL_DEFAULT) ||
    escalate !== Number(cfg.escalate_after_iteration ?? FU_ESCALATE_DEFAULT);

  // ── Meeting-Einladung (auto = counterpart + Verteiler + Extra, Owner raus) ──
  const ownerL = (track.owner_email ?? "").toLowerCase();
  const excludeSet = new Set(attendeeExclude.map((a) => a.toLowerCase()));
  const inviteBase = useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    const push = (a?: string | null) => {
      const s = (a ?? "").trim();
      if (!s) return;
      const l = s.toLowerCase();
      if (l === ownerL || seen.has(l)) return;
      seen.add(l);
      out.push(s);
    };
    push(track.counterpart_email);
    [...to, ...cc, ...bitopAtt, ...partnerAtt].forEach(push);
    return out;
  }, [track.counterpart_email, ownerL, to, cc, bitopAtt, partnerAtt]);
  const activeInviteCount = inviteBase.filter(
    (a) => !excludeSet.has(a.toLowerCase())
  ).length;
  const toggleExclude = (addr: string) => {
    const l = addr.toLowerCase();
    setAttendeeExclude((prev) =>
      prev.some((x) => x.toLowerCase() === l)
        ? prev.filter((x) => x.toLowerCase() !== l)
        : [...prev, addr]
    );
  };

  // ── Header-Meta: Account · Maildomain · Hauptempfänger ─────────────────────
  const mailDomain = (track.counterpart_email ?? "").split("@")[1] || null;
  const mainRecipient = track.counterpart_name || track.counterpart_email || null;
  const headerMeta = [track.account_name, mailDomain, mainRecipient]
    .filter(Boolean)
    .join(" · ");
  const modalTitle = track.topic || track.subject || mt.keinBetreff;

  const buildConfig = (): FollowUpConfig => ({
    interval_days: Math.max(1, Math.floor(interval || 0)),
    escalate_after_iteration: Math.max(1, Math.floor(escalate || 0)),
    language: cfg.language ?? null,
    to,
    cc,
    reminders_reply_all: replyAll,
    escalation: {
      kind: "meeting",
      bitop_attendees: bitopAtt,
      partner_attendees: partnerAtt,
      attendee_exclude: attendeeExclude,
      note: note.trim() || null,
    },
  });

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await api.patch<FollowUpTrack>(
        `/mail/follow-ups/${track.id}`,
        { config: buildConfig() }
      );
      toast.ok(katalog().mail.nachfass.konfigurationGespeichert);
      onSaved(updated);
      onClose();
    } catch (e) {
      // Der Toast verschwindet; die Meldung gehört DAUERHAFT in den Dialog, in
      // dem die beanstandete Adresse steht — sonst bleibt „nicht gespeichert"
      // ein stiller Fehlschlag.
      const text = fuSaveFehlerText(e as ApiError);
      setSaveError(text);
      toast.err(katalog().common.speichernFehlgeschlagen, text);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      icon={<AlarmClockCheck className="w-4 h-4" />}
      title={modalTitle}
      description={headerMeta || track.subject || mt.keinBetreff}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {ct.abbrechen}
          </Button>
          <Button variant="primary" size="sm" loading={saving} onClick={() => void save()}>
            {ct.speichern}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        {/* ── Aktueller Stand: Badges + echter Fahrplan + Zusammenfassung ── */}
        <div className="rounded-lg border border-border bg-surface-2/40 p-3 space-y-2.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge status={st}>{mt.zustand[track.state]}</StatusBadge>
            {track.response_status !== "none" && (
              <StatusBadge status={resp}>{fuAntwortLabel(track)}</StatusBadge>
            )}
            {zusageLabel && (
              <StatusBadge
                status="info"
                data-fu-promise
                title={mt.chipZusageTitel(zusageLabel)}
              >
                {mt.chipZusage}
              </StatusBadge>
            )}
            {langLabel && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-surface-3 text-text-3">
                {langLabel}
              </span>
            )}
            {track.subject && track.topic && (
              <span className="text-[11px] text-text-3 truncate">
                {mt.betreffPraefix(track.subject)}
              </span>
            )}
          </div>

          <FahrplanRow
            initialDate={fuParseDate(track.anchor_date) ?? fuStartOfToday()}
            steps={hasRealSteps ? realSteps : previewSteps}
            preview={!hasRealSteps}
          />

          <p className="text-[11px] text-text-3">
            {!hasRealSteps ? (
              <>{mt.nochNichtScharf}</>
            ) : escalated ? (
              <>{mt.eskaliert(sentReminders, totalReminders)}</>
            ) : (
              <>
                {mt.nachfragenGesendet(sentReminders, totalReminders)}
                {nextOpen
                  ? mt.naechsteAm(
                      nextOpen.kind === "escalation"
                        ? mt.wortEskalation
                        : mt.wortNaechste,
                      fmtDayMonth(nextOpen.due)
                    )
                  : ""}
                {mt.tonLadder}
              </>
            )}
          </p>

          {/* Was die Engine selbst getan hat. Steht UNTER dem Fahrplan, weil es
              ihn erklaert — und nur dann, wenn es etwas zu erklaeren gibt. */}
          {notizen.length > 0 && (
            <ul data-fu-engine-notes className="text-[11px] text-text-2 space-y-0.5">
              {notizen.map((n) => (
                <li key={n} className="flex items-start gap-1.5">
                  <span className="mt-[6px] w-1 h-1 rounded-full bg-text-3 shrink-0" aria-hidden />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Einstellungen ── */}
        <div className="grid grid-cols-2 gap-3">
          <Field label={mt.intervall} hint={mt.intervallHinweis}>
            <Input
              type="number"
              min={1}
              value={interval}
              onChange={(e) => setIntervalDays(Number(e.target.value))}
            />
          </Field>
          <Field label={mt.anzahlNachfragen} hint={mt.anzahlNachfragenHinweis}>
            <Input
              type="number"
              min={1}
              value={escalate}
              onChange={(e) => setEscalate(Number(e.target.value))}
            />
          </Field>
        </div>

        {settingsChanged && (
          <div className="rounded-lg border border-dashed border-border bg-surface-2/40 p-2.5">
            <div className="text-[11px] font-medium text-text-3 mb-1">
              {mt.neuerFahrplan}
            </div>
            <FahrplanRow
              initialDate={fuParseDate(track.anchor_date) ?? fuStartOfToday()}
              steps={previewSteps}
              preview
            />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={mt.an}>
            <ChipsInput
              field="to"
              label={mt.an}
              value={to}
              onChange={setTo}
              colleagues={colleagues}
              placeholder={mt.anPlatzhalter}
            />
          </Field>
          <Field label={mt.cc} optional>
            <ChipsInput
              field="cc"
              label={mt.cc}
              value={cc}
              onChange={setCc}
              colleagues={colleagues}
              placeholder={mt.ccPlatzhalter}
            />
          </Field>
        </div>

        {/* Reply-all-Umfang */}
        <button
          type="button"
          onClick={() => setReplyAll((v) => !v)}
          className="flex w-full items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2 text-left hover:bg-surface-2/40 transition-colors"
        >
          {replyAll ? (
            <ToggleRight className="w-6 h-6 text-accent-500 shrink-0" />
          ) : (
            <ToggleLeft className="w-6 h-6 text-text-3 shrink-0" />
          )}
          <span className="min-w-0">
            <span className="block text-sm text-text">{mt.verteilerAlle}</span>
            <span className="block text-[11px] text-text-3">
              {replyAll ? mt.verteilerAlleAn : mt.verteilerAlleAus}
            </span>
          </span>
        </button>

        {/* ── Eskalations-Meeting ── */}
        <div className="rounded-lg border border-border bg-surface-2/40 p-3 space-y-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-text-2">
            <Triangle className="w-3.5 h-3.5" /> {mt.eskalationsMeeting}
            <span className="font-normal text-text-3">
              {mt.eingeladenAnzahl(activeInviteCount)}
            </span>
          </div>
          <p className="text-[11px] text-text-3">{mt.eskalationHinweis}</p>
          {inviteBase.length === 0 ? (
            <p className="text-[11px] text-text-3">{mt.keinVerteiler}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {inviteBase.map((a) => {
                const excluded = excludeSet.has(a.toLowerCase());
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleExclude(a)}
                    title={excluded ? mt.wiederEinladen : mt.ausEinladungEntfernen}
                    className={clsx(
                      "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs border transition-colors max-w-full",
                      excluded
                        ? "border-dashed border-border text-text-3 line-through"
                        : "border-border bg-surface-3 text-text hover:border-danger/50"
                    )}
                  >
                    <span className="truncate max-w-[18ch]">{a}</span>
                    {excluded ? (
                      <Plus className="w-3 h-3 shrink-0" />
                    ) : (
                      <X className="w-3 h-3 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={mt.bitopTeilnehmer} optional>
              <ChipsInput
                field="bitop"
                label={mt.bitopTeilnehmer}
                value={bitopAtt}
                onChange={setBitopAtt}
                colleagues={colleagues}
                placeholder={mt.bitopTeilnehmerPlatzhalter}
              />
            </Field>
            <Field label={mt.partnerTeilnehmer} optional>
              <ChipsInput
                field="partner"
                label={mt.partnerTeilnehmer}
                value={partnerAtt}
                onChange={setPartnerAtt}
                colleagues={colleagues}
                placeholder={mt.partnerTeilnehmerPlatzhalter}
              />
            </Field>
          </div>
          <Field label={mt.notiz} optional>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={mt.notizPlatzhalter}
            />
          </Field>
        </div>

        {/* Warum der letzte Speicherversuch abgelehnt wurde — mit der Adresse,
            um die es geht. Steht direkt über der Fußzeile, also neben dem Knopf,
            den man gerade gedrückt hat. */}
        {saveError && (
          <div
            ref={saveErrorRef}
            role="alert"
            data-fu-save-error
            className="flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="min-w-0">
              <span className="block font-medium">{mt.nichtGespeichert}</span>
              <span className="block">{saveError}</span>
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Kompakter Zeitpunkt der auslösenden Antwort — „06.08.2026, 06:15".
 *
 *  Bewusst nicht `fmtFullDate` (der Detail-Kopf): dessen ausgeschriebene Form
 *  („Mi., 06. August 2026, 06:15") frisst bei 390 px die halbe Zeile, und in
 *  dieser Zeile steht der Absender daneben. */
function fuAntwortZeit(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Die auslösende Antwort in der Entscheidungszeile: Kopf immer, Mailtext auf
 * Klick. Ziel ist, dass die Entscheidung OHNE Sprung fällt.
 *
 * ZWEI Punkte, die nicht Geschmack sind:
 *
 *  1. **Ein einziger Render-Pfad.** Der Mailtext geht durch `MessageBody` →
 *     `RenderedBody` — dieselbe Komponente wie der Lesebereich, also dieselbe
 *     `sanitizeBody`-Bereinigung, dieselbe `cid:`-Auflösung eingebetteter
 *     Bilder, dieselbe Zitat-Zerlegung. Ein zweiter, eigener Renderer wäre die
 *     Stelle, an der Bereinigung und Bilder auseinanderlaufen; genau deshalb
 *     steht hier keine einzige eigene HTML-Zeile.
 *  2. **Erst beim Aufklappen laden.** Der Abruf hängt an `offen`, nicht am
 *     Rendern der Zeile. Bei elf offenen Vorgängen wären das sonst elf
 *     Postfach-Abrufe für Text, den niemand angesehen hat.
 *
 * KEINE Antwort-Möglichkeit hier: geantwortet wird im Mail-Bereich, wo
 * Signatur-Schalter, Anhänge und Vorlagen leben. Ein zweiter Editor würde davon
 * unweigerlich abdriften.
 */
function DecisionAnswer({ track }: { track: FollowUpTrack }) {
  const mt = useT().mail.nachfass;
  const imid = track.decision_message_id;
  const [offen, setOffen] = useState(false);
  const [msg, setMsg] = useState<MessageResponse | null>(null);
  const [zustand, setZustand] = useState<"leer" | "laedt" | "da" | "weg" | "fehler">(
    "leer"
  );
  const [fehlertext, setFehlertext] = useState<string | null>(null);

  // „Für diese Mail lief der Abruf schon" — als Ref, NICHT als Zustand.
  //
  // Gemessen, nicht überlegt: mit `zustand` in der Abhängigkeitsliste bricht der
  // Effekt seinen eigenen Abruf ab. `setZustand("laedt")` ändert die
  // Abhängigkeit, React räumt den vorigen Lauf auf, `abgebrochen` wird true —
  // und die Antwort, die kurz darauf eintrifft, wird verworfen. Die Vorschau
  // blieb dann für immer im Ladezustand.
  const geholtRef = useRef<string | null>(null);
  useEffect(() => {
    // Der Riegel: ohne `offen` passiert hier NICHTS. Eine zugeklappte Zeile
    // löst keinen einzigen Abruf aus.
    if (!offen || !imid || geholtRef.current === imid) return;
    geholtRef.current = imid;
    let abgebrochen = false;
    let fertig = false;
    setZustand("laedt");
    (async () => {
      try {
        const m = await api.get<MessageResponse>(
          `/mail/messages/${encodeURIComponent(imid)}`
        );
        if (abgebrochen) return;
        fertig = true;
        setMsg(m);
        setZustand("da");
      } catch (e) {
        if (abgebrochen) return;
        fertig = true;
        const err = e as ApiError;
        // 404 ist der EIGENE Fall, nicht „leer": die Auflösung im Postfach ist
        // postfachweit, verschobene Mails werden weiter gefunden — auch aus
        // „Gelöschte Elemente". Ein 404 heißt also wirklich endgültig gelöscht,
        // und das gehört gesagt statt als leere Vorschau gezeigt.
        setZustand(err?.status === 404 ? "weg" : "fehler");
        setFehlertext(err?.message ?? null);
      }
    })();
    return () => {
      abgebrochen = true;
      // Vor der Antwort zugeklappt: die Sperre wieder lösen, sonst hinge die
      // Zeile beim nächsten Aufklappen dauerhaft im Ladezustand.
      if (!fertig) geholtRef.current = null;
    };
  }, [offen, imid]);

  if (!imid) return null;

  const zeit = fuAntwortZeit(track.decision_at);
  const absender = track.decision_from || track.counterpart_email || mt.antwortUnbekannt;

  return (
    <div
      data-fu-decision-answer
      className="mt-2 rounded-lg border border-border bg-surface-2 overflow-hidden"
    >
      <button
        type="button"
        data-fu-answer-toggle
        onClick={() => setOffen((v) => !v)}
        aria-expanded={offen}
        className="w-full flex items-start gap-2 px-2.5 py-2 text-left hover:bg-surface transition-colors"
      >
        <ChevronDown
          className={clsx(
            "mt-0.5 shrink-0 w-3.5 h-3.5 text-text-3 transition-transform",
            offen && "rotate-180"
          )}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span
              className="min-w-0 flex-1 truncate text-xs font-medium text-text-2"
              title={absender}
              data-fu-answer-from
            >
              {absender}
            </span>
            {zeit && (
              <span className="shrink-0 text-[11px] text-text-3" data-fu-answer-time>
                {zeit}
              </span>
            )}
          </span>
          {/* Die gespeicherte Einstufungs-Begründung. Zugeklappt gekürzt, offen
              vollständig — sie ist erstaunlich brauchbar und oft schon die
              halbe Entscheidung. */}
          {track.decision_reason && (
            <span
              data-fu-answer-reason
              className={clsx(
                "block text-[11px] text-text-3 mt-0.5",
                !offen && "truncate"
              )}
            >
              {track.decision_reason}
            </span>
          )}
        </span>
      </button>
      {offen && (
        <div className="border-t border-border px-2.5 py-2">
          {zustand === "laedt" && (
            <div className="space-y-1.5">
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-5/6" />
              <Skeleton className="h-3.5 w-3/6" />
            </div>
          )}
          {zustand === "weg" && (
            <div
              data-fu-answer-missing
              className="flex items-start gap-2 text-xs text-text-2"
            >
              <SearchX className="w-3.5 h-3.5 shrink-0 mt-0.5 text-text-3" />
              <span>{mt.antwortWeg}</span>
            </div>
          )}
          {zustand === "fehler" && (
            <div
              data-fu-answer-error
              className="flex items-start gap-2 text-xs text-text-2"
            >
              <ServerCrash className="w-3.5 h-3.5 shrink-0 mt-0.5 text-text-3" />
              <span>{fehlertext || mt.antwortFehler}</span>
            </div>
          )}
          {zustand === "da" && msg && (
            <>
              {msg.subject && (
                <div className="mb-1.5 text-xs font-medium text-text truncate">
                  {msg.subject}
                </div>
              )}
              {/* Deckel + eigener Scroll: eine lange Mail darf die Zeile nicht
                  sprengen und die Liste nicht unbedienbar machen. */}
              <div
                data-fu-answer-body
                className="max-h-72 overflow-y-auto overflow-x-auto pr-1"
              >
                <MessageBody msg={msg} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Der vorgeschlagene Eskalations-Termin in der Entscheidungszeile (F-21).
 *
 * Termin UND Teilnehmer stehen offen da, ohne Aufklappen: das sind die zwei
 * Angaben, ohne die niemand entscheiden kann, ob in seinem Namen ein Meeting
 * entstehen soll. Sie hinter einem Klick zu verstecken hiesse, die Entscheidung
 * wieder blind zu machen — der Befund vom 05.08. war genau das („zufaellig
 * darueber gestolpert").
 *
 * Die Teilnehmer stehen vollstaendig da und nicht als „+3 weitere": wer im
 * Kalender des Kunden landet, ist der Kern der Frage.
 */
function DecisionMeeting({ track }: { track: FollowUpTrack }) {
  const mt = useT().mail.nachfass;
  const slot = fuSlotLabel(track.decision_slot_start, track.decision_slot_end);
  const eingeladen = track.decision_attendees ?? [];
  if (!slot && eingeladen.length === 0) return null;
  return (
    <div
      data-fu-decision-meeting
      className="mt-2 rounded-lg border border-border bg-surface-2 px-2.5 py-2 space-y-1.5"
    >
      {slot && (
        <div className="flex items-start gap-2">
          <CalendarClock
            className="mt-0.5 shrink-0 w-3.5 h-3.5 text-text-3"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <div className="text-[11px] text-text-3">{mt.vorgeschlagenerTermin}</div>
            <div
              data-fu-decision-slot
              className="text-xs font-medium text-text tabular-nums"
            >
              {slot}
            </div>
          </div>
        </div>
      )}
      {eingeladen.length > 0 && (
        <div className="flex items-start gap-2">
          <UserRound
            className="mt-0.5 shrink-0 w-3.5 h-3.5 text-text-3"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <div className="text-[11px] text-text-3">
              {mt.eingeladen(eingeladen.length)}
            </div>
            <div
              data-fu-decision-attendees
              className="text-xs text-text-2 break-words"
            >
              {eingeladen.join(", ")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Track-Zeile ------------------------------------------------------------
/** Eine Zeile im Auffangkorb: worum es geht, warum sie fragt, die auslösende
 *  Antwort — und zwei Auswege.
 *
 *  Bewusst nur ZWEI Entscheidungen. Der Vorgang ist angehalten, und die einzige
 *  Frage ist, ob das so bleiben soll („Erledigt") oder nicht („Weiter
 *  nachfassen"). Alles Übrige (Verteiler, Kadenz, Löschen) ist danach in der
 *  normalen Zeile erreichbar — hier wäre es Ablenkung von genau der einen
 *  Entscheidung. „In Mail öffnen" ist keine dritte Entscheidung, sondern der
 *  Weg zum vollen Kontext.
 *
 *  Die Termin-Anfrage (F-21, `decision_trigger === "escalation_meeting"`) läuft
 *  durch DIESELBE Zeile: statt der auslösenden Antwort steht dort der
 *  vorgeschlagene Termin mit Teilnehmern, und die zwei Auswege heissen „Termin
 *  anlegen" / „Keine Eskalation". Ein eigenes Bedienkonzept für eine Frage, die
 *  genauso „ja oder nein" ist, waere Ballast. */
function DecisionRow({
  track,
  onDecide,
}: {
  track: FollowUpTrack;
  onDecide: (decision: FollowUpDecisionKind) => void;
}) {
  const mt = useT().mail.nachfass;
  const titel = track.topic || track.subject || mt.keinBetreff;
  const gegenueber = track.counterpart_name || track.counterpart_email;
  const imid = track.decision_message_id;
  const [, setParams] = useSearchParams();
  // Die Termin-Anfrage benutzt DIESELBE Zeile und DIESELBEN zwei Auswege — nur
  // heissen sie hier, was sie tun. Ein drittes Bedienkonzept fuer eine Frage,
  // die genauso „ja oder nein" ist, waere Ballast.
  const istTermin = track.decision_trigger === "escalation_meeting";
  return (
    <div
      data-fu-decision-row
      className="rounded-lg border border-border bg-surface px-3 py-2.5"
    >
      <div className="min-w-0">
        <div className="text-sm font-medium text-text truncate">{titel}</div>
        {gegenueber && (
          <div className="text-xs text-text-3 truncate">{gegenueber}</div>
        )}
        <p className="mt-1 text-xs text-text-2">
          {fuDecisionReason(track.decision_trigger)}
        </p>
      </div>
      {istTermin ? <DecisionMeeting track={track} /> : <DecisionAnswer track={track} />}
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          icon={
            istTermin ? (
              <CalendarPlus className="w-3.5 h-3.5" />
            ) : (
              <Check className="w-3.5 h-3.5" />
            )
          }
          onClick={() => onDecide(istTermin ? "escalate" : "done")}
        >
          {istTermin ? mt.terminAnlegen : mt.erledigt}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon={
            istTermin ? (
              <X className="w-3.5 h-3.5" />
            ) : (
              <AlarmClockCheck className="w-3.5 h-3.5" />
            )
          }
          onClick={() => onDecide(istTermin ? "no_escalate" : "continue")}
        >
          {istTermin ? mt.keineEskalation : mt.weiterNachfassen}
        </Button>
        {imid && (
          <Button
            variant="ghost"
            size="sm"
            data-fu-answer-open
            icon={<ExternalLink className="w-3.5 h-3.5" />}
            onClick={() => {
              const next = new URLSearchParams();
              next.set("m", imid);
              setParams(next);
            }}
          >
            {mt.inMailOeffnen}
          </Button>
        )}
      </div>
    </div>
  );
}

function TrackRow({
  track,
  onConfigure,
  onArm,
  onSwitch,
  onDelete,
}: {
  track: FollowUpTrack;
  onConfigure: () => void;
  onArm: () => void;
  onSwitch: () => void;
  onDelete: () => void;
}) {
  const mt = useT().mail.nachfass;
  const ct = useT().common;
  const st = FU_STATE_BADGE[track.state];
  const resp = FU_RESPONSE_BADGE[track.response_status];
  const complete = fuConfigComplete(track.config ?? {});
  const canSwitch = track.state !== "draft";
  const isOn = track.state === "armed";
  const langLabel = fuLangLabel(track.config?.language);

  // Nächster offener Schritt (Reminder bevorzugt, sonst Eskalation) — als
  // kompakte „nächste 30.7."-Angabe in der Zeile. Der volle Fahrplan lebt im
  // Anpassen-Dialog (Fahrplan-Vorschau).
  const nextReminder = [...(track.steps ?? [])]
    .filter((s) => s.kind === "reminder" && s.status === "scheduled")
    .sort((a, b) => a.seq - b.seq)[0];
  const nextEsc = (track.steps ?? []).find(
    (s) => s.kind === "escalation" && s.status === "scheduled"
  );
  const nextStep = nextReminder ?? nextEsc;
  const nextLabel = nextStep ? fmtDayMonth(fuParseDate(nextStep.due_date)) : null;
  const nextIsEsc = !!nextStep && !nextReminder;

  // Was die Engine an DIESEM Vorgang selbst getan hat. Die Zeile bleibt
  // einzeilig, solange es nichts zu erklaeren gibt — der Grund-Befund des
  // Testers war „cluttered", und eine dauerhaft zweite Zeile waere genau das.
  // Steht sie da, hat sie einen Anlass: eingerastete Zusage, verschickte
  // Bestaetigung, uebersprungene Nachfrage.
  const notizen = fuEngineNotes(track);
  const zusageLabel = track.promise_date
    ? fmtDayMonth(fuParseDate(track.promise_date))
    : null;

  return (
    <div className="group rounded-lg border border-border bg-surface px-3 py-2">
      <div className="flex items-center gap-2.5">
        {/* Läuft/Pausiert-Schalter */}
        <button
          type="button"
          disabled={!canSwitch}
          onClick={onSwitch}
          aria-label={isOn ? mt.laeuftPausieren : mt.pausiertFortsetzen}
          title={
            !canSwitch
              ? mt.erstScharfschalten
              : isOn
              ? mt.laeuftKlickPausieren
              : mt.pausiertKlickFortsetzen
          }
          className={clsx("shrink-0 transition-colors", !canSwitch && "opacity-30 cursor-not-allowed")}
        >
          {isOn ? (
            <ToggleRight className="w-6 h-6 text-accent-500" />
          ) : (
            <ToggleLeft className="w-6 h-6 text-text-3" />
          )}
        </button>

        {/* Betreff · Gegenüber — füllt die Zeile, trunkiert bei Überlänge */}
        <div className="min-w-0 flex-1 truncate">
          <span
            className="text-sm font-medium text-text"
            title={track.subject || undefined}
          >
            {track.topic || track.subject || mt.keinBetreff}
          </span>
          <span className="text-xs text-text-3">
            {" · "}
            {track.counterpart_name || track.counterpart_email || mt.unbekannt}
          </span>
        </div>

        {/* Rechts: nächster Termin · Sprache · Antwort · Status · Aktionen (hover) */}
        <div className="flex items-center gap-1.5 shrink-0">
          {nextLabel && (isOn || track.state === "paused") && (
            <span
              className="hidden md:inline text-[11px] tabular-nums text-text-3"
              title={nextIsEsc ? mt.naechsterSchrittEskalation : mt.naechsteNachfrage}
            >
              {nextIsEsc ? mt.eskalationPraefix : mt.naechstePraefix}
              {nextLabel}
            </span>
          )}
          {langLabel && (
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-surface-3 text-text-3"
              title={
                // „automatisch erkannt" war nur die halbe Wahrheit: sitzen im
                // Verteiler ausschliesslich Kolleg:innen, gilt Deutsch als
                // Hausprache, egal was im Thread steht.
                track.house_language ? mt.spracheHaus : mt.spracheErkannt
              }
            >
              {langLabel}
            </span>
          )}
          {zusageLabel && (
            <StatusBadge
              status="info"
              data-fu-promise
              title={mt.chipZusageTitel(zusageLabel)}
            >
              {mt.chipZusage}
            </StatusBadge>
          )}
          {track.response_status !== "none" && (
            <StatusBadge status={resp}>{fuAntwortLabel(track)}</StatusBadge>
          )}
          <StatusBadge status={st}>{mt.zustand[track.state]}</StatusBadge>
          {track.state === "draft" && (
            <span
              title={
                !complete
                  ? mt.empfaengerFehlt
                  : track.internal_counterpart
                  ? // Sonst liest sich der Entwurf-Status wie ein Fehler beim
                    // Markieren. Der Grund gehört an den Knopf, der ihn auflöst.
                    mt.internesGegenueber
                  : ""
              }
            >
              <Button
                variant="primary"
                size="sm"
                icon={<AlarmClockCheck className="w-3.5 h-3.5" />}
                disabled={!complete}
                onClick={onArm}
              >
                {mt.scharf}
              </Button>
            </span>
          )}
          <button
            type="button"
            onClick={onConfigure}
            aria-label={mt.anpassen}
            title={mt.anpassen}
            className="p-1.5 rounded-md text-text-3 transition-colors hover:bg-surface-3 hover:text-text md:opacity-0 md:group-hover:opacity-100"
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label={ct.loeschen}
            title={ct.loeschen}
            className="p-1.5 rounded-md text-text-3 transition-colors hover:bg-danger-soft hover:text-danger md:opacity-0 md:group-hover:opacity-100"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Was die Engine an diesem Vorgang selbst getan hat. Über die VOLLE
          Zeilenbreite und nicht in der Titelspalte: dort blieben von „…in
          deinem Namen verschickt" nach der Trunkierung 118 px übrig — gemessen,
          und damit wäre die Aussage genauso unsichtbar gewesen wie vorher. Der
          linke Einzug hält sie unter dem Titel, nicht unter dem Schalter. */}
      {notizen.length > 0 && (
        <div
          data-fu-engine-notes
          className="mt-1 pl-[2.125rem] text-[11px] text-text-3 leading-snug"
        >
          {notizen.map((n) => (
            <div key={n} className="truncate" title={n}>
              {n}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Nachverfolgungs-Tab ----------------------------------------------------
function FollowUpTab({
  tracks,
  loading,
  error,
  onReload,
  onApply,
  onRemove,
  colleagues = [],
}: {
  tracks: FollowUpTrack[] | null;
  loading: boolean;
  error: ApiError | null;
  onReload: () => void;
  /** Kollegen-Verzeichnis (MailPage lädt es einmalig) — Basis der Vorschläge im
   *  Anpassen-Dialog. Leer = die Felder bleiben reiner Freitext. */
  colleagues?: Colleague[];
  // Authoritatives State-Update aus der Mutations-Antwort — vermeidet die
  // Read-after-write-Race eines sofortigen Reload-GET (Commit evtl. noch nicht
  // sichtbar). Der Reload-Button (onReload) bleibt der manuelle Voll-Refresh.
  onApply: (t: FollowUpTrack) => void;
  onRemove: (id: string) => void;
}) {
  const toast = useToast();
  const mt = useT().mail.nachfass;
  const ct = useT().common;
  const { confirm } = useModal();
  const [configTrack, setConfigTrack] = useState<FollowUpTrack | null>(null);

  const arm = async (t: FollowUpTrack) => {
    try {
      const updated = await api.post<FollowUpTrack>(`/mail/follow-ups/${t.id}/arm`);
      onApply(updated);
      const n = katalog().mail.nachfass;
      toast.ok(n.scharfgeschaltet, n.scharfgeschaltetText);
    } catch (e) {
      toast.err(katalog().mail.nachfass.scharfschaltenFehler, (e as ApiError).message);
    }
  };
  const doSwitch = async (t: FollowUpTrack) => {
    const path =
      t.state === "armed" ? "pause" : "resume"; // paused/answered/stopped → resume
    const wasArmed = t.state === "armed";
    try {
      const updated = await api.post<FollowUpTrack>(`/mail/follow-ups/${t.id}/${path}`);
      onApply(updated);
      const n = katalog().mail.nachfass;
      toast.ok(wasArmed ? n.gestoppt : n.reaktiviert);
    } catch (e) {
      toast.err(katalog().mail.nachfass.aktionFehlgeschlagen, (e as ApiError).message);
    }
  };
  const remove = async (t: FollowUpTrack) => {
    const n = katalog().mail.nachfass;
    const ok = await confirm({
      title: n.loeschenTitel,
      description: n.loeschenBeschreibung(t.subject || n.keinBetreff),
      confirmLabel: ct.loeschen,
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/mail/follow-ups/${t.id}`);
      onRemove(t.id);
      toast.ok(katalog().mail.nachfass.geloescht);
    } catch (e) {
      toast.err(katalog().mail.nachfass.loeschenFehlgeschlagen, (e as ApiError).message);
    }
  };

  const decide = async (t: FollowUpTrack, decision: FollowUpDecisionKind) => {
    try {
      const updated = await api.post<FollowUpTrack>(
        `/mail/follow-ups/${t.id}/decide`,
        { decision }
      );
      onApply(updated);
      // Beim Termin sagt die Meldung, WANN er liegt: der Slot kann beim
      // Zustimmen neu gerechnet worden sein (wenn der Vorschlag inzwischen
      // vorbei war). Ein blosses „angelegt" verschwiege genau die Abweichung,
      // um die es in diesem Befund geht.
      const n = katalog().mail.nachfass;
      const meldung: Record<FollowUpDecisionKind, [string, string]> = {
        done: [n.meldungDoneTitel, n.meldungDoneText],
        continue: [n.meldungContinueTitel, n.meldungContinueText],
        escalate: [
          n.meldungEscalateTitel,
          n.meldungEscalateText(
            fuSlotLabel(updated.decision_slot_start, updated.decision_slot_end) ??
              n.meldungEscalateFallback
          ),
        ],
        no_escalate: [n.meldungNoEscalateTitel, n.meldungNoEscalateText],
      };
      const [titel, text] = meldung[decision];
      toast.ok(titel, text);
    } catch (e) {
      toast.err(katalog().mail.nachfass.entscheidungFehlgeschlagen, (e as ApiError).message);
    }
  };

  const list = tracks ?? [];
  // Der Auffangkorb steht OBEN und ist aus der Hauptliste ausgenommen — sonst
  // stünde derselbe Track zweimal auf der Seite und der Nutzer müsste raten,
  // welche der beiden Zeilen die gemeinte ist.
  const entscheidungen = list.filter((t) => t.state === "needs_decision");
  const uebrige = list.filter((t) => t.state !== "needs_decision");
  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="text-sm text-text-2">{mt.reiterHinweis}</p>
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw className={clsx("w-4 h-4", loading && "animate-spin")} />}
            onClick={onReload}
            disabled={loading}
          >
            {ct.aktualisieren}
          </Button>
        </div>

        {loading && !tracks && (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-xl" />
            ))}
          </div>
        )}

        {!loading && error && (
          <EmptyState
            icon={<ServerCrash className="w-6 h-6" />}
            title={mt.ladefehlerTitel}
            hint={mt.ladefehlerHinweis}
            action={
              <Button variant="secondary" icon={<RefreshCw className="w-4 h-4" />} onClick={onReload}>
                {ct.erneutVersuchen}
              </Button>
            }
          />
        )}

        {!error && (tracks !== null) && list.length === 0 && (
          <EmptyState
            icon={<AlarmClockPlus className="w-6 h-6" />}
            title={mt.leerTitel}
            hint={mt.leerHinweis}
          />
        )}

        {entscheidungen.length > 0 && (
          <section
            data-fu-decisions
            className="mb-5 rounded-xl border border-border bg-warning-soft/40 p-3.5"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <AlertTriangle className="w-4 h-4 shrink-0 text-warning" />
              <h3 className="text-sm font-semibold text-text">
                {mt.entscheidungTitel}
              </h3>
              <DecisionCountPill count={entscheidungen.length} />
            </div>
            <p className="text-xs text-text-2 mb-3">{mt.entscheidungHinweis}</p>
            <div className="space-y-2">
              {entscheidungen.map((t) => (
                <DecisionRow
                  key={t.id}
                  track={t}
                  onDecide={(d) => void decide(t, d)}
                />
              ))}
            </div>
          </section>
        )}

        {uebrige.length > 0 && (
          <div className="space-y-3">
            {uebrige.map((t) => (
              <TrackRow
                key={t.id}
                track={t}
                onConfigure={() => setConfigTrack(t)}
                onArm={() => void arm(t)}
                onSwitch={() => void doSwitch(t)}
                onDelete={() => void remove(t)}
              />
            ))}
          </div>
        )}
      </div>

      {configTrack && (
        <TrackConfigModal
          track={configTrack}
          onClose={() => setConfigTrack(null)}
          onSaved={onApply}
          colleagues={colleagues}
        />
      )}
    </div>
  );
}

// ---- Web-Push Opt-in (P1.7, Slice B) ---------------------------------------
//
// Dezenter Opt-in im Posteingang-Bereich. NIE beim Load nagen — der
// Permission-Prompt läuft AUSSCHLIESSLICH auf die explizite Nutzer-Geste (Klick).
// Zustände: unkonfiguriert (VAPID-Public-Key leer) → komplett versteckt;
// `default` → Button; `granted` + Abo vorhanden → dezenter „aktiv"-Hinweis;
// `denied` → dezenter „deaktiviert"-Hinweis. Der Public-Key kommt aus
// GET /mail/push/key (leer = Feature nicht scharfgeschaltet).

/** VAPID-Public-Key (base64url) → Uint8Array für `applicationServerKey`. Rückgabe
 *  explizit über einen `ArrayBuffer` (nicht `ArrayBufferLike`), damit der Wert dem
 *  `BufferSource`-Typ von `pushManager.subscribe` genügt (TS 5.7 lib.dom). */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function PushOptIn() {
  const toast = useToast();
  const mt = useT().mail.benachrichtigung;
  const [pubKey, setPubKey] = useState<string | null>(null); // null = noch unbekannt
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  // Public-Key + evtl. bestehendes Abo einmalig (still) laden.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<{ public_key: string }>("/mail/push/key");
        if (!cancelled) setPubKey((res?.public_key || "").trim());
      } catch {
        if (!cancelled) setPubKey(""); // Fehler → wie unkonfiguriert behandeln
      }
    })();
    void (async () => {
      try {
        if (!("serviceWorker" in navigator)) return;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled && sub) setSubscribed(true);
      } catch {
        /* SW/PushManager nicht verfügbar → als „nicht abonniert" behandeln. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    if (!pubKey || busy) return;
    if (typeof Notification === "undefined" || !("serviceWorker" in navigator)) {
      const b = katalog().mail.benachrichtigung;
      toast.err(b.nichtVerfuegbar, b.nichtVerfuegbarText);
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission(); // NUR per Nutzer-Geste
      setPermission(perm);
      if (perm !== "granted") {
        setBusy(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pubKey),
      });
      await api.post("/mail/push/subscribe", sub.toJSON());
      setSubscribed(true);
      const b = katalog().mail.benachrichtigung;
      toast.ok(b.aktiv, b.aktivText);
    } catch (e) {
      const b = katalog().mail.benachrichtigung;
      toast.err(b.aktivierungFehlgeschlagen, (e as Error)?.message || b.bitteErneut);
    } finally {
      setBusy(false);
    }
  }, [pubKey, busy, toast]);

  // Unkonfiguriert (Key leer/noch unbekannt) oder Browser ohne Support → nichts rendern.
  if (!pubKey || permission === "unsupported") return null;

  if (permission === "denied") {
    return (
      <span
        data-push-optin="denied"
        className="inline-flex items-center gap-1.5 text-xs text-text-3"
        title={mt.imBrowserDeaktiviertTitel}
      >
        <Bell className="w-3.5 h-3.5" />
        {mt.imBrowserDeaktiviert}
      </span>
    );
  }

  if (permission === "granted" && subscribed) {
    return (
      <span
        data-push-optin="active"
        className="inline-flex items-center gap-1.5 text-xs text-accent-600"
      >
        <BellRing className="w-3.5 h-3.5" />
        {mt.aktiv}
      </span>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      icon={<Bell className="w-4 h-4" />}
      onClick={() => void enable()}
      loading={busy}
      data-push-optin="prompt"
    >
      {mt.aktivieren}
    </Button>
  );
}

// ---- Abwesenheitsnotiz / Out-of-Office (P2.12) -----------------------------
//
// EIN zentriertes Modal (an/aus prominent), optional Zeitraum (von/bis) +
// intern-/extern-Text + Audience. Egress: GET/PUT /mail/out-of-office (UPN aus
// dem Token). WH-Postfach (nicht freigeschaltet) → 403 → freundlicher „nicht
// freigeschaltet"-Zustand. Fehlt der Graph-Scope MailboxSettings.ReadWrite,
// kommt GET/PUT live als 502 → Info-Zustand (Speichern gesperrt) bzw. Inline-
// Fehler beim Speichern (Modal bleibt offen, kein Toast-Spam).

interface OutOfOfficeDto {
  enabled: boolean;
  scheduled: boolean;
  start: string | null;
  end: string | null;
  internal_message: string;
  external_message: string;
  external_audience: string;
}

// Graph liefert die Texte als HTML; die Textareas zeigen Plaintext.
// <br>/</p>/</div>/… → Zeilenumbruch, restliche Tags strippen, Entities
// entschärfen (via DOMParser).
function htmlToPlain(html: string): string {
  if (!html) return "";
  const withBreaks = html
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|li|tr|h[1-6])\s*>/gi, "\n");
  const noTags = withBreaks.replace(/<[^>]+>/g, "");
  const decoded =
    new DOMParser().parseFromString(noTags, "text/html").body.textContent || "";
  return decoded.replace(/\n{3,}/g, "\n\n").trim();
}

// Plaintext → HTML für Graph: escapen (reuse aus mailRichText), dann
// Zeilenumbrüche → <br>.
function plainToOooHtml(text: string): string {
  return escapeHtml(text).replace(/\n/g, "<br>");
}

function OutOfOfficeModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const mt = useT().mail.abwesenheit;
  const ct = useT().common;
  const [loading, setLoading] = useState(true);
  // null = ok; "not_enabled" = 403 (Postfach nicht freigeschaltet);
  // "unavailable" = GET-502 (Scope/Graph) → Info-Zustand + Speichern gesperrt.
  const [loadError, setLoadError] = useState<null | "not_enabled" | "unavailable">(
    null
  );
  const [enabled, setEnabled] = useState(false);
  const [scheduled, setScheduled] = useState(false);
  // Zeitraum als deutsches Datum + Uhrzeit-Auswahl statt `datetime-local` (das
  // rendert im englischen Chrome „mm/dd/yyyy --:-- --" mitten im deutschen
  // Dialog). `start`/`end` bleiben unten die lokal-naive Form „YYYY-MM-DDTHH:MM"
  // und werden nur noch ABGELEITET — der PUT-Vertrag (`new Date(v).toISOString()`)
  // ist damit unverändert.
  const [startDateText, setStartDateText] = useState("");
  const [startTime, setStartTime] = useState("08:00");
  const [endDateText, setEndDateText] = useState("");
  const [endTime, setEndTime] = useState("17:00");
  const [internalMsg, setInternalMsg] = useState("");
  const [externalMsg, setExternalMsg] = useState("");
  const [audience, setAudience] = useState("all");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api
      .get<OutOfOfficeDto>("/mail/out-of-office")
      .then((res) => {
        if (cancelled) return;
        setEnabled(!!res.enabled);
        setScheduled(!!res.scheduled);
        // Graph-dateTime (naive-local, evtl. mit Sekunden/Millis) → in deutsches
        // Datum + Uhrzeit zerlegen. Fehlt ein Wert, bleibt die Default-Uhrzeit
        // stehen (das Datumsfeld bleibt leer = kein Zeitraum gesetzt).
        const s = splitLocalNaive(res.start ? res.start.slice(0, 16) : "");
        const e = splitLocalNaive(res.end ? res.end.slice(0, 16) : "");
        setStartDateText(s.date);
        if (s.time) setStartTime(s.time);
        setEndDateText(e.date);
        if (e.time) setEndTime(e.time);
        setInternalMsg(htmlToPlain(res.internal_message || ""));
        setExternalMsg(htmlToPlain(res.external_message || ""));
        setAudience(res.external_audience || "all");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setLoadError("not_enabled");
        else setLoadError("unavailable");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Bezugsdatum für zweistellige Jahre („1.8.26"), pro Dialog-Öffnung fixiert.
  const dateRef = useMemo(() => new Date(), []);
  // Anzeige (deutsch) → lokal-naiver Zeitpunkt für den PUT. Unlesbares Datum
  // ergibt "" und damit `null` im Body — NIE ein geratener Zeitpunkt.
  const start = useMemo(
    () => joinLocalNaive(startDateText, startTime, dateRef),
    [startDateText, startTime, dateRef]
  );
  const end = useMemo(
    () => joinLocalNaive(endDateText, endTime, dateRef),
    [endDateText, endTime, dateRef]
  );
  // „Zeitraum an" ohne verwertbaren Zeitraum darf NICHT still weggeschickt
  // werden. Zwei getrennte Befunde, weil sie verschiedene Ursachen haben:
  //   *Invalid* = getippt, aber kein Datum daraus zu lesen („32.13.").
  //   *Missing* = leer. Entsteht auf ZWEI Wegen: der Server liefert
  //     `status == "scheduled"` ohne `scheduled_start/-end` (Router
  //     app/routers/mail.py:837-839 reicht `None` durch → Ladepfad oben setzt
  //     `scheduled` an und lässt die Textfelder leer), oder der Nutzer leert
  //     das Feld wieder. Die alte Bedingung verlangte `trim() !== ""` und hielt
  //     „leer" deshalb für in Ordnung: Speichern blieb frei und schickte
  //     `{scheduled:true, start:null, end:null}` — eine Abwesenheitsnotiz, die
  //     laut Zustand einen Zeitraum hat und keinen kennt.
  const startDateInvalid = startDateText.trim() !== "" && !start;
  const endDateInvalid = endDateText.trim() !== "" && !end;
  const startDateMissing = enabled && scheduled && startDateText.trim() === "";
  const endDateMissing = enabled && scheduled && endDateText.trim() === "";
  const scheduleIncomplete = startDateMissing || endDateMissing;
  const scheduleInvalid =
    enabled &&
    scheduled &&
    (startDateInvalid || endDateInvalid || scheduleIncomplete);

  /** „Zeitraum festlegen" ist ein MODUS-Schalter — er darf den Modus nicht ohne
   *  Zeitraum lassen. Beim EINschalten aus dem leeren Zustand wird deshalb ein
   *  Vorschlag eingetragen (heute → in einer Woche, mit den Default-Uhrzeiten);
   *  der Nutzer überschreibt ihn, aber „geplant an, kein Zeitraum" entsteht auf
   *  diesem Weg gar nicht erst. Nur wenn BEIDE Felder leer sind: bei einem
   *  halb gefüllten Zeitraum wäre „heute + 7" womöglich vor dem Startdatum —
   *  dort greift die Unvollständigkeits-Meldung. Beim AUSschalten bleibt alles
   *  stehen (Rückweg ohne Datenverlust). Ein vom SERVER gelieferter
   *  `scheduled`-Zustand wird NICHT vorbelegt: einen Zeitraum zu erfinden,
   *  den das Postfach nicht hat, wäre eine stille Änderung. */
  const toggleScheduled = () => {
    if (!scheduled && !startDateText.trim() && !endDateText.trim()) {
      const from = new Date(dateRef);
      const to = new Date(dateRef);
      to.setDate(to.getDate() + 7);
      setStartDateText(fmtDeDate(from));
      setEndDateText(fmtDeDate(to));
    }
    setScheduled((v) => !v);
  };

  /** Beim Verlassen des Feldes auf TT.MM.JJJJ normalisieren (1.8. → 01.08.2026). */
  const normalizeOooDate = (v: string, set: (s: string) => void) => {
    if (!v.trim()) return;
    const d = parseDeDate(v, dateRef);
    if (d) set(fmtDeDate(d));
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    const useSchedule = enabled && scheduled;
    const body = {
      enabled,
      scheduled: useSchedule,
      // Slice B / Driver-Fix #1: datetime-local → echtes ISO-UTC. Die sb_*-
      // Schicht rechnet es in Graphs {dateTime, timeZone}-Form (Berlin) um.
      start: useSchedule && start ? new Date(start).toISOString() : null,
      end: useSchedule && end ? new Date(end).toISOString() : null,
      internal_message: plainToOooHtml(internalMsg),
      external_message: plainToOooHtml(externalMsg),
      external_audience: audience,
    };
    try {
      await api.put("/mail/out-of-office", body);
      const a = katalog().mail.abwesenheit;
      toast.ok(enabled ? a.aktiviert : a.deaktiviert);
      onClose();
    } catch (e: unknown) {
      // Modal bleibt offen; Inline-Fehler statt Toast-Spam.
      const a = katalog().mail.abwesenheit;
      if (e instanceof ApiError && e.status === 403) {
        setSaveError(a.fehler403);
      } else {
        setSaveError(a.fehlerSonst);
      }
    } finally {
      setSaving(false);
    }
  };

  const saveDisabled =
    saving || loading || loadError !== null || scheduleInvalid;

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      icon={<CalendarClock className="w-4 h-4" />}
      title={mt.titel}
      footer={
        loadError === "not_enabled" ? (
          <Button variant="secondary" size="sm" onClick={onClose}>
            {ct.schliessen}
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={saving}
            >
              {ct.abbrechen}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void save()}
              loading={saving}
              disabled={saveDisabled}
              data-ooo-save
            >
              {ct.speichern}
            </Button>
          </>
        )
      }
    >
      {loading ? (
        <div className="py-6 text-sm text-text-2">{mt.laedt}</div>
      ) : loadError === "not_enabled" ? (
        <div data-ooo-state="not_enabled" className="py-4 text-sm text-text-2">
          {mt.nichtFreigeschaltet}
        </div>
      ) : (
        <div className="space-y-4">
          {loadError === "unavailable" && (
            <div
              data-ooo-state="unavailable"
              className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-text-2"
            >
              {mt.nichtVerfuegbar}
            </div>
          )}

          {/* An/Aus — prominent */}
          <div className="flex items-center justify-between rounded-lg border border-border bg-surface-2 px-3 py-2.5">
            <div className="min-w-0">
              <div className="text-sm font-medium text-text">
                {mt.automatischeAntwort}
              </div>
              <div className="text-xs text-text-3">
                {enabled ? mt.aktiv : mt.aus}
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label={mt.aktivierenAria}
              data-ooo-toggle
              onClick={() => setEnabled((v) => !v)}
              className={clsx(
                "relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors",
                enabled ? "bg-accent-500" : "bg-border"
              )}
            >
              <span
                className={clsx(
                  "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
                  enabled ? "translate-x-6" : "translate-x-1"
                )}
              />
            </button>
          </div>

          {enabled && (
            <>
              <Field label={mt.internerText} hint={mt.internerTextHinweis}>
                <Textarea
                  rows={4}
                  value={internalMsg}
                  onChange={(e) => setInternalMsg(e.target.value)}
                  placeholder={mt.internerTextPlatzhalter}
                  data-ooo-internal
                />
              </Field>

              {/* Zeitraum (Slice B) */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-text">
                    {mt.zeitraumFestlegen}
                  </div>
                  <div className="text-xs text-text-3">
                    {scheduled ? mt.zeitraumAktiv : mt.zeitraumInaktiv}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={scheduled}
                  aria-label={mt.zeitraumFestlegen}
                  data-ooo-scheduled
                  onClick={toggleScheduled}
                  className={clsx(
                    "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                    scheduled ? "bg-accent-500" : "bg-border"
                  )}
                >
                  <span
                    className={clsx(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      scheduled ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>
              {scheduled && (
                <div className="space-y-3">
                  {/* Benannter Unvollständigkeits-Zustand: „geplant an, kein
                      Zeitraum" ist damit sichtbar statt still speicherbar. */}
                  {scheduleIncomplete && (
                    <div
                      data-ooo-schedule-incomplete
                      className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger-soft px-3 py-2 text-xs text-danger"
                    >
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>
                        {mt.zeitraumUnvollstaendigA}
                        {startDateMissing && endDateMissing
                          ? mt.zeitraumBeideFehlen
                          : startDateMissing
                            ? mt.zeitraumVonFehlt
                            : mt.zeitraumBisFehlt}
                        {mt.zeitraumUnvollstaendigB}
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label={mt.vonDatum}
                      error={
                        startDateInvalid
                          ? mt.datumFormat
                          : startDateMissing
                            ? mt.datumFehlt
                            : undefined
                      }
                    >
                      <Input
                        value={startDateText}
                        onChange={(e) => setStartDateText(e.target.value)}
                        onBlur={(e) =>
                          normalizeOooDate(e.target.value, setStartDateText)
                        }
                        placeholder="TT.MM.JJJJ"
                        inputMode="numeric"
                        aria-label={mt.vonDatumAria}
                        data-ooo-start
                      />
                    </Field>
                    <Field label={mt.vonUhrzeit}>
                      <Select
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        aria-label={mt.vonUhrzeitAria}
                        data-ooo-start-time
                      >
                        {withCurrent(TIME_OPTIONS, startTime).map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label={mt.bisDatum}
                      error={
                        endDateInvalid
                          ? mt.datumFormat
                          : endDateMissing
                            ? mt.datumFehlt
                            : undefined
                      }
                    >
                      <Input
                        value={endDateText}
                        onChange={(e) => setEndDateText(e.target.value)}
                        onBlur={(e) =>
                          normalizeOooDate(e.target.value, setEndDateText)
                        }
                        placeholder="TT.MM.JJJJ"
                        inputMode="numeric"
                        aria-label={mt.bisDatumAria}
                        data-ooo-end
                      />
                    </Field>
                    <Field label={mt.bisUhrzeit}>
                      <Select
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        aria-label={mt.bisUhrzeitAria}
                        data-ooo-end-time
                      >
                        {withCurrent(TIME_OPTIONS, endTime).map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                </div>
              )}

              {/* Externe Empfänger + Text (Slice B) */}
              <Field label={mt.externeEmpfaenger}>
                <Select
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  data-ooo-audience
                >
                  <option value="none">{mt.externKeine}</option>
                  <option value="contactsOnly">{mt.externKontakte}</option>
                  <option value="all">{mt.externAlle}</option>
                </Select>
              </Field>
              {audience !== "none" && (
                <Field label={mt.externerText} hint={mt.externerTextHinweis}>
                  <Textarea
                    rows={4}
                    value={externalMsg}
                    onChange={(e) => setExternalMsg(e.target.value)}
                    placeholder={mt.externerTextPlatzhalter}
                    data-ooo-external
                  />
                </Field>
              )}
            </>
          )}

          {saveError && (
            <div
              data-ooo-error
              className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger"
            >
              {saveError}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ---- Seite -----------------------------------------------------------------

// X1: Kürzel-Übersicht (Doc 04). Zwei-Spalten-<kbd>-Tabelle, gespeist AUSSCHLIESSLICH
// aus shortcuts.ts (SSoT) → keine Divergenz zwischen Overlay, Tooltips und Palette.
function ShortcutsOverlay({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const mt = useT().mail.kuerzel;
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={mt.titel}
      icon={<Keyboard className="w-4 h-4" />}
      description={mt.beschreibung}
    >
      <div className="space-y-0.5">
        {SHORTCUTS.map((s) => (
          <div
            key={s.action}
            data-shortcut-row
            className="flex items-center justify-between gap-4 border-b border-border/60 py-2 last:border-0"
          >
            <span className="text-sm text-text">{shortcutLabel(s.action)}</span>
            <span className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
              {s.keys.map((k, i) => (
                <span key={k} className="flex items-center gap-1.5">
                  {i > 0 && <span className="text-[10px] text-text-3">/</span>}
                  <kbd className="whitespace-nowrap rounded-md border border-border px-1.5 py-0.5 text-[11px] text-text-2">
                    {k}
                  </kbd>
                </span>
              ))}
              {s.note && (
                <span className="text-[11px] text-text-3">({mt[s.note]})</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

const REFRESH_MS = 120_000;

/** Eine Abfrage — Volltext plus die Slice-B-Filter. */
interface SearchQuery {
  q: string;
  from: string;
  since: string;
  until: string;
}

/** Benennt eine Abfrage in einem Satzteil, damit die Oberfläche sagen kann, zu
 *  WELCHER Frage die Liste unten gehört. Datumswerte in deutscher Anzeige — der
 *  ISO-Wert ist ein API-Detail und hat am Bildschirm nichts verloren. */
function describeQuery(q: SearchQuery): string {
  const mt = katalog().mail.suche;
  return [
    q.q && mt.begriff(q.q),
    q.from && mt.vonWem(q.from),
    q.since && mt.abDatum(isoToDeDate(q.since)),
    q.until && mt.bisDatum(isoToDeDate(q.until)),
  ]
    .filter(Boolean)
    .join(" · ");
}

const sameSearchQuery = (a: SearchQuery | null, b: SearchQuery) =>
  !!a && a.q === b.q && a.from === b.from && a.since === b.since && a.until === b.until;

export default function MailPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("m");

  const toast = useToast();
  const mt = useT().mail;
  const ct = useT().common;
  const [inbox, setInbox] = useState<InboxResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  // Empfänger-Autocomplete-Basis (still geladen) + „Neue Mail"-Composer-Modal.
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [composeNew, setComposeNew] = useState(false);
  // Abwesenheitsnotiz-Modal (P2.12) — mailbox-weit, im PageHeader.
  const [oooOpen, setOooOpen] = useState(false);
  // X1: Tastenkürzel-Übersicht (F1 / ? / „Kürzel"-Chip / Palette „Mail: Tastenkürzel").
  const [helpOpen, setHelpOpen] = useState(false);
  // X1: einmaliger First-Run-Hinweis im Posteingang (localStorage-gestützt).
  const [kbdHintSeen, setKbdHintSeen] = useState(() => isMailKbdHintSeen());

  // ── Multi-/Funktionspostfach (P2.13) ────────────────────────────────────────
  // Server-berechnete Umschalter-Menge (eigenes + freigegebene Funktionspostfächer)
  // aus GET /mail/mailboxes + aktives Postfach. `activeMailbox` leer ODER == eigenes
  // → eigenes Postfach (heutiges Verhalten, kein `?mailbox=`). Ein freigegebenes
  // Postfach schaltet die Ansicht READ-ONLY (P1.5-Mechanik) und hängt `?mailbox=`
  // an ALLE Lese-Fetches (zentraler `withMailbox`-Helfer). Senden/Handeln bleibt
  // owner-only (R1-Grenze) → Composer/Abwesenheitsnotiz im Shared-Scope versteckt.
  const [mailboxes, setMailboxes] = useState<MailboxOut[]>([]);
  const [ownMailbox, setOwnMailbox] = useState<string>("");
  const [activeMailbox, setActiveMailbox] = useState<string>("");
  const sharedScope =
    !!activeMailbox &&
    !!ownMailbox &&
    activeMailbox.toLowerCase() !== ownMailbox.toLowerCase();
  // Für den Umschalter-Wert: leer → eigenes Postfach.
  const effectiveMailbox = activeMailbox || ownMailbox;

  // Spiegel des aktuellen Inbox-States für markRead: hält den useCallback stabil
  // (leere Deps) und stale-closure-frei — Effizienz-Guard + Rollback lesen immer
  // den frischen Stand.
  const inboxRef = useRef<InboxResponse | null>(null);
  useEffect(() => {
    inboxRef.current = inbox;
  }, [inbox]);

  // ── Datei-Drop NEBEN den Composer abfangen ────────────────────────────────
  // Ohne diesen Wächter ersetzt eine danebengefallene PDF die ganze Seite durch
  // den eingebauten Browser-Viewer — der halb geschriebene Entwurf ist dann weg
  // (genau die Beobachtung des Melders: „dann öffnet er das PDF"). Der Wächter
  // hängt an ``window`` und verschluckt Datei-Züge NUR dort, wo sie niemand
  // annimmt: die Composer-Karte behandelt ihren Drop vorher (React-Wurzel liegt
  // unter ``window``, ihr Handler läuft also zuerst) und hängt die Datei an.
  // Reagiert ausschließlich auf ``Files``-Züge — der Anhang-Zug ins Dateisystem
  // (``anlageZiehStart``, nur ``DownloadURL``) bleibt unberührt.
  useEffect(() => {
    const abfangen = (e: DragEvent) => {
      if (ziehtDateien(e.dataTransfer)) e.preventDefault();
    };
    window.addEventListener("dragover", abfangen);
    window.addEventListener("drop", abfangen);
    return () => {
      window.removeEventListener("dragover", abfangen);
      window.removeEventListener("drop", abfangen);
    };
  }, []);

  // Ungelesen-Zähler (P1.7, Slice A): rein client-seitig aus dem SCHON geladenen
  // Inbox-State ableiten — KEIN zweiter Fetch/Egress. Speist den Badge an der
  // Mail-Nav (Sidebar/Kachel) UND am Posteingang-Tab über den geteilten Provider.
  // Läuft auf jede Inbox-Änderung (Poll-Refresh + markRead-Flip). `inbox === null`
  // (initial, vor dem ersten Load) lässt den persistierten Wert unangetastet.
  const { unreadCount, setUnreadCount } = useMailUnread();
  useEffect(() => {
    if (!inbox) return;
    setUnreadCount(inbox.messages.filter((m) => !m.is_read).length);
  }, [inbox, setUnreadCount]);

  // ── Suche (P0.2) ──────────────────────────────────────────────────────────
  // Volltext-Query + Slice-B-Filter (Von/Zeitraum). `searchResults` ist die
  // aktive Render-Quelle, sobald eine Suche läuft (null = Inbox sichtbar).
  const [search, setSearch] = useState("");
  const [searchFrom, setSearchFrom] = useState("");
  // Zeitraum-Filter: ZWEI Ebenen. `searchSince`/`searchUntil` sind der
  // API-Wert („YYYY-MM-DD", genau wie bisher an /mail/search), `sinceText`/
  // `untilText` die deutsche ANZEIGE (TT.MM.JJJJ). Getrennt, weil ein natives
  // `<input type="date">` im englischen Chrome MM/DD/YYYY rendert — die Anzeige
  // muss also selbst gebaut werden, der Egress-Wert bleibt unverändert ISO.
  // Unlesbarer Text → ISO leer → der Filter reist NICHT mit (kein Fehlwert),
  // stattdessen steht eine sichtbare Meldung am Feld.
  const [searchSince, setSearchSince] = useState("");
  const [searchUntil, setSearchUntil] = useState("");
  const [sinceText, setSinceText] = useState("");
  const [untilText, setUntilText] = useState("");
  // Fokus-Zustand der beiden Felder, NUR für die Fehleranzeige: „12.0" ist beim
  // Tippen ein Zwischenstand, keine Fehleingabe. Die Meldung erscheint deshalb
  // erst, wenn das Feld den Fokus verlässt — sonst blitzt bei JEDER Eingabe ein
  // Fehler auf, der keiner ist.
  const [sinceFocused, setSinceFocused] = useState(false);
  const [untilFocused, setUntilFocused] = useState(false);
  const [searchResults, setSearchResults] = useState<InboxResponse | null>(null);
  const [searching, setSearching] = useState(false);
  // Ausfall ≠ Leere. Vorher wurde JEDER Fehler (Netzabbruch, 500, und vor allem
  // der 503 `email_service_unavailable`) zu einer leeren Trefferliste, und die
  // Liste zeigte dann „Keine Treffer — Suchbegriff oder Filter anpassen". Das
  // schickt den Nutzer auf die Suche nach einem Fehler bei sich selbst, während
  // in Wahrheit das Backend weg war. Derselbe Zweig existiert für den
  // Ordner-Aufklapper (`foldersError`).
  const [searchError, setSearchError] = useState(false);
  // Spiegel der Such-Treffer für die Mutations-Handler (dual-state, wie inboxRef):
  // markRead/archive/delete/Rollback müssen BEIDE Quellen pflegen, sonst „springt"
  // eine Treffer-Row nicht mit.
  const searchResultsRef = useRef<InboxResponse | null>(null);
  useEffect(() => {
    searchResultsRef.current = searchResults;
  }, [searchResults]);
  // Sequenz-Guard gegen out-of-order-Antworten (nur die neueste Query zählt) +
  // Ref auf die Suchbox (F3-Fokus) + Ref auf den aktiven Tab (URL-abgeleitet,
  // stale-closure-frei für den globalen keydown-Listener).
  const searchSeqRef = useRef(0);
  // Bricht die laufende Suchanfrage WIRKLICH ab (nicht nur ihr Ergebnis).
  const searchAbortRef = useRef<AbortController | null>(null);
  const searchBoxRef = useRef<HTMLInputElement | null>(null);
  const tabRef = useRef<string>("inbox");

  // ── Ordner (P1.5) ─────────────────────────────────────────────────────────
  // Ordner-Ansicht ist TRIAGE-FREI: `folderMessages` nimmt an KEINEM Mutations-
  // Handler teil (markRead/archive/delete berühren nur `inbox`/`searchResults`).
  // `folderView` = "inbox" (Default), ein `well_known_name` ODER eine folder_id.
  // Folder und Suche bleiben mutual-exklusiv (ein Chip-Klick leert die Suche),
  // aber seit F-35 wirft die Suche den Ordner nicht mehr weg: sie sucht IN ihm.
  const [folderView, setFolderView] = useState<string>("inbox");
  // F-35: ausdrücklicher Wunsch „im ganzen Postfach" — der Ausweg für den Fall,
  // dass man vom Posteingang aus eine gesendete Mail sucht. Er hält, bis der
  // Nutzer den Ordner wechselt (dort ist die Frage neu zu stellen), NICHT nur
  // bis zur nächsten Eingabe: sonst müsste man ihn bei jeder Suche neu klicken.
  const [sucheGanzesPostfach, setSucheGanzesPostfach] = useState(false);
  const [folderMessages, setFolderMessages] = useState<InboxResponse | null>(null);
  const [folderLoading, setFolderLoading] = useState(false);
  const folderSeqRef = useRef(0);
  // Zählt Lade-ANFORDERUNGEN (nicht Scope-Wechsel) — s. `selectFolder`.
  const [folderReloadNonce, setFolderReloadNonce] = useState(0);
  // Eigene Ordner (lazy: erst beim ersten Aufklappen des „Ordner"-Aufklappers
  // geladen — der Default-Mount feuert NUR /mail/inbox).
  const [folders, setFolders] = useState<FolderOut[] | null>(null);
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);
  // Ordner-Abruf gescheitert (≠ „Postfach hat keine weiteren Ordner").
  const [foldersError, setFoldersError] = useState(false);
  // F-05: imid der Mail, für die der „In Ordner verschieben"-Dialog offen ist
  // (null = zu). Der Dialog nutzt DIESELBE `folders`-Quelle wie der Aufklapper —
  // eine zweite Ordner-Liste wäre eine zweite Wahrheit.
  const [moveFor, setMoveFor] = useState<string | null>(null);
  // F-15: Zielordner-Vorschlag. `moveSuggestions` sind die 1-3 Ordner, in denen
  // die Maildomain DIESES Absenders bisher am häufigsten lag (Server zählt,
  // kein Modell). Leer ist der Normalfall — interne Absender und Absender ohne
  // Ablage-Historie bekommen bewusst gar keinen Vorschlag, weil ein falscher
  // mehr Vertrauen kostet als ein fehlender.
  const [moveSuggestions, setMoveSuggestions] = useState<FolderSuggestion[]>([]);
  // F-15: Filter im Verschieben-Dialog. Er schränkt nur die ANZEIGE ein und
  // ändert den Aufklapp-Zustand NICHT — beim Leeren steht wieder da, was der
  // Nutzer selbst aufgeklappt hatte.
  const [moveFilter, setMoveFilter] = useState("");
  const moveFilterRef = useRef<HTMLInputElement | null>(null);
  // Callback-Ref statt useRef-Zuweisung: die Ordner werden LAZY geholt, das
  // Filterfeld existiert beim Öffnen also oft noch gar nicht (Ladezustand).
  // `onOpenAutoFocus` liefe dann ins Leere und der Fokus bliebe auf dem
  // Auslöser. Eine stabile useCallback-Ref feuert genau beim Anhängen — egal
  // ob sofort oder erst nach der Antwort — und NICHT bei jedem Tastendruck.
  const setMoveFilterEl = useCallback((el: HTMLInputElement | null) => {
    moveFilterRef.current = el;
    el?.focus();
  }, []);
  // Das Bedienelement, das den Dialog geöffnet hat — dorthin geht der Fokus
  // nach Escape zurück (Hausregel, vgl. Ordner-Aufklapper).
  const moveTriggerRef = useRef<HTMLElement | null>(null);
  // Der Aufklapper hing als `absolute` im scrollenden <aside> (380px,
  // `overflow-y-auto`) — CSS erzwingt damit auch horizontales Clipping, das
  // Menü wurde zur Hälfte abgeschnitten. Es rendert deshalb per Portal mit
  // `position: fixed` am Body und wird an der Schaltfläche ausgerichtet.
  const foldersBtnRef = useRef<HTMLButtonElement | null>(null);
  const folderMenuRef = useRef<HTMLDivElement | null>(null);
  const [folderMenuBox, setFolderMenuBox] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  // Aufgeklappte Ordner (folder_id). Beim Laden wird NUR der Pfad zum aktiven
  // Ordner geöffnet — wolfgang.hansen hat 66 Ordner, alles offen wäre eine
  // 66-Zeilen-Wand.
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    () => new Set()
  );

  // Entwürfe (P1.8, Slice B): der „Entwürfe"-Chip lädt aus GET /mail/drafts
  // (nicht /mail/folder/drafts — echte Graph-Entwürfe haben oft KEINE
  // internetMessageId, der Lese-Detail-Klick wäre live 404 gelaufen). Row-Klick
  // öffnet den EINEN Composer vorbefüllt (Weiter-Editieren statt Lese-Detail).
  const [drafts, setDrafts] = useState<DraftOut[] | null>(null);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [resumeDraft, setResumeDraft] = useState<DraftOut | null>(null);
  const draftsSeqRef = useRef(0);

  // Nachverfolgung: eigene Tracks (Quelle für Inbox-Marker + Tab).
  const [tracks, setTracks] = useState<FollowUpTrack[] | null>(null);
  const [tracksLoading, setTracksLoading] = useState(true);
  const [tracksError, setTracksError] = useState<ApiError | null>(null);

  // Konfigurierbare Mail-Tiefe (P1.11): Preset → top/days statt hart 25/14
  // (Werte ≤ Backend-Caps top≤100/days≤90). Der Wechsel ändert die
  // `loadInbox`-Identität (Deps [inboxTop, inboxDays]), wodurch der Lade-Effekt
  // GENAU EINEN Refetch mit der neuen Tiefe auslöst — KEIN zusätzlicher
  // manueller loadInbox()-Aufruf (sonst Doppel-Fetch). Der 120s-Poll nutzt
  // automatisch die aktuelle Tiefe. Dieselbe Tiefe gilt für Gesendet
  // (`loadFolder`, top+days) und Entwürfe (`loadDrafts`, NUR top).
  //
  // Persistenz: server-seitig am NUTZER (user_settings.mail_inbox_depth via
  // GET/PUT /settings) statt im localStorage → gilt geräteübergreifend und ist
  // in den Einstellungen (Tab „E-Mail") als Standard wählbar. `depthReady`
  // gated ALLE Listen-Loader: erst wenn die Einstellung da ist, wird geladen —
  // sonst feuerte der erste Fetch mit dem Default und direkt danach ein zweiter
  // mit dem echten Wert.
  const { depth, ready: depthReady, setDepth } = useInboxDepth();
  const { top: inboxTop, days: inboxDays } = INBOX_DEPTH_PRESETS[depth];

  // F-06: „Beim Öffnen als gelesen markieren" — Nutzer-Einstellung aus derselben
  // Quelle (GET /settings), Default an. Kein eigener `ready`-Gate nötig: die
  // Listen-Loader hängen bereits an `depthReady`, also existiert überhaupt erst
  // eine anklickbare Zeile, nachdem /settings geantwortet hat.
  const { autoMarkRead } = useAutoMarkRead();

  // Zentraler Helfer (P2.13): hängt `?/&mailbox=` NUR im Shared-Scope an — genutzt
  // von ALLEN Lese-Fetches (kein Copy-Paste). Als useCallback → beim Postfach-
  // Wechsel ändert sich die `loadInbox`-Identität, und der Mount-Effekt feuert
  // GENAU EINEN Refetch mit dem neuen Postfach (dieselbe Dep-Mechanik wie die
  // Posteingang-Tiefe P1.11 — kein manueller Doppel-Call). Behandelt sowohl frische
  // (`?`) als auch bestehende (`&`) Query-Strings korrekt.
  const withMailbox = useCallback(
    (url: string): string => {
      if (!sharedScope) return url;
      const sep = url.includes("?") ? "&" : "?";
      return `${url}${sep}mailbox=${encodeURIComponent(activeMailbox)}`;
    },
    [sharedScope, activeMailbox]
  );

  const loadInbox = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const res = await api.get<InboxResponse>(
        withMailbox(`/mail/inbox?top=${inboxTop}&days=${inboxDays}`)
      );
      setInbox(res);
    } catch (e) {
      setError(e as ApiError);
    } finally {
      setLoading(false);
    }
  }, [inboxTop, inboxDays, withMailbox]);

  const loadTracks = useCallback(async () => {
    setTracksLoading(true);
    setTracksError(null);
    try {
      const res = await api.get<FollowUpTrack[]>("/mail/follow-ups");
      setTracks(res);
    } catch (e) {
      setTracksError(e as ApiError);
    } finally {
      setTracksLoading(false);
    }
  }, []);

  // Mount-Load. Inbox und Tracks in GETRENNTEN Effekten: der Inbox-Load wartet
  // auf die Nutzer-Tiefe (`depthReady`), die Tracks nicht — lägen beide im
  // selben Effekt, würde `loadTracks` beim Ready-Wechsel ein zweites Mal feuern.
  useEffect(() => {
    if (!depthReady) return;
    void loadInbox();
  }, [depthReady, loadInbox]);
  useEffect(() => {
    void loadTracks();
  }, [loadTracks]);

  // Postfach-Umschalter-Menge EINMALIG laden (P2.13). Degradiert still: ohne
  // Freigabe kommt nur das eigene Postfach zurück → Umschalter bleibt versteckt.
  useEffect(() => {
    let alive = true;
    void api
      .get<MailboxListResponse>("/mail/mailboxes")
      .then((res) => {
        if (!alive) return;
        setOwnMailbox(res.active);
        setMailboxes(res.mailboxes ?? []);
      })
      .catch(() => {
        // still: kein Umschalter, eigenes Postfach bleibt Default.
      });
    return () => {
      alive = false;
    };
  }, []);

  // Kollegen einmalig (still) laden — Basis für das Empfänger-Autocomplete im
  // Composer (Muster CalendarMs.tsx). Degradiert zu leer (dann kein Dropdown).
  useEffect(() => {
    void (async () => {
      try {
        setColleagues(await api.get<Colleague[]>("/calendar/colleagues"));
      } catch {
        /* Autocomplete degradiert zu Freitext-only. */
      }
    })();
  }, []);

  const trackedImids = useMemo(
    () => new Set((tracks ?? []).map((t) => t.internet_message_id)),
    [tracks]
  );
  // Wie viele Vorgänge warten auf eine Entscheidung? Speist die Pille am
  // Nachverfolgungs-Tab. Abgeleitet aus derselben Liste, die der Tab rendert —
  // eine zweite Quelle wäre die Stelle, an der Zähler und Liste auseinanderlaufen.
  // Der Zaehler AM REITER zaehlt beides: die Vorgaenge im Auffangkorb UND die,
  // die gerade abwarten. Grund ist der Befund vom 19.08.: ein Vorgang, auf den
  // eine Vertroestung kam, sieht von aussen aus wie ein Vorgang, auf dem nichts
  // passiert — und genau daran las der Tester eine kaputte Engine. Ein Track in
  // `needs_decision` kann zusaetzlich abwartend sein; er wird nur EINMAL
  // gezaehlt (sonst staende bei einem Vorgang eine 2).
  const decisionCount = useMemo(
    () =>
      (tracks ?? []).filter(
        (t) => t.state === "needs_decision" || t.response_status === "holding"
      ).length,
    [tracks]
  );

  // Authoritatives State-Update aus einer Mutations-Antwort (upsert nach id) —
  // race-frei gegenüber einem sofortigen Reload-GET (dessen Commit evtl. noch
  // nicht sichtbar ist). removeTrack für DELETE.
  const applyTrack = useCallback((t: FollowUpTrack) => {
    setTracks((prev) => {
      const arr = prev ?? [];
      const i = arr.findIndex((x) => x.id === t.id);
      if (i >= 0) {
        const copy = [...arr];
        copy[i] = t;
        return copy;
      }
      return [t, ...arr];
    });
  }, []);
  const removeTrack = useCallback((id: string) => {
    setTracks((prev) => (prev ?? []).filter((t) => t.id !== id));
  }, []);

  // In-Flight-Guard: verhindert, dass ein Doppelklick aufs Marker-Icon zwei
  // POSTs derselben imid absetzt (zweiter Klick = no-op, solange der erste
  // POST läuft). Ref statt State → synchroner check-and-set, race-frei.
  const markInFlight = useRef<Set<string>>(new Set());
  // In-Flight-Guard für Zero-Inbox-Mutationen (Archiv/Löschen), analog markInFlight.
  const mutInFlight = useRef<Set<string>>(new Set());

  // ── X1: zentrale Tastatur-Verdrahtung ────────────────────────────────────────
  // Dock-Brücke: DetailPane spiegelt hierher seine `detailActions` +
  // `openReplySuggest` (s. DetailPane-`actionsRef`-Prop). null = kein Detail offen.
  const detailActionsRef = useRef<MailDetailActionsBridge | null>(null);
  // Spiegel des tastatur-relevanten States, damit der EINE window-keydown-Listener
  // stabil bleibt (kein Re-Subscribe) und trotzdem stets frische Werte liest.
  const kbdStateRef = useRef<{
    selected: string | null;
    tab: string;
    inFolderView: boolean;
    inDraftsView: boolean;
    sharedScope: boolean;
    rows: InboxItem[];
    select: (imid: string) => void;
    deleteMessage: (imid: string) => void;
  }>({
    selected: null,
    tab: "inbox",
    inFolderView: false,
    inDraftsView: false,
    sharedScope: false,
    rows: [],
    select: () => {},
    deleteMessage: () => {},
  });
  const markFollowUp = useCallback(
    async (item: InboxItem) => {
      const imid = item.internet_message_id;
      if (markInFlight.current.has(imid)) return;
      markInFlight.current.add(imid);
      try {
        const created = await api.post<FollowUpTrack>("/mail/follow-ups", {
          internet_message_id: imid,
          subject: item.subject,
          from_address: item.from_address,
          from_name: item.from_name,
          received_at: item.received_at,
          body_preview: item.body_preview,
          // Voller Verteiler → Reply-all-Nachverfolgung (Server filtert den Owner).
          to_recipients: (item.to_recipients ?? []).map((r) => r.address),
          cc_recipients: (item.cc_recipients ?? []).map((r) => r.address),
        });
        applyTrack(created);
        if (created.state === "armed") {
          const firstReminder = (created.steps ?? [])
            .filter((s) => s.kind === "reminder")
            .sort((a, b) => a.seq - b.seq)[0];
          const when = firstReminder
            ? fmtDayMonth(fuParseDate(firstReminder.due_date))
            : null;
          const n = katalog().mail.nachfass;
          toast.ok(
            n.markiertLaeuftTitel,
            when ? n.markiertLaeuftText(when) : n.markiertLaeuftOhneDatum
          );
        } else if (created.internal_counterpart) {
          // Nicht „Empfänger fehlt" — der ist da. Der Track wartet bewusst auf
          // eine Bestätigung, weil das Gegenüber ein Kollege ist (#72a). Eine
          // falsche Begründung wäre hier schlimmer als keine: der Nutzer würde
          // im Anpassen-Dialog nach einem Empfänger suchen, der längst steht.
          const n = katalog().mail.nachfass;
          toast.ok(n.markiertTitel, n.markiertInternText);
        } else {
          const n = katalog().mail.nachfass;
          toast.ok(n.markiertTitel, n.markiertOffenText);
        }
      } catch (e) {
        toast.err(katalog().mail.nachfass.markierenFehlgeschlagen, (e as ApiError).message);
      } finally {
        markInFlight.current.delete(imid);
      }
    },
    [toast, applyTrack]
  );

  // Auto-Refresh alle 120s, aber NUR wenn der Tab sichtbar ist. Timer wird bei
  // Unsichtbarkeit pausiert und bei Rückkehr sofort einmal nachgeladen.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (document.visibilityState === "visible") void loadInbox({ silent: true });
      }, REFRESH_MS);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void loadInbox({ silent: true });
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [loadInbox]);

  // Lese-Status: optimistisch flippen + über den email-service nach Graph/Outlook
  // zurückschreiben (PATCH /mail/messages/{imid}/read). Effizienz-Guard: kein
  // Egress, wenn die Mail schon im Zielzustand ist (Auto-Mark beim Öffnen einer
  // bereits gelesenen Mail feuert NICHT). Fehler → still zurückrollen (kein
  // Toast/Dialog). Deep-Link auf eine nicht gelistete Mail: Egress ohne
  // Listen-Update.
  const markRead = useCallback((imid: string, read: boolean) => {
    // Dual-state (P0.2): den Lese-Status in BEIDEN Quellen flippen — Inbox UND
    // (falls aktiv) Such-Treffer. Sonst flippt der Dot einer Treffer-Row nicht.
    const flipOne = (resp: InboxResponse | null, val: boolean) =>
      resp
        ? {
            ...resp,
            messages: resp.messages.map((m) =>
              m.internet_message_id === imid ? { ...m, is_read: val } : m
            ),
          }
        : resp;
    const flip = (val: boolean) => {
      setInbox((c) => flipOne(c, val));
      setSearchResults((c) => flipOne(c, val));
    };
    const target =
      inboxRef.current?.messages.find((m) => m.internet_message_id === imid) ??
      searchResultsRef.current?.messages.find(
        (m) => m.internet_message_id === imid
      );
    if (target) {
      if (target.is_read === read) return; // schon im Zielzustand → kein Egress
      const prevRead = target.is_read;
      flip(read);
      void api
        .patch(`/mail/messages/${encodeURIComponent(imid)}/read`, { is_read: read })
        .catch(() => flip(prevRead));
    } else {
      void api
        .patch(`/mail/messages/${encodeURIComponent(imid)}/read`, { is_read: read })
        .catch(() => {});
    }
  }, []);

  const select = (imid: string) => {
    const next = new URLSearchParams(params);
    next.set("m", imid);
    setParams(next);
    // Auto-Mark beim Öffnen NUR im eigenen Posteingang-Scope (folderView==="inbox";
    // das gilt auch während einer aktiven Suche, die folderView auf "inbox" zwingt).
    // In Gesendet/Entwürfe/eigenen Ordnern NIEMALS — die Outlook-Lese-Wahrheit
    // bleibt unangetastet (nicht auf den is_read-Guard in markRead verlassen). Im
    // Shared-Postfach (P2.13) ebenfalls NIE: die Ansicht ist read-only, das
    // Öffnen einer fremden Mail darf ihren Lese-Status nicht verändern.
    //
    // `autoMarkRead` (F-06) ist die NUTZER-Einstellung darüber. Sie sitzt hier
    // an der EINEN Eintrittsstelle und deckt damit auch Suchtreffer und die
    // Tastatur-Navigation ab — beide laufen durch `select()`. Aus heisst: kein
    // PATCH beim blossen Öffnen; von Hand bleibt der Lese-Status über das
    // Kontextmenü der Zeile („Als (un)gelesen markieren") unverändert
    // umschaltbar — nur eben nicht mehr über ein eigenes Zeilen-Symbol.
    if (folderView === "inbox" && !sharedScope && autoMarkRead) markRead(imid, true);
  };
  const clearSelection = () => {
    const next = new URLSearchParams(params);
    next.delete("m");
    setParams(next);
  };

  // Zero-Inbox (P0.3): Archivieren/Löschen entfernt die Mail OPTIMISTISCH aus der
  // Liste und rollt bei Fehler STILL zurück (Vorbild markRead — kein Toast, kein
  // Dialog). War die Mail selektiert → clearSelection (das Detail würde sonst auf
  // 404 laufen). mutInFlight-Ref-Guard gegen Doppel-Feuer bei schnellem Doppelklick.
  // `onError` (F-05): Archivieren/Löschen rollen bewusst STILL zurück — die Mail
  // ist danach wieder da, wo sie war, und ein Toast für jeden verschluckten
  // Netz-Hüpfer wäre Lärm. Verschieben ist anders: der Nutzer hat ein ZIEL
  // gewählt, die Zeile verschwindet, und wenn sie zurückkommt, muss er erfahren
  // warum — sonst sieht es aus, als hätte die App seine Wahl ignoriert. Der
  // Parameter ist optional, damit die beiden bestehenden Aufrufer unverändert
  // still bleiben.
  // `onSuccess` (Lösch-Toast, 19.08.2026): läuft NACH der geglückten Mutation und
  // bekommt genau den Griff, der das optimistische Entfernen wieder aufhebt —
  // dieselben Backups und dieselben Positionen, die der Fehlerfall benutzt. So
  // gibt es EINE Wahrheit darüber, wie eine Zeile zurückkommt, statt einer
  // zweiten Kopie im Undo-Pfad. Erst NACH dem Erfolg, weil ein Toast „In
  // ‚Gelöschte Elemente' verschoben" über einem fehlgeschlagenen Löschen die
  // glatte Unwahrheit wäre.
  const removeAndMutate = (
    imid: string,
    call: (id: string) => Promise<unknown>,
    onError?: (e: unknown) => void,
    onSuccess?: (wiederEinfuegen: () => void) => void
  ) => {
    if (mutInFlight.current.has(imid)) return;
    // Dual-state (P0.2): aus BEIDEN Quellen entfernen + je eigene Position/Backup
    // für den stillen Rollback merken. So verschwindet eine Treffer-Row aus den
    // Treffern UND aus der Inbox (nach Suche-Leeren weg); ein Fehler rollt sie in
    // der aktiven Ansicht zurück.
    const curInbox = inboxRef.current;
    const inboxIndex = curInbox
      ? curInbox.messages.findIndex((m) => m.internet_message_id === imid)
      : -1;
    const inboxBackup =
      curInbox && inboxIndex >= 0 ? curInbox.messages[inboxIndex] : null;
    const curSearch = searchResultsRef.current;
    const searchIndex = curSearch
      ? curSearch.messages.findIndex((m) => m.internet_message_id === imid)
      : -1;
    const searchBackup =
      curSearch && searchIndex >= 0 ? curSearch.messages[searchIndex] : null;
    mutInFlight.current.add(imid);
    // optimistisch aus beiden Listen nehmen
    const removeOne = (resp: InboxResponse | null) =>
      resp
        ? { ...resp, messages: resp.messages.filter((m) => m.internet_message_id !== imid) }
        : resp;
    setInbox((c) => removeOne(c));
    setSearchResults((c) => removeOne(c));
    // war sie offen → Detail schließen (sonst läuft DetailPane auf 404)
    if (selected === imid) clearSelection();
    // an der alten Position re-inserten. Genutzt von BEIDEN Wegen zurück: dem
    // stillen Rollback (Mutation fehlgeschlagen) und „Rückgängig" (Mutation war
    // erfolgreich, der Nutzer nimmt sie zurück).
    const reinsert = (
      resp: InboxResponse | null,
      index: number,
      backup: InboxItem | null
    ) => {
      if (!resp || !backup) return resp;
      if (resp.messages.some((m) => m.internet_message_id === imid)) return resp;
      const next = [...resp.messages];
      next.splice(Math.min(index, next.length), 0, backup);
      return { ...resp, messages: next };
    };
    const wiederEinfuegen = () => {
      if (inboxBackup) setInbox((c) => reinsert(c, inboxIndex, inboxBackup));
      if (searchBackup)
        setSearchResults((c) => reinsert(c, searchIndex, searchBackup));
    };
    void call(imid)
      .then(() => {
        // Abgeschirmt: die Mutation IST durch. Ein Fehler beim Melden darf nicht
        // im `catch` unten landen und die Zeile wieder einsetzen — das sähe aus,
        // als wäre das Löschen fehlgeschlagen.
        try {
          onSuccess?.(wiederEinfuegen);
        } catch {
          /* nichts — s. oben */
        }
      })
      .catch((e) => {
        // Ohne `onError` still (kein Toast, kein Dialog) — mit `onError` sagt
        // der Aufrufer es dem Nutzer.
        wiederEinfuegen();
        onError?.(e);
      })
      .finally(() => {
        mutInFlight.current.delete(imid);
      });
  };
  const archiveMessage = (imid: string) =>
    removeAndMutate(imid, (id) =>
      api.post(`/mail/messages/${encodeURIComponent(id)}/archive`)
    );
  // ── Löschen: Quittung + Rückgängig (Tester-Feedback 19.08.2026) ───────────
  // „Ich lösche eine Mail und weiß nicht, wo sie geblieben ist." Graph macht
  // einen SOFT-Delete nach „Gelöschte Elemente" — das sagt der Toast jetzt, und
  // er bietet den Weg zurück gleich mit.
  //
  // WOHIN zurück? Nur dorthin, wo die Mail nachweislich HERKAM. Der Ursprung ist
  // genau dann bekannt, wenn die Liste, aus der die Zeile verschwand, EIN
  // bestimmter Ordner war:
  //   * keine Suche  → `folderView` (im Posteingang die Konstante „inbox"; in
  //     einer Ordner-Ansicht gibt es kein Löschen, s. `rowMenuItems`
  //     `rowReadOnly` — der Zweig bleibt trotzdem korrekt).
  //   * Suche IM ORDNER → derselbe Ordner (`suchbarerOrdner`).
  //   * postfachweite Suche → UNBEKANNT. Ein Treffer kann aus jedem Ordner
  //     stammen; ihn „zurück" in den Posteingang zu schieben wäre ein Umzug,
  //     kein Rückgängig. Dann steht die Quittung ohne Aktion da — ehrlicher als
  //     ein Knopf, der rät.
  // Beide Werte sind Ordner-Schlüssel aus `GET /mail/folders` (well_known_name ||
  // folder_id) — dieselbe geschlossene Menge, die der Server beim Verschieben
  // noch einmal prüft.
  const loeschUrsprung = (): string | null => {
    if (searchResults !== null)
      return sucheGanzesPostfach ? null : SUCHBARE_ORDNER[folderView] ? folderView : null;
    return folderView;
  };
  const deleteMessage = (imid: string) => {
    const ursprung = loeschUrsprung();
    removeAndMutate(
      imid,
      (id) => api.delete(`/mail/messages/${encodeURIComponent(id)}`),
      undefined,
      (wiederEinfuegen) => {
        const l = katalog().mail.loeschen;
        toast.push(
          "info",
          l.verschoben,
          undefined,
          ursprung
            ? {
                label: l.rueckgaengig,
                onAction: () => {
                  // Zurückholen = derselbe Verschieben-Pfad, den F-12/F-05
                  // benutzen (POST /mail/messages/{imid}/move). Die Zeile kommt
                  // ERST nach der bestätigten Antwort zurück — sonst stünde sie
                  // wieder in der Liste, während sie im Postfach noch im
                  // Papierkorb liegt.
                  void api
                    .post(`/mail/messages/${encodeURIComponent(imid)}/move`, {
                      destination: ursprung,
                    })
                    .then(() => wiederEinfuegen())
                    .catch((e) => {
                      const k = katalog().mail.loeschen;
                      toast.err(
                        k.rueckgaengigFehlgeschlagen,
                        k.rueckgaengigFehlgeschlagenText(
                          (e as ApiError)?.message ?? ""
                        )
                      );
                    });
                },
              }
            : undefined
        );
      }
    );
  };
  // F-05 — Verschieben in einen frei gewählten Ordner. Genau der Archiv-Weg
  // (optimistisch aus beiden Listen, Rollback an der alten Position), nur mit
  // Ziel aus dem Dialog. `destination` kommt IMMER aus `GET /mail/folders`
  // (`folderKey` = well_known_name || folder_id) — kein Freitext; der Server
  // prüft dieselbe Menge noch einmal (app/routers/mail.py::move_message_to_folder),
  // ein Frontend-Filter allein wäre keine Grenze.
  const moveMessage = (imid: string, destination: string, label: string) =>
    removeAndMutate(
      imid,
      (id) =>
        api.post(`/mail/messages/${encodeURIComponent(id)}/move`, {
          destination,
        }),
      (e) => {
        const o = katalog().mail.ordner;
        toast.err(
          o.verschiebenFehlgeschlagen,
          o.verschiebenFehlgeschlagenText(label, (e as ApiError)?.message ?? "")
        );
      }
    );

  // ── Suche: Fokus / Leeren / Preset (P0.2) ─────────────────────────────────
  // Fokus-Aktion — bewusst als benannte Funktion, damit X1 die zentrale
  // Tastatur-Bindung später hier andocken/ablösen kann (genau EIN Owner).
  const focusSearch = useCallback(() => {
    searchBoxRef.current?.focus();
    searchBoxRef.current?.select();
  }, []);
  const clearSearch = useCallback(() => {
    setSearch("");
    setSearchFrom("");
    setSearchSince("");
    setSearchUntil("");
    setSinceText("");
    setUntilText("");
    setSubmitted(null);   // setzt via Effekt Ergebnis/Fehler/Spinner zurueck
    searchSeqRef.current++; // in-flight-Antworten invalidieren
    searchAbortRef.current?.abort();
  }, []);
  /** Ein Zeitraum-Feld setzen: Anzeige-Text roh übernehmen (der Nutzer tippt),
   *  API-Wert nur bei LESBAREM Datum. Unlesbar → ISO leer (Filter reist nicht
   *  mit) und `sinceInvalid`/`untilInvalid` zeigt die Meldung. */
  const setDateFilter = (
    raw: string,
    setText: (s: string) => void,
    setIso: (s: string) => void
  ) => {
    setText(raw);
    const t = raw.trim();
    setIso(t ? deDateToIso(t, new Date()) ?? "" : "");
  };
  const setPresetDays = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    // `fmtIsoDate` statt `toISOString().slice(0,10)`: letzteres rechnet nach UTC
    // und liefert in Berlin vor 02:00 Ortszeit den VORTAG — dann hätten Anzeige
    // und Filterwert unterschiedliche Tage gezeigt.
    setSearchSince(fmtIsoDate(d));
    setSinceText(fmtDeDate(d));
    setSearchUntil("");
    setUntilText("");
  };

  // ── Ordner: laden / wechseln (P1.5) ───────────────────────────────────────
  // Ordner-Nachrichten laden (Sequenz-Guard gegen out-of-order-Antworten, wie
  // bei der Suche). folder_id wird URL-encodet: Well-Known-Namen (inbox/
  // sentitems/drafts/archive) sind path-safe; Eigenordner tragen einen rohen
  // Graph-Id (kann `/`/`=` enthalten) → encodeURIComponent + serverseitig
  // {folder_id:path}. TRIAGE-FREI: folderMessages fließt in KEINEN Mutations-
  // Handler.
  // Trägt die gewählte Mail-Tiefe (top UND days — `/mail/folder/{id}` kennt
  // beide, wie `/mail/inbox`). Als useCallback, damit der Scope-Effekt unten
  // bei einem Tiefen-Wechsel GENAU EINEN Refetch der sichtbaren Liste auslöst.
  const loadFolder = useCallback((folderId: string) => {
    const seq = ++folderSeqRef.current;
    setFolderLoading(true);
    void api
      .get<InboxResponse>(
        withMailbox(
          `/mail/folder/${encodeURIComponent(folderId)}?top=${inboxTop}&days=${inboxDays}`
        )
      )
      .then((res) => {
        if (seq !== folderSeqRef.current) return;
        setFolderMessages(res);
      })
      .catch(() => {
        if (seq !== folderSeqRef.current) return;
        // Fehler wie leerer Ordner behandeln (still, kein Toast/Dialog).
        setFolderMessages({
          mailbox_enabled: true, mailbox: null, fetched_at: null, messages: [],
        });
      })
      .finally(() => {
        if (seq === folderSeqRef.current) setFolderLoading(false);
      });
  }, [withMailbox, inboxTop, inboxDays]);

  // Entwürfe laden (P1.8, Slice B): eigener Egress GET /mail/drafts (NICHT der
  // generische Ordner-Read). Sequenz-Guard gegen out-of-order-Antworten.
  // Trägt NUR `top` — Entwürfe sind nicht nach Empfangsdatum gefenstert, der
  // Endpoint kennt bewusst KEIN `days` (anders als /inbox und /folder/{id}).
  const loadDrafts = useCallback((): void => {
    const seq = ++draftsSeqRef.current;
    setDraftsLoading(true);
    void api
      .get<DraftOut[]>(`/mail/drafts?top=${inboxTop}`)
      .then((res) => {
        if (seq !== draftsSeqRef.current) return;
        setDrafts(res);
      })
      .catch(() => {
        if (seq !== draftsSeqRef.current) return;
        setDrafts([]); // still (kein Toast/Dialog)
      })
      .finally(() => {
        if (seq === draftsSeqRef.current) setDraftsLoading(false);
      });
  }, [inboxTop]);

  // Entwurf löschen: OPTIMISTISCH aus der Liste, wie Archivieren/Löschen in der
  // Mail-Zeile — und aus demselben Grund ohne Bestätigungs-Dialog: Graph
  // verschiebt ihn nach „Gelöschte Elemente", in Outlook wiederherstellbar.
  // Anders als dort ist der Fehlerfall NICHT still: die Entwürfe-Ansicht hat
  // keinen zweiten Kanal, und eine Zeile, die wortlos wieder auftaucht, sieht
  // aus, als hätte die App den Klick verschluckt.
  const deleteDraft = (draftId: string): void => {
    const backup = drafts;
    setDrafts((cur) => (cur ?? []).filter((d) => d.draft_id !== draftId));
    void api
      .delete(`/mail/drafts/${encodeURIComponent(draftId)}`)
      .catch((e) => {
        setDrafts(backup);
        toast.err(katalog().mail.entwuerfe.loeschenFehlgeschlagen, (e as ApiError).message);
      });
  };

  // Chip-Klick: Scope wechseln. Folder ⇔ Suche sind mutual-exklusiv → Suche
  // leeren; offenes Detail schließen (zeigte sonst eine Mail aus dem alten
  // Scope). "inbox" ist der bestehende Posteingang-Pfad (kein Extra-Egress).
  const selectFolder = (folderId: string) => {
    clearSearch();
    clearSelection();
    // F-35: „im ganzen Postfach" gilt für den Ordner, in dem man es gewählt
    // hat. Wer den Scope wechselt, stellt die Frage neu — sonst schleppt ein
    // einmaliger Klick den weiten Bereich durch die ganze Sitzung.
    setSucheGanzesPostfach(false);
    setFoldersOpen(false);
    // Jeder Chip-Klick ist eine Lade-Anforderung — AUCH der Re-Klick auf den
    // bereits aktiven Chip (die natürliche „Aktualisieren"-Geste). Ohne diesen
    // Nonce bliebe `folderView` dabei gleich, der Lade-Effekt würde nicht
    // feuern, `folderMessages` bliebe nach dem `null`-Reset unten leer — und die
    // Ansicht behauptete einen leeren Ordner, obwohl Mails da sind.
    setFolderReloadNonce((n) => n + 1);
    if (folderId === "inbox") {
      setFolderView("inbox");
      setFolderMessages(null);
      folderSeqRef.current++; // eine in-flight Folder-Antwort invalidieren
      return;
    }
    // Entwürfe: eigene Datenquelle (GET /mail/drafts) + Composer-Resume statt
    // Lese-Detail. Kein generischer Ordner-Read auf „drafts".
    if (folderId === "drafts") {
      setFolderView("drafts");
      setFolderMessages(null);
      folderSeqRef.current++;
      return;
    }
    setFolderView(folderId);
    setFolderMessages(null);
  };

  // EIN Owner für „lade den aktiven Nicht-Posteingang-Scope": Chip-Wechsel
  // (folderView) UND Tiefen-Wechsel (loadFolder/loadDrafts-Identität) laufen
  // durch denselben Effekt → jeweils GENAU EIN Fetch, kein manueller Zweitruf
  // aus `selectFolder`. Ohne das würde ein Tiefen-Wechsel in Gesendet/Entwürfen
  // still wirkungslos bleiben, bis man die Ansicht verlässt und neu betritt.
  // `folderReloadNonce` macht den Re-Klick auf den aktiven Chip zu einem echten
  // Reload, ohne den Ein-Owner-Vorteil aufzugeben: Chip-Wechsel und Re-Klick
  // laufen durch DENSELBEN Effekt. Bei einem echten Wechsel ändern sich
  // `folderView` und Nonce im selben Render → weiterhin GENAU EIN Fetch.
  useEffect(() => {
    if (!depthReady) return;
    if (folderView === "inbox" || folderView === "drafts") return;
    loadFolder(folderView);
  }, [depthReady, folderView, folderReloadNonce, loadFolder]);
  useEffect(() => {
    if (!depthReady) return;
    if (folderView !== "drafts") return;
    loadDrafts();
  }, [depthReady, folderView, folderReloadNonce, loadDrafts]);

  // Postfach wechseln (P2.13): den Scope IMMER auf den Posteingang zurücksetzen
  // (Suche/Ordner leeren, Detail schließen), damit ein Wechsel nicht im
  // Ordner-/Suchzustand des alten Postfachs landet. Der Inbox-Refetch mit dem
  // neuen `?mailbox=` passiert über die `loadInbox`-Dep-Identität (withMailbox
  // ändert sich) — GENAU EIN Call, kein manueller loadInbox(). Ordner sind
  // postfach-spezifisch → beim nächsten Aufklappen neu laden.
  const selectMailbox = (upn: string) => {
    if (upn === activeMailbox) return;
    clearSearch();
    clearSelection();
    setFolderView("inbox");
    setFolderMessages(null);
    folderSeqRef.current++;
    setFoldersOpen(false);
    setFolders(null);
    setFoldersError(false);
    // Ordner-Ids gehören zum alten Postfach — sonst wächst der Zustand mit
    // jedem Wechsel weiter an.
    setExpandedFolders(new Set());
    setActiveMailbox(upn);
  };

  // Eigene Ordner LAZY laden (erst beim Aufklappen des „Ordner"-Aufklappers —
  // der Default-Mount feuert NUR /mail/inbox).
  // Ein Fehler darf NICHT als leerer Zustand erscheinen: der Ordner-Endpunkt
  // lieferte monatelang 500 (beta-only `wellKnownName` im $select), und das
  // frühere `catch { setFolders([]) }` zeigte dafür „Keine weiteren Ordner" —
  // der Ausfall war damit unsichtbar. Fehler und Leere sind jetzt getrennt.
  const loadFolders = useCallback(async () => {
    setFoldersLoading(true);
    setFoldersError(false);
    try {
      const res = await api.get<FolderListResponse>(withMailbox("/mail/folders"));
      setFolders(res.folders ?? []);
    } catch {
      setFolders(null);
      setFoldersError(true);
    } finally {
      setFoldersLoading(false);
    }
  }, [withMailbox]);
  // Menü an der Schaltfläche ausrichten und im Sichtfenster halten. Ordnernamen
  // werden lang (gemessen: 49 Zeichen in eva.galik@bitop.de) und der breiteste
  // Ungelesen-Zähler ist fünfstellig, deshalb deutlich breiter als die alten
  // 224px: bei 420px passt der längste gemessene Name auf den oberen Ebenen
  // ungekürzt. Auf schmalen Geräten auf die Fensterbreite begrenzt, damit
  // nichts aus dem Bild läuft (dort wird gekürzt — mit vollem `title`).
  const FOLDER_MENU_MAX_WIDTH = 420;
  const placeFolderMenu = useCallback(() => {
    const el = foldersBtnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const width = Math.min(FOLDER_MENU_MAX_WIDTH, window.innerWidth - margin * 2);
    const left = Math.max(
      margin,
      Math.min(r.left, window.innerWidth - width - margin)
    );
    setFolderMenuBox({ left, top: r.bottom + 4, width });
  }, []);

  // Schließen mit Fokus-Rückgabe: der Aufklapper hängt per Portal am Body, der
  // Fokus liegt also mitten im Menü — ohne Rückgabe landet er beim Schließen
  // am Dokumentanfang und der Nutzer muss sich neu durchtabben.
  const closeFolders = useCallback(() => {
    setFoldersOpen(false);
    foldersBtnRef.current?.focus();
  }, []);

  const toggleFolders = () => {
    // Seiteneffekt (loadFolders) NICHT in den setState-Updater legen — React
    // StrictMode ruft Updater doppelt auf → sonst zwei /mail/folders-Calls.
    const next = !foldersOpen;
    if (!next) {
      closeFolders();
      return;
    }
    placeFolderMenu();
    setFoldersOpen(true);
    if (folders === null && !foldersLoading) void loadFolders();
  };

  // F-05: Verschieben-Dialog öffnen. Ordner werden — wie beim Aufklapper — LAZY
  // geholt; wer nie verschiebt, feuert nie ein /mail/folders.
  const openMoveDialog = (
    imid: string,
    trigger?: HTMLElement | null,
    fromAddress?: string | null
  ) => {
    // Wohin der Fokus nach dem Schließen zurückgeht. Radix merkt sich das
    // sonst selbst — findet den Auslöser hier aber nicht wieder, weil er ein
    // `span[role=button][tabindex=-1]` in einer Zeile ist, die zwischendurch
    // neu rendert. Gemessen: nach Escape lag der Fokus auf <body>.
    moveTriggerRef.current = trigger ?? (document.activeElement as HTMLElement | null);
    setMoveFor(imid);
    // Vorschläge des VORIGEN Dialogs sofort wegräumen, nicht erst wenn die
    // neue Antwort da ist: sonst stünde für einen Sekundenbruchteil der
    // Vorschlag für eine ANDERE Mail über der Liste — und genau der würde
    // angeklickt, weil er oben steht.
    setMoveSuggestions([]);
    if (folders === null && !foldersLoading) void loadFolders();
    void loadMoveSuggestions(fromAddress ?? null);
  };

  // F-15: die 1-3 Ordner holen, in denen die Domain dieses Absenders bisher am
  // häufigsten lag. Reiner DB-Read serverseitig (kein Graph, kein Modell).
  //
  // Fail-silent mit Absicht: schlägt der Abruf fehl, bleibt der Dialog exakt
  // der, der er ohne diese Funktion wäre. Ein Fehlerbanner über der Ordnerliste
  // wäre lauter als der Nutzen — der Nutzer wollte einen Ordner wählen, nicht
  // von einer Statistik hören.
  const moveSuggestSeq = useRef(0);
  const loadMoveSuggestions = async (fromAddress: string | null) => {
    const lauf = ++moveSuggestSeq.current;
    const absender = (fromAddress ?? "").trim();
    if (!absender) return;
    try {
      const res = await api.get<{ suggestions: FolderSuggestion[] }>(
        withMailbox(
          `/mail/folder-suggestions?from_address=${encodeURIComponent(absender)}`
        )
      );
      // Zwei schnell nacheinander geöffnete Dialoge: nur die Antwort auf den
      // ZULETZT gestellten Abruf darf anzeigen. Ohne die Laufnummer gewinnt
      // die langsamere Antwort und beschriftet die falsche Mail.
      if (lauf !== moveSuggestSeq.current) return;
      setMoveSuggestions(res.suggestions ?? []);
    } catch {
      if (lauf === moveSuggestSeq.current) setMoveSuggestions([]);
    }
  };

  // ── Kontextmenü der Listenzeile ─────────────────────────────────────────────
  // Die Zeile trägt nur noch Archivieren + Löschen als Symbol. Alles Übrige ist
  // über den Rechtsklick (Desktop) bzw. den langen Fingerdruck (Touch) erreichbar
  // — und zusätzlich weiterhin über das …-Menü der offenen Mail, das
  // UNVERÄNDERT bleibt.
  const [rowMenu, setRowMenu] = useState<{
    item: InboxItem;
    x: number;
    y: number;
  } | null>(null);
  // Die auslösende Zeile: Ziel der Fokus-Rückgabe bei Escape UND `trigger` des
  // Verschieben-Dialogs. Ein Menü-Eintrag taugt dafür nicht — der ist beim
  // Schließen des Dialogs längst aus dem DOM (der Fokus landete auf <body>).
  const rowMenuTrigger = useRef<HTMLElement | null>(null);
  const [pendingRowAction, setPendingRowAction] = useState<{
    imid: string;
    key: string;
  } | null>(null);
  const clearPendingRowAction = useCallback(() => setPendingRowAction(null), []);
  const [rowMenuHintSeen, setRowMenuHintSeen] = useState(() =>
    isMailRowMenuHintSeen()
  );

  const openRowMenu = useCallback(
    (item: InboxItem, x: number, y: number, trigger: HTMLElement) => {
      rowMenuTrigger.current = trigger;
      setRowMenu({ item, x, y });
      // Wer das Menü einmal geöffnet hat, hat es gefunden — der Hinweis hat
      // seine Aufgabe erfüllt und kommt nicht wieder.
      setMailRowMenuHintSeen();
      setRowMenuHintSeen(true);
    },
    []
  );
  const closeRowMenu = useCallback((returnFocus: boolean) => {
    setRowMenu(null);
    if (returnFocus) rowMenuTrigger.current?.focus();
  }, []);

  /** Die Aktionsliste EINER Zeile. Rechte-Gate wortgleich mit dem, was die Zeile
   *  selbst zeigt: `readOnly` (Ordner-Ansicht — „Ordner sind Outlook-Lese-
   *  Wahrheit") nimmt Lesen/Archiv/Verschieben/Löschen, das fremde
   *  Funktionspostfach zusätzlich Nachverfolgen und alle Verfassen-Einstiege
   *  (dort ist auch das …-Menü der offenen Mail komplett ausgeblendet,
   *  `noCompose`). Ein Kontextmenü, das anbietet, was die Zeile bewusst
   *  verbirgt, wäre eine Hintertür um genau diese Sperren. */
  const rowMenuItems = (item: InboxItem): DetailAction[] => {
    const imid = item.internet_message_id;
    const rowReadOnly = inFolderView || sharedScope;
    const out: DetailAction[] = [];
    if (!sharedScope) {
      // Antworten/Weiterleiten/Vorlage/Termin brauchen die GELADENE Mail. Der
      // Eintrag öffnet sie deshalb und hinterlegt den Key; DetailPane löst ihn
      // aus, sobald der Body da ist (siehe `pendingAction` dort).
      const viaDetail = (key: string) => () => {
        select(imid);
        setPendingRowAction({ imid, key });
      };
      out.push(
        { key: "reply-all", label: mt.liste.menuAllenAntworten, icon: <ReplyAll className="w-4 h-4" />, onSelect: viaDetail("reply-all") },
        { key: "reply-blank", label: mt.liste.menuFreiAntworten, icon: <Pencil className="w-4 h-4" />, onSelect: viaDetail("reply-blank") },
        { key: "forward", label: mt.liste.menuWeiterleiten, icon: <Forward className="w-4 h-4" />, onSelect: viaDetail("forward") },
        { key: "template", label: mt.liste.menuVorlage, icon: <FileText className="w-4 h-4" />, onSelect: viaDetail("template") },
        { key: "schedule-from-mail", label: mt.liste.menuTermin, icon: <CalendarPlus className="w-4 h-4" />, onSelect: viaDetail("schedule-from-mail"), title: mt.liste.menuTerminTitel(shortcutHint("termin")) }
      );
      // Nur die AKTION wandert hierher. Läuft schon eine Nachverfolgung, steht
      // der Eintrag nicht da — der ZUSTAND ist in der Zeile zu sehen, und ein
      // zweites „markieren" hat noch nie etwas getan.
      if (!trackedImids.has(imid))
        out.push({
          key: "track",
          label: mt.liste.menuNachverfolgen,
          icon: <AlarmClockPlus className="w-4 h-4" />,
          onSelect: () => void markFollowUp(item),
        });
    }
    if (!rowReadOnly) {
      out.push({
        key: "read",
        label: item.is_read ? mt.liste.menuAlsUngelesen : mt.liste.menuAlsGelesen,
        icon: item.is_read ? (
          <CircleDot className="w-4 h-4" />
        ) : (
          <CircleCheck className="w-4 h-4" />
        ),
        onSelect: () => markRead(imid, !item.is_read),
      });
      out.push({ key: "archive", label: mt.liste.menuArchivieren, icon: <Archive className="w-4 h-4" />, onSelect: () => archiveMessage(imid) });
      out.push({
        key: "move",
        label: mt.liste.menuVerschieben,
        icon: <FolderInput className="w-4 h-4" />,
        // Fokus-Ziel ist die ZEILE, nicht der Menü-Eintrag (der ist gleich weg).
        onSelect: () =>
          openMoveDialog(imid, rowMenuTrigger.current, item.from_address),
      });
      out.push({ key: "delete", label: ct.loeschen, icon: <Trash2 className="w-4 h-4" />, onSelect: () => deleteMessage(imid), variant: "danger", title: mt.liste.menuLoeschenTitel(shortcutHint("delete")) });
    }
    return out;
  };

  // Escape schließt (Standard-Dismiss, kein Aktions-Kürzel; Handler NUR bei
  // offenem Menü aktiv, Doc 04) — dasselbe Muster wie DetailActionMenu
  // (Mail.tsx:3588-3599). Ohne das brach der Aufklapper die Hausregel dieser
  // Datei.
  useEffect(() => {
    if (!foldersOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeFolders();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [foldersOpen, closeFolders]);

  // Beim Öffnen: Fokus INS Menü setzen UND dieselbe Zeile ins Bild holen.
  //
  // Beides gehört zwingend in EINEN Effekt. Vorher waren es zwei: der eine
  // fokussierte den ersten Ordner (und scrollte ihn nach oben), der andere
  // zentrierte den aktiven — der spätere gewann, und der Fokusring landete
  // ~150 px oberhalb des sichtbaren Ausschnitts. Zwei Effekte, die dieselbe
  // Scrollposition setzen, ziehen zwangsläufig gegeneinander.
  //
  // Der Fokus geht auf den AKTIVEN Ordner (Rückfall: erster Ordner), denn das
  // Portal hängt das Menü ans Body-Ende — ohne den Sprung sind es 10 Tabs vom
  // „Ordner"-Knopf bis zum ersten Ordner.
  //
  // Absichtlich NICHT von `expandedFolders` abhängig — sonst springt die Liste
  // bei jedem Auf-/Zuklappen.
  useEffect(() => {
    if (!foldersOpen) return;
    const menu = folderMenuRef.current;
    if (!menu) return;
    const btn = (sel: string) =>
      menu.querySelector(sel)?.closest("button") as HTMLElement | null;
    const target =
      btn('[data-active="true"] [data-testid="folder-name"]') ??
      btn('[data-testid="folder-name"]') ??
      menu.querySelector<HTMLElement>("button") ??
      menu;
    // `preventScroll`: das Positionieren macht ausschließlich scrollIntoView,
    // sonst scrollt erst der Fokus und danach noch einmal die Zentrierung.
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: "center" });
  }, [foldersOpen, folders, foldersLoading, foldersError]);

  // Solange offen: bei Resize/Scroll nachführen (fixed folgt dem Anker nicht
  // von selbst).
  useEffect(() => {
    if (!foldersOpen) return;
    const onMove = () => placeFolderMenu();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [foldersOpen, placeFolderMenu]);

  // AUSLÖSER: Eingabetaste, NICHT das Tippen.
  //
  // Vorher lief die Suche entprellt bei jeder Tipppause (280 ms). Gemessen gegen
  // das Produktionspostfach war das keine Geschmacksfrage, sondern ein
  // Fehlerthema: laufende Abfragen wurden nie abgebrochen (der Sequenz-Guard
  // verwarf nur das ERGEBNIS), also lagen bei einem langsamen Tipper auf einem
  // häufigen Begriff bis zu NEUN Abfragen gleichzeitig an. Microsoft erlaubt
  // vier je Postfach — Graph antwortete mit 429 `ApplicationThrottled` und
  // Retry-After 3–8 s, die der Nutzer mitwartet. Dasselbe Kontingent teilen sich
  // Posteingang, Body-Abruf und Kalender desselben Postfachs.
  //
  // `submitted` ist die abgeschickte Abfrage (nicht der Eingabetext): nur ihre
  // Änderung löst Egress aus. Die Filter wandern beim Abschicken mit, lösen aber
  // selbst nichts aus — sonst wäre die Entscheidung „wann wird gesucht" wieder
  // aus der Hand des Nutzers.
  const [submitted, setSubmitted] = useState<(SearchQuery & { nonce: number }) | null>(
    null
  );

  // Zu WELCHER Abfrage gehören die gerade angezeigten Treffer? Ohne diese Angabe
  // kann die Oberfläche nicht sagen, dass die Liste unter dem Feld eine ANDERE
  // Frage beantwortet als die im Feld — und genau das ist während einer
  // Drosselung (Retry-After 3–8 s) der Normalfall.
  const [resultsFor, setResultsFor] = useState<SearchQuery | null>(null);

  // Sichtbare Filter-Eingabe (Slice B). Die ANZEIGE-Texte zählen mit: sonst wäre
  // der „Suche leeren"-Knopf bei einem getippten, noch unlesbaren Datum weg
  // (Feld voll, aber ISO leer). Steht hier oben, weil beide Seiten sie brauchen —
  // der Rückfall in den Posteingang unten und die Knopf-Logik im Listenkopf.
  // Bewusst NICHT dasselbe wie die Bedingung in `submitSearch`: dort zählen nur
  // die ISO-Werte, ein unlesbares Datum darf nicht als Filter mitreisen.
  const filtersActive = !!(
    searchFrom.trim() ||
    searchSince.trim() ||
    searchUntil.trim() ||
    sinceText.trim() ||
    untilText.trim()
  );
  // Im Feld steht nichts mehr — weder Volltext noch Filter.
  const queryEmpty = search.trim() === "" && !filtersActive;

  // ── E1: Filter im Popover ─────────────────────────────────────────────────
  // Wie viele der drei Filter-Dimensionen (Absender · von · bis) sind gesetzt?
  // Gezählt werden — wie bei `filtersActive` — die ANZEIGE-Texte: ein getipptes,
  // noch unlesbares Datum ist für den Nutzer ein gesetzter Filter, auch wenn er
  // (noch) nicht mitreist. Sonst verschwände der Zähler genau dann, wenn die
  // Meldung „TT.MM.JJJJ" darunter steht.
  const [filterOpen, setFilterOpen] = useState(false);
  const filterCount =
    (searchFrom.trim() ? 1 : 0) +
    (sinceText.trim() ? 1 : 0) +
    (untilText.trim() ? 1 : 0);
  /** Nur die Filter räumen — der Suchbegriff bleibt stehen. Absichtlich OHNE
   *  eigene Abschick-Logik: ist danach gar nichts mehr gesetzt, räumt der
   *  bestehende `queryEmpty`-Effekt die Trefferliste weg; steht noch ein
   *  Suchbegriff da, meldet die vorhandene „veraltet"-Zeile, dass die
   *  Eingabetaste die Liste nachzieht. Genau wie beim Ändern eines Filters. */
  const resetFilters = useCallback(() => {
    setSearchFrom("");
    setSearchSince("");
    setSearchUntil("");
    setSinceText("");
    setUntilText("");
  }, []);

  // ── F-35: Suchbereich ─────────────────────────────────────────────────────
  // `null` = der aktive Scope ist ein eigener Unterordner; für den kennt der
  // Server keinen zulässigen `folder`-Wert (geschlossene Liste), die Suche
  // läuft dort zwangsläufig postfachweit.
  const suchbarerOrdner = SUCHBARE_ORDNER[folderView] ? folderView : null;
  // Anzeige-Name des Bereichs. Die Konstante oben trägt nur noch die
  // WHITELIST (Schlüssel = Server-Vokabular), der Name steht im Katalog.
  const suchbereichName = suchbarerOrdner
    ? (mt.ordner.suchbereich as Record<string, string | undefined>)[
        suchbarerOrdner
      ] ?? suchbarerOrdner
    : "";

  const submitSearch = useCallback(() => {
    const term = search.trim();
    const fromF = searchFrom.trim();
    const sinceF = searchSince.trim();
    const untilF = searchUntil.trim();
    if (term.length < 2 && !(fromF || sinceF || untilF)) {
      // Nichts Suchbares → zurück in den Posteingang, kein Egress.
      setSubmitted(null);
      return;
    }
    // `nonce` macht jedes Abschicken unterscheidbar — zweimal dieselbe Eingabe
    // abzuschicken soll erneut suchen (etwa nach einem Fehlschlag).
    setSubmitted({ q: term, from: fromF, since: sinceF, until: untilF, nonce: Date.now() });
  }, [search, searchFrom, searchSince, searchUntil]);

  useEffect(() => {
    if (submitted === null) {
      searchSeqRef.current++; // in-flight-Antwort verwerfen
      searchAbortRef.current?.abort();
      searchAbortRef.current = null;
      setSearchResults(null);
      setResultsFor(null);
      setSearchError(false);
      setSearching(false);
      return;
    }
    // F-35: Die Suche wirft den Ordner-Scope NICHT mehr weg. Vorher sprang sie
    // hart auf `folderView="inbox"` — wer in „Gesendet" suchte, bekam die
    // postfachweite Trefferliste, in der die empfangenen Mails dominieren
    // („zeigt mir trotzdem Ergebnisse im Posteingang", Bentler 18.08.2026).
    // `searchResults` hat in `rows` weiterhin Vorrang, `inFolderView` ist
    // während einer Suche ohnehin false — der Scope bleibt nur ERHALTEN,
    // damit er mitgeschickt werden kann und das Leeren der Suche dort landet,
    // wo der Nutzer war. Ein Ordnerwechsel beendet die Suche wie bisher
    // (`selectFolder` → `clearSearch`), die beiden bleiben also exklusiv.
    const seq = ++searchSeqRef.current;
    // Die vorige Abfrage WIRKLICH abbrechen, nicht nur ihr Ergebnis wegwerfen.
    // `api.get` reicht `init` an `fetch` durch (api.ts:56-59).
    searchAbortRef.current?.abort();
    const ac = new AbortController();
    searchAbortRef.current = ac;
    setSearching(true);
    // Die Abfrage OHNE `nonce` — das ist die Frage, zu der ein Ergebnis gehört.
    const abfrage: SearchQuery = {
      q: submitted.q,
      from: submitted.from,
      since: submitted.since,
      until: submitted.until,
    };
    const qs = new URLSearchParams();
    if (submitted.q) qs.set("q", submitted.q);
    if (submitted.from) qs.set("from", submitted.from);
    if (submitted.since) qs.set("since", submitted.since);
    if (submitted.until) qs.set("until", submitted.until);
    // F-35: den aktiven Ordner mitschicken, solange der Nutzer nicht
    // ausdrücklich das ganze Postfach will. Ohne `folder` sucht der Server
    // postfachweit (app/routers/mail.py:310) — das ist der Fehler, nicht der
    // Rückfall: bei einem eigenen Unterordner gibt es keinen zulässigen Wert,
    // und dann ist postfachweit die einzige ehrliche Antwort.
    if (!sucheGanzesPostfach && suchbarerOrdner) qs.set("folder", suchbarerOrdner);
    void api
      .get<InboxResponse>(withMailbox(`/mail/search?${qs.toString()}`), {
        signal: ac.signal,
      })
      .then((res) => {
        if (seq !== searchSeqRef.current) return; // out-of-order → verwerfen
        setSearchError(false);
        setSearchResults(res);
        // Ab jetzt gehört die Liste zu DIESER Abfrage (ohne `nonce` — zweimal
        // dasselbe abzuschicken ist dieselbe Frage).
        setResultsFor(abfrage);
      })
      .catch((err) => {
        // Ein selbst ausgelöster Abbruch ist KEIN Fehler — sonst zeigte jede
        // Folgesuche kurz den Fehlerkasten.
        if (ac.signal.aborted || (err as { name?: string })?.name === "AbortError") return;
        if (seq !== searchSeqRef.current) return;
        // Fehler NICHT als leeres Ergebnis ausgeben — sonst behauptet die Liste
        // „Keine Treffer", obwohl niemand gesucht hat. `searchResults` bleibt
        // absichtlich auf einem leeren Ergebnis, damit die Suche die aktive
        // Render-Quelle bleibt (sonst blitzte die Inbox darunter auf);
        // `searchError` entscheidet dann, WAS an der Stelle steht.
        setSearchError(true);
        setSearchResults({
          mailbox_enabled: true,
          mailbox: null,
          fetched_at: null,
          messages: [],
        });
        // Auch der Fehlschlag gehört zu DIESER Abfrage — sonst hinge der
        // Zustand danach an der vorletzten Frage.
        setResultsFor(abfrage);
      })
      .finally(() => {
        if (seq === searchSeqRef.current) setSearching(false);
      });
    return () => ac.abort();
  }, [submitted, suchbarerOrdner, sucheGanzesPostfach]);

  // Leeren OHNE Esc und ohne Eingabetaste muss dort landen, wo Esc landet: im
  // Posteingang. Sonst blieb die gefilterte Trefferliste unter einem leeren Feld
  // stehen — ohne Hinweis, wozu sie gehört, und ohne sichtbaren Weg zurück: der
  // „Suche leeren"-Knopf hängt an `search || filtersActive`, und genau die sind
  // dann beide leer. Vor dem Umbau auf die Eingabetaste räumte die Entprellung
  // das von selbst weg; das ist die stillschweigende zweite Hälfte jener
  // Verhaltensänderung, hier nachgezogen.
  //
  // Gewählt wurde dieser Weg und NICHT „X-Knopf auch bei searchResults !== null
  // zeigen": ein leeres Feld benennt keine Frage, also kann keine Beschriftung
  // die Liste darunter ehrlich zuordnen („Treffer zu «»"). Der X-Knopf hätte nur
  // den Rückweg wiederhergestellt, den falschen Zustand aber gelassen.
  //
  // Löst KEINE Suche aus: `submitted = null` bricht die laufende Abfrage ab und
  // räumt auf (Zweig oben), es geht kein Request raus. Der Auslöser bleibt die
  // Eingabetaste.
  useEffect(() => {
    if (submitted !== null && queryEmpty) setSubmitted(null);
  }, [submitted, queryEmpty]);

  const stand = fmtStand(inbox?.fetched_at ?? null);

  // Aktiver Tab (URL-abgeleitet) — VOR `header` berechnet, weil die Tiefen-
  // Steuerung (P1.11) nur im Posteingang-Tab in den PageHeader-actions-Slot
  // gerendert wird.
  const rawTab = params.get("tab");
  const tab: "inbox" | "vorlagen" | "fragen" | "follow" =
    rawTab === "vorlagen"
      ? "vorlagen"
      : rawTab === "fragen"
      ? "fragen"
      : rawTab === "follow"
      ? "follow"
      : "inbox";
  const setTab = (id: string) => {
    const next = new URLSearchParams(params);
    if (id === "vorlagen" || id === "fragen" || id === "follow") next.set("tab", id);
    else next.delete("tab");
    setParams(next);
  };

  const header = (
    <PageHeader
      title={mt.seite.titel}
      description={
        inbox?.mailbox
          ? `${inbox.mailbox}${stand ? ` · ${mt.seite.stand(stand)}` : ""}`
          : undefined
      }
      actions={
        <div className="flex items-center gap-2">
          <PushOptIn />
          {/* Postfach-Umschalter (P2.13): NUR sichtbar bei >1 Eintrag (eigenes +
              freigegebene Funktionspostfächer). Klick-Aktion, kein Kürzel.
              Bleibt SICHTBAR (E1): das ist eine Wahl des Arbeitskontextes, kein
              Werkzeug — in einem Überlauf-Menü wäre nicht mehr zu sehen, in
              wessen Postfach man gerade liest. */}
          <MailboxSwitcher
            mailboxes={mailboxes}
            activeUpn={effectiveMailbox}
            onSelect={selectMailbox}
          />
          {/* Mail-Tiefe (P1.11): NUR im Posteingang-Tab. Wechsel → speichert am
              NUTZER (PUT /settings) + genau ein Refetch der sichtbaren Liste mit
              neuer Tiefe. Gilt für Posteingang, Gesendet und Entwürfe; die Suche
              bleibt unberührt.
              E1: aus der dreiteiligen Segmented-Control (3 Knöpfe dauerhaft) in
              das „Ansicht"-Menü umgezogen. `data-inbox-depth` sitzt am WRAPPER
              (nicht mehr an der Gruppe): so bleibt „gibt es die Tiefen-Steuerung
              auf diesem Reiter?" messbar, ohne das Menü zu öffnen — die drei
              `[data-depth]`-Knöpfe stehen darin unverändert. */}
          {tab === "inbox" && (
            <KopfMenu
              // Traegt die AKTIVE Stufe als Wert: so bleibt „welche Tiefe gilt
              // gerade?" ablesbar, ohne das Menue zu oeffnen — sonst waere der
              // Zustand nur noch hinter einem Klick sichtbar.
              wrapperProps={{ "data-inbox-depth": depth }}
              triggerProps={{
                "data-ansicht-menu": true,
                title: mt.seite.ansichtTitel,
                className:
                  "inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-medium text-text-2 transition-colors hover:bg-surface-2 hover:text-text",
              }}
              trigger={
                <>
                  <List className="w-4 h-4 shrink-0" />
                  <span>{mt.seite.ansicht}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </>
              }
            >
              {(close) => (
                <div role="group" aria-label={mt.seite.tiefeAria}>
                  <p className="px-3 pb-1 pt-0.5 text-[10px] font-medium uppercase tracking-wider text-text-3">
                    {mt.seite.ansichtDichte}
                  </p>
                  {INBOX_DEPTH_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="menuitemradio"
                      data-depth={opt.value}
                      onClick={() => {
                        close();
                        void setDepth(opt.value);
                      }}
                      aria-checked={depth === opt.value}
                      aria-pressed={depth === opt.value}
                      title={mt.seite.tiefe[opt.value].hinweis}
                      className={clsx(
                        "flex w-full items-start gap-2.5 px-3 py-1.5 text-left transition-colors hover:bg-surface-2",
                        depth === opt.value ? "text-text" : "text-text-2"
                      )}
                    >
                      <Check
                        className={clsx(
                          "mt-0.5 w-3.5 h-3.5 shrink-0",
                          depth === opt.value
                            ? "text-accent-500"
                            : "text-transparent"
                        )}
                      />
                      <span className="min-w-0">
                        <span className="block text-xs font-medium">
                          {mt.seite.tiefe[opt.value].label}
                        </span>
                        {/* Der Hinweis stand bisher NUR im `title` — im Menü ist
                            Platz, ihn hinzuschreiben. */}
                        <span className="block text-[10px] leading-tight text-text-3">
                          {mt.seite.tiefe[opt.value].hinweis}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </KopfMenu>
          )}
          {/* ···-Überlauf: Aktualisieren · Abwesenheitsnotiz · Tastenkürzel.
              X1-Zusage bleibt gewahrt — der persistente Sichtbarkeits-Anker für
              die Kürzel-Übersicht (`data-kbd-help-chip`) steht hier drin, MIT
              sichtbarem F1-Chip. Handeln bleibt owner-only (R1-Grenze, P2.13):
              im Shared-Postfach ist die Ansicht read-only, deshalb entfällt die
              Abwesenheitsnotiz dort (sie wirkte aufs EIGENE Postfach). */}
          <KopfMenu
            wrapperProps={{ "data-mail-mehr": true }}
            triggerProps={{
              "data-mail-mehr-trigger": true,
              title: mt.seite.weitereBefehle,
              "aria-label": mt.seite.weitereBefehle,
              className:
                "inline-flex items-center justify-center rounded-lg border border-border bg-surface px-2 py-1.5 text-text-2 transition-colors hover:bg-surface-2 hover:text-text",
            }}
            trigger={<MoreHorizontal className="w-4 h-4" />}
          >
            {(close) => (
              <>
                <KopfMenuItem
                  icon={
                    <RefreshCw
                      className={clsx("w-3.5 h-3.5", loading && "animate-spin")}
                    />
                  }
                  label={ct.aktualisieren}
                  disabled={loading}
                  onClick={() => {
                    close();
                    void loadInbox();
                  }}
                />
                {!sharedScope && (
                  <KopfMenuItem
                    data-ooo-open
                    icon={<CalendarClock className="w-3.5 h-3.5" />}
                    label={mt.seite.abwesenheitsnotiz}
                    onClick={() => {
                      close();
                      setOooOpen(true);
                    }}
                  />
                )}
                <div className="my-1 border-t border-border" />
                <KopfMenuItem
                  data-kbd-help-chip
                  icon={<Keyboard className="w-3.5 h-3.5" />}
                  label={mt.kuerzel.titel}
                  taste={shortcutHint("help")}
                  onClick={() => {
                    close();
                    setHelpOpen(true);
                  }}
                />
              </>
            )}
          </KopfMenu>
          {!sharedScope && (
            <Button
              variant="primary"
              size="sm"
              icon={<MailPlus className="w-4 h-4" />}
              onClick={() => setComposeNew(true)}
            >
              {mt.seite.neueMail}
            </Button>
          )}
        </div>
      }
    />
  );

  // tab (URL-abgeleitet) in eine Ref spiegeln → der zentrale keydown-Effect (unten)
  // liest stets den frischen Wert (kein stale-closure, Listener bleibt mit stabilen
  // Deps). Der frühere separate Strg+E/F3-Listener ist von X1 in den EINEN
  // zentralen Mail-keydown-Effect überführt (genau EIN Owner, Doc 04).
  useEffect(() => {
    tabRef.current = tab;
  }, [tab]);

  const messages = inbox?.messages ?? [];
  // Render-Quelle (Priorität): aktive Suche > Entwürfe > Ordner-Ansicht >
  // Posteingang. Der Entwürfe-Scope (P1.8) ist ein Sonderfall: eigene Datenquelle
  // (GET /mail/drafts) + Composer-Resume, KEIN Lese-Detail → daher aus dem
  // generischen `inFolderView` ausgeklammert.
  const inDraftsView = !searchResults && folderView === "drafts";
  const inFolderView =
    !searchResults && folderView !== "inbox" && folderView !== "drafts";
  const rows = searchResults
    ? searchResults.messages
    : inFolderView
    ? folderMessages?.messages ?? []
    : messages;
  const searchActive = searchResults !== null;
  // ── F-27: Wann zeigt die Liste den Empfänger statt des Absenders? ─────────
  //
  // Entschieden wird PRO ZEILE am Absender — NICHT am Ordnernamen. Grund:
  //  (a) „Unterordner, die gesendete Mails enthalten" ist am Namen nicht
  //      erkennbar, und ein Projektordner enthält typischerweise BEIDES.
  //      Eine ordnerweite Umschaltung würde dort die empfangenen Mails
  //      falsch beschriften — sie zeigte den Empfänger einer Mail, die man
  //      selbst bekommen hat.
  //  (b) Die Suche liefert eine gemischte Liste, sobald sie postfachweit läuft
  //      (kein `folder`-Parameter, `app/routers/mail.py:310`) — und das ist
  //      seit F-35 nicht mehr der einzige Fall, aber weiterhin der Normalfall
  //      für eigene Unterordner. Ein Ordner, an dem man sich orientieren
  //      könnte, steht dort nicht zur Verfügung.
  //
  // Bezugsgröße ist das aktive Postfach (auch im Funktionspostfach-Scope
  // korrekt, weil `inbox.mailbox` dann die freigegebene Adresse trägt) —
  // dieselbe Quelle, aus der die Oberfläche schon `ownerEmail` speist.
  const eigeneAdresse = (inbox?.mailbox ?? "").trim().toLowerCase();
  const istAusgehend = (m: InboxItem) =>
    !!eigeneAdresse && (m.from_address ?? "").trim().toLowerCase() === eigeneAdresse;
  // `filtersActive` steht weiter oben (bei `submitSearch`) — der Rückfall in den
  // Posteingang braucht dieselbe Bedingung.

  // ── Gehört die Liste unten zur Eingabe oben? ──────────────────────────────
  // Während einer Drosselung (Graph: 429 + Retry-After 3–8 s) steht unter dem
  // Feld die Trefferliste der VORIGEN Abfrage und sieht aus wie ein fertiges
  // Ergebnis zur neuen Eingabe — der Nutzer entscheidet dann auf der Antwort zu
  // einer anderen Frage. Dasselbe gilt beim Weitertippen, seit erst die
  // Eingabetaste sucht. Beides ist derselbe Fehler und wird hier gemeinsam
  // beantwortet.
  //
  // Gewählt: KENNZEICHNEN. Nicht LEEREN — eine leere Liste wäre wieder
  // „Ausfall sieht aus wie kein Treffer", und genau das hat P0.2 beseitigt.
  // Nicht DÄMPFEN — Listentext auf halbe Deckkraft fällt in beiden Modi unter
  // die Kontrast-Schwelle; die Liste bleibt lesbar und sagt stattdessen, wozu
  // sie gehört.
  const liveQuery: SearchQuery = {
    q: search.trim(),
    from: searchFrom.trim(),
    since: searchSince.trim(),
    until: searchUntil.trim(),
  };
  // `null` heißt „unten steht der Posteingang" (noch kein Ergebnis geladen).
  const shownFor = searchResults ? resultsFor : null;
  // Beim Fehler steht der Fehlerkasten STATT einer Liste — da ist nichts falsch
  // zuzuordnen. Und bei leerem Feld räumt der Rückfall in den Posteingang auf.
  const staleList =
    !searchError && !queryEmpty && !sameSearchQuery(shownFor, liveQuery);
  // Ohne Volltext-Begriff ergibt „das Ergebnis zu von kunde@…" keinen Satz —
  // eine reine Filter-Suche wird darum als solche benannt (am Bildschirm
  // nachgesehen, nicht angenommen).
  const shownLabel = !shownFor
    ? mt.suche.zeigtPosteingang
    : shownFor.q
      ? mt.suche.zeigtErgebnis(describeQuery(shownFor))
      : mt.suche.zeigtFilterErgebnis(describeQuery(shownFor));
  // Getippt, aber nicht als Datum lesbar → sichtbare Meldung am Feld. Erst NACH
  // dem Verlassen des Feldes (siehe *Focused): während des Tippens ist ein
  // unvollständiges Datum normal. Beim Zurückkehren ins Feld verschwindet die
  // Meldung sofort wieder, damit sie beim Korrigieren nicht im Weg steht.
  const sinceInvalid = !sinceFocused && sinceText.trim() !== "" && !searchSince;
  const untilInvalid = !untilFocused && untilText.trim() !== "" && !searchUntil;

  // ── „Was wurde tatsächlich geladen?" (Tiefen-Quittung) ────────────────────
  // Behebt eine echte Verwirrung: Wer auf „Erweitert" stellt und KEINE weiteren
  // Mails sieht, hielt das für einen Fehler — dabei enthält der Posteingang in
  // 90 Tagen schlicht nur 18 Nachrichten. Die Zeile nennt darum Anzahl UND
  // Zeitraum des Geladenen, damit „nichts Neues" selbsterklärend ist.
  // Bewusst NICHT während einer aktiven Suche (die hat ihren eigenen Zeitraum-
  // Filter) und erst, wenn die jeweilige Quelle geladen ist (sonst blitzte
  // „Keine Nachrichten" vor den Daten auf).
  const scopeLoaded = inDraftsView
    ? drafts !== null
    : inFolderView
      ? folderMessages !== null
      : inbox !== null;
  const scopeCount = inDraftsView ? (drafts ?? []).length : rows.length;
  const depthStatus = inDraftsView
    ? // Entwürfe kennen KEINEN Zeitraum (der Endpoint nimmt nur `top`) — das
      // ehrlich sagen statt einen Zeitraum zu erfinden, den es nicht gibt.
      mt.liste.tiefeEntwuerfe(scopeCount)
    : mt.liste.tiefeNachrichten(scopeCount, inboxDays);

  // „Mehr laden" (P1.11, Slice B): hebt die Tiefe auf die nächste Preset-Stufe
  // (setDepth → genau ein Refetch mit größerem top/days), auf „maximal"
  // ausgeblendet (nextInboxDepth === null). Kein neuer Endpoint.
  //
  // 19.08.2026 — gilt jetzt AUCH in Ordner-Ansichten. Vorher hing am Knopf ein
  // `!inFolderView`: in „Gelöschte Elemente" standen damit 4 von 20 255
  // Nachrichten (14-Tage-Fenster) ohne jeden Weg, mehr zu laden. Das
  // „Ansicht"-Menü im Seitenkopf greift zwar auch dort (`loadFolder` trägt top
  // UND days), aber es steht hinter einem Klick und heißt „Dichte" — gefragt
  // wird die Frage UNTEN, am Ende einer zu kurzen (oder leeren) Liste.
  //
  // NICHT in der Suche: die hat ihren eigenen Zeitraum-Filter und einen eigenen
  // Server-Deckel — ein „Mehr laden", das an beiden vorbeigreift, wäre ein
  // Versprechen ohne Deckung.
  //
  // Als Variable und nicht zweimal als JSX: der Knopf steht an ZWEI Stellen (am
  // Listenende und unter dem Leer-Zustand des Ordners), und zwei Kopien wären
  // die nächste Stelle, an der eine von beiden vergessen wird.
  const mehrLadenKnopf =
    !searchActive && nextInboxDepth(depth) ? (
      <div className="px-3 py-3">
        <button
          type="button"
          data-inbox-load-more
          // Der Ladezustand der SICHTBAREN Liste — `loading` gehört dem
          // Posteingang, in einer Ordner-Ansicht sagt er nichts.
          disabled={inFolderView ? folderLoading : loading}
          onClick={() => {
            const next = nextInboxDepth(depth);
            if (next) void setDepth(next);
          }}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-text-2 transition-colors hover:bg-surface-2 hover:text-text disabled:opacity-50"
        >
          {mt.liste.mehrLaden}
        </button>
      </div>
    ) : null;

  // X1: den tastatur-relevanten State + die (jede Render neu gebauten) Handler in
  // den Spiegel schreiben. Kein Deps-Array → läuft nach jedem Render, sodass der
  // zentrale Listener stets die frischen Werte/Closures liest.
  useEffect(() => {
    kbdStateRef.current = {
      selected,
      tab,
      inFolderView,
      inDraftsView,
      sharedScope,
      rows,
      select,
      deleteMessage,
    };
  });

  // ── X1: EIN zentraler Mail-keydown-Effect (ownt ALLE Mail-Kürzel) ────────────
  // Löst den früheren lokalen Strg+E/F3-Listener ab (genau EIN Owner). Guards:
  // (1) Fokus in INPUT/TEXTAREA/contentEditable → nur F1 feuert; (2) offener
  // Dialog ODER offene Befehls-Palette → nichts feuert (DOM-Check erfasst sowohl
  // Radix-`[role=dialog]` als auch die Palette `[data-command-palette]`); die
  // Mutations-Guards (mutInFlight) leben in den Aktionen selbst.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const st = kbdStateRef.current;
      const el = e.target as HTMLElement | null;
      const inField =
        el?.tagName === "INPUT" ||
        el?.tagName === "TEXTAREA" ||
        !!el?.isContentEditable;
      const overlayOpen = !!document.querySelector(
        '[role="dialog"], [data-command-palette]'
      );

      // F1 → Kürzel-Übersicht (auch in Feldern, Doc 04).
      if (e.key === "F1") {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      // Das Zeichen „?" (layoutunabhängig) → Übersicht; NICHT in Feldern, nicht
      // wenn schon ein Dialog/Palette offen ist.
      if (e.key === "?" && !inField && !overlayOpen) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      // Alle übrigen Mail-Kürzel: nicht in Feldern, nicht bei offenem Dialog/Palette.
      if (inField || overlayOpen) return;

      // F3 → Suche fokussieren (nur Posteingang-Tab, Outlook-nativ).
      // Bewusst die EINZIGE Bindung: `Strg+E` ist in Chrome der Sprung in die
      // Adress-/Suchleiste. Ein preventDefault darauf nimmt dem Nutzer eine
      // Browser-Funktion weg — F3 leistet dasselbe ohne Kollision. Deshalb
      // `Strg+E` hier NICHT wieder hinzufügen.
      if (e.key === "F3") {
        if (st.tab !== "inbox") return;
        e.preventDefault();
        focusSearch();
        return;
      }

      // Strg+Umschalt+A → „Termin aus dieser Mail" (nur bei offener Mail, via
      // Dock-Brücke). Kein CustomEvent — die Aktion kommt aus DetailPane.
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        !e.altKey &&
        e.key.toLowerCase() === "a"
      ) {
        if (!st.selected) return;
        const act = detailActionsRef.current?.actions.find(
          (a) => a.key === "schedule-from-mail"
        );
        if (act) {
          e.preventDefault();
          act.onSelect();
        }
        return;
      }

      // Entf → Löschen (nur bei offener Mail). Gating EXAKT wie das delete-Dock-
      // Item: die Aktion existiert in der Brücke nur, wenn DetailPane sie gerendert
      // hat (nicht readOnly / nicht Shared-Postfach). Aktion = P0.3-deleteMessage
      // (optimistisch, stiller Rollback, kein Confirm — mutInFlight-Guard).
      if (e.key === "Delete") {
        if (!st.selected) return;
        const canDelete = !!detailActionsRef.current?.actions.some(
          (a) => a.key === "delete"
        );
        if (canDelete) {
          e.preventDefault();
          st.deleteMessage(st.selected);
        }
        return;
      }

      // Leertaste / Umschalt+Leertaste → Triage. NUR im Posteingang-/Such-Scope
      // (Ordner-/Entwürfe-Ansicht sind triage-frei). Quelle = `rows` (respektiert
      // den Such-Dualstate). Immer preventDefault, damit <main> nicht mitscrollt.
      if (e.key === " ") {
        if (st.inFolderView || st.inDraftsView) return;
        const list = st.rows;
        const shift = e.shiftKey;
        const container = document.querySelector(
          "[data-mail-body-scroll]"
        ) as HTMLElement | null;
        // 1) Offene Mail + Body-Scroller nicht am Rand → blättern.
        if (st.selected && container) {
          const step = Math.max(40, container.clientHeight * 0.9);
          const atBottom =
            container.scrollTop + container.clientHeight >=
            container.scrollHeight - 4;
          const atTop = container.scrollTop <= 4;
          if (!shift && !atBottom) {
            container.scrollTop = Math.min(
              container.scrollHeight,
              container.scrollTop + step
            );
            e.preventDefault();
            return;
          }
          if (shift && !atTop) {
            container.scrollTop = Math.max(0, container.scrollTop - step);
            e.preventDefault();
            return;
          }
        }
        // 2) Am Rand (oder keine Mail offen) → Row-Navigation.
        if (!list.length) {
          e.preventDefault();
          return;
        }
        if (!st.selected) {
          // keine Mail offen → erste ungelesene öffnen.
          const firstUnread = list.find((r) => !r.is_read);
          if (firstUnread) st.select(firstUnread.internet_message_id);
          e.preventDefault();
          return;
        }
        const idx = list.findIndex(
          (r) => r.internet_message_id === st.selected
        );
        if (!shift) {
          // am Ende → nächste UNGELESENE Row (Doc 04).
          const nextUnread = list.slice(idx + 1).find((r) => !r.is_read);
          if (nextUnread) st.select(nextUnread.internet_message_id);
        } else {
          // am Anfang → vorige Row.
          if (idx > 0) st.select(list[idx - 1].internet_message_id);
        }
        e.preventDefault();
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusSearch]);

  // ── X1: `?do=` aus der Befehls-Palette ausführen ─────────────────────────────
  // search→Suche fokussieren · help→Übersicht · termin/ki/vorlage→ nur bei offener
  // Mail via Dock-Brücke, sonst stiller No-Op (User landet im Posteingang). Danach
  // den `do`-Param wegräumen (andere Params erhalten, Muster ?m=/?tab=).
  useEffect(() => {
    const doParam = params.get("do");
    if (!doParam) return;
    if (doParam === "search") {
      focusSearch();
    } else if (doParam === "help") {
      setHelpOpen(true);
    } else if (
      doParam === "termin" ||
      doParam === "ki" ||
      doParam === "vorlage"
    ) {
      const bridge = detailActionsRef.current;
      if (selected && bridge) {
        if (doParam === "termin") {
          bridge.actions
            .find((a) => a.key === "schedule-from-mail")
            ?.onSelect();
        } else if (doParam === "ki") {
          bridge.openReplySuggest();
        } else if (doParam === "vorlage") {
          bridge.actions.find((a) => a.key === "template")?.onSelect();
        }
      }
      // sonst: stiller No-Op (keine Mail offen)
    }
    const next = new URLSearchParams(params);
    next.delete("do");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Ordner-Chips (P1.5): die drei festen Chips + der „Ordner"-Aufklapper.
  // Der Aufklapper zeigt den VOLLSTÄNDIGEN Ordnerbaum, nicht nur die oberste
  // Ebene: bei wolfgang.hansen liegen 51 von 66 Ordnern ab Ebene 2, und 48
  // davon unter „Posteingang". Würde der Aufklapper die festen Chips wie früher
  // herausfiltern, bliebe genau dieser Ast — die Mehrheit des Postfachs —
  // unerreichbar.
  const FIXED_WELL_KNOWN = ["inbox", "sentitems", "drafts"];
  // Standard: alles zugeklappt — der Nutzer sieht zuerst die oberste Ebene
  // (wie in Outlook) und klappt selbst auf.
  const folderRows = flattenFolderTree(folders ?? [], expandedFolders);
  // Der Zähler am „Ordner"-Knopf meint weiterhin die Ordner JENSEITS der drei
  // Chips (sonst zählte er den Posteingang doppelt, der schon einen Chip hat).
  const otherUnread = (folders ?? [])
    .filter((f) => !FIXED_WELL_KNOWN.includes(f.well_known_name ?? ""))
    .reduce((sum, f) => sum + (f.unread_count || 0), 0);
  const ownFolderActive =
    folderView !== "inbox" && !FIXED_WELL_KNOWN.includes(folderView);

  const toggleFolderExpanded = (folderId: string) =>
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });

  // ── Verschieben-Dialog: Zeilen mit Filter (F-15) ──────────────────────────
  // Ohne Filter genau die Zeilen des Aufklappers. MIT Filter wird der GANZE
  // Baum flach durchsucht — Name ODER Elternpfad —, damit ein tief liegender
  // Treffer wie /Posteingang/Vorstand/Strategy ohne drei Klicks dasteht.
  // `expandedFolders` bleibt dabei unangetastet: das Feld leeren stellt den
  // vorherigen Aufklapp-Zustand wieder her, weil er nie verändert wurde.
  const moveQuery = moveFilter.trim().toLowerCase();
  const moveTreffer = () => {
    // Alles aufgeklappt denken, dann flach filtern — sonst blieben Treffer in
    // zugeklappten Ästen unsichtbar.
    const alle = new Set<string>();
    const sammeln = (ns: FolderOut[]) =>
      ns.forEach((f) => {
        alle.add(f.folder_id);
        sammeln(f.children ?? []);
      });
    sammeln(folders ?? []);
    return flattenFolderTree(folders ?? [], alle).filter((r) =>
      [...r.path, r.folder.display_name].join(" / ").toLowerCase().includes(moveQuery)
    );
  };
  const moveRows = moveQuery ? moveTreffer() : folderRows;

  // ── Vorschlags-Zeilen (F-15) ──────────────────────────────────────────────
  // Der Server schickt nur Ordner-Ids. Hier bekommen sie Namen und Elternpfad
  // aus DEMSELBEN Baum, den die Liste darunter zeigt — nie aus einer zweiten
  // Quelle. Eine Id, die sich nicht auflösen lässt (Ordner seit dem letzten
  // Crawl umbenannt ist egal, GELÖSCHT nicht), fällt still heraus: lieber ein
  // Vorschlag weniger als eine Zeile, die ins Leere klickt.
  const moveVorschlaege = (() => {
    if (moveSuggestions.length === 0 || !folders) return [];
    const index = new Map<string, FolderRow>();
    const alle = new Set<string>();
    const sammeln = (ns: FolderOut[]) =>
      ns.forEach((f) => {
        alle.add(f.folder_id);
        sammeln(f.children ?? []);
      });
    sammeln(folders);
    flattenFolderTree(folders, alle).forEach((r) =>
      index.set(r.folder.folder_id, r)
    );
    return moveSuggestions
      .map((s) => ({ vorschlag: s, row: index.get(s.folder_id) }))
      .filter((v): v is { vorschlag: FolderSuggestion; row: FolderRow } => !!v.row);
  })();

  // Beim Filtern verschwinden die Vorschläge. BEGRÜNDUNG: Tippen ist die
  // Ansage „ich weiß, wohin" — daneben noch einen Rat zu stellen, macht aus
  // einer Antwort zwei. Die gefilterte Liste ist ohnehin flach und zeigt den
  // Elternpfad; ein angehefteter Zwilling derselben Zeile stünde doppelt da.
  // Dass dabei nichts springt, trägt der eingefrorene Behälter: die Vorschläge
  // stehen INNERHALB der Liste, deren Höhe ungefiltert gemessen und beim
  // Tippen gehalten wird.
  const zeigeVorschlaege = !moveQuery && moveVorschlaege.length > 0;

  // Jeder Dialog startet mit leerem Filter — ein stehengebliebener Filter aus
  // dem letzten Verschieben wäre eine unsichtbare Einschränkung.
  useEffect(() => {
    if (moveFor) setMoveFilter("");
  }, [moveFor]);

  // Die Liste behält beim Filtern die Höhe, die sie UNGEFILTERT hatte. Ohne das
  // schrumpft der Behälter mit jedem Tastendruck (gemessen: 452 → 364 → 168 px)
  // — und weil das Modal mittig sitzt, springt es dabei unter dem Cursor weg.
  // Gemessen statt gerechnet: die Zeilenhöhe steckt in den Klassen, eine
  // Zahl im Code wäre beim nächsten Spacing-Wechsel still falsch.
  const moveListRef = useRef<HTMLDivElement | null>(null);
  const [moveListH, setMoveListH] = useState<number | null>(null);
  useEffect(() => {
    if (!moveFor) {
      setMoveListH(null);
      return;
    }
    // Nur im ungefilterten Zustand messen — sonst friert die Trefferhöhe ein.
    if (moveQuery) return;
    const el = moveListRef.current;
    if (el) setMoveListH(Math.min(el.scrollHeight, MOVE_LIST_MAX_H));
    // `moveSuggestions` MUSS in den Deps stehen: die Vorschläge kommen
    // asynchron und stehen INNERHALB der gemessenen Liste. Ohne sie bliebe die
    // eingefrorene Höhe die von VOR der Antwort — und beim ersten Tastendruck
    // schrumpfte der Behälter doch wieder.
  }, [moveFor, moveQuery, folders, foldersLoading, expandedFolders, moveSuggestions]);

  // Inbox-Tab-Inhalt (Fehler/Empty/Split alle innerhalb der flex-1-Zone, damit
  // die Tab-Leiste sichtbar bleibt).
  let inboxContent: React.ReactNode;
  if (!loading && error) {
    inboxContent = (
      <div className="flex-1 min-h-0 grid place-items-center p-6">
        <EmptyState
          icon={<ServerCrash className="w-6 h-6" />}
          title={mt.seite.nichtErreichbarTitel}
          hint={mt.seite.nichtErreichbarHinweis}
          action={
            <Button
              variant="primary"
              icon={<RefreshCw className="w-4 h-4" />}
              onClick={() => void loadInbox()}
            >
              {ct.erneutVersuchen}
            </Button>
          }
        />
      </div>
    );
  } else if (!loading && inbox && !inbox.mailbox_enabled) {
    inboxContent = (
      <div className="flex-1 min-h-0 grid place-items-center p-6">
        <EmptyState
          icon={<MailIcon className="w-6 h-6" />}
          title={mt.seite.nichtFreigeschaltetTitel}
          hint={mt.seite.nichtFreigeschaltetHinweis}
        />
      </div>
    );
  } else {
    inboxContent = (
      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[380px_1fr]">
        {/* Liste — mobil ausgeblendet sobald eine Mail selektiert ist. */}
        <aside
          className={clsx(
            "min-h-0 overflow-y-auto md:border-r border-border bg-surface",
            selected ? "hidden md:block" : "block"
          )}
        >
          {/* Suchkopf: klebt oben, wird beim Scrollen von der Liste NICHT
              überdeckt (sticky + bg). Erstes Kind des scrollenden <aside>. */}
          <div className="sticky top-0 z-10 bg-surface border-b border-border px-2 py-2">
            {/* Ordner-Chips (P1.5): ERSTES Element im sticky-Kopf, ÜBER der Suche
                — Chips = Scope-Navigation, die Suche filtert im (Inbox-)Scope;
                beide pinnen zusammen. Dezent + lesend (Auto-Label-statt-Auto-
                Move-Doktrin): Posteingang bleibt Default + einziger Triage-Weg.
                Im Shared-Postfach (P2.13) ausgeblendet: R1 = read-only Inbox +
                Suche; „Entwürfe" ist owner-only (GET /mail/drafts) → würde sonst
                die EIGENEN Entwürfe im fremden Scope zeigen. */}
            {!sharedScope && (
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              <FolderChip
                label={mt.ordner.posteingang}
                active={folderView === "inbox"}
                onClick={() => selectFolder("inbox")}
              />
              <FolderChip
                label={mt.ordner.gesendet}
                active={folderView === "sentitems"}
                onClick={() => selectFolder("sentitems")}
              />
              <FolderChip
                label={mt.ordner.entwuerfe}
                active={folderView === "drafts"}
                onClick={() => selectFolder("drafts")}
              />
              {/* „Ordner"-Aufklapper: eigene Ordner LAZY (GET /mail/folders erst
                  beim ersten Öffnen). Kein Ordner-Wildwuchs in der Chip-Reihe. */}
              <div className="relative">
                <button
                  ref={foldersBtnRef}
                  type="button"
                  onClick={toggleFolders}
                  aria-expanded={foldersOpen}
                  aria-label={mt.ordner.weitereOrdner}
                  className={clsx(
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                    foldersOpen || ownFolderActive
                      ? "bg-accent-500 text-white"
                      : "bg-surface-3 text-text-2 hover:bg-surface-2 hover:text-text"
                  )}
                >
                  {mt.ordner.ordner}
                  <ChevronDown
                    className={clsx(
                      "w-3 h-3 transition-transform",
                      foldersOpen && "rotate-180"
                    )}
                  />
                  {otherUnread > 0 && !foldersOpen && (
                    <span
                      className={clsx(
                        "rounded-full px-1.5 text-[10px] tabular-nums leading-tight",
                        ownFolderActive
                          ? "bg-white/20 text-white"
                          : "bg-accent-500/15 text-accent-600"
                      )}
                    >
                      {otherUnread}
                    </span>
                  )}
                </button>
                {foldersOpen && folderMenuBox && createPortal(
                  <>
                    {/* Klick außerhalb schließt den Aufklapper (kein Doc-Listener). */}
                    <button
                      type="button"
                      aria-hidden="true"
                      tabIndex={-1}
                      data-testid="folders-backdrop"
                      onClick={closeFolders}
                      className="fixed inset-0 z-30 cursor-default"
                    />
                    {/* Portal + `fixed`: als `absolute` im scrollenden <aside>
                        (380px, overflow-y-auto) wurde das Menü horizontal
                        beschnitten — Namen und Ungelesen-Zähler lagen außerhalb. */}
                    <div
                      ref={folderMenuRef}
                      data-testid="folders-menu"
                      tabIndex={-1}
                      // `role="group"` gibt dem Behälter einen Namen, ohne
                      // Tastatur-Semantik zu erzwingen. Die eigentliche
                      // Baum-Semantik (role="tree"/treeitem/aria-level) bleibt
                      // vertagt: sie verlangt Roving-Tabindex und würde den
                      // gerade hergestellten Tab-Vertrag ersetzen.
                      role="group"
                      aria-label={mt.ordner.menuAria}
                      style={{
                        left: folderMenuBox.left,
                        top: folderMenuBox.top,
                        width: folderMenuBox.width,
                      }}
                      className="fixed z-30 flex max-h-72 flex-col rounded-lg border border-border bg-surface shadow-lg"
                    >
                    {/* Nur die LISTE scrollt — der Grenz-Hinweis steht darunter
                        außerhalb des Scrollbereichs. Als `sticky` im selben
                        Container überdeckte er beim Scrollen die Zeile darunter. */}
                    <div
                      data-testid="folders-scroll"
                      className="min-h-0 flex-1 overflow-y-auto py-1"
                    >
                      {foldersLoading ? (
                        <div className="px-3 py-2 text-xs text-text-3">
                          {mt.ordner.laedt}
                        </div>
                      ) : foldersError ? (
                        // Ausfall ≠ Leere. Ohne diesen Zweig meldet ein 500 sich
                        // als „Keine weiteren Ordner" und bleibt unbemerkt.
                        <div
                          role="alert"
                          data-testid="folders-error"
                          className="px-3 py-2 text-xs text-danger"
                        >
                          <div className="flex items-start gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                            <span>{mt.ordner.fehler}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => void loadFolders()}
                            className="mt-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-accent-600 hover:bg-surface-2"
                          >
                            <RefreshCw className="w-3 h-3" />
                            {ct.erneutVersuchen}
                          </button>
                        </div>
                      ) : folderRows.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-text-3">
                          {mt.ordner.keine}
                        </div>
                      ) : (
                        folderRows.map((row) => {
                          const { folder: f, depth, hasChildren } = row;
                          const id = folderKey(f);
                          const open = expandedFolders.has(f.folder_id);
                          // Einrückung nach 4 Ebenen gedeckelt: bei Tiefe 6 und
                          // Namen bis 49 Zeichen frisst eine feste Staffelung
                          // sonst den Text auf. Ab dort trägt der volle Pfad im
                          // `title` die Ebene, nicht immer mehr Rand.
                          const indent = 4 + Math.min(depth, 4) * 12;
                          return (
                            <div
                              key={f.folder_id}
                              data-testid="folder-row"
                              data-depth={depth}
                              data-active={folderView === id ? "true" : undefined}
                              className="flex items-center"
                              style={{ paddingLeft: indent }}
                            >
                              {hasChildren ? (
                                <button
                                  type="button"
                                  data-testid="folder-toggle"
                                  aria-expanded={open}
                                  aria-label={
                                    open
                                      ? mt.ordner.zuklappen(f.display_name)
                                      : mt.ordner.aufklappen(f.display_name)
                                  }
                                  onClick={() => toggleFolderExpanded(f.folder_id)}
                                  className="shrink-0 rounded p-0.5 text-text-3 hover:bg-surface-2 hover:text-text"
                                >
                                  <ChevronDown
                                    className={clsx(
                                      "w-3 h-3 transition-transform",
                                      !open && "-rotate-90"
                                    )}
                                  />
                                </button>
                              ) : (
                                <span className="w-4 shrink-0" aria-hidden="true" />
                              )}
                              <button
                                type="button"
                                onClick={() => selectFolder(id)}
                                // Pfeiltasten klappen auf/zu — Auf- und Zuklappen
                                // muss ohne Maus gehen.
                                onKeyDown={(e) => {
                                  if (!hasChildren) return;
                                  if (e.key === "ArrowRight" && !open) {
                                    e.preventDefault();
                                    toggleFolderExpanded(f.folder_id);
                                  } else if (e.key === "ArrowLeft" && open) {
                                    e.preventDefault();
                                    toggleFolderExpanded(f.folder_id);
                                  }
                                }}
                                // Voller PFAD als Tooltip — auf schmalen Geräten
                                // werden lange Namen gekürzt, und ab Ebene 5
                                // ist die Einrückung gedeckelt.
                                title={folderTitle(row)}
                                className={clsx(
                                  "flex min-w-0 flex-1 items-center justify-between gap-2 rounded px-1.5 py-1.5 text-left text-xs transition-colors hover:bg-surface-2",
                                  folderView === id
                                    ? "text-accent-600 font-medium"
                                    : "text-text-2"
                                )}
                              >
                                <span className="truncate" data-testid="folder-name">
                                  {f.display_name}
                                </span>
                                {f.unread_count > 0 && (
                                  <span
                                    data-testid="folder-unread"
                                    className="shrink-0 rounded-full bg-accent-500/15 text-accent-600 px-1.5 text-[10px] tabular-nums leading-tight"
                                  >
                                    {f.unread_count}
                                  </span>
                                )}
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                    </div>
                  </>,
                  document.body
                )}
              </div>
            </div>
            )}
            {/* Reihe 2 (E1): Suchfeld + Filter-Knopf. Absender, Zeitraum und
                Schnellwahl standen bis 19.08.2026 als drei weitere Reihen
                darunter — der Kopf war 7 Reihen / 184 px hoch, die erste
                Mailzeile begann bei 1280×800 erst auf y=390. Sie liegen jetzt
                im Popover; NICHTS fällt weg. */}
            <div className="flex items-start gap-1.5">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-3" />
              <input
                ref={searchBoxRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    clearSearch();
                  }
                  // Outlook-nativ: Eingabetaste sucht. Kein Gmail-Kürzel, und
                  // Entf/Leertaste bleiben unangetastet.
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    submitSearch();
                  }
                }}
                placeholder={mt.suche.platzhalter(shortcutHint("search"))}
                title={mt.suche.titel(shortcutKeys("search").join(" oder "))}
                aria-label={mt.suche.aria(shortcutKeys("search").join(" oder "))}
                className="w-full rounded-lg border border-border bg-canvas pl-8 pr-8 py-1.5 text-sm text-text placeholder:text-text-3 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500"
              />
              {searching ? (
                // Für Sprachausgaben KEIN stummes Bildchen: `role="status"`
                // macht die Stelle zur Meldung, der sr-only-Text gibt ihr einen
                // Namen. Ein `aria-label` allein reicht nicht — eine Live-Region
                // ohne Inhalt wird nicht vorgelesen. Das Haus kennt das Muster
                // vom Fehlerkasten (`role="alert"`, s. u.).
                <span
                  role="status"
                  data-mail-search-spinner
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex text-text-3"
                >
                  <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" />
                  <span className="sr-only">{mt.suche.laeuft}</span>
                </span>
              ) : search || filtersActive ? (
                <button
                  type="button"
                  aria-label={mt.suche.leeren}
                  title={mt.suche.leerenTitel}
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-text-3 hover:text-text hover:bg-surface-3 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : null}
            </div>
            {/* Filter-Knopf + Popover. Der Zähler-Chip ist Pflicht, nicht
                Zierde: ein Filter, den man nicht mehr sieht, muss sich melden —
                sonst steht eine gefilterte Liste ohne erkennbaren Grund da. */}
            <div
              className="relative shrink-0"
              data-mail-filter
              // Esc am WRAPPER, nicht am Popover: sonst wirkte die Taste nur,
              // wenn der Fokus schon in einem der Felder stand — direkt nach
              // dem Aufklappen liegt er aber auf dem Filter-Knopf, und Esc
              // fiel ins Leere (der Klick-Fänger blieb über der Seite liegen).
              onKeyDown={(e) => {
                if (e.key === "Escape" && filterOpen) {
                  e.stopPropagation();
                  setFilterOpen(false);
                }
              }}
            >
              <button
                type="button"
                data-mail-filter-trigger
                onClick={() => setFilterOpen((o) => !o)}
                aria-expanded={filterOpen}
                aria-haspopup="dialog"
                title={mt.suche.filterTitel}
                aria-label={mt.suche.filterAria(filterCount)}
                className={clsx(
                  "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors",
                  filterCount > 0 || filterOpen
                    ? "border-accent-500 bg-accent-500/10 text-accent-600"
                    : "border-border bg-canvas text-text-2 hover:bg-surface-2 hover:text-text"
                )}
              >
                <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" />
                <span>{mt.suche.filter}</span>
                {filterCount > 0 && (
                  <span
                    data-mail-filter-badge
                    className="rounded-full bg-accent-500 px-1.5 text-[10px] font-semibold leading-tight tabular-nums text-white"
                  >
                    {filterCount}
                  </span>
                )}
              </button>
              {filterOpen && (
                <>
                  {/* Klick außerhalb schließt (kein Doc-Listener). */}
                  <button
                    type="button"
                    aria-hidden="true"
                    tabIndex={-1}
                    onClick={() => setFilterOpen(false)}
                    className="fixed inset-0 z-20 cursor-default"
                  />
                  <div
                    data-mail-filter-pop
                    role="dialog"
                    aria-label={mt.suche.filterTitel}
                    className="absolute right-0 z-30 mt-1 w-[18.5rem] rounded-lg border border-border bg-surface p-3 shadow-lg"
                  >
            {/* Slice B — Von-/Zeitraum-Filter (kompakt). Werte hängen als
                from/since/until an denselben debounced /mail/search-Call. */}
            {/* Absender-Filter auf EIGENER Zeile: mit den beschrifteten
                Zeitraum-Feldern daneben blieben von ihm nur ~40 px übrig
                („Von (Absender)" war auf „Vo" abgeschnitten) — und zwei
                Nachbarfelder, die beide „von" lesen, sind nicht unterscheidbar. */}
            <div className="space-y-1.5">
              <input
                type="text"
                value={searchFrom}
                onChange={(e) => setSearchFrom(e.target.value)}
                placeholder={mt.suche.absender}
                aria-label={mt.suche.absenderAria}
                className="w-full rounded-md border border-border bg-canvas px-2 py-1 text-xs text-text placeholder:text-text-3 focus:outline-none focus:border-accent-500"
              />
              {/* Zwei gleich breite Spalten mit der Beschriftung ÜBER dem Feld:
                  nebeneinander in EINER Zeile brachen „von"/„bis" im Popover um
                  und ergaben zwei Zeilen mit je einem halb leeren Feld. */}
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                <span className="col-span-2 text-[11px] text-text-3">
                  {mt.suche.zeitraum}
                </span>
                {/* Zeitraum: SICHTBAR beschriftet („von"/„bis") — vorher nur per
                    aria-label unterschieden, was am Bildschirm zwei gleich
                    aussehende Felder ergab. Kein `type="date"`: das rendert im
                    englischen Chrome MM/DD/YYYY. */}
                <label className="flex min-w-0 flex-col gap-0.5 text-xs text-text-2">
                  {mt.suche.von}
                  <input
                    type="text"
                    value={sinceText}
                    onChange={(e) =>
                      setDateFilter(e.target.value, setSinceText, setSearchSince)
                    }
                    onFocus={() => setSinceFocused(true)}
                    onBlur={() => {
                      setSinceFocused(false);
                      // reine String-Umformung (kein `new Date`) → keine Zeitzonen-
                      // Verschiebung beim Normalisieren von „1.2." auf „01.02.2026".
                      if (searchSince) setSinceText(isoToDeDate(searchSince));
                    }}
                    placeholder="TT.MM.JJJJ"
                    inputMode="numeric"
                    size={10}
                    aria-label={mt.suche.vonAria}
                    aria-invalid={sinceInvalid || undefined}
                    title={mt.suche.vonTitel}
                    data-mail-since
                    className={clsx(
                      "w-full min-w-0 rounded-md border bg-canvas px-2 py-1 text-xs text-text placeholder:text-text-3 focus:outline-none",
                      sinceInvalid
                        ? "border-danger focus:border-danger"
                        : "border-border focus:border-accent-500"
                    )}
                  />
                </label>
                <label className="flex min-w-0 flex-col gap-0.5 text-xs text-text-2">
                  {mt.suche.bis}
                  <input
                    type="text"
                    value={untilText}
                    onChange={(e) =>
                      setDateFilter(e.target.value, setUntilText, setSearchUntil)
                    }
                    onFocus={() => setUntilFocused(true)}
                    onBlur={() => {
                      setUntilFocused(false);
                      if (searchUntil) setUntilText(isoToDeDate(searchUntil));
                    }}
                    placeholder="TT.MM.JJJJ"
                    inputMode="numeric"
                    size={10}
                    aria-label={mt.suche.bisAria}
                    aria-invalid={untilInvalid || undefined}
                    title={mt.suche.bisTitel}
                    data-mail-until
                    className={clsx(
                      "w-full min-w-0 rounded-md border bg-canvas px-2 py-1 text-xs text-text placeholder:text-text-3 focus:outline-none",
                      untilInvalid
                        ? "border-danger focus:border-danger"
                        : "border-border focus:border-accent-500"
                    )}
                  />
                </label>
              </div>
              {/* Schnellwahl: setzt „von" auf heute minus N Tage. Eigene Zeile —
                  im 264-px-Popover stünden fünf Elemente nebeneinander sonst
                  gebrochen. */}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="w-full text-[11px] text-text-3">
                  {mt.suche.schnellwahl}
                </span>
                <button
                  type="button"
                  onClick={() => setPresetDays(7)}
                  className="rounded-md border border-border px-2 py-1 text-xs text-text-2 hover:bg-surface-2 hover:text-text transition-colors"
                >
                  {mt.suche.tage7}
                </button>
                <button
                  type="button"
                  onClick={() => setPresetDays(30)}
                  className="rounded-md border border-border px-2 py-1 text-xs text-text-2 hover:bg-surface-2 hover:text-text transition-colors"
                >
                  {mt.suche.tage30}
                </button>
              </div>
              {/* Unlesbares Datum sichtbar melden — statt den Filter still
                  weglassen und ein „falsches" Ergebnis zeigen. */}
              {(sinceInvalid || untilInvalid) && (
                <p data-mail-date-error className="text-xs text-danger">
                  {sinceInvalid && untilInvalid
                    ? mt.suche.datumBeideFalsch
                    : sinceInvalid
                      ? mt.suche.datumVonFalsch
                      : mt.suche.datumBisFalsch}
                </p>
              )}
            </div>
                  </div>
                </>
              )}
            </div>
            </div>
            {/* Filter sind jetzt zugeklappt — also sagt eine Zeile, DASS
                gefiltert wird, und bietet genau einen Weg zurück. Ohne sie
                stünde eine gefilterte Liste ohne sichtbaren Grund da. */}
            {filterCount > 0 && (
              <p
                data-mail-filter-active
                className="mt-1.5 flex flex-wrap items-center gap-x-2 text-[11px] leading-tight text-text-3"
              >
                <span>{mt.suche.filterAktiv(filterCount)}</span>
                <button
                  type="button"
                  data-mail-filter-reset
                  onClick={resetFilters}
                  className="font-medium text-accent-600 hover:underline"
                >
                  {mt.suche.filterZuruecksetzen}
                </button>
              </p>
            )}
            {/* Die Liste unten gehört zu einer ANDEREN Frage als der im Feld.
                Steht bewusst IM klebenden Kopf, direkt zwischen Feld und Liste:
                so bleibt die Zuordnung auch beim Scrollen sichtbar (die
                Deckel-Quittung brauchte dafür ein zweites Element am
                Listenende). `role="status"` sagt es auch den Sprachausgaben. */}
            {staleList && (
              <p
                data-mail-search-stale
                role="status"
                className="mt-2 flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning-soft px-2.5 py-1.5 text-xs leading-snug text-text-2"
              >
                <Info className="mt-px h-3.5 w-3.5 shrink-0 text-warning" />
                <span>
                  {searching
                    ? mt.suche.veraltetLaeuft(shownLabel)
                    : mt.suche.veraltet(shownLabel)}
                </span>
              </p>
            )}
          </div>
          {/* X1 First-Run-Hinweis: einmaliger, dismissbarer Inline-Banner im
              Posteingang (unter dem sticky Kopf, über der Liste). Persistenz via
              localStorage (shortcuts.ts). Kein Browser-Dialog. */}
          {!kbdHintSeen && folderView === "inbox" && !sharedScope && (
            <div
              data-kbd-firstrun
              className="mx-2 mt-2 flex items-start gap-2 rounded-lg border border-accent-300 bg-accent-50/60 px-3 py-2 text-xs text-text-2"
            >
              <Keyboard className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-600" />
              <span className="flex-1">
                {mt.kuerzel.erstHinweisNeu}
                <kbd className="rounded-md border border-border px-1 py-0.5 text-[10px]">
                  {shortcutHint("palette")}
                </kbd>
                {mt.kuerzel.erstHinweisPalette}
                <kbd className="rounded-md border border-border px-1 py-0.5 text-[10px]">
                  {shortcutHint("help")}
                </kbd>
                {mt.kuerzel.erstHinweisUebersicht}
              </span>
              <button
                type="button"
                data-kbd-firstrun-dismiss
                onClick={() => {
                  setMailKbdHintSeen();
                  setKbdHintSeen(true);
                }}
                aria-label={mt.kuerzel.hinweisSchliessen}
                className="shrink-0 rounded p-0.5 text-text-3 hover:bg-surface-3 hover:text-text"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {/* Auffindbarkeit des Kontextmenüs: dieselbe Bauform wie der
              Kürzel-Banner darüber (dezent, inline, dismissbar) — kein Modal,
              kein Dauer-Banner. Er geht auch von selbst, sobald jemand das Menü
              zum ersten Mal öffnet (`openRowMenu`); wer es gefunden hat, braucht
              den Satz nicht mehr. Ein Symbol ohne Ersatz wegzunehmen und darauf
              zu hoffen, dass der Rechtsklick erraten wird, wäre kein Aufräumen.
              Nicht im fremden Funktionspostfach — dort gibt es kein Menü. */}
          {!rowMenuHintSeen && folderView === "inbox" && !sharedScope && (
            <div
              data-rowmenu-firstrun
              className="mx-2 mt-2 flex items-start gap-2 rounded-lg border border-accent-300 bg-accent-50/60 px-3 py-2 text-xs text-text-2"
            >
              <MousePointerClick className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-600" />
              <span className="flex-1">
                {mt.kuerzel.rechtsklickNeu}
                <strong className="font-medium text-text">
                  {mt.kuerzel.rechtsklickWort}
                </strong>
                {mt.kuerzel.rechtsklickText}
                <kbd className="rounded-md border border-border px-1 py-0.5 text-[10px]">
                  {mt.kuerzel.rechtsklickTaste}
                </kbd>
                .
              </span>
              <button
                type="button"
                data-rowmenu-firstrun-dismiss
                onClick={() => {
                  setMailRowMenuHintSeen();
                  setRowMenuHintSeen(true);
                }}
                aria-label={mt.kuerzel.hinweisSchliessen}
                className="shrink-0 rounded p-0.5 text-text-3 hover:bg-surface-3 hover:text-text"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {/* Tiefen-Quittung: sagt, was tatsächlich geladen wurde (Anzahl +
              Zeitraum). Steht ÜBER der Liste, damit sie auch im leeren Zustand
              sichtbar ist. Bewusst leise (11px, text-3) — die Liste soll nicht
              lärmen. */}
          {!searchActive && scopeLoaded && (
            <p
              data-mail-depth-status
              className="px-3 pt-2 pb-1 text-[11px] leading-tight text-text-3"
            >
              {depthStatus}
            </p>
          )}
          {/* F-35 — Bereichs-Quittung: WO wurde gesucht? Ohne sie war der
              Bereich unsichtbar, und eine postfachweite Trefferliste sah aus
              wie das Ergebnis einer Ordner-Suche („zeigt mir trotzdem
              Ergebnisse im Posteingang"). Dieselbe leise Bauform wie die
              Tiefen-Quittung darüber — und mit genau EINEM Umschalter, damit
              der Nutzer den Bereich ändern kann, statt ihn zu erraten.
              Bei einem eigenen Unterordner gibt es nichts umzuschalten: der
              Server kennt für ihn keinen Bereichs-Wert (geschlossene Liste),
              also sagt die Zeile das und bietet keinen toten Knopf an. */}
          {searchActive && !searchError && (
            <p
              data-mail-search-scope
              className="flex flex-wrap items-center gap-x-2 px-3 pt-2 pb-1 text-[11px] leading-tight text-text-3"
            >
              {!suchbarerOrdner ? (
                <span>{mt.suche.bereichGanzesPostfachFest}</span>
              ) : sucheGanzesPostfach ? (
                <>
                  <span>{mt.suche.bereichGanzesPostfach}</span>
                  <button
                    type="button"
                    data-mail-scope-toggle
                    onClick={() => setSucheGanzesPostfach(false)}
                    className="font-medium text-accent-600 hover:underline"
                  >
                    {mt.suche.bereichNurOrdner(suchbereichName)}
                  </button>
                </>
              ) : (
                <>
                  <span>{mt.suche.bereichInOrdner(suchbereichName)}</span>
                  <button
                    type="button"
                    data-mail-scope-toggle
                    onClick={() => setSucheGanzesPostfach(true)}
                    className="font-medium text-accent-600 hover:underline"
                  >
                    {mt.suche.bereichWechselGanzesPostfach}
                  </button>
                </>
              )}
            </p>
          )}
          {/* Deckel-Quittung der Suche: Microsoft liefert zu einem Begriff
              höchstens ~275 Treffer und sagt danach nicht mehr, ob es weitere
              gibt. Ohne diesen Hinweis sähe eine abgeschnittene Liste aus wie
              das vollständige Ergebnis. Sortiert wird nach Datum, nicht nach
              Relevanz — deshalb steht hier „die jüngsten", nicht „die besten".

              Diese Meldung stand zuerst als 11-px-Grau unter dem lauteren
              Tastenkürzel-Hinweis — also an der schwächsten Stelle der Seite,
              und beim ersten Scrollen war sie weg. Für einen Satz, dessen
              einziger Zweck „diese Liste ist NICHT vollständig" ist, war das
              die falsche Stelle. Jetzt: eigene abgesetzte Zeile mit Zeichen,
              in der Fließtext-Farbe (text-2) — und zusätzlich ein Abschluss am
              Listenende, wo die Frage „war das alles?" tatsächlich entsteht.
              Bewusst KEIN Warn-Rot: es ist kein Fehler, sondern eine Grenze. */}
          {searchActive && !searchError && searchResults?.truncated && (
            <p
              data-mail-search-capped
              className="mx-2 mt-2 flex items-start gap-1.5 rounded-md border border-border bg-surface-2 px-2.5 py-1.5 text-xs leading-snug text-text-2"
            >
              <Info className="mt-px h-3.5 w-3.5 shrink-0 text-text-3" />
              <span>{mt.suche.gedeckelt(rows.length)}</span>
            </p>
          )}
          {inDraftsView ? (
            // Entwürfe-Ansicht (P1.8, Slice B): eigene Datenquelle, Composer-Resume.
            draftsLoading && !drafts ? (
              <ListSkeleton />
            ) : (drafts ?? []).length === 0 ? (
              <EmptyState
                icon={<Pencil className="w-6 h-6" />}
                title={mt.entwuerfe.leerTitel}
                hint={mt.entwuerfe.leerHinweis}
              />
            ) : (
              (drafts ?? []).map((d) => (
                <DraftRow
                  key={d.draft_id}
                  draft={d}
                  onOpen={setResumeDraft}
                  onDelete={deleteDraft}
                />
              ))
            )
          ) : (loading && !inbox && !inFolderView) ||
            (inFolderView && folderLoading && !folderMessages) ? (
            <ListSkeleton />
          ) : searchError ? (
            // Ausfall ≠ Leere — steht VOR jedem „Keine …"-Zweig, sonst gewinnt
            // wieder die irreführende Empty-State-Meldung.
            <div
              role="alert"
              data-mail-search-error
              className="mx-2 mt-2 rounded-lg border border-danger/40 bg-danger/5 px-3 py-3 text-xs text-danger"
            >
              <div className="flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                <span>{mt.suche.fehler}</span>
              </div>
              <button
                type="button"
                data-mail-search-retry
                onClick={submitSearch}
                className="mt-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-accent-600 hover:bg-surface-2"
              >
                <RefreshCw className="w-3 h-3" />
                {ct.erneutVersuchen}
              </button>
            </div>
          ) : rows.length === 0 ? (
            inFolderView ? (
              <>
                <EmptyState
                  icon={<InboxIcon className="w-6 h-6" />}
                  title={mt.liste.leerOrdnerTitel}
                  hint={mt.liste.leerOrdnerHinweis}
                />
                {/* Der leere Ordner ist derselbe Fall wie die zu kurze Liste,
                    nur zugespitzt: der Hinweis nennt „im gewählten Zeitraum" als
                    Grund — dann muss der Weg, den Zeitraum zu weiten, auch hier
                    stehen und nicht nur unter einer Liste, die es nicht gibt. */}
                {mehrLadenKnopf}
              </>
            ) : searchActive || filtersActive ? (
              <EmptyState
                icon={<Search className="w-6 h-6" />}
                title={mt.liste.leerTrefferTitel}
                hint={mt.liste.leerTrefferHinweis}
              />
            ) : (
              <EmptyState
                icon={<InboxIcon className="w-6 h-6" />}
                title={mt.liste.leerPostfachTitel}
                hint={mt.liste.leerPostfachHinweis}
              />
            )
          ) : (
            <>
              {rows.map((m) => (
                <ListRow
                  key={m.internet_message_id}
                  item={m}
                  active={selected === m.internet_message_id}
                  onSelect={select}
                  tracked={trackedImids.has(m.internet_message_id)}
                  onArchive={archiveMessage}
                  onDelete={deleteMessage}
                  onOpenMenu={openRowMenu}
                  // Fremdes Funktionspostfach: es gäbe KEINEN Eintrag (kein
                  // Verfassen, keine Triage, kein Nachverfolgen) → gar kein
                  // Menü, und der native Rechtsklick bleibt unangetastet.
                  menuEnabled={!sharedScope}
                  readOnly={inFolderView || sharedScope}
                  // F-04: der Nachverfolgungs-Marker hängt NICHT mehr an
                  // `readOnly` (Ordner-Ansicht), aber weiterhin voll am
                  // Postfach-Gate — im fremden Funktionspostfach ändert sich nichts.
                  allowTrack={!sharedScope}
                  zeigeEmpfaenger={istAusgehend(m)}
                />
              ))}
              {/* Listen-Abschluss bei erreichtem Deckel. Der Hinweis oben wird
                  weggescrollt, sobald jemand die 275 Zeilen durchgeht — und
                  genau am Ende entsteht die Frage „war das alles?". Deshalb
                  steht die Antwort auch dort, wo sie gebraucht wird. */}
              {searchActive && searchResults?.truncated && (
                <p
                  data-mail-search-capped-end
                  className="border-t border-border px-3 py-3 text-xs leading-snug text-text-2"
                >
                  {mt.suche.gedeckeltEnde(rows.length)}
                </p>
              )}
              {mehrLadenKnopf}
            </>
          )}
        </aside>

        {/* Detail — mobil nur sichtbar wenn selektiert. min-w-0: Grid-Items haben
            min-width:auto → ohne das läuft die Detail-Spalte (Email + breit
            gezogenes SF-Panel) über den 1fr-Track/Viewport hinaus. */}
        <section
          className={clsx(
            "min-w-0 min-h-0 bg-canvas",
            selected ? "block" : "hidden md:block"
          )}
        >
          {selected ? (
            <DetailPane
              imid={selected}
              onBack={clearSelection}
              isRead={
                (
                  inbox?.messages.find(
                    (m) => m.internet_message_id === selected
                  ) ??
                  searchResults?.messages.find(
                    (m) => m.internet_message_id === selected
                  ) ??
                  folderMessages?.messages.find(
                    (m) => m.internet_message_id === selected
                  )
                )?.is_read ?? true
              }
              onToggleRead={markRead}
              onArchive={archiveMessage}
              onMove={openMoveDialog}
              onDelete={deleteMessage}
              colleagues={colleagues}
              ownerEmail={inbox?.mailbox ?? ""}
              readOnly={inFolderView || sharedScope}
              mailbox={sharedScope ? activeMailbox : ""}
              actionsRef={detailActionsRef}
              pendingAction={pendingRowAction}
              onPendingActionDone={clearPendingRowAction}
            />
          ) : (
            <div className="hidden md:grid h-full place-items-center p-6">
              <EmptyState
                icon={inDraftsView ? <Pencil className="w-6 h-6" /> : <MailIcon className="w-6 h-6" />}
                title={
                  inDraftsView
                    ? mt.liste.keinEntwurfGewaehltTitel
                    : mt.liste.keineMailGewaehltTitel
                }
                hint={
                  inDraftsView
                    ? mt.liste.keinEntwurfGewaehltHinweis
                    : mt.liste.keineMailGewaehltHinweis
                }
              />
            </div>
          )}
        </section>
        {/* Zeilen-Kontextmenü — EINE Instanz für die ganze Liste (die Position
            kommt vom Zeiger, nicht von der Zeile). Steht außerhalb des
            scrollenden <aside>, portalisiert sich aber ohnehin an <body>. */}
        {rowMenu && (
          <MailRowContextMenu
            x={rowMenu.x}
            y={rowMenu.y}
            label={rowMenu.item.subject || mt.liste.keinBetreff}
            items={rowMenuItems(rowMenu.item)}
            onClose={closeRowMenu}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="shrink-0 px-6 pt-6 bg-canvas">
        {header}
        <Tabs
          className="mt-4"
          value={tab}
          onChange={setTab}
          items={[
            {
              id: "inbox",
              label: (
                <span className="inline-flex items-center gap-1.5">
                  {mt.seite.reiterPosteingang}
                  <UnreadPill count={unreadCount} />
                </span>
              ),
            },
            {
              id: "follow",
              // Zähler wie beim Posteingang: der Auffangkorb ist nur dann ein
              // Fortschritt gegenüber dem Schweigen, wenn man ihn sieht, ohne
              // den Tab zu öffnen. Die Pille blendet sich bei 0 selbst aus.
              label: (
                <span className="inline-flex items-center gap-1.5">
                  {mt.seite.reiterNachverfolgung}
                  <DecisionCountPill
                    count={decisionCount}
                    label={mt.nachfass.nachfassZaehler}
                  />
                </span>
              ),
            },
            { id: "vorlagen", label: mt.seite.reiterVorlagen },
            { id: "fragen", label: mt.seite.reiterFragen },
          ]}
        />
      </div>

      {tab === "vorlagen" ? (
        <TemplatesTab />
      ) : tab === "fragen" ? (
        <SfQueryTab />
      ) : tab === "follow" ? (
        <FollowUpTab
          tracks={tracks}
          loading={tracksLoading}
          error={tracksError}
          onReload={() => void loadTracks()}
          onApply={applyTrack}
          onRemove={removeTrack}
          colleagues={colleagues}
        />
      ) : (
        inboxContent
      )}

      {/* Neue Mail: EIN Composer (ReplyPanel) im zentrierten Modal, ohne imid.
          Modal ohne eigenen Titel → die ReplyPanel-Kopfzeile trägt Titel +
          Schließen (kein Doppel-Header). */}
      {composeNew && (
        <Modal open onClose={() => setComposeNew(false)} size="lg" bodyClassName="p-3">
          <ReplyPanel
            kind="new"
            mode="blank"
            category={null}
            subCategory={null}
            replyTo=""
            replySubject=""
            recordLinks={[]}
            colleagues={colleagues}
            ownerEmail={inbox?.mailbox ?? ""}
            onClose={() => setComposeNew(false)}
          />
        </Modal>
      )}

      {/* Abwesenheitsnotiz (P2.12): zentriertes Modal, an/aus + Zeitraum/Texte.
          Primt beim Öffnen via GET /mail/out-of-office; Speichern → EIN
          PUT /mail/out-of-office. */}
      {oooOpen && <OutOfOfficeModal onClose={() => setOooOpen(false)} />}

      {/* F-05 — „In Ordner verschieben": zentriertes Modal (kein Side-Drawer,
          keine Browser-Dialoge — Hausregel). Die Ziel-Menge ist GENAU die des
          Ordner-Aufklappers (`folders` aus GET /mail/folders, `folderKey` als
          Wert) — kein Freitext-ZIEL, und der Server prüft dieselbe Menge noch
          einmal. Das Filterfeld (F-15) schränkt nur diese Menge ein; es kann
          niemals ein Ziel erzeugen, das kein echter Ordner ist. Fehler und Leere
          sind getrennt, aus demselben Grund wie im Aufklapper: ein 500 darf
          nicht als „keine Ordner" erscheinen. */}
      {moveFor && (
        <Modal
          open
          onClose={() => setMoveFor(null)}
          title={mt.ordner.verschiebenTitel}
          description={mt.ordner.verschiebenBeschreibung}
          icon={<FolderInput className="w-4 h-4" />}
          size="sm"
          bodyClassName="p-0"
          // Fokus beim Öffnen ins Filterfeld (sonst nimmt Radix den
          // Schließen-Knopf im Kopf, das erste fokussierbare Element).
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            moveFilterRef.current?.focus();
          }}
          // … und beim Schließen zurück an den Auslöser.
          onCloseAutoFocus={(e) => {
            const el = moveTriggerRef.current;
            if (!el || !document.contains(el)) return; // Radix macht den Rest
            e.preventDefault();
            el.focus();
          }}
        >
          <div data-testid="move-dialog" className="flex flex-col">
            {/* Filter: steht ÜBER der Liste und scrollt nicht mit weg. Nur
                sinnvoll, wenn es überhaupt Ordner gibt — im Fehler- und
                Leerzustand wäre ein Suchfeld eine leere Geste. */}
            {!foldersLoading && !foldersError && (folders ?? []).length > 0 && (
              <div className="shrink-0 border-b border-border px-3 py-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-3" />
                  <input
                    ref={setMoveFilterEl}
                    type="text"
                    data-testid="move-filter"
                    value={moveFilter}
                    onChange={(e) => setMoveFilter(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        // Die Eingabetaste übernimmt NUR, wenn genau ein Ordner
                        // übrig ist. Bei 0 oder mehreren Treffern passiert
                        // nichts: das Feld ist ein Filter, kein Ziel-Eingabe-
                        // feld — ein getippter Text darf nie zu einem Verschieben
                        // führen.
                        if (moveRows.length !== 1) return;
                        const ziel = moveRows[0].folder;
                        const imid = moveFor;
                        setMoveFor(null);
                        moveMessage(imid, folderKey(ziel), ziel.display_name);
                      }
                    }}
                    placeholder={mt.ordner.filterPlatzhalter}
                    aria-label={mt.ordner.filterAria}
                    className="w-full rounded-lg border border-border bg-canvas pl-8 pr-2 py-1.5 text-sm text-text placeholder:text-text-3 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500"
                  />
                </div>
              </div>
            )}
            <div
              ref={moveListRef}
              data-testid="move-folder-list"
              className="max-h-80 overflow-y-auto py-1"
              style={moveQuery && moveListH ? { minHeight: moveListH } : undefined}
            >
            {/* F-15: Zielordner-Vorschlag. Steht INNERHALB der Liste, nicht
                darüber — nur so hält der eingefrorene Behälter die Höhe, wenn
                der Block beim Tippen verschwindet. Optisch bewusst leise: die
                gewohnten Zeilen, eine Überschrift in Text-3, eine Trennlinie.
                Kein Farbfeld, kein Symbol-Akzent — der Vorschlag soll gefunden,
                nicht beworben werden (Ruhe durch Subtraktion). */}
            {zeigeVorschlaege && (
              <div data-testid="move-suggestions" className="pb-1">
                {/* „Meist", nicht „Zuletzt": gezählt wird die HÄUFIGKEIT über
                    ein Jahr, nicht die jüngste Ablage. Eine Überschrift, die
                    Aktualität behauptet, während darunter eine Häufigkeit
                    steht, wäre genau die Art Beschriftung, der man nach dem
                    ersten Nachrechnen nicht mehr glaubt. */}
                <div className="px-3 pt-1 pb-1.5 text-[10px] font-medium uppercase tracking-wide text-text-3">
                  {mt.ordner.vorschlaegeTitel}
                </div>
                {moveVorschlaege.map(({ vorschlag, row }) => (
                  <div key={`v-${row.folder.folder_id}`} className="flex items-center pr-2">
                    <span className="w-4 shrink-0" aria-hidden="true" />
                    <button
                      type="button"
                      data-testid="move-suggestion"
                      data-dest={folderKey(row.folder)}
                      title={folderTitle(row)}
                      onClick={() => {
                        const imid = moveFor;
                        setMoveFor(null);
                        moveMessage(
                          imid,
                          folderKey(row.folder),
                          row.folder.display_name
                        );
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 rounded px-1.5 py-1.5 text-left text-xs text-text-2 transition-colors hover:bg-surface-2 hover:text-text"
                    >
                      <span className="truncate font-medium">
                        {row.folder.display_name}
                      </span>
                      {/* Die Begründung. `count` statt Prozent, weil „4 von 5"
                          nachprüfbar ist und „80 %" nur geglaubt werden kann. */}
                      <span
                        data-testid="move-suggestion-grund"
                        className="ml-auto shrink-0 truncate text-[10px] text-text-3"
                      >
                        {mt.ordner.vorschlagGrund(vorschlag.count, vorschlag.domain)}
                      </span>
                    </button>
                  </div>
                ))}
                <div className="mx-3 mt-1 border-b border-border" />
              </div>
            )}
            {foldersLoading ? (
              <div className="px-4 py-3 text-xs text-text-3">{mt.ordner.laedt}</div>
            ) : foldersError ? (
              <div
                role="alert"
                data-testid="move-folders-error"
                className="px-4 py-3 text-xs text-danger"
              >
                <div className="flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <span>{mt.ordner.fehler}</span>
                </div>
                <button
                  type="button"
                  onClick={() => void loadFolders()}
                  className="mt-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-accent-600 hover:bg-surface-2"
                >
                  <RefreshCw className="w-3 h-3" />
                  {ct.erneutVersuchen}
                </button>
              </div>
            ) : moveRows.length === 0 ? (
              // Zwei verschiedene Leeren, zwei verschiedene Sätze: „das Postfach
              // hat keine Ordner" ist etwas anderes als „dein Filter trifft
              // keinen". Ruhige Aussage in Textfarbe — kein Fehlerton.
              <div data-testid="move-empty" className="px-4 py-3 text-xs text-text-3">
                {moveQuery
                  ? mt.ordner.filterOhneTreffer(moveFilter.trim())
                  : mt.ordner.keine}
              </div>
            ) : (
              moveRows.map((row) => {
                const { folder: f, depth, hasChildren } = row;
                // Beim Filtern ist die Liste flach (Treffer aus dem ganzen
                // Baum). Dann trägt sie keinen Aufklapp-Pfeil und keine
                // Staffelung — beides wäre eine Hierarchie-Behauptung über eine
                // Trefferliste. Stattdessen steht der Elternpfad daneben: ohne
                // ihn wären „China" und „06 China" zwei gleich aussehende
                // Zeilen.
                const gefiltert = moveQuery.length > 0;
                return (
                <div
                  key={f.folder_id}
                  data-testid="move-folder-row"
                  data-depth={gefiltert ? 0 : depth}
                  className="flex items-center pr-2"
                  style={{ paddingLeft: gefiltert ? 8 : 8 + Math.min(depth, 4) * 12 }}
                >
                  {gefiltert ? (
                    <span className="w-4 shrink-0" aria-hidden="true" />
                  ) : hasChildren ? (
                    <button
                      type="button"
                      data-testid="move-folder-toggle"
                      aria-expanded={expandedFolders.has(f.folder_id)}
                      aria-label={
                        expandedFolders.has(f.folder_id)
                          ? mt.ordner.zuklappen(f.display_name)
                          : mt.ordner.aufklappen(f.display_name)
                      }
                      onClick={() => toggleFolderExpanded(f.folder_id)}
                      className="shrink-0 rounded p-0.5 text-text-3 hover:bg-surface-2 hover:text-text"
                    >
                      <ChevronDown
                        className={clsx(
                          "w-3 h-3 transition-transform",
                          !expandedFolders.has(f.folder_id) && "-rotate-90"
                        )}
                      />
                    </button>
                  ) : (
                    <span className="w-4 shrink-0" aria-hidden="true" />
                  )}
                  <button
                    type="button"
                    data-testid="move-folder"
                    data-dest={folderKey(f)}
                    // Voller Pfad als Tooltip: ab Ebene 5 ist die Einrückung
                    // gedeckelt, der Pfad hält die Ebene trotzdem eindeutig.
                    title={folderTitle(row)}
                    onClick={() => {
                      const imid = moveFor;
                      setMoveFor(null);
                      moveMessage(imid, folderKey(f), f.display_name);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded px-1.5 py-1.5 text-left text-xs text-text-2 transition-colors hover:bg-surface-2 hover:text-text"
                  >
                    <span className="truncate">{f.display_name}</span>
                    {gefiltert && row.path.length > 0 && (
                      <span
                        data-testid="move-folder-path"
                        className="ml-auto shrink-0 truncate text-[10px] text-text-3"
                      >
                        {row.path.join(" / ")}
                      </span>
                    )}
                  </button>
                </div>
                );
              })
            )}
            </div>
          </div>
        </Modal>
      )}

      {/* X1: Kürzel-Übersicht (F1 / ? / „Kürzel"-Chip / Palette). Zentriertes
          Radix-Modal aus shortcuts.ts — die EINE sichtbare Quelle des R1-Sets. */}
      <ShortcutsOverlay open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Entwurf fortsetzen (P1.8, Slice B): DERSELBE Composer wie „Neue Mail",
          vorbefüllt aus dem Entwurf; Weiter-Tippen PATCHt denselben Graph-Entwurf.
          Beim Schließen die Entwürfe-Liste neu laden (Inhalt kann sich geändert
          haben / via Slice C gesendet worden sein → verschwindet). */}
      {resumeDraft && (
        <Modal
          open
          onClose={() => {
            setResumeDraft(null);
            if (folderView === "drafts") loadDrafts();
          }}
          size="lg"
          bodyClassName="p-3"
        >
          <ReplyPanel
            kind="new"
            mode="blank"
            category={null}
            subCategory={null}
            replyTo=""
            replySubject=""
            recordLinks={[]}
            colleagues={colleagues}
            ownerEmail={inbox?.mailbox ?? ""}
            draftId={resumeDraft.draft_id}
            initialSubject={resumeDraft.subject}
            initialTo={resumeDraft.to.join(", ")}
            initialCc={resumeDraft.cc.join(", ")}
            initialBcc={resumeDraft.bcc.join(", ")}
            initialBody={resumeDraft.body_html}
            onClose={() => {
              setResumeDraft(null);
              if (folderView === "drafts") loadDrafts();
            }}
          />
        </Modal>
      )}
    </div>
  );
}
